import React, { useState, useEffect, useCallback, useRef } from 'react';
import PartnerReassignPanel from '../components/admin/PartnerReassignPanel';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { parseModelAmount } from '../utils/parseModelNumber';
import axios from 'axios';
import { incomeTypesOf, INCOME_TYPES, INCOME_TYPE_LABELS } from '../constants/sectionModel';

import { isBorrowerViewReadOnly, currentStaffRole } from '../constants/staffRoleAccess';
import AdminLayout from '../components/AdminLayout';
import MessageAttachment from '../components/MessageAttachment';
import RichMessageBody, { hasRichBody } from '../components/RichMessageBody';
import StaffComposer from '../components/admin/StaffComposer';
import { STATUS_COLORS, MOVE_STAGE_OPTIONS, CLOSING_STAGES, HOME_SHOPPING_STAGES, POST_ACCEPTANCE_STAGES } from '../constants/statusColors';
import { DOORLY_PROGRAM_TERMS } from '../constants/doorlyProgramTerms';
import { getCreditScore, getCreditScoreColor } from '../utils/creditScore';
import AddressAutocomplete from '../components/AddressAutocomplete';
import SmartStartAdminCard from '../components/admin/SmartStartAdminCard';
import AbPsaInAppPanel from '../components/admin/AbPsaInAppPanel';
import JointIntentAdminCard from '../components/admin/JointIntentAdminCard';
import GuidedFlowReopenCard from '../components/admin/GuidedFlowReopenCard';
import TestAccountCard from '../components/admin/TestAccountCard';
import ApplicationEditsCard from '../components/admin/ApplicationEditsCard';
import DisclosureDefaultsCard from '../components/admin/DisclosureDefaultsCard';
import PropertyModule, { deriveCurrentStep as derivePropertyModuleStep, STEPS as PROPERTY_MODULE_STEPS } from '../components/admin/PropertyModule';
import ClosingModule, { STEPS as CLOSING_MODULE_STEPS } from '../components/admin/ClosingModule';
import EmailDeliveryPill from '../components/admin/EmailDeliveryPill';
import ReviewModule from '../components/admin/ReviewModule';
import RereadStoredReport from '../components/admin/RereadStoredReport';
import CreditRereadSnapshot from '../components/admin/CreditRereadSnapshot';
import ModuleNotes from '../components/admin/ModuleNotes';
import { useStaffRole } from '../hooks/useStaffRole';

import { pathOptions as RC_PATH_OPTIONS } from '../constants/readycheckPaths';
import { isOpenNeed, isAwaitingBorrower } from '../constants/needStatus';
import { identityAdminLabel } from '../utils/identityAdminLabel';
import PreClosingChecksPanel from '../components/admin/PreClosingChecksPanel';
import ReapplyEarlyCard from '../components/admin/ReapplyEarlyCard';
import GuidedReviewTab from '../components/admin/guidedReview/GuidedReviewTab';
import GuidedModuleActions from '../components/admin/guidedReview/GuidedModuleActions';
import { currentDurationText } from '../constants/addressHistory';
import { FlagList, GuidedIncomeStep, GuidedAssetsStep, StepItems, DocumentsReadOnly, IdentityRecordRow, RentHistoryTable, RentalPropertiesStaff } from '../components/admin/guidedReview/GuidedModuleSteps';
import BankStatementComparison from '../components/admin/guidedReview/BankStatementComparison';
import CreditRuleFlags from '../components/admin/guidedReview/CreditRuleFlags';
import Guided1003Step from '../components/admin/guidedReview/Guided1003Step';   // the Module's "1003 and summary" step (2026-10-27)
import { MloOfRecordLine } from '../components/admin/MloOfRecord';   // the MLO of record on the header (push B, 2026-10-31)
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 THE INCOME TYPE, EDITED AS THE LIST IT IS (one source — constants/sectionModel incomeTypesOf, 2026-10-22). The single
// select wrote one capitalised value to the mirror only, so the array every reader trusts never changed — and on a
// W-2 + 1099 borrower, picking one would now drop the other. Checkboxes over the canonical types; the server writes
// both keys (routes/admin.js PATCH /borrowers/:id/profile).
function IncomeTypePicker({ value, onChange }) {
  const v = Array.isArray(value) ? value : [];
  return (
    <div data-testid="admin-income-type-picker" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', fontSize: '12px' }}>
      {INCOME_TYPES.map((t) => (
        <label key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" data-testid={`admin-income-type-${t}`} checked={v.includes(t)}
            onChange={(e) => onChange(e.target.checked ? INCOME_TYPES.filter((x) => x === t || v.includes(x)) : v.filter((x) => x !== t))} />
          {INCOME_TYPE_LABELS[t]}
        </label>
      ))}
    </div>
  );
}
const incomeTypeLabel = (src) => incomeTypesOf(src || {}).map((t) => INCOME_TYPE_LABELS[t]).join(' + ') || null;


// ─── Property Module Step 8 — Inspection & Appraisal Access ───────────────────
// RE desk captures property access + availability so the Inspectify
// inspection/appraisal scheduling has what it needs. Standalone component so
// its hooks stay out of the parent's renderStep if-chain (rules of hooks).
// Saves to POST /api/admin/borrowers/:id/inspectify-access.
const ACCESS_OPTIONS = [
  { value: 'combination_lockbox', label: 'Combination Lockbox', fields: ['code', 'directions'] },
  { value: 'app_lockbox',         label: 'App-Enabled Lockbox', fields: [] },
  { value: 'meet_contact',        label: 'Meet Contact On-Site', fields: ['contact_name', 'contact_phone', 'contact_email'] },
  { value: 'hidden_key',          label: 'Hidden Key', fields: ['directions'] },
  { value: 'community_access',    label: 'Community Access', fields: ['code', 'directions'] },
  { value: 'door_code',           label: 'Door Code', fields: ['code', 'directions'] },
];
const ACCESS_FIELD_LABEL = {
  code: 'Access code', directions: 'Directions / location',
  contact_name: 'Contact full name', contact_phone: 'Contact phone', contact_email: 'Contact email',
};
const s8input = { fontSize: 12, padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 };

function InspectifyAccessStep({ optionPeriod, userId, onSaved }) {
  const op = optionPeriod || {};
  const [availType, setAvailType]           = useState(op.inspectify_availability_type || '');
  const [availLatestDate, setAvailLatestDate] = useState(op.inspectify_availability_latest_date ? String(op.inspectify_availability_latest_date).slice(0, 10) : '');
  const [availContact, setAvailContact]     = useState(op.inspectify_availability_contact || {});
  const [availWindows, setAvailWindows]     = useState(Array.isArray(op.inspectify_availability_windows) ? op.inspectify_availability_windows : []);
  const [accessTypes, setAccessTypes]       = useState(Array.isArray(op.inspectify_access_types) ? op.inspectify_access_types : []);
  const [accessDetails, setAccessDetails]   = useState(op.inspectify_access_details || {});
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const contractDatePlus3 = (() => {
    const base = op.contract_date ? new Date(op.contract_date) : new Date();
    base.setDate(base.getDate() + 3);
    return base.toISOString().split('T')[0];
  })();

  const toggleAccess = (type, checked) =>
    setAccessTypes(prev => checked ? [...prev, type] : prev.filter(t => t !== type));
  const updateAccessDetail = (type, field, value) =>
    setAccessDetails(prev => ({ ...prev, [type]: { ...(prev[type] || {}), [field]: value } }));

  const handleSave = async () => {
    setSaving(true); setSavedOk(false);
    try {
      await axios.post(
        `${API}/api/admin/borrowers/${userId}/inspectify-access`,
        {
          availability_type: availType,
          availability_latest_date: availLatestDate || null,
          availability_contact: availContact,
          availability_windows: availWindows,
          access_types: accessTypes,
          access_details: accessDetails,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` } }
      );
      setSavedOk(true);
      if (onSaved) await onSaved();
    } catch (e) {
      alert('Could not save access & availability: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Availability */}
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>When can the service provider arrive?</div>
        {[
          { value: 'appointment', label: 'By Appointment', desc: 'Select available time windows below' },
          { value: 'flexible',    label: 'Flexible (between two dates)', desc: `Latest date: up to ${contractDatePlus3}` },
          { value: 'contact',     label: 'Request availability from contact', desc: 'Inspectify will contact someone to schedule' },
        ].map(opt => (
          <label key={opt.value} style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 14px', marginBottom: 8,
            border: `1px solid ${availType === opt.value ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: 8,
            cursor: 'pointer', background: availType === opt.value ? '#eff6ff' : '#fff',
          }}>
            <input type="radio" value={opt.value} checked={availType === opt.value}
              onChange={() => setAvailType(opt.value)} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{opt.desc}</div>
            </div>
          </label>
        ))}

        {availType === 'appointment' && (
          <div style={{ marginTop: 8, paddingLeft: 8 }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Select at least 4 hours total in 2+ hour increments</div>
            {availWindows.map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input type="date" value={w.date || ''} onChange={e => { const u = [...availWindows]; u[i] = { ...u[i], date: e.target.value }; setAvailWindows(u); }} style={s8input} />
                <input type="time" value={w.start || ''} onChange={e => { const u = [...availWindows]; u[i] = { ...u[i], start: e.target.value }; setAvailWindows(u); }} style={s8input} />
                <span style={{ fontSize: 12, color: '#6b7280' }}>to</span>
                <input type="time" value={w.end || ''} onChange={e => { const u = [...availWindows]; u[i] = { ...u[i], end: e.target.value }; setAvailWindows(u); }} style={s8input} />
                <button onClick={() => setAvailWindows(prev => prev.filter((_, j) => j !== i))} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>
              </div>
            ))}
            <button onClick={() => setAvailWindows(prev => [...prev, { date: '', start: '', end: '' }])}
              style={{ fontSize: 12, color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add window</button>
          </div>
        )}

        {availType === 'flexible' && (
          <div style={{ marginTop: 8, paddingLeft: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Latest date</div>
            <input type="date" value={availLatestDate} max={contractDatePlus3} onChange={e => setAvailLatestDate(e.target.value)} style={s8input} />
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Must be within 3 days of contract date</div>
          </div>
        )}

        {availType === 'contact' && (
          <div style={{ marginTop: 8, paddingLeft: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['name', 'email', 'phone'].map(f => (
              <input key={f} placeholder={`Contact ${f}`} value={availContact[f] || ''}
                onChange={e => setAvailContact(prev => ({ ...prev, [f]: e.target.value }))} style={s8input} />
            ))}
          </div>
        )}
      </div>

      {/* Access */}
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>How will the service provider access the property?</div>
        {ACCESS_OPTIONS.map(opt => (
          <div key={opt.value} style={{ marginBottom: 8 }}>
            <label style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 14px',
              border: `1px solid ${accessTypes.includes(opt.value) ? '#3a6ad9' : '#e5e7eb'}`, borderRadius: 8,
              cursor: 'pointer', background: accessTypes.includes(opt.value) ? '#eff6ff' : '#fff',
            }}>
              <input type="checkbox" checked={accessTypes.includes(opt.value)}
                onChange={e => toggleAccess(opt.value, e.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</span>
            </label>
            {accessTypes.includes(opt.value) && opt.fields.length > 0 && (
              <div style={{ marginLeft: 14, marginTop: 6, padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {opt.fields.map(f => (
                  <input key={f} placeholder={ACCESS_FIELD_LABEL[f] || f}
                    value={accessDetails[opt.value]?.[f] || ''}
                    onChange={e => updateAccessDetail(opt.value, f, e.target.value)} style={s8input} />
                ))}
              </div>
            )}
            {/* App-enabled lockbox needs a brand (Inspectify requires it) + directions. */}
            {accessTypes.includes('app_lockbox') && opt.value === 'app_lockbox' && (
              <div style={{ marginLeft: 14, marginTop: 6, padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <select
                  value={accessDetails['app_lockbox']?.lockbox_brand || ''}
                  onChange={e => updateAccessDetail('app_lockbox', 'lockbox_brand', e.target.value)}
                  style={{ ...s8input, width: '100%' }}>
                  <option value=''>Select lockbox brand</option>
                  <option value='Supra/Ekey'>Supra / eKey</option>
                  <option value='Rently'>Rently</option>
                  <option value='SentriLock'>SentriLock</option>
                  <option value='Other'>Other</option>
                </select>
                <input placeholder="Lockbox location / directions"
                  value={accessDetails['app_lockbox']?.directions || ''}
                  onChange={e => updateAccessDetail('app_lockbox', 'directions', e.target.value)}
                  style={{ ...s8input, width: '100%' }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={handleSave} disabled={saving || !availType || accessTypes.length === 0}
          style={{
            background: (saving || !availType || accessTypes.length === 0) ? '#9ca3af' : '#3a6ad9',
            color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'default' : 'pointer',
          }}>
          {saving ? 'Saving…' : 'Save Access & Availability'}
        </button>
        {savedOk && <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>✓ Saved</span>}
      </div>
    </div>
  );
}

// ─── Style constants ──────────────────────────────────────────────────────────
const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
// CLOSING_STAGES sourced from the central stage definitions. See
// frontend/src/constants/statusColors.js.
// Tabs visible to a given staff role. `gateRoles` (when present) is an
// allow-list; omitted means "every role sees this tab". RE Desk
// (contract_desk) is limited to acquisition-relevant tabs — no Loan,
// Financials, Review, or Activity views, and the Documents tab is
// rendered in a narrowed-down mode (Property Documents section only) —
// see `activeTab === 'documents'` render for the gate.
const ALL_ROLES_EXCEPT_REDESK = ['admin','account_manager','loan_officer','client_success'];
const TABS = [
  { id: 'borrower_details',  label: 'Borrower(s)' },
  { id: 'closings',          label: 'Closings', gateStages: CLOSING_STAGES },
  { id: 'loan_details',      label: 'Loan',       gateRoles: ALL_ROLES_EXCEPT_REDESK },
  { id: 'financial_details', label: 'Financials', gateRoles: ALL_ROLES_EXCEPT_REDESK },
  { id: 'review',            label: 'Review',     gateRoles: ALL_ROLES_EXCEPT_REDESK },
  { id: 'property_details',  label: 'Property' },
  { id: 'date_tracking',     label: 'Dates' },
  { id: 'activity',          label: 'Activity',   gateRoles: ALL_ROLES_EXCEPT_REDESK },
  { id: 'communication',     label: 'Communication', countKey: 'messages' },
  { id: 'documents',         label: 'Documents',     countKey: 'documentReview' },
];

// ─── Status display mapping (bg = background, color = text) ──────────────────
const STATUS_META = Object.fromEntries(
  Object.entries(STATUS_COLORS).map(([k, v]) => [k, { bg: v.bg, color: v.text, label: v.label }])
);

const STAGE_STEPS = [
  { label: 'Application', statuses: ['not_started','pre_qualified','new','started','registered'] },
  { label: 'Verification', statuses: ['submitted','applied'] },
  { label: 'Approval',    statuses: ['approved','conditional_approval'] },
  { label: 'Shopping',    statuses: ['home_shopping'] },
  { label: 'Offer',       statuses: ['offer_requested'] },
  { label: 'Submitted',   statuses: ['offer_submitted'] },
  // Accepted covers offer_accepted + under_contract — both are post-LA-acceptance,
  // pre-option-period states. Without this entry getCurrentStep returns 0 for
  // offer_accepted borrowers, which collapses the Property tab back to the
  // pre-Offer "Shopping Preferences" mini-card.
  { label: 'Accepted',    statuses: ['offer_accepted','under_contract'] },
  { label: 'Option',      statuses: ['option_period'] },
  { label: 'Closing',     statuses: ['closing','clear_to_close','loan_docs','closing_disclosures'] },
  { label: 'Funded',      statuses: ['closed','funded'] },
];


// Doc categories for Documents tab

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt$(v) { if (!v) return '—'; const n = Number(v); if (isNaN(n)) return '—'; return '$' + (n >= 1e6 ? (n/1e6).toFixed(2)+'M' : n.toLocaleString()); }
function fmtPct(v) { if (v === null || v === undefined || v === '') return '—'; return Number(v).toFixed(1) + '%'; }

// Small hover-triggered help tooltip — no external deps. Click-activated too
// (for touch/screen-reader), dismisses on outside click. Positions above
// the trigger and caps at 260px wide so long explanations wrap cleanly.
function HelpTip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: 4, verticalAlign: 'middle' }}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}>
      <span
        onClick={(e) => { e.stopPropagation(); setShow(s => !s); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 13, height: 13, borderRadius: '50%',
          background: '#e5e7eb', color: '#6b7280',
          fontSize: 9, fontWeight: 700, cursor: 'help',
          userSelect: 'none',
        }}
        role="button"
        aria-label="Help"
      >?</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          background: '#111827', color: '#fff',
          padding: '8px 10px', borderRadius: 6, fontSize: 11, lineHeight: 1.5,
          width: 260, textAlign: 'left', zIndex: 50,
          boxShadow: '0 4px 12px rgba(0,0,0,0.18)', whiteSpace: 'normal',
          fontWeight: 400, textTransform: 'none', letterSpacing: 0,
          pointerEvents: 'none',
        }}>
          {text}
        </span>
      )}
    </span>
  );
}
function relTime(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
function daysInStage(statusUpdatedAt) {
  if (!statusUpdatedAt) return 0;
  return Math.floor((Date.now() - new Date(statusUpdatedAt).getTime()) / 86400000);
}

// ─── Declaration labels (mirrors Documents.js ViewApplicationModal) ───────────
const DECL_LABELS = {
  bankruptcy:        'Have you declared bankruptcy?',
  foreclosure:       'Had property foreclosed or deed-in-lieu?',
  behind_on_mortgage:'Currently behind on any mortgage payments or in default on a home loan?',
  federal_debt:      'Delinquent on federal debt?',
  lawsuit_current:   'Currently a party to a lawsuit?',
  occupy_property:   'Will this property serve any purpose other than your primary residence?',
  borrowed_down:     'Are you borrowing money for the down payment?',
  cosigner:          'Are you a co-signer on another loan?',
  alimony:           'Obligated to pay alimony or child support?',
  undisclosed_debts: 'Any undisclosed debts?',
};

function CreditScoreDisplay({ borrower, coBorrower, size = 'normal' }) {
  const info = getCreditScore(borrower, coBorrower);
  const hasBothScores = info.primaryScore && info.coScore;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <span style={{
        fontSize: size === 'large' ? '20px' : '14px',
        fontWeight: '700',
        color: info.isActual ? getCreditScoreColor(info.value) : '#374151',
      }}>
        {info.display}
      </span>
      {info.isActual ? (
        <span style={{ fontSize: '10px', background: '#acf44e', color: '#1a1a1a', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
          Qualifying
        </span>
      ) : (
        <span style={{ fontSize: '10px', background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: '4px' }}>
          Self-reported
        </span>
      )}
      {hasBothScores && (
        <span style={{ fontSize: '10px', color: '#9ca3af' }} title={`Primary: ${info.primaryScore} | Co-Borrower: ${info.coScore}`}>
          ({info.primaryScore}/{info.coScore})
        </span>
      )}
      {info.pulledAt && (
        <span style={{ fontSize: '10px', color: '#9ca3af' }}>
          Pulled {new Date(info.pulledAt).toLocaleDateString()}
        </span>
      )}
    </div>
  );
}

function getCurrentStep(status) {
  const idx = STAGE_STEPS.findIndex((s) => s.statuses.includes(status));
  return idx === -1 ? 0 : idx;
}
// ─── Sub-components ───────────────────────────────────────────────────────────

// ── 🚨 `wide` EXISTS BECAUSE space-between SPANS WHATEVER IT IS GIVEN ───────
//
// The default row is label-left / value-right, which reads correctly inside a
// ~200px grid cell: the two sit a few pixels apart and the eye pairs them.
//
// 🚨 PUT THAT SAME ROW IN A FULL-WIDTH GRID CELL AND IT BREAKS. Current Address
// needs the whole row because an address is long, so it was wrapped in a
// gridColumn: '1 / -1' cell — and `justify-content: space-between` then threw
// the value to the far edge of the card, a cardsworth of whitespace away from
// the label it belongs to. The row was not "wrong" in CSS terms; it did exactly
// what it says, against a container that had become 5x wider.
//
// 🔑 SO A WIDE ROW READS LEFT-TO-RIGHT INSTEAD. The value sits next to its label
// with a fixed gap, and the label stops shrinking — there is no opposite edge to
// align to when the row owns the whole card.
//
// ⚠️ AND THE SPAN MOVES INTO THE PROP, so the wrapper div goes away. Keeping
// gridColumn at the call site is what made this possible to get wrong: the two
// facts — "spans the grid" and "therefore cannot use space-between" — were in
// two different places, and only one of them was written down.
function InfoRow({ label, value, wide }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: wide ? 'flex-start' : 'space-between',
      ...(wide ? { gridColumn: '1 / -1', gap: '12px' } : null),
      padding: '7px 0', borderBottom: '1px solid #f9fafb',
    }}>
      <span style={{ fontSize: '12px', color: '#6b7280', ...(wide ? { flexShrink: 0 } : null) }}>{label}</span>
      <span style={{
        fontSize: '12px', fontWeight: '500', color: '#111827',
        textAlign: wide ? 'left' : 'right',
        ...(wide ? null : { maxWidth: '60%' }),
        wordBreak: 'break-word',
      }}>{value || '—'}</span>
    </div>
  );
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div role="dialog" style={{ backgroundColor: '#fff', borderRadius: '14px', padding: '24px', width: '480px', maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', margin: '0 auto', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#9ca3af', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// 🔑 guided (2026-10-26): a guided file's approval comes from step 8 — "Approved" is not offered (the server refuses it too,
// GUIDED_APPROVAL_IS_STEP8), and a refused move says why instead of closing silently.
function MoveStageModal({ currentStatus, userId, token, onClose, onMoved }) {
  const [selected, setSelected] = useState(currentStatus);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [moveError, setMoveError] = useState(null);
  const doMove = async () => {
    setLoading(true); setMoveError(null);
    try {
      await axios.put(`${API}/api/admin/borrowers/${userId}/status`, { status: selected }, { headers: { Authorization: `Bearer ${token}` } });
      onMoved(selected);
    } catch (e) { setMoveError(e?.response?.data?.error || 'That move did not go through.'); setConfirm(false); }
    setLoading(false);
  };
  return (
    <Modal title="Move Stage" onClose={onClose}>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>Select the new stage for this borrower.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
        {MOVE_STAGE_OPTIONS.filter((stage) => !(stage.value === 'approved' && currentStatus !== 'approved')).map((stage) => (
          <button key={stage.value} data-testid={`move-stage-${stage.value}`} onClick={() => setSelected(stage.value)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
              padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
              fontSize: '13px', fontWeight: selected === stage.value ? '600' : '400',
              background: selected === stage.value ? stage.color : '#f9fafb',
              color: selected === stage.value ? stage.textColor : '#374151',
              border: `2px solid ${selected === stage.value ? stage.color : '#e5e7eb'}`,
              transition: 'all 0.15s ease',
            }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: stage.color, border: '2px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
            {stage.label}
            {stage.value === currentStatus && <span style={{ marginLeft: 'auto', fontSize: '10px', opacity: 0.6 }}>current</span>}
          </button>
        ))}
      </div>
      {currentStatus !== 'approved' && (
        <p data-testid="move-stage-guided-note" style={{ fontSize: '12.5px', color: '#111827', marginBottom: '12px' }}>Approved isn&rsquo;t offered here: a file&rsquo;s approval comes from the step-8 decision.</p>
      )}
      {moveError && <p role="alert" data-testid="move-stage-error" style={{ fontSize: '12.5px', color: '#111827', background: '#FFDD86', borderRadius: 8, padding: '8px 10px', marginBottom: '12px' }}>{moveError}</p>}
      {!confirm ? (
        <button onClick={() => setConfirm(true)} disabled={selected === currentStatus}
          style={{ width: '100%', padding: '10px', borderRadius: '8px', backgroundColor: '#3a6ad9', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: selected === currentStatus ? 0.4 : 1 }}>
          Confirm Move
        </button>
      ) : (
        <div>
          <p style={{ fontSize: '13px', color: '#ff082f', marginBottom: '12px' }}>⚠️ Move to <strong>{MOVE_STAGE_OPTIONS.find((o) => o.value === selected)?.label || selected}</strong>? This will notify the borrower.</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setConfirm(false)} style={{ flex: 1, padding: '9px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>Cancel</button>
            <button onClick={doMove} disabled={loading}
              style={{ flex: 1, padding: '9px', borderRadius: '8px', background: '#3a6ad9', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
              {loading ? 'Moving…' : 'Yes, Move'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}


function RequestDocsModal({ userId, token, onClose, onRequested }) {
  const COMMON_DOCS = ['Pay Stubs (2 months)', 'W-2s (2 years)', 'Tax Returns (2 years)', 'Bank Statements (3 months)', 'Photo ID', '1099 Forms', 'Letter of Explanation', 'Gift Letter', 'VOR / VOM', 'Offer Letter'];
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const toggle = (d) => setSelected((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);
  const doRequest = async () => {
    const docs = [...selected, ...(custom.trim() ? [custom.trim()] : [])];
    if (!docs.length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/borrowers/${userId}/request-docs`,
        { docs, message_to_borrower: message.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRequested();
    } catch {}
    setLoading(false);
  };
  const docCount = selected.length + (custom.trim() ? 1 : 0);
  const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' };
  return (
    <Modal title="Request Documents" onClose={onClose}>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>Select documents to request from the borrower.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
        {COMMON_DOCS.map((d) => (
          <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', backgroundColor: selected.includes(d) ? '#eff4ff' : 'transparent' }}>
            <input type="checkbox" checked={selected.includes(d)} onChange={() => toggle(d)} style={{ accentColor: '#3a6ad9' }} />
            {d}
          </label>
        ))}
      </div>
      <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom document name…"
        style={{ ...inp, marginBottom: '16px' }} />

      {/* Message to borrower */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontSize: '12px', color: '#374151', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
          Message to Borrower
          <span style={{ fontWeight: '400', color: '#6b7280', marginLeft: '6px' }}>(visible on their dashboard)</span>
        </label>
        <textarea
          value={message}
          onChange={(e) => { if (e.target.value.length <= 500) setMessage(e.target.value); }}
          placeholder="e.g. Hi! We need your most recent 2 months of bank statements to verify your assets. Please upload them in the Documents section below."
          rows={4}
          style={{ ...inp, resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>This message will appear as a notification on the borrower's dashboard.</p>
          <span style={{ fontSize: '11px', color: message.length > 400 ? '#ff082f' : '#9ca3af' }}>{message.length}/500</span>
        </div>
      </div>

      <button onClick={doRequest} disabled={loading || docCount === 0}
        style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#3a6ad9', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: docCount === 0 ? 0.4 : 1 }}>
        {loading ? 'Sending…' : `Send Request to Borrower${docCount > 0 ? ` (${docCount} doc${docCount !== 1 ? 's' : ''})` : ''}`}
      </button>
    </Modal>
  );
}

// ─── Document Review components ───────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const DOCUMENT_SECTIONS = [
  { key: 'identity',      label: 'Identity Documents',  docTypes: ['id_front','id_back','passport','identity','drivers_license','selfie'] },
  { key: 'income',        label: 'Income Documents',    docTypes: ['pay_stub','paystub','w2','tax_return','1099','offer_letter','employment_verification','employment_letter','p_and_l'] },
  { key: 'assets',        label: 'Asset Documents',     docTypes: ['bank_statement','investment_statement','retirement_statement','asset','gift_letter'] },
  { key: 'verification',  label: 'Verification',        docTypes: ['vor','vom','voe','verification'] },
  { key: 'misc',          label: 'Miscellaneous',       docTypes: [] },
  { key: 'loan_docs',     label: 'Loan Documents',      docTypes: ['loan_estimate','le','closing_disclosure','cd','promissory','note','deed_of_trust','deed','pre_approval','preapproval'] },
  { key: 'property_docs', label: 'Property Documents',  docTypes: ['seller','purchase_agreement','psa','appraisal','inspection','title'] },
];

// eslint-disable-next-line no-unused-vars
// Title Contact form modal — create or edit the single title-company record
// used by the double-close workflow. Mirrors the gift-letter modal pattern.
function TitleContactFormModal({ open, initial, onClose, onSaved, headers }) {
  const [form, setForm] = useState({
    company_name: '', escrow_agent_name: '', escrow_agent_email: '',
    title_officer_name: '', title_officer_email: '', phone: '', address: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open) return;
    setErrors({}); setSaveError('');
    setForm({
      // Create mode pre-fills company_name with BluePrint Title (the standing
      // title company for Doorly closings). Admin can override when adding; edit
      // mode always shows the actual stored value.
      company_name:        initial?.company_name        || 'BluePrint Title',
      escrow_agent_name:   initial?.escrow_agent_name   || '',
      escrow_agent_email:  initial?.escrow_agent_email  || '',
      title_officer_name:  initial?.title_officer_name  || '',
      title_officer_email: initial?.title_officer_email || '',
      phone:               initial?.phone              || '',
      address:             initial?.address            || '',
    });
  }, [open, initial]);

  if (!open) return null;

  const setField = (k) => (e) => {
    setForm(p => ({ ...p, [k]: e.target.value }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = 'Company name is required.';
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.escrow_agent_email   && !emailRe.test(form.escrow_agent_email.trim()))   e.escrow_agent_email   = 'Invalid email.';
    if (form.title_officer_email  && !emailRe.test(form.title_officer_email.trim()))  e.title_officer_email  = 'Invalid email.';
    if (!form.escrow_agent_email && !form.title_officer_email) {
      e.escrow_agent_email = 'Provide at least one contact email (escrow agent or title officer).';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true); setSaveError('');
    try {
      const payload = {
        company_name:        form.company_name.trim(),
        escrow_agent_name:   form.escrow_agent_name.trim()   || null,
        escrow_agent_email:  form.escrow_agent_email.trim()  || null,
        title_officer_name:  form.title_officer_name.trim()  || null,
        title_officer_email: form.title_officer_email.trim() || null,
        phone:               form.phone.trim()               || null,
        address:             form.address.trim()             || null,
      };
      const API_ = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      if (initial?.id) {
        await axios.patch(`${API_}/api/admin/title-contacts/${initial.id}`, payload, { headers });
      } else {
        // Only one title company in use — always mark new ones default.
        await axios.post(`${API_}/api/admin/title-contacts`, { ...payload, is_default: true }, { headers });
      }
      onSaved();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Failed to save.');
      setSaving(false);
    }
  };

  const inputCls = (field) => ({
    width: '100%', height: 40, padding: '0 12px', borderRadius: 10, fontSize: 14,
    border: `1px solid ${errors[field] ? '#fca5a5' : '#e5e7eb'}`, outline: 'none', boxSizing: 'border-box',
  });
  const labelCls = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };

  return (
    <Modal title={initial?.id ? 'Edit Title Company' : 'Add Title Company'} onClose={saving ? () => {} : onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelCls}>Company Name <span style={{ color: '#ef4444' }}>*</span></label>
          <input type="text" value={form.company_name} onChange={setField('company_name')} placeholder="e.g., Alamo Title — Austin" style={inputCls('company_name')} />
          {errors.company_name && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.company_name}</p>}
        </div>
        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelCls}>Escrow Agent Name</label>
            <input type="text" value={form.escrow_agent_name} onChange={setField('escrow_agent_name')} style={inputCls('escrow_agent_name')} />
          </div>
          <div>
            <label style={labelCls}>Escrow Agent Email</label>
            <input type="email" value={form.escrow_agent_email} onChange={setField('escrow_agent_email')} style={inputCls('escrow_agent_email')} />
            {errors.escrow_agent_email && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.escrow_agent_email}</p>}
          </div>
        </div>
        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelCls}>Title Officer Name</label>
            <input type="text" value={form.title_officer_name} onChange={setField('title_officer_name')} style={inputCls('title_officer_name')} />
          </div>
          <div>
            <label style={labelCls}>Title Officer Email</label>
            <input type="email" value={form.title_officer_email} onChange={setField('title_officer_email')} style={inputCls('title_officer_email')} />
            {errors.title_officer_email && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.title_officer_email}</p>}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <div>
            <label style={labelCls}>Phone</label>
            <input type="tel" value={form.phone} onChange={setField('phone')} placeholder="(512) 555-0123" style={inputCls('phone')} />
          </div>
          <div>
            <label style={labelCls}>Address</label>
            <input type="text" value={form.address} onChange={setField('address')} placeholder="Street, City, State Zip" style={inputCls('address')} />
          </div>
        </div>
        {saveError && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{saveError}</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button onClick={onClose} disabled={saving} type="button"
            style={{ flex: 1, height: 42, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} type="button"
            style={{ flex: 1, height: 42, borderRadius: 10, border: 'none', background: '#3a6ad9', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : (initial?.id ? 'Save Changes' : 'Create Title Company')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Inspection/appraisal schedule row — uses local useState for the datetime-local
// draft, so it must live at module scope (otherwise it'd be redefined every
// render and the input would lose focus on each keystroke).
// Inspection & Appraisal schedule row — DISPLAY-ONLY as of the Dates
// tab cleanup. The Inspectify webhook (POST /api/webhooks/inspectify)
// is the canonical writer for option_periods.{inspection,appraisal}_-
// scheduled_at when the inspector confirms an appointment. The legacy
// PATCH /api/admin/listing-agent-schedule manual override was removed
// from this surface — the row now just renders the Inspectify-set
// datetime + source badge, with an "awaiting" state when null.
function ScheduleRow({ label, currentValue, source }) {
  const fromInspectify = !!currentValue && source === 'inspectify';
  // Pretty-format the datetime for display. Returns null when input
  // is null so the 'Awaiting Inspectify schedule' state renders.
  const formatted = currentValue
    ? (() => {
        try {
          const d = new Date(currentValue);
          if (isNaN(d.getTime())) return null;
          return d.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
            timeZone: 'America/Chicago', timeZoneName: 'short',
          });
        } catch { return null; }
      })()
    : null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 8, background: '#f9fafb', border: '1px solid #f3f4f6', marginBottom: 6, gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          {label}
          {fromInspectify && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#f3f4f6', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04 }}>via Inspectify</span>
          )}
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
          Auto-set by the Inspectify webhook when the inspector confirms the appointment.
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: formatted ? 700 : 400, color: formatted ? '#111827' : '#9ca3af', fontStyle: formatted ? 'normal' : 'italic', whiteSpace: 'nowrap' }}>
        {formatted || 'Awaiting Inspectify schedule'}
      </div>
    </div>
  );
}

// Listing-agent update modal — mirrors the gift-letter / title-contact modal
// style. Sends via POST /api/admin/listing-agent-comms which is gated at the
// server layer to A-B-only fields (see services/email.js sendListingAgentUpdate).
function ListingAgentUpdateModal({ open, onClose, onSent, userId, headers, defaults }) {
  const EVENTS = [
    { id: 'inspection_scheduled',  label: 'Inspection scheduled',            auto: true },
    { id: 'appraisal_scheduled',   label: 'Appraisal scheduled',             auto: true },
    { id: 'reports_received',      label: 'Reports received',                auto: true },
    { id: 'reports_reviewed',      label: 'Report review complete',          auto: true },
    { id: 'decision_proceed',      label: 'Proceeding to close',             auto: true },
    { id: 'decision_terminate',    label: 'Not proceeding (termination)',    auto: false },
    { id: 'closing_date_confirmed',label: 'Closing date confirmed',          auto: true },
    { id: 'closing_date_updated',  label: 'Closing date updated',            auto: true },
  ];
  const [eventType, setEventType] = useState('decision_terminate');
  const [note, setNote] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [inspectionComplete, setInspectionComplete] = useState(true);
  const [appraisalComplete, setAppraisalComplete]   = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setNote(''); setErr(''); setSending(false);
    setEventType('decision_terminate');
    setScheduledAt('');
    setInspectionComplete(true);
    setAppraisalComplete(true);
  }, [open]);

  if (!open) return null;

  const currentEvent = EVENTS.find(e => e.id === eventType);
  const needsSchedule = eventType === 'inspection_scheduled' || eventType === 'appraisal_scheduled';
  const needsReportsCheckbox = eventType === 'reports_received';

  const send = async () => {
    setSending(true); setErr('');
    try {
      const details = {};
      if (needsSchedule) {
        if (!scheduledAt) { setErr('Scheduled date/time is required.'); setSending(false); return; }
        details.scheduled_at = new Date(scheduledAt).toISOString();
      }
      if (needsReportsCheckbox) {
        details.inspection_complete = inspectionComplete;
        details.appraisal_complete  = appraisalComplete;
      }
      const API_ = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await axios.post(`${API_}/api/admin/listing-agent-comms`,
        { user_id: userId, event_type: eventType, details, note: note || null },
        { headers }
      );
      onSent();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to send.');
      setSending(false);
    }
  };

  const inputCls = { width: '100%', height: 40, padding: '0 12px', borderRadius: 10, fontSize: 14, border: '1px solid #e5e7eb', outline: 'none', boxSizing: 'border-box' };
  const labelCls = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };

  return (
    <Modal title="Send Update to Listing Agent" onClose={sending ? () => {} : onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#2854c0', lineHeight: 1.5 }}>
          A-B only. Emails sent from this dialog reference Doorly as the buyer and the property address — no borrower data is ever included.
          {defaults?.recipient ? <><br /><strong>Recipient:</strong> {defaults.recipient}</> : <><br /><strong>Warning:</strong> No listing agent email on file. Send will fail.</>}
        </div>

        <div>
          <label style={labelCls}>Event</label>
          <select value={eventType} onChange={e => setEventType(e.target.value)} style={inputCls}>
            {EVENTS.map(e => (
              <option key={e.id} value={e.id}>{e.label}{e.auto ? ' (auto-fires too)' : ' (manual only)'}</option>
            ))}
          </select>
          {currentEvent?.auto && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9ca3af' }}>
              This event normally fires automatically on state change. Use this to re-send or to trigger ahead of the state change.
            </p>
          )}
          {eventType === 'decision_terminate' && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#c2410c' }}>
              Termination emails are manual only — use the note below to explain if needed.
            </p>
          )}
        </div>

        {needsSchedule && (
          <div>
            <label style={labelCls}>{eventType === 'inspection_scheduled' ? 'Inspection date & time' : 'Appraisal date & time'}</label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} style={inputCls} />
          </div>
        )}

        {needsReportsCheckbox && (
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
              <input type="checkbox" checked={inspectionComplete} onChange={e => setInspectionComplete(e.target.checked)} /> Inspection
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
              <input type="checkbox" checked={appraisalComplete} onChange={e => setAppraisalComplete(e.target.checked)} /> Appraisal
            </label>
          </div>
        )}

        <div>
          <label style={labelCls}>Note (optional)</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
            placeholder="Optional note appended to the email body. Keep it A-B only."
            style={{ ...inputCls, height: 'auto', padding: '10px 12px', fontFamily: 'inherit', resize: 'vertical' }} />
        </div>

        {err && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>{err}</p>}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button onClick={onClose} disabled={sending} type="button"
            style={{ flex: 1, height: 42, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.5 : 1 }}>
            Cancel
          </button>
          <button onClick={send} disabled={sending || !defaults?.recipient} type="button"
            style={{ flex: 1, height: 42, borderRadius: 10, border: 'none', background: (sending || !defaults?.recipient) ? '#9ca3af' : '#111827', color: '#acf44e', fontSize: 13, fontWeight: 700, cursor: (sending || !defaults?.recipient) ? 'not-allowed' : 'pointer' }}>
            {sending ? 'Sending…' : 'Send Update'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// eslint-disable-next-line no-unused-vars
function DocumentReviewRow({ doc, onAccept, onReject, onView }) {
  const adminStatus = doc.admin_status || 'pending';
  const sc = adminStatus === 'accepted'
    ? { label: '✅ Accepted', bg: '#acf44e', color: '#1a1a1a' }
    : adminStatus === 'rejected'
    ? { label: '❌ Rejected', bg: '#ff082f', color: '#fff' }
    : { label: '⏳ Pending', bg: '#fffbeb', color: '#d97706' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: '8px', border: '1px solid #f3f4f6', marginBottom: '6px', background: adminStatus === 'accepted' ? '#f8fef4' : adminStatus === 'rejected' ? '#fff5f5' : '#fff' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.original_name || doc.document_type}</div>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
          {doc.upload_date ? new Date(doc.upload_date).toLocaleDateString() : ''}
          {doc.admin_reviewed_by && ` · Reviewed by ${doc.admin_reviewed_by}`}
        </div>
        {doc.rejection_message && <div style={{ fontSize: '11px', color: '#ff082f', marginTop: '2px' }}>Note: {doc.rejection_message}</div>}
        {doc.duplicate_of && (
          <div data-testid="admin-doc-duplicate" style={{ fontSize: '11px', color: '#92400e', marginTop: '2px', fontWeight: 600 }}>
            Duplicate of document #{doc.duplicate_of}: same file, not extracted again
          </div>
        )}
        {/* 🔑 Kept on file, never checked or counted again (backend utils/documentRemoval, 2026-10-25). */}
        {doc.removed_label && (
          <div data-testid="admin-doc-removed-by-borrower" style={{ fontSize: '11px', color: '#92400e', marginTop: '2px', fontWeight: 600 }}>
            {doc.removed_label}
          </div>
        )}
        {doc.uploaded_via && (
          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>
            Uploaded via {doc.uploaded_via}{doc.uploaded_by ? ` (#${doc.uploaded_by})` : ''}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: '12px' }}>
        <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 7px', borderRadius: '9999px', backgroundColor: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>{sc.label}</span>
        {doc.file_url && (
          <button onClick={() => onView(doc)} style={{ padding: '3px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>View</button>
        )}
        {adminStatus !== 'accepted' && (
          <button onClick={() => onAccept(doc.id)} style={{ padding: '3px 10px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>Accept</button>
        )}
        {adminStatus !== 'rejected' && (
          <button onClick={() => onReject(doc.id)} style={{ padding: '3px 10px', background: '#fff0f3', color: '#ff082f', border: '1px solid #ff082f', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>Reject</button>
        )}
      </div>
    </div>
  );
}

// ─── NeedCard component ───────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function NeedCard({ need, onUpdate, token, borrowerId }) {
  const [adminNote, setAdminNote] = useState(need.admin_notes || '');
  const [showNote, setShowNote] = useState(false);
  const priorityColors = {
    urgent: { bg: '#fff0f3', border: '#ff082f', dot: '#ff082f' },
    high:   { bg: '#fff7ed', border: '#f5b832', dot: '#f5b832' },
    normal: { bg: '#f9fafb', border: '#e5e7eb', dot: '#6b7280' },
    low:    { bg: '#f9fafb', border: '#e5e7eb', dot: '#d1d5db' },
  };
  const colors = priorityColors[need.priority] || priorityColors.normal;
  const statusConfig = {
    pending:   { label: 'Pending',              bg: '#f3f4f6', color: '#374151' },
    uploaded:  { label: '📎 Uploaded — Review', bg: '#fef9c3', color: '#854d0e' },
    reviewing: { label: 'Reviewing',            bg: '#eff6ff', color: '#3a6ad9' },
    satisfied: { label: '✓ Satisfied',          bg: '#acf44e', color: '#1a1a1a' },
    waived:    { label: 'Waived',               bg: '#f3f4f6', color: '#6b7280' },
  };
  const statusStyle = statusConfig[need.status] || statusConfig.pending;
  const handleStatusChange = async (newStatus) => {
    await axios.patch(`${API}/api/admin/borrowers/${borrowerId}/needs/${need.id}`,
      { status: newStatus, admin_notes: adminNote },
      { headers: { Authorization: `Bearer ${token}` } });
    onUpdate();
  };
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '16px', position: 'relative' }}>
      {need.status === 'uploaded' && (
        <div style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#FBBF24', color: '#1a1a1a', borderRadius: '10px', padding: '2px 10px', fontSize: '11px', fontWeight: '700', boxShadow: '0 2px 4px rgba(0,0,0,0.15)', animation: 'pulse 2s infinite' }}>
          🔔 New Upload
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors.dot, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{(need.need_type || '').replace('_', ' ')}</span>
            <span style={{ padding: '1px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
          </div>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#111827' }}>{need.title}</h4>
          {need.description && <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>{need.description}</p>}
          {need.status === 'uploaded' && need.uploaded_file_name && (
            <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 12px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📎</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#7a5200' }}>Uploaded: {need.uploaded_file_name}</span>
              {need.uploaded_file_url && (
                <button onClick={() => window.open(need.uploaded_file_url, '_blank')}
                  style={{ marginLeft: 'auto', padding: '2px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>View</button>
              )}
            </div>
          )}
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
            Requested by {need.requested_by} · {new Date(need.created_at).toLocaleDateString()}
            {need.uploaded_at && ` · Uploaded ${new Date(need.uploaded_at).toLocaleDateString()}`}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginLeft: '16px', minWidth: '120px' }}>
          {need.status === 'uploaded' && (<>
            <button onClick={() => handleStatusChange('reviewing')} style={{ padding: '5px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Start Review</button>
            <button onClick={() => handleStatusChange('satisfied')} style={{ padding: '5px 10px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>✓ Satisfied</button>
          </>)}
          {need.status === 'reviewing' && (<>
            <button onClick={() => handleStatusChange('satisfied')} style={{ padding: '5px 10px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>✓ Satisfied</button>
            <button onClick={() => handleStatusChange('pending')} style={{ padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}>Request Again</button>
          </>)}
          {(need.status === 'pending' || need.status === 'satisfied') && (
            <button onClick={() => handleStatusChange('waived')} style={{ padding: '5px 10px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}>Waive</button>
          )}
          <button onClick={() => setShowNote(!showNote)} style={{ padding: '5px 10px', background: 'none', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}>
            {showNote ? 'Hide Note' : 'Add Note'}
          </button>
        </div>
      </div>
      {showNote && (
        <div style={{ marginTop: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
          <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Internal note (not visible to borrower)…" rows={2}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box' }} />
          <button onClick={() => handleStatusChange(need.status)} style={{ marginTop: '6px', padding: '4px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>Save Note</button>
        </div>
      )}
    </div>
  );
}

// ─── CreditIssueCard ──────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const CreditIssueCard = ({ issue, onDismiss }) => {
  const [showDismiss, setShowDismiss] = React.useState(false);
  const [dismissReason, setDismissReason] = React.useState('');

  const severityConfig = {
    blocking:      { bg: '#fff0f3', border: '#ff082f', badge: { bg: '#ff082f', text: '#ffffff' }, icon: '🔴', label: 'Blocking' },
    significant:   { bg: '#fff7ed', border: '#f5b832', badge: { bg: '#f5b832', text: '#ffffff' }, icon: '🟠', label: 'Significant' },
    warning:       { bg: '#fffbeb', border: '#f59e0b', badge: { bg: '#f59e0b', text: '#1a1a1a' }, icon: '🟡', label: 'Warning' },
    informational: { bg: '#eff6ff', border: '#3a6ad9', badge: { bg: '#3a6ad9', text: '#ffffff' }, icon: '🔵', label: 'Info' },
  };

  const eventTypeLabels = {
    bankruptcy:    'Bankruptcy',
    foreclosure:   'Foreclosure',
    collection:    'Collection Account',
    charge_off:    'Charge-Off',
    late_payment:  'Late Payment(s)',
    derogatory:    'Derogatory Account',
    public_record: 'Public Record',
    judgment:      'Civil Judgment',
    tax_lien:      'Tax Lien',
    repossession:  'Repossession',
    inquiry_high:  'High Inquiry Count',
    thin_file:     'Thin Credit File',
    dispute:       'Active Dispute',
  };

  const cfg = severityConfig[issue.severity] || severityConfig.warning;

  return (
    <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: '10px', padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '15px' }}>{cfg.icon}</span>
            <span style={{ background: cfg.badge.bg, color: cfg.badge.text, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>{cfg.label}</span>
            <span style={{ background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
              {eventTypeLabels[issue.event_type] || issue.event_type}
            </span>
            {issue.months_ago != null && (
              <span style={{ fontSize: '11px', color: '#6b7280' }}>{issue.months_ago} months ago</span>
            )}
          </div>
          <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#111827', lineHeight: '1.5' }}>{issue.description}</p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {issue.creditor_name && <span style={{ fontSize: '12px', color: '#6b7280' }}><strong>Creditor:</strong> {issue.creditor_name}</span>}
            {issue.amount && parseFloat(issue.amount) > 0 && <span style={{ fontSize: '12px', color: '#6b7280' }}><strong>Amount:</strong> ${parseFloat(issue.amount).toLocaleString()}</span>}
            {issue.event_date && <span style={{ fontSize: '12px', color: '#6b7280' }}><strong>Date:</strong> {new Date(issue.event_date).toLocaleDateString()}</span>}
            {issue.account_number_masked && <span style={{ fontSize: '12px', color: '#6b7280' }}><strong>Account:</strong> {issue.account_number_masked}</span>}
          </div>
        </div>
        <button
          onClick={() => setShowDismiss(v => !v)}
          style={{ padding: '5px 10px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', flexShrink: 0 }}
        >
          Dismiss
        </button>
      </div>

      {showDismiss && (
        <div style={{ marginTop: '12px', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '12px' }}>
          <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 8px' }}>Reason for dismissing this issue:</p>
          <textarea
            value={dismissReason}
            onChange={e => setDismissReason(e.target.value)}
            placeholder="e.g. Discharge papers on file; outside the waiting period."
            rows={2}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button onClick={() => setShowDismiss(false)} style={{ padding: '5px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
            <button
              onClick={() => { onDismiss(dismissReason); setShowDismiss(false); }}
              disabled={!dismissReason.trim()}
              style={{ padding: '5px 12px', background: dismissReason.trim() ? '#6b7280' : '#d1d5db', color: '#fff', border: 'none', borderRadius: '6px', cursor: dismissReason.trim() ? 'pointer' : 'not-allowed', fontSize: '12px' }}
            >
              Dismiss Issue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ReviewBorrowerGroup (collapsible wrapper with progress bar) ──────────────
// eslint-disable-next-line no-unused-vars
const ReviewBorrowerGroup = ({ title, color, bgColor, borderColor, progressBar, children }) => {
  // Default expanded — sub-cards (Identity / Income / Assets / Housing /
  // Misc) inside still default to collapsed, so the admin lands on a
  // visible list of sections and chooses which to drill into.
  const [isOpen, setIsOpen] = React.useState(true);
  return (
    <div style={{ border: `1px solid ${borderColor}`, borderRadius: '12px', marginBottom: '16px', overflow: 'hidden', background: '#fff' }}>
      <button type="button" onClick={() => setIsOpen(v => !v)}
        style={{ width: '100%', padding: '14px 20px', background: bgColor, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left' }}>
        <span style={{ fontSize: '13px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>{title}</span>
        <span style={{ fontSize: '16px', color: '#9ca3af', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', display: 'inline-block' }}>▾</span>
      </button>
      <div style={{ padding: '0 20px', borderTop: `1px solid ${borderColor}` }}>
        {progressBar}
      </div>
      {isOpen && <div style={{ padding: '8px 16px 16px' }}>{children}</div>}
    </div>
  );
};

// ─── ReviewSection ─────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const ReviewSection = ({ section, sectionRow, documents, onAcceptSection, onFlagSection, isSaving, openNeedsCount, children }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [flagNote, setFlagNote] = React.useState('');
  const [showFlagModal, setShowFlagModal] = React.useState(false);

  const sectionStatus = sectionRow?.status || 'pending';
  const sectionAccepted = sectionStatus === 'accepted' || sectionStatus === 'approved';
  const sectionFlagged = sectionStatus === 'flagged';
  const sectionLocked = sectionStatus === 'locked';

  const getItemDocs = (item) => documents.filter(d =>
    item.doc_types.some(t => (d.document_type || d.doc_type || '').toLowerCase().includes(t))
  );

  const statusConfig = {
    accepted:  { bg: '#acf44e', color: '#1a1a1a', label: '✓ Accepted' },
    approved:  { bg: '#acf44e', color: '#1a1a1a', label: '✓ Accepted' },
    flagged:   { bg: '#ff082f', color: '#ffffff', label: '⚑ Flagged' },
    locked:    { bg: '#f3f4f6', color: '#9ca3af', label: '🔒 Locked' },
    pending:   { bg: '#f3f4f6', color: '#374151', label: 'Pending Review' },
  };
  const sc = statusConfig[sectionStatus] || statusConfig.pending;

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', marginBottom: '4px', overflow: 'hidden' }}>
      <div
        onClick={() => !sectionLocked && setExpanded(v => !v)}
        style={{ background: sectionAccepted ? '#f0fdf4' : sectionFlagged ? '#fff0f3' : '#ffffff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: sectionLocked ? 'default' : 'pointer', borderBottom: expanded && !sectionLocked ? '1px solid #f3f4f6' : 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{section.label}</span>
          {section.auto_accept_source && sectionAccepted && sectionRow?.admin_note?.includes('Auto-accepted via Persona') && (
            <span style={{ fontSize: '11px', background: '#acf44e', color: '#1a1a1a', padding: '2px 8px', borderRadius: '4px', fontWeight: '700' }}>✓ Auto-Approved via {section.auto_accept_source}</span>
          )}
          {/* AI Underwriter open-needs count — same pill format as the
              Persona auto-verified badge. Green when 0, red when ≥1.
              Hidden on Identity (no AI analysis runs there). */}
          {Number.isFinite(openNeedsCount) && section.id !== 'identity' && (
            <span style={{
              fontSize: '11px',
              background: openNeedsCount === 0 ? '#acf44e' : '#ff082f',
              color: openNeedsCount === 0 ? '#1a1a1a' : '#fff',
              padding: '2px 8px', borderRadius: '4px', fontWeight: '600',
            }}>
              {openNeedsCount} open need{openNeedsCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: sc.bg, color: sc.color }}>{sc.label}</span>
          {!sectionLocked && !sectionAccepted && (
            <button onClick={e => { e.stopPropagation(); onAcceptSection(section.id); }} disabled={isSaving}
              style={{ padding: '4px 12px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', opacity: isSaving ? 0.5 : 1 }}>
              Accept All
            </button>
          )}
          {/* Section-level Flag — opens the existing flag modal which
              persists a need + flips the section to 'flagged' so it
              surfaces on the borrower's Dashboard + /verification.
              Shown for pending or flagged states (becomes Re-Flag when
              already flagged) so admin can resend with edits. */}
          {!sectionLocked && !sectionAccepted && (
            <button onClick={e => { e.stopPropagation(); setShowFlagModal(true); }} disabled={isSaving}
              style={{ padding: '4px 12px', background: sectionFlagged ? '#fff' : '#ff082f', color: sectionFlagged ? '#ff082f' : '#fff', border: sectionFlagged ? '1px solid #ff082f' : 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', opacity: isSaving ? 0.5 : 1 }}>
              ⚑ {sectionFlagged ? 'Re-Flag' : 'Flag'}
            </button>
          )}
          {!sectionLocked && <span style={{ fontSize: '14px', color: '#9ca3af' }}>{expanded ? '▲' : '▼'}</span>}
        </div>
      </div>

      {expanded && !sectionLocked && (
        <div style={{ padding: '14px 20px' }}>
          {sectionFlagged && sectionRow?.admin_note && (
            <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
              <strong style={{ fontSize: '13px', color: '#ff082f' }}>⚑ Flagged: </strong>
              <span style={{ fontSize: '13px', color: '#374151' }}>{sectionRow.admin_note}</span>
            </div>
          )}
          {/* Borrower response to flag */}
          {sectionFlagged && (() => {
            const flagDocs = documents.filter(d => (d.document_type || '').toLowerCase() === `flagged_${section.id}`);
            const hasResponse = sectionRow?.borrower_response || flagDocs.length > 0;
            if (!hasResponse) return null;
            return (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Borrower Response</div>
                {sectionRow?.borrower_response && (
                  <div style={{ fontSize: '13px', color: '#374151', marginBottom: flagDocs.length > 0 ? '8px' : 0, lineHeight: '1.5' }}>
                    {sectionRow.borrower_response}
                    {sectionRow.borrower_responded_at && (
                      <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '8px' }}>
                        — {new Date(sectionRow.borrower_responded_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
                {flagDocs.length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Uploaded documents:</div>
                    {flagDocs.map(doc => (
                      <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span style={{ fontSize: '12px' }}>📎</span>
                        <span style={{ fontSize: '12px', color: '#3a6ad9', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => {
                            axios.get(`${API}/api/documents/view/${doc.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` }, responseType: 'blob' })
                              .then(res => { const url = URL.createObjectURL(res.data); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000); })
                              .catch(() => alert('Could not open document.'));
                          }}>
                          {doc.original_name || doc.document_type || 'Document'}
                        </span>
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(doc.upload_date || doc.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          {section.items.map(item => {
            const itemDocs = getItemDocs(item);
            const primaryDocs = itemDocs.filter(d => d.owner_type !== 'coborrower');
            const coDocs = itemDocs.filter(d => d.owner_type === 'coborrower');
            const hasCoDocs = coDocs.length > 0;
            const renderDoc = (doc) => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <span style={{ fontSize: '12px' }}>📎</span>
                <span style={{ fontSize: '12px', color: '#3a6ad9', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    axios.get(`${API}/api/documents/view/${doc.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` }, responseType: 'blob' })
                      .then(res => { const url = URL.createObjectURL(res.data); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000); })
                      .catch(() => alert('Could not open document.'));
                  }}>
                  {doc.original_name || doc.file_name || doc.document_type || 'Document'}
                </span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(doc.upload_date || doc.created_at).toLocaleDateString()}</span>
                {(doc.admin_status === 'accepted' || doc.status === 'approved') && <span style={{ fontSize: '10px', background: '#acf44e', color: '#1a1a1a', padding: '1px 6px', borderRadius: '4px' }}>✓</span>}
                {doc.admin_status === 'rejected' && <span style={{ fontSize: '10px', background: '#ff082f', color: '#fff', padding: '1px 6px', borderRadius: '4px' }}>✗</span>}
              </div>
            );
            return (
              <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>{item.label}</div>
                {itemDocs.length === 0 ? (
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>No documents uploaded</span>
                ) : hasCoDocs ? (
                  <div>
                    {primaryDocs.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#3a6ad9', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Primary Borrower</div>
                        {primaryDocs.map(renderDoc)}
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#7a5200', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Co-Borrower {coDocs[0]?.owner_name ? `(${coDocs[0].owner_name})` : ''}</div>
                      {coDocs.map(renderDoc)}
                    </div>
                  </div>
                ) : (
                  <div>{itemDocs.map(renderDoc)}</div>
                )}
              </div>
            );
          })}
          {/* Optional footer slot — used by the Review tab to mount the
              AI Underwriter analyze panel inside the section card for
              Income/Assets/Housing. Renders below the existing document
              list and is separated by its own border-t inside the panel. */}
          {children}
        </div>
      )}

      {showFlagModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '460px', width: '90%' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>Flag — {section.label}</h3>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>This message will be sent to the borrower with instructions to re-upload or address the issue.</p>
            <textarea value={flagNote} onChange={e => setFlagNote(e.target.value)}
              placeholder="Describe what's needed from the borrower..." rows={4} autoFocus
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => { setShowFlagModal(false); setFlagNote(''); }}
                style={{ padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                Cancel
              </button>
              <button onClick={() => { onFlagSection(section.id, flagNote); setShowFlagModal(false); setFlagNote(''); }}
                disabled={!flagNote.trim()}
                style={{ padding: '8px 16px', background: flagNote.trim() ? '#ff082f' : '#fca5a5', color: '#fff', border: 'none', borderRadius: '6px', cursor: flagNote.trim() ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600' }}>
                Flag & Notify Borrower
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── AnalyzeSectionPanel ─────────────────────────────────────────────────────
// Renders the "Analyze with AI" CTA + findings panel + needs list for one
// of: income / assets / housing. The parent owns analysis/needs state and
// passes the per-section slices through props so the panel stays a pure
// renderer.
//
// Doorly brand colors:
//   #3a6ad9 primary, #acf44e success, #ff082f error, #ffdd86 warning,
//   #fff7ed conditional/warning bg, #f0fdf4 approved bg, #fff0f3 fail bg.
// eslint-disable-next-line no-unused-vars
const AnalyzeSectionPanel = ({
  section,
  analysis,
  runAt,
  analyzing,
  error,
  oversizedAll,
  needs,
  needsBusy,
  manualText,
  onAnalyze,
  onPatchNeed,
  onDeleteNeed,
  onFlagNeed,
  onManualTextChange,
  onAddManualNeed,
  applying,
  applied,
  applyError,
  onApplyFinancials,
  // Section-level flag + response context. Drives the per-need
  // [Approve] / [Awaiting Response] state below — flagging a need
  // currently flags the parent section under the hood (single
  // section flag, multiple needs share the response surface), so
  // these flow through from the parent that owns reviewData +
  // documents and can compute them once.
  sectionFlagged,
  borrowerResponded,
}) => {
  // Format the persisted run_at timestamp as e.g. "May 7, 2025 at 2:34 PM".
  const fmtRunAt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${date} at ${time}`;
  };
  const fmtMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const confidenceCfg = {
    high:   { bg: '#dcfce7', text: '#166534', label: 'High' },
    medium: { bg: '#fef3c7', text: '#7a5200', label: 'Medium' },
    low:    { bg: '#fee2e2', text: '#9f1239', label: 'Low' },
  };
  const sectionLabel = { income: 'Income', assets: 'Assets', housing: 'Housing' }[section] || section;
  const statusCfg = {
    approved:    { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', label: 'Approved' },
    conditional: { bg: '#fff7ed', border: '#fed7aa', text: '#7a5200', label: 'Conditional' },
    needs_items: { bg: '#fff0f3', border: '#fecdd3', text: '#9f1239', label: 'Needs Items' },
  };
  const status = analysis?.status;
  const sc = statusCfg[status];

  // Map DB statuses (pending|resolved) → UI semantics (open|received).
  const uiStatusFor = (dbStatus) => (dbStatus === 'resolved' ? 'received' : 'open');

  const findingIcon = (type) => type === 'pass' ? '✅' : type === 'warning' ? '⚠️' : '❌';
  const findingColor = (type) => type === 'pass' ? '#166534' : type === 'warning' ? '#7a5200' : '#9f1239';

  return (
    // Render as a card-internal footer section. The parent ReviewSection
    // already provides the white card + border; we just add a top divider
    // and inherit the parent's interior padding via marginTop alone.
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          📋 ReadyCheck Output
        </span>
        {fmtRunAt(runAt) && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
            Analyzed {fmtRunAt(runAt)}
          </span>
        )}
      </div>

      {!analysis && !analyzing && (
        <div style={{ background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#6b7280', marginBottom: 10, lineHeight: 1.5 }}>
          Awaiting the document check — it runs when the borrower finishes step 5, and the output will populate here automatically.
        </div>
      )}

      {oversizedAll && (
        <div style={{ background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#7a5200', marginBottom: 10, lineHeight: 1.5 }}>
          ⚠ {oversizedAll.message}
          {Array.isArray(oversizedAll.skipped) && oversizedAll.skipped.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#92652a' }}>
              Skipped: {oversizedAll.skipped.join(', ')}
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#9f1239', marginBottom: 10 }}>
          {error}
        </div>
      )}

      {analysis && (
        <div style={{ background: sc?.bg || '#f9fafb', border: `1px solid ${sc?.border || '#e5e7eb'}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            {sc && (
              <span style={{ background: sc.text, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 9999, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {sc.label}
              </span>
            )}
            {analysis.summary && (
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{analysis.summary}</span>
            )}
          </div>

          {Array.isArray(analysis.countable) && analysis.countable.length > 0 && (
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Countable</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#6b7280' }}>Description</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#6b7280' }}>Amount</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#6b7280' }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.countable.map((c, i) => (
                    <tr key={i}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#111827' }}>{c.description || '—'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 600, color: '#111827', fontFamily: 'monospace' }}>{c.amount || '—'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{c.doc_reference || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {Array.isArray(analysis.findings) && analysis.findings.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Findings</div>
              {analysis.findings.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0', fontSize: 12, color: findingColor(f.type), lineHeight: 1.5 }}>
                  <span>{findingIcon(f.type)}</span>
                  <span>{f.description}</span>
                </div>
              ))}
            </div>
          )}

        </div>
      )}

      {/* Recommended-figures sub-card — only rendered for income/assets and
          only after Claude returns a recommended_* block. Shows the qualifying
          figure(s), basis, confidence badge, and an Apply-to-Financials CTA
          that pushes the values into borrower_income / asset_sources via
          PATCH /api/admin/borrowers/:id/financials. */}
      {section === 'income' && analysis?.recommended_monthly_income !== undefined && (() => {
        const rec = analysis.recommended_monthly_income;
        const cc = confidenceCfg[String(rec?.confidence || '').toLowerCase()] || confidenceCfg.medium;
        const hasAmount = rec && rec.amount != null && Number.isFinite(Number(rec.amount));
        return (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
              Recommended Qualifying Income
            </div>
            <div style={{ background: '#fafdf6', border: '1px solid #d6f0a8', borderRadius: 10, padding: 14 }}>
              {!hasAmount ? (
                <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                  Insufficient documentation to calculate a qualifying income figure.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                        Recommended Qualifying Monthly Income
                      </div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
                        {fmtMoney(rec.amount)}<span style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>/mo</span>
                      </div>
                    </div>
                    <span style={{ background: cc.bg, color: cc.text, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 9999, textTransform: 'uppercase', letterSpacing: '0.04em', alignSelf: 'flex-start' }}>
                      {cc.label} confidence
                    </span>
                  </div>
                  {rec.basis && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, lineHeight: 1.5 }}>
                      {rec.basis}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12 }}>
                    {applied && (
                      <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>
                        ✓ Applied — Monthly Income updated to {fmtMoney(applied.amount)}
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={applying || !!applied}
                      onClick={() => onApplyFinancials && onApplyFinancials('income')}
                      style={{
                        padding: '8px 16px',
                        background: applied ? '#86efac' : (applying ? '#d6f0a8' : '#acf44e'),
                        color: '#111827', border: 'none', borderRadius: 8,
                        fontSize: 12, fontWeight: 700,
                        cursor: applying || applied ? 'default' : 'pointer',
                        opacity: applying ? 0.7 : 1,
                      }}
                    >
                      {applied ? '✓ Applied' : (applying ? 'Applying…' : 'Apply to Financials')}
                    </button>
                  </div>
                  {applyError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#9f1239', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '8px 10px' }}>
                      {applyError}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}

      {section === 'assets' && analysis?.recommended_assets !== undefined && (() => {
        const rec = analysis.recommended_assets;
        const cc = confidenceCfg[String(rec?.confidence || '').toLowerCase()] || confidenceCfg.medium;
        const hasAny = rec && (
          ['checking_savings','retirement','stocks_bonds','crypto','other','total_verified']
            .some(k => Number.isFinite(Number(rec?.[k])) && Number(rec[k]) > 0)
        );
        const rows = rec ? [
          ['Checking / Savings', rec.checking_savings],
          ['Retirement',         rec.retirement],
          ['Stocks / Bonds',     rec.stocks_bonds],
          ['Crypto',             rec.crypto],
          ['Other',              rec.other],
        ].filter(([, v]) => Number(v) > 0) : [];
        const total   = Number(rec?.total_verified) || rows.reduce((s, [, v]) => s + Number(v || 0), 0);
        const reserves = Number(rec?.usable_for_reserves) || total;
        const downPay = Number(rec?.usable_for_down_payment) || total;
        return (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
              Recommended Verified Assets
            </div>
            <div style={{ background: '#fafdf6', border: '1px solid #d6f0a8', borderRadius: 10, padding: 14 }}>
              {!hasAny ? (
                <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                  Insufficient documentation to calculate verified asset figures.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <span style={{ background: cc.bg, color: cc.text, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 9999, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {cc.label} confidence
                    </span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {rows.map(([label, value]) => (
                        <tr key={label}>
                          <td style={{ padding: '6px 0', color: '#374151' }}>{label}</td>
                          <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'monospace', color: '#111827' }}>{fmtMoney(value)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={2} style={{ borderTop: '1px solid #d6f0a8', padding: 0 }} />
                      </tr>
                      <tr>
                        <td style={{ padding: '8px 0 4px', color: '#111827', fontWeight: 800 }}>Total Verified</td>
                        <td style={{ padding: '8px 0 4px', textAlign: 'right', fontFamily: 'monospace', color: '#111827', fontWeight: 800 }}>{fmtMoney(total)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 0', color: '#374151' }}>Usable for Reserves</td>
                        <td style={{ padding: '4px 0', textAlign: 'right', fontFamily: 'monospace', color: '#111827' }}>{fmtMoney(reserves)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 0', color: '#374151' }}>Usable for Down Payment</td>
                        <td style={{ padding: '4px 0', textAlign: 'right', fontFamily: 'monospace', color: '#111827' }}>{fmtMoney(downPay)}</td>
                      </tr>
                    </tbody>
                  </table>
                  {rec?.basis && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 10, lineHeight: 1.5 }}>
                      {rec.basis}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12 }}>
                    {applied && (
                      <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>
                        ✓ Applied — Verified assets updated
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={applying || !!applied}
                      onClick={() => onApplyFinancials && onApplyFinancials('assets')}
                      style={{
                        padding: '8px 16px',
                        background: applied ? '#86efac' : (applying ? '#d6f0a8' : '#acf44e'),
                        color: '#111827', border: 'none', borderRadius: 8,
                        fontSize: 12, fontWeight: 700,
                        cursor: applying || applied ? 'default' : 'pointer',
                        opacity: applying ? 0.7 : 1,
                      }}
                    >
                      {applied ? '✓ Applied' : (applying ? 'Applying…' : 'Apply to Financials')}
                    </button>
                  </div>
                  {applyError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#9f1239', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '8px 10px' }}>
                      {applyError}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Additional Needs panel — sits below the analyze button + findings
          inside the same parent card. Always visible so admin can manage
          needs even without having run analysis yet. */}
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Additional Needs ({(needs || []).filter(n => uiStatusFor(n.status) === 'open').length} open)
        </div>
        {(!needs || needs.length === 0) && (
          <div style={{ fontSize: 12, color: '#9ca3af', padding: '6px 0' }}>No additional needs yet.</div>
        )}
        {(needs || []).map(n => {
          const ui = uiStatusFor(n.status);
          const isAi = n.source === 'ai_analysis';
          const isResolved = ui === 'received';
          // Admin uploaded docs on the borrower's behalf — a cosmetic marker
          // stamped on borrower_response. The need stays open until a
          // ReadyCheck re-run reads the docs and resolves it.
          const adminUploaded = (n.borrower_response || '').startsWith('📎 Admin uploaded');
          const busy = needsBusy === n.id;
          return (
            <div key={n.id} style={{ background: isResolved ? '#f0fdf4' : '#f9fafb', border: `1px solid ${isResolved ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: 8, padding: '10px 12px', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{n.title}</span>
                  {isAi && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#eff6ff', color: '#3a6ad9' }}>AI</span>}
                  {isResolved && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#acf44e', color: '#1a1a1a' }}>RECEIVED</span>}
                  {!isResolved && adminUploaded && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af' }}>📎 UPLOADED</span>}
                </div>
                {n.description && <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>{n.description}</div>}
                {!isResolved && adminUploaded && (
                  <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 600, lineHeight: 1.5, marginTop: 4 }}>
                    {n.borrower_response} — re-run ReadyCheck to process.
                  </div>
                )}
              </div>
              {/* Per-need actions.
                    · Flag (default state)        → opens existing section-flag
                                                    flow with this need's text
                                                    as the note (admin can edit
                                                    before sending).
                    · Awaiting Response (flagged) → status pill instead of
                                                    Approve while we wait on
                                                    the borrower. Re-Flag
                                                    stays available so admin
                                                    can resend with edits;
                                                    Dismiss waives the need.
                    · Approve (response in)       → only renders once the
                                                    section's borrower
                                                    response (text or uploaded
                                                    docs) is on file. Flips
                                                    status to received.
                  sectionFlagged + borrowerResponded come from the parent
                  (computed once from reviewData.sections + documents). */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                {!isResolved && borrowerResponded && (
                  <button type="button" disabled={busy}
                    onClick={() => onPatchNeed(n.id, 'received')}
                    title="Approve / mark received"
                    style={{ padding: '4px 10px', background: busy ? '#e5e7eb' : '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700 }}>
                    Approve
                  </button>
                )}
                {!isResolved && sectionFlagged && !borrowerResponded && (
                  <span style={{ padding: '4px 10px', background: '#fff7ed', color: '#7a5200', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    Awaiting Response
                  </span>
                )}
                <button type="button" disabled={busy || !onFlagNeed}
                  onClick={() => onFlagNeed && onFlagNeed(n)}
                  title="Flag — request this from the borrower"
                  style={{ padding: '4px 10px', background: busy ? '#e5e7eb' : '#ff082f', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700 }}>
                  {(sectionFlagged || isResolved) ? 'Re-Flag' : 'Flag'}
                </button>
                <button type="button" disabled={busy}
                  onClick={() => onDeleteNeed(n.id)}
                  title="Dismiss"
                  style={{ padding: '4px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontSize: 11, fontWeight: 600 }}>
                  Dismiss
                </button>
              </div>
            </div>
          );
        })}

        {/* Manual add */}
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input
            type="text"
            placeholder={`Add a ${sectionLabel.toLowerCase()} need…`}
            value={manualText}
            onChange={(e) => onManualTextChange(section, e.target.value)}
            style={{ flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }}
            onKeyDown={(e) => { if (e.key === 'Enter') onAddManualNeed(section); }}
          />
          <button
            type="button"
            onClick={() => onAddManualNeed(section)}
            disabled={!manualText.trim()}
            style={{ padding: '7px 14px', background: manualText.trim() ? '#3a6ad9' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: manualText.trim() ? 'pointer' : 'not-allowed' }}
          >
            Add Need
          </button>
        </div>
      </div>
    </div>
  );
};

const DocumentsTab = ({ documents, borrowerId, onUploadComplete, onMarkAllReviewed, onSwitchTab, housingStatus, user, closingDisclosure, isREDesk = false }) => {
  const [clearingDocs, setClearingDocs] = React.useState(false);
  // Sectioned Documents tab — replaces the prior flat-list-with-pill-filter
  // view. Each row is one document_type slot; missing docs show "Not on file"
  // plus an Upload or Generate button. Multi-version doc_types stack under
  // their slot. LOX section is read-only aggregation across the existing
  // flagged_*, lox_letter, and miscellaneous-LOX document_types — the
  // Verification page and Review tab remain the canonical write surfaces.
  const [seModal, setSeModal] = React.useState(null);
  // 🔑 THE STORED OPTION'S PROGRAM FEE RATE AND BASIS, FROM THE SERVER (push C item 4, Jesse 2026-10-31 — backend
  // constants/feeSchedule programFeeTermsForOption, the borrower card's source). Never guessed from the resale percentage,
  // never a flat 2%: retired options 2 (0%) and 3 (3% of the price) resolve as they were quoted.
  const [seFeeTerms, setSeFeeTerms] = React.useState(null);
  React.useEffect(() => {
    setSeFeeTerms(null);
    const opt = seModal && seModal.selected_option;
    if (opt == null || opt === '') return undefined;
    let live = true;
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/smartestimate/fee-terms`, { params: { option: opt }, headers: { Authorization: `Bearer ${adminToken()}` } })
      .then((r) => { if (live) setSeFeeTerms(r.data); })
      .catch(() => { if (live) setSeFeeTerms({ unavailable: true }); });
    return () => { live = false; };
  }, [seModal, borrowerId]);
  const [generating, setGenerating] = React.useState(null);
  const [uploadingType, setUploadingType] = React.useState(null);
  const [toast, setToast] = React.useState(null);
  // ── 🚨 "IS ANYTHING IN THE WRONG SLOT?" ASKED HERE, NOT IN THE RUN ─────────
  //
  // Jesse: "build the classifier pass for misfiled documents" — after ReadyCheck
  // tried to answer it inside the underwriting prompt, wrote a document-by-
  // document essay, and returned 502 at 187s with no analysis at all.
  //
  // 🔑 THE FINDINGS WERE REAL: a 1040 transcript and a Form 8879 in W-2 slots on
  // Cathy's file. A per-document question belongs in a per-document pass, on
  // demand, where nobody is waiting on it.
  //
  // ⚠️ ON DEMAND AND NOT ON MOUNT. The pass reads PDF bytes and calls a model
  // per document; running it every time somebody opens the tab would be the
  // shelved corpus pass again, one file at a time. A button is the whole
  // difference between a tool and a background cost.
  const [typeCheck, setTypeCheck] = React.useState(null);
  const [typeChecking, setTypeChecking] = React.useState(false);
  const runTypeCheck = async () => {
    setTypeChecking(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/borrowers/${borrowerId}/classify-documents`, {},
        { headers: { Authorization: `Bearer ${adminToken()}` }, timeout: 180_000 });
      setTypeCheck(r.data || null);
    } catch (e) {
      setToast({ message: e.response?.data?.error || 'Could not check document types.', type: 'error' });
    } finally { setTypeChecking(false); }
  };
  const [collapsedIA, setCollapsedIA] = React.useState({
    w2: true, selfemployed: true, _1099: true, bank_statements: true, other: true, assets: true,
  });
  // Income & Assets — hide empty sub-sections by default. Admins can
  // surface them via the "Show all sub-sections" toggle when they need
  // to upload into a category that has no docs yet. Documents tab is
  // primarily an aggregator; the Review tab is the canonical entry
  // point for new uploads, so hiding empty sub-sections here cleans up
  // the visual without losing the ability to upload manually.
  const [showAllIA, setShowAllIA] = React.useState(false);
  // Open-state for top-level <Section> blocks. Keyed by title so a
  // re-render of DocumentsTab (toast / upload / generate) doesn't blow
  // away which section the admin had open. Defaults to closed.
  const [sectionOpen, setSectionOpen] = React.useState({});
  const fileInputRef = React.useRef(null);
  const pendingUploadRef = React.useRef(null);

  // SmartStart enrollment for the Underwriting > SmartStart Certificate row.
  // Reuses the existing admin endpoint (GET /api/admin/users/:id/smartstart,
  // shaped by utils/smartstart.js#getSmartStartStatus). Borrowers who were
  // never enrolled return { enrolled: false } and the row is suppressed.
  const [smartStartEnrollment, setSmartStartEnrollment] = React.useState(null);
  React.useEffect(() => {
    if (!borrowerId) return;
    let cancelled = false;
    axios
      .get(`${API}/api/admin/users/${borrowerId}/smartstart`, {
        headers: { Authorization: `Bearer ${adminToken()}` },
      })
      .then((r) => { if (!cancelled) setSmartStartEnrollment(r.data || null); })
      .catch(() => { if (!cancelled) setSmartStartEnrollment(null); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId]);

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const adminToken = () => localStorage.getItem('doorly_admin_token');
  const docsByType = React.useMemo(() => {
    const map = {};
    (documents || []).forEach((d) => {
      const t = (d.document_type || '').toLowerCase();
      if (!map[t]) map[t] = [];
      map[t].push(d);
    });
    Object.keys(map).forEach((t) => {
      map[t].sort((a, b) => new Date(b.upload_date || b.created_at || 0) - new Date(a.upload_date || a.created_at || 0));
    });
    return map;
  }, [documents]);
  const getDocs = (type) => docsByType[type.toLowerCase()] || [];

  // LOX aggregation buckets — pulled from the existing documents prop so
  // there's no parallel write path; the Verification page + Review tab
  // are the source of truth for adding LOXs.
  const AUTO_LOX_TYPES = new Set(['bankruptcy_lox', 'foreclosure_lox', 'employment_gap_lox', 'mortgage_default_lox']);
  const CREDIT_LOX_TYPES = new Set(['lox', 'lox_letter']);
  const loxAuto = (documents || []).filter((d) => AUTO_LOX_TYPES.has((d.document_type || '').toLowerCase()));
  const loxCredit = (documents || []).filter((d) => CREDIT_LOX_TYPES.has((d.document_type || '').toLowerCase()));
  const loxFlagged = (documents || []).filter((d) => (d.document_type || '').toLowerCase().startsWith('flagged_'));

  const parseSeNotes = (doc) => { try { return doc.notes ? JSON.parse(doc.notes) : null; } catch { return null; } };

  // axios returns blob errors with the body as a Blob too, which makes
  // the actual server message invisible until we read it. Walk through
  // the response object to surface a meaningful toast (instead of the
  // prior generic "File not found on server" which masked auth, server,
  // or routing failures).
  const blobErrorMessage = async (err, fallback) => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = (() => { try { return JSON.parse(text); } catch (_) { return null; } })();
        const msg = parsed?.error || text;
        return status ? `${status}: ${msg}` : msg;
      } catch (_) { /* fall through */ }
    }
    if (status) return `${status}: ${data?.error || err?.message || fallback}`;
    return err?.message || fallback;
  };

  const viewDoc = (docId) => {
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/documents/${docId}/view`, {
      headers: { Authorization: `Bearer ${adminToken()}` }, responseType: 'blob',
    }).then((r) => {
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }).catch(async (err) => {
      console.error('[viewDoc]', err);
      const msg = await blobErrorMessage(err, 'View failed.');
      setToast({ message: msg, type: 'error' });
    });
  };

  const downloadDoc = (docId, name) => {
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/documents/${docId}/view`, {
      headers: { Authorization: `Bearer ${adminToken()}` }, responseType: 'blob',
    }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name || 'document.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }).catch(async (err) => {
      console.error('[downloadDoc]', err);
      const msg = await blobErrorMessage(err, 'Download failed.');
      setToast({ message: msg, type: 'error' });
    });
  };

  const generate = async (endpoint, label) => {
    setGenerating(endpoint);
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${adminToken()}` },
      });
      setToast({ message: `${label} generated successfully.`, type: 'success' });
      onUploadComplete?.();
    } catch (err) {
      setToast({ message: err.response?.data?.error || `${label} generation failed.`, type: 'error' });
    } finally { setGenerating(null); }
  };

  const triggerUpload = (document_type, doc_subcategory, doc_category) => {
    pendingUploadRef.current = { document_type, doc_subcategory, doc_category };
    fileInputRef.current?.click();
  };
  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !pendingUploadRef.current) return;
    const { document_type, doc_subcategory, doc_category } = pendingUploadRef.current;
    pendingUploadRef.current = null;
    if (file.type !== 'application/pdf') {
      setToast({ message: 'PDF files only.', type: 'error' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setToast({ message: 'File too large (max 20MB).', type: 'error' });
      return;
    }
    setUploadingType(document_type);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', document_type);
    if (doc_subcategory) formData.append('doc_subcategory', doc_subcategory);
    if (doc_category) formData.append('doc_category', doc_category);
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/upload-document`, formData, {
        headers: { Authorization: `Bearer ${adminToken()}`, 'Content-Type': 'multipart/form-data' },
      });
      setToast({ message: 'Uploaded successfully.', type: 'success' });
      onUploadComplete?.();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Upload failed.', type: 'error' });
    } finally { setUploadingType(null); }
  };

  // ─── Visual primitives ──────────────────────────────────────────────
  // Top-level collapsible section. Open state lives in DocumentsTab so
  // it survives parent re-renders — when toast/upload/generate state
  // changes it would otherwise reset every section to collapsed because
  // Section is an inline-defined component (its identity changes per
  // render). Section is now a controlled presentational component.
  const Section = ({ title, children }) => {
    const open = !!sectionOpen[title];
    const toggle = () => setSectionOpen(s => ({ ...s, [title]: !s[title] }));
    return (
      <div style={{ marginTop: 22, marginBottom: 10 }}>
        <div onClick={toggle}
          role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
          style={{ fontSize: 13, fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.06em', paddingBottom: 6, borderBottom: '2px solid #3a6ad9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}>
          <span>{title}</span>
          <span style={{ fontSize: 12, color: '#3a6ad9' }}>{open ? '▾' : '▸'}</span>
        </div>
        {open && <div style={{ marginTop: 10 }}>{children}</div>}
      </div>
    );
  };
  const SubsectionHeader = ({ children, collapsible, collapsed, onToggle }) => (
    <div onClick={collapsible ? onToggle : undefined}
      style={{ marginTop: 12, marginBottom: 6, padding: '6px 8px', background: '#f3f4f6', borderRadius: 6, fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', cursor: collapsible ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>{children}</span>
      {collapsible && <span style={{ fontSize: 10, color: '#6b7280' }}>{collapsed ? '▸' : '▾'}</span>}
    </div>
  );
  const fmtDate = (v) => { try { return v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; } catch { return '—'; } };

  const DocActions = ({ doc }) => (
    <>
      <button onClick={() => viewDoc(doc.id)}
        style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>View</button>
      <button onClick={() => downloadDoc(doc.id, doc.original_name || 'document.pdf')}
        style={{ padding: '4px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Download</button>
    </>
  );

  // One slot = one document_type. Renders all versions, plus an Upload
  // or Generate button when the slot is empty (or when allowMulti).
  // acceptedBadge: { signedAt, signerName? } — when supplied, renders a
  //   green "Signed ✓ {date}" pill next to the slot title (the doc the
  //   badge represents is a signed PDF in every current use — LE/CD
  //   signed bytes, IDs e-consent record, SmartEstimate signature).
  // acceptedViewDocId: optional doc id — when set, the most-recent row's
  //   View button opens THIS doc instead of the row's own id. Used for
  //   the LE slot so the View on the accepted row pulls the stamped
  //   itp_signed_le bytes (with the borrower's drawn signature + date)
  //   rather than the unsigned DocMagic original.
  const DocSlot = ({ label, document_type, alias_types, doc_subcategory, doc_category, generateEndpoint, generateLabel, generateButton, uploadable = true, readOnly = false, allowMulti = false, note, special, acceptedBadge, acceptedViewDocId }) => {
    // alias_types lets one slot pull in documents stored under
    // alternative document_type values without changing where new
    // uploads route. The VOR slot uses this to also surface VORs that
    // landed under 'vor_vom' (the borrower-portal landlord submission
    // path), which would otherwise be hidden from the Documents tab.
    const aliasList = Array.isArray(alias_types) ? alias_types : [];
    const allTypes = [document_type, ...aliasList].filter(Boolean);
    const docs = allTypes.flatMap((t) => getDocs(t))
      .sort((a, b) => new Date(b.upload_date || b.created_at || 0) - new Date(a.upload_date || a.created_at || 0));
    const isUploading = uploadingType === document_type;
    const isGenerating = generateEndpoint && generating === generateEndpoint;
    const showUpload = uploadable && !readOnly && (docs.length === 0 || allowMulti);
    // Generate is independent of readOnly. Auto-populated docs (LE/CD)
    // can be both "Read-only" (no manual upload) AND admin-triggered
    // (Generate) — DocMagic emits the PDF and we want the indicator
    // alongside the Generate button.
    const showGenerate = !!generateEndpoint;

    return (
      <div style={{ marginBottom: 8, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{label}</span>
              {acceptedBadge && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: '#acf44e', color: '#1a1a1a', letterSpacing: '0.3px' }}>
                  Signed ✓{acceptedBadge.signedAt ? ` ${fmtDate(acceptedBadge.signedAt)}` : ''}
                </span>
              )}
            </div>
            {note && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{note}</div>}
            {acceptedBadge?.signerName && (
              <div style={{ fontSize: 10, color: '#16a34a', marginTop: 2, fontWeight: 600 }}>
                Signed by {acceptedBadge.signerName}
              </div>
            )}
            {docs.length === 0 && (
              <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', marginTop: 2 }}>Not on file</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {showGenerate && (
              <button disabled={isGenerating}
                onClick={() => generate(generateEndpoint, generateLabel || label)}
                style={{ padding: '5px 12px', background: isGenerating ? '#bfdbfe' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, cursor: isGenerating ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {isGenerating ? <><span style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Generating…</> : (generateButton || 'Generate')}
              </button>
            )}
            {showUpload && (
              <button disabled={isUploading}
                onClick={() => triggerUpload(document_type, doc_subcategory, doc_category)}
                style={{ padding: '5px 12px', background: isUploading ? '#e5e7eb' : '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: isUploading ? 'wait' : 'pointer', fontSize: 11, fontWeight: 600 }}>
                {isUploading ? 'Uploading…' : (docs.length === 0 ? 'Upload' : '+ Upload another')}
              </button>
            )}
            {readOnly && docs.length === 0 && (
              <span style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>Read-only · auto-populated</span>
            )}
          </div>
        </div>
        {docs.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {docs.map((d, i) => {
              const isSE = special === 'smart_estimate';
              const isSESigned = isSE && (d.mime_type === 'application/pdf' || d.has_file_data);
              // Only the most-recent doc (i === 0) is considered the
              // "accepted" version when acceptedBadge is supplied.
              // Older versions stay unflagged so admins can see history
              // without each revision claiming acceptance.
              const isAcceptedRow = !!acceptedBadge && i === 0;
              // For the accepted row, the View button can be wired to a
              // different doc id (e.g. itp_signed_le instead of
              // loan_estimate) so the admin sees the stamped/signed PDF.
              const rowViewDocId = (isAcceptedRow && acceptedViewDocId) || d.id;
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fff', border: '1px solid #f3f4f6', borderRadius: 6 }}>
                  <span style={{ fontSize: 14 }}>📄</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.original_name || d.filename || label}
                      {docs.length > 1 && i === 0 && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 5px', background: '#3a6ad9', color: '#fff', borderRadius: 3 }}>Latest</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(d.upload_date || d.created_at)}</div>
                  </div>
                  {isSE && parseSeNotes(d) && (
                    <button onClick={() => setSeModal(parseSeNotes(d))}
                      style={{ padding: '3px 8px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>Details</button>
                  )}
                  {isSE && !isSESigned && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', background: '#f3f4f6', color: '#6b7280', borderRadius: 4 }}>Awaiting Sig</span>}
                  {isSE && isSESigned && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', background: '#acf44e', color: '#1a1a1a', borderRadius: 4 }}>Signed ✓</span>}
                  {isAcceptedRow && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', background: '#acf44e', color: '#1a1a1a', borderRadius: 4 }}>
                      Signed ✓{acceptedBadge.signedAt ? ` ${fmtDate(acceptedBadge.signedAt)}` : ''}
                    </span>
                  )}
                  <DocActions doc={{ ...d, id: rowViewDocId }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Read-only LOX aggregator row
  const LoxRow = ({ label, doc, status }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff', border: '1px solid #f3f4f6', borderRadius: 6, marginBottom: 4 }}>
      <span style={{ fontSize: 14 }}>📝</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>
          {fmtDate(doc.upload_date || doc.created_at)}
          {status && <span style={{ marginLeft: 6 }}>· {status}</span>}
        </div>
      </div>
      <button onClick={() => viewDoc(doc.id)}
        style={{ padding: '3px 9px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>View</button>
    </div>
  );

  return (
    <div>
      {/* Hidden file picker — drives every Upload button via ref */}
      <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={onFileSelected} />

      {/* Toast — auto-dismisses after 3s */}
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, padding: '10px 16px',
          background: toast.type === 'success' ? '#f4fee8' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#acf44e' : '#fecaca'}`,
          color: toast.type === 'success' ? '#1a1a1a' : '#9f1239',
          borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}>
          {toast.type === 'success' ? '✓ ' : '⚠ '}{toast.message}
        </div>
      )}

      {/* ── 🚨 DOCUMENT TYPE CHECK — flags, never moves ───────────────────────
          Jesse, asked whether to re-file automatically: "flag instead of
          auto-moving." document_type decides investor-package inclusion and
          borrower visibility, so the fix stays the existing Re-tag control on
          the Review tab — one click, with an audit row. This only ever says
          what it saw. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" onClick={runTypeCheck} disabled={typeChecking}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700, borderRadius: 8,
            border: '1px solid #d1d5db', background: typeChecking ? '#f3f4f6' : '#fff',
            color: '#374151', cursor: typeChecking ? 'default' : 'pointer' }}>
          {typeChecking ? 'Checking document types…' : 'Check document types'}
        </button>
        {typeCheck && (
          <span style={{ fontSize: 12, color: typeCheck.mismatches.length ? '#9f1239' : '#15803d', fontWeight: 600 }}>
            {typeCheck.mismatches.length
              ? `${typeCheck.mismatches.length} possible misfile${typeCheck.mismatches.length === 1 ? '' : 's'}`
              : 'nothing filed in the wrong place'}
          </span>
        )}
      </div>
      {typeCheck && typeCheck.mismatches.length > 0 && (
        <div style={{ border: '1px solid #fecaca', background: '#fff5f5', borderRadius: 10,
          padding: '12px 14px', marginBottom: 16, fontSize: 12.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: '#9f1239' }}>
            Possible misfiles — re-tag from the Review tab if you agree
          </div>
          {typeCheck.mismatches.map((m) => (
            <div key={m.id} style={{ padding: '6px 0', borderTop: '1px solid #fee2e2' }}>
              <div style={{ fontWeight: 600 }}>{m.name || `document #${m.id}`}</div>
              <div style={{ color: '#6b7280' }}>
                filed as <code>{m.document_type}</code> · reads as{' '}
                <code>{m.detail?.seen || 'unknown'}</code>
                {m.detail?.confidence ? ` · ${m.detail.confidence} confidence` : ''}
              </div>
              {/* 🔑 THE EVIDENCE IS THE POINT. "reads as tax_return" is an
                  opinion; "Form 1040 Account Transcript" is something a staff
                  member can check in two seconds by opening the document. */}
              {m.detail?.evidence && (
                <div style={{ color: '#374151', fontStyle: 'italic', marginTop: 2 }}>
                  “{m.detail.evidence}”
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* ⚠️ SKIPPED IS SHOWN, NEVER FOLDED INTO "CLEAN". A document over the 4 MB
          cap or an image with no extractable text is a document nobody
          classified — reporting it as fine is the one thing this must not do. */}
      {typeCheck && (() => {
        const unscanned = (typeCheck.documents || []).filter(
          (d) => d.outcome === 'skipped_too_large' || d.outcome === 'uncertain'
            || d.outcome === 'skipped_no_bytes' || d.outcome === 'not_reached');
        if (unscanned.length === 0) return null;
        return (
          <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb',
            border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}>
            {unscanned.length} document{unscanned.length === 1 ? '' : 's'} could not be
            classified (too large, not text-extractable, or the classifier was unavailable) —
            not checked rather than clean.
          </div>
        );
      })()}

      {/* Clear the Documents-tab "needs review" badge. Counts the primary's
          docs with admin_status pending/null (auto-generated LOX/1003 etc.
          have no per-doc Accept row, so this is the only way to clear them). */}
      {(() => {
        const pendingCount = (documents || []).filter(d => d.owner_type !== 'coborrower' && (!d.admin_status || d.admin_status === 'pending')).length;
        if (pendingCount === 0 || typeof onMarkAllReviewed !== 'function') return null;
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button type="button" disabled={clearingDocs}
              onClick={async () => { setClearingDocs(true); try { await onMarkAllReviewed(); } finally { setClearingDocs(false); } }}
              title="Mark all documents awaiting review as reviewed — clears the Documents tab badge"
              style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', background: clearingDocs ? '#e5e7eb' : '#3a6ad9', color: clearingDocs ? '#9ca3af' : '#fff', border: 'none', borderRadius: 7, cursor: clearingDocs ? 'default' : 'pointer' }}>
              {clearingDocs ? 'Clearing…' : `✓ Mark ${pendingCount} reviewed`}
            </button>
          </div>
        );
      })()}

      {/* Sections outside Property Documents are hidden for the Real
          Estate Desk role — they don't need the loan/UW/financial doc
          slots, just the property paperwork. Property Documents below
          renders for every role. */}
      {!isREDesk && (<>
      {/* ─── LOAN APPLICATION ─── */}
      {/* 1003 is auto-written when the borrower submits their
          application — no admin Generate button. The slot just lists
          whatever's already on file. */}
      <Section title="Loan Application">
        <DocSlot label="Uniform Residential Loan Application (1003)"
          document_type="urla_1003" doc_subcategory="loan_application" doc_category="loan_documents"
          uploadable={false} readOnly
          note="Auto-generated after ReadyCheck submission. On joint files, both borrowers must submit their ReadyCheck before the 1003 generates — it pulls verified income, assets, and credit from the combined underwriting snapshot." />
      </Section>

      {/* ─── DISCLOSURES ─── */}
      <Section title="Disclosures">
        {/* LE / CD are emitted by DocMagic from the Closings flow when
            the package is generated there. No admin-side Generate
            button on this tab — the slot just lists whatever DocMagic
            has already produced. */}
        {/* LE card — gates the "Signed ✓" badge on the signed-document
            FK rather than intent_to_proceed_at, which can be NULL on
            borrowers whose ITP timestamp was wiped by an offer-cycle
            reset or who signed under an earlier code path that didn't
            populate it. Falls back to the signed doc's upload_date for
            the pill text when the timestamp column is empty. Without
            this, the LE card's View button on the latest row opened the
            unsigned DocMagic PDF (i.e. acceptedViewDocId never took
            effect because isAcceptedRow needs acceptedBadge truthy). */}
        <DocSlot label="Loan Estimate (all versions)"
          document_type="loan_estimate" doc_subcategory="disclosures" doc_category="loan_documents"
          uploadable={false} readOnly allowMulti
          acceptedBadge={(() => {
            const itpDocId = user?.intent_to_proceed_signed_document_id;
            const hasItpAt = !!user?.intent_to_proceed_at;
            if (!itpDocId && !hasItpAt) return null;
            const itpDoc = itpDocId
              ? (documents || []).find((d) => d.id === itpDocId)
              : (documents || []).find((d) => d.document_type === 'itp_signed_le');
            // signerName intentionally omitted — the 'Signed by <name>'
            // copy under the badge was redundant noise on the LE card.
            // The Signed ✓ pill in the title + the signed-doc timestamp
            // are already enough; the signer's name lives on the
            // borrower row and is visible elsewhere.
            return {
              signedAt: user?.intent_to_proceed_at || itpDoc?.upload_date || null,
            };
          })()}
          acceptedViewDocId={user?.intent_to_proceed_signed_document_id || null}
          note="Auto-populated when the LE package is generated from the Closings tab" />
        {/* CD card — mirrors the LE card. closing_disclosure_signed is
            included as an alias so the signed PDF appears as a row even
            when no unsigned closing_disclosure draft was ever stored
            (borrower-signed flows write only the signed copy). Without
            the alias the card rendered "Not on file" with a Signed ✓
            pill in the title, which read inconsistently next to LE. */}
        <DocSlot label="Closing Disclosure (all versions)"
          document_type="closing_disclosure" alias_types={['closing_disclosure_signed']}
          doc_subcategory="disclosures" doc_category="loan_documents"
          uploadable={false} readOnly allowMulti
          acceptedBadge={closingDisclosure?.cd_signed_at ? {
            signedAt: closingDisclosure.cd_signed_at,
            signerName: closingDisclosure.cd_signed_typed_name || null,
          } : null}
          acceptedViewDocId={closingDisclosure?.cd_signed_document_id || null}
          note="Auto-populated when the CD package is generated from the Closings tab" />
        {/* 🔑 THE NOTICES SENT, READ-ONLY (2026-10-29 — found by e2e classic-files-staff-view): every adverse-action and
            counteroffer notice posted to the file's documents is listed here too, not only in the Module. */}
        <DocSlot label="Adverse action notices (all sent)"
          document_type="adverse_action_notice" doc_subcategory="disclosures" doc_category="loan_documents"
          uploadable={false} readOnly allowMulti
          note="Posted to the file when a notice is sent" />
        <DocSlot label="Initial Disclosures"
          document_type="initial_disclosures" doc_subcategory="disclosures" doc_category="loan_documents"
          uploadable={false} readOnly
          acceptedBadge={user?.offer_review_completed_at ? {
            signedAt: user.offer_review_completed_at,
            signerName: user.intent_to_proceed_typed_name || null,
          } : null}
          note="Auto-populated when borrower e-consents during the offer-accepted review module" />
        <DocSlot label="SmartEstimate (signed)" special="smart_estimate"
          document_type="smart_estimate" doc_subcategory="underwriting" doc_category="loan_documents"
          readOnly note="Auto-populated when borrower signs" />
        {/* ComplianceCheck audit certificate — closing-gate compliance
            document. API-driven generation triggered from Closing Module
            Step 4 (no manual upload); read-only here. */}
        <DocSlot label="ComplianceCheck Certificate"
          document_type="compliance_certificate" doc_subcategory="disclosures" doc_category="loan_documents"
          uploadable={false} readOnly
          note="Auto-generated by the ComplianceCheck audit when run from Closing Module Step 4." />
      </Section>

      {/* ─── UNDERWRITING ─── */}
      <Section title="Underwriting">
        {/* SmartStart certificate — closing-gate compliance document.
            Row hidden entirely for borrowers who were never enrolled
            ({ enrolled: false }). Pending while modules incomplete,
            green with a View button once certificate is issued. */}
        {smartStartEnrollment?.enrolled && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 0',
            borderBottom: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{smartStartEnrollment.completedAt ? '🎓' : '📚'}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>
                  SmartStart™ Certificate of Completion
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  {smartStartEnrollment.completedAt
                    ? `Completed ${fmtDate(smartStartEnrollment.completedAt)}`
                    : `Enrolled ${fmtDate(smartStartEnrollment.enrolledAt)} · ${smartStartEnrollment.completedCount || 0} of ${smartStartEnrollment.totalModules || 6} modules complete`}
                </div>
              </div>
            </div>
            {smartStartEnrollment.completedAt ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{
                  background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac',
                  borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600,
                }}>✓ Complete</span>
                {/* Hits the admin SmartStart certificate endpoint, which
                    self-heals when certificate_document_id is null on a
                    completed enrollment (issueCertificate ran on demand).
                    That keeps the button reliable regardless of DB drift. */}
                <button
                  onClick={async () => {
                    try {
                      const r = await axios.get(
                        `${API}/api/admin/users/${borrowerId}/smartstart-certificate`,
                        { headers: { Authorization: `Bearer ${adminToken()}` }, responseType: 'blob' }
                      );
                      const url = URL.createObjectURL(r.data);
                      window.open(url, '_blank', 'noopener');
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    } catch (err) {
                      const msg = await blobErrorMessage(err, 'Unable to open certificate.');
                      setToast({ message: msg, type: 'error' });
                    }
                  }}
                  style={{
                    background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
                    padding: '4px 10px', fontSize: 12, color: '#3a6ad9',
                    cursor: 'pointer', fontWeight: 600,
                  }}>
                  View Certificate
                </button>
              </div>
            ) : (
              <span style={{
                background: '#fffbeb', color: '#b45309', border: '1px solid #ffdd86',
                borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
              }}>
                ⏳ Pending — Required Before Closing
              </span>
            )}
          </div>
        )}
        <DocSlot label="Underwriting Summary"
          document_type="uw_summary" doc_subcategory="underwriting" doc_category="loan_documents"
          generateEndpoint="generate-uw-summary" generateButton="Generate UW Summary" generateLabel="UW Summary"
          uploadable={false} allowMulti />
        <DocSlot label="Credit Report (tri-merge)"
          document_type="credit_report" doc_subcategory="underwriting" doc_category="loan_documents"
          readOnly note="Pulled via Xactus" />
        <DocSlot label="OFAC / AML Clearance"
          document_type="ofac_clearance" doc_subcategory="underwriting" doc_category="loan_documents"
          generateEndpoint="generate-ofac-clearance" generateButton="Generate OFAC Clearance" generateLabel="OFAC Clearance"
          uploadable={false} note="ComplianceEase integration pending" />
        <DocSlot label="Loan Estimate — Signed (Intent to Proceed)"
          document_type="itp_signed_le" doc_subcategory="underwriting" doc_category="loan_documents"
          readOnly
          acceptedBadge={user?.intent_to_proceed_at ? {
            signedAt: user.intent_to_proceed_at,
            signerName: user.intent_to_proceed_typed_name || null,
          } : null}
          note="Auto-populated when borrower signs the ITP acknowledgment on the offer-accepted review (Step 1)" />
      </Section>

      {/* ─── INCOME & ASSETS ─── */}
      {/* Subsections are config-driven so we can compute hasDocs per
          subsection from a single source of truth and skip the ones
          that have nothing on file. The "Show all sub-sections" toggle
          surfaces empty buckets when an admin needs to upload manually
          into a category that hasn't received documents yet. */}
      {(() => {
        // alias_types lets each slot pull in documents stored under
        // legacy or borrower-portal-specific document_type values. The
        // borrower's document portal (backend/routes/documents.js) saves
        // pluralized names — paystubs, w2s, bank_statements — so without
        // these aliases the Documents tab couldn't see Review-tab
        // uploads. Rule of thumb: include both singular + plural and
        // any historical synonym we've observed in production data.
        const iaSubs = [
          { key: 'w2', title: 'W-2 / Traditional Employment', slots: [
            { label: 'Pay Stubs',   document_type: 'paystub', alias_types: ['paystubs', 'pay_stub', 'pay_stubs'], doc_subcategory: 'income_w2', allowMulti: true },
            { label: 'W-2s',        document_type: 'w2',      alias_types: ['w2s', 'w-2', 'w_2', 'w2_form', 'w2_forms'], doc_subcategory: 'income_w2', allowMulti: true },
            { label: 'Written VOE', document_type: 'wvoe',    alias_types: ['voe', 'employment_verification', 'employment_letter'], doc_subcategory: 'income_w2' },
          ]},
          { key: 'selfemployed', title: 'Self-Employed / Business Owner', slots: [
            { label: 'Tax Returns',             document_type: 'tax_return',           alias_types: ['tax_returns', 'business_tax_returns', 'personal_tax_returns'], doc_subcategory: 'income_selfemployed', allowMulti: true },
            { label: 'P&L Statement',           document_type: 'pnl_statement',        alias_types: ['pl_statement', 'profit_loss', 'p_and_l'], doc_subcategory: 'income_selfemployed' },
            { label: 'Business License',        document_type: 'business_license',     doc_subcategory: 'income_selfemployed' },
            { label: 'CPA Letter',              document_type: 'cpa_letter',           doc_subcategory: 'income_selfemployed' },
            { label: 'Business Formation Docs', document_type: 'business_formation_docs', doc_subcategory: 'income_selfemployed' },
          ]},
          { key: '_1099', title: '1099 / Independent Contractor', slots: [
            { label: '1099 Forms',                document_type: '1099_forms',           alias_types: ['1099', '1099s', '1099_form'], doc_subcategory: 'income_1099', allowMulti: true },
            { label: '12-Month Bank Statements',  document_type: 'bank_statement_12mo',  doc_subcategory: 'income_1099' },
            { label: '24-Month Bank Statements',  document_type: 'bank_statement_24mo',  doc_subcategory: 'income_1099' },
          ]},
          { key: 'bank_statements', title: 'Bank Statements', slots: [
            { label: 'Personal Bank Statements',  document_type: 'bank_statement',          alias_types: ['bank_statements', 'bank_statement_income'], doc_subcategory: 'income_bank_statements', allowMulti: true },
            { label: 'Business Bank Statements',  document_type: 'bank_statement_business', alias_types: ['business_bank_statement', 'business_bank_statements'], doc_subcategory: 'income_bank_statements', allowMulti: true },
          ]},
          { key: 'other', title: 'Other / Alternative Income', slots: [
            { label: 'SSA Award Letter',          document_type: 'ssa_award_letter',     alias_types: ['ss_award_letter', 'social_security_award'], doc_subcategory: 'income_other' },
            { label: 'Pension / Retirement Letter', document_type: 'pension_letter',     alias_types: ['pension_statement', 'retirement_letter'], doc_subcategory: 'income_other' },
            { label: 'Rental Income Docs',        document_type: 'rental_income_docs',   doc_subcategory: 'income_other', note: 'Include lease agreements and Schedule E', allowMulti: true },
            { label: 'Asset Depletion Docs',      document_type: 'asset_depletion_docs', doc_subcategory: 'income_other' },
            { label: 'Other Income',              document_type: 'other_income_docs',    doc_subcategory: 'income_other', allowMulti: true },
          ]},
          { key: 'assets', title: 'Assets', slots: [
            { label: 'Investment / Brokerage Statements', document_type: 'investment_statements', alias_types: ['investment_statement', 'brokerage_statement', 'brokerage_statements'], doc_subcategory: 'assets', allowMulti: true },
            { label: 'Retirement Statements',             document_type: 'retirement_statements', alias_types: ['retirement_statement', '401k_statement'], doc_subcategory: 'assets', allowMulti: true },
            { label: 'Gift Letter',                       document_type: 'gift_letter',           doc_subcategory: 'assets' },
            { label: 'Source of Funds',                   document_type: 'source_of_funds',       doc_subcategory: 'assets' },
          ]},
        ];
        // hasDocs check must respect aliases too, otherwise an empty-
        // section detection on the canonical document_type would hide a
        // sub-section that actually has docs stored under an alias.
        const slotHasDocs = (sl) => {
          if (getDocs(sl.document_type).length > 0) return true;
          const aliases = Array.isArray(sl.alias_types) ? sl.alias_types : [];
          return aliases.some((t) => getDocs(t).length > 0);
        };
        const subWithDocs = iaSubs.map((sub) => ({
          ...sub,
          hasDocs: sub.slots.some(slotHasDocs),
        }));
        const visibleSubs = subWithDocs.filter((s) => showAllIA || s.hasDocs);
        const hiddenCount = subWithDocs.length - visibleSubs.length;
        return (
          <Section title="Income & Assets">
            {visibleSubs.length === 0 && (
              <div style={{ fontSize: 12, color: '#6b7280', padding: '12px 10px', background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, marginBottom: 8 }}>
                No income or asset documents on file yet.
              </div>
            )}
            {visibleSubs.map((sub) => (
              <React.Fragment key={sub.key}>
                <SubsectionHeader collapsible collapsed={collapsedIA[sub.key]}
                  onToggle={() => setCollapsedIA((s) => ({ ...s, [sub.key]: !s[sub.key] }))}>
                  {sub.title}
                </SubsectionHeader>
                {!collapsedIA[sub.key] && sub.slots.map((sl) => (
                  <DocSlot key={sl.document_type} {...sl} />
                ))}
              </React.Fragment>
            ))}
            {(hiddenCount > 0 || showAllIA) && (
              <div style={{ marginTop: 8, textAlign: 'right' }}>
                <button onClick={() => setShowAllIA((v) => !v)} type="button"
                  style={{ padding: '5px 12px', background: 'none', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                  {showAllIA
                    ? 'Hide empty sub-sections'
                    : `+ Show all sub-sections (${hiddenCount} empty)`}
                </button>
              </div>
            )}
          </Section>
        );
      })()}

      {/* ─── RENTAL & MORTGAGE HISTORY ─── */}
      {/* Slots filtered by housingStatus from the borrower's application
          (formData.housing_status). Renter → VOR + landlord form + summary;
          owner/mortgage → VOM + mortgage statements; rent-free → LOX slot;
          unknown → show all so admin can collect whatever applies. */}
      <Section title="Rental & Mortgage History">
        {(() => {
          const hs = (housingStatus || '').toLowerCase();
          const isRenter = hs === 'rent' || hs === 'renting';
          const isOwner  = hs === 'own' || hs === 'mortgage' || hs === 'owner';
          const isFree   = hs === 'rent_free' || hs === 'rentfree';
          const showAll  = !hs; // unknown housing status → show everything
          return (
            <>
              {(isRenter || showAll) && (
                <DocSlot label="VOR — Verification of Rent" document_type="vor_report" alias_types={['vor_vom', 'vor', 'rent_verification']} doc_subcategory="rental_history"
                  note="Upload paper VOR if not collected via Doorly's portal — borrower-portal-collected VORs land under vor_vom and surface here automatically" />
              )}
              {(isOwner || showAll) && (<>
                <DocSlot label="VOM — Verification of Mortgage" document_type="vom_report" alias_types={['vor_vom', 'vom', 'mortgage_verification']} doc_subcategory="rental_history"
                  note="For borrowers with an existing mortgage — borrower-portal-collected VOMs land under vor_vom and surface here automatically" />
                <DocSlot label="Mortgage Statements (12 months)" document_type="mortgage_statements" doc_subcategory="rental_history" allowMulti />
              </>)}
              {(isFree || showAll) && (
                <DocSlot label="Rent-free letter from the property owner" document_type="rent_free_lox" doc_subcategory="rental_history"
                  note="Required when borrower lives rent-free with family / friend" />
              )}
            </>
          );
        })()}
      </Section>

      {/* ─── LETTERS OF EXPLANATION ─── */}
      <Section title="Letters of Explanation (LOX)">
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10, fontStyle: 'italic' }}>
          LOXs are managed via the borrower's Documents page and the Review tab. This section displays all LOXs on file.
        </div>
        <SubsectionHeader>Auto-Generated LOXs</SubsectionHeader>
        {loxAuto.length === 0 && <div style={{ fontSize: 11, color: '#9ca3af', padding: '6px 10px' }}>None on file.</div>}
        {loxAuto.map((d) => (
          <LoxRow key={d.id} label={(d.document_type || '').replace(/_/g, ' ').replace(/\blox\b/i, 'LOX')} doc={d} status="Completed ✓" />
        ))}
        <SubsectionHeader>Credit Conditions & LOXs</SubsectionHeader>
        {loxCredit.length === 0 && <div style={{ fontSize: 11, color: '#9ca3af', padding: '6px 10px' }}>None on file.</div>}
        {loxCredit.map((d) => (
          <LoxRow key={d.id} label={d.original_name || (d.document_type === 'lox_letter' ? 'LOX Letter' : 'LOX')} doc={d} />
        ))}
        <SubsectionHeader>Flagged Section Uploads</SubsectionHeader>
        {loxFlagged.length === 0 && <div style={{ fontSize: 11, color: '#9ca3af', padding: '6px 10px' }}>None on file.</div>}
        {loxFlagged.map((d) => {
          const sectionName = (d.document_type || '').replace(/^flagged_/, '').replace(/_/g, ' ');
          return <LoxRow key={d.id} label={`${sectionName} — ${d.original_name || 'document'}`} doc={d} />;
        })}
        {(loxAuto.length === 0 && loxCredit.length === 0 && loxFlagged.length === 0) && (
          <div style={{ fontSize: 12, color: '#6b7280', padding: '12px 10px', background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8 }}>
            No LOXs on file. LOXs are auto-generated during application review and can be requested via the Review tab.
          </div>
        )}
        <div style={{ marginTop: 10, textAlign: 'right' }}>
          <button onClick={() => onSwitchTab && onSwitchTab('review')}
            style={{ padding: '6px 12px', background: 'none', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Go to Review Tab →
          </button>
        </div>
      </Section>

      {/* ─── LOAN INSTRUMENTS ─── */}
      <Section title="Loan Instruments">
        <DocSlot label="Promissory Note" document_type="promissory_note" doc_subcategory="loan_instruments" doc_category="loan_documents" />
        <DocSlot label="Deed of Trust / Mortgage" document_type="deed_of_trust" doc_subcategory="loan_instruments" doc_category="loan_documents" />
        <DocSlot label="A-B Purchase Agreement (executed)" document_type="psa_ab_signed" doc_subcategory="loan_instruments" doc_category="loan_documents"
          readOnly note="Read-only — populated when the A-B PSA is executed" />
        <DocSlot label="B-C Purchase Agreement (executed)" document_type="borrower_psa_signed" doc_subcategory="loan_instruments" doc_category="loan_documents"
          readOnly note="Read-only — populated when the A-B PSA is executed" />
      </Section>
      </>)}

      {/* ─── PROPERTY DOCUMENTS ─── */}
      <Section title="Property Documents">
        <SubsectionHeader>Valuation</SubsectionHeader>
        <DocSlot label="Full Appraisal Report" document_type="appraisal_report" doc_subcategory="valuation"
          readOnly note="Read-only — populated by Inspectify webhook" />
        <DocSlot label="Appraisal Summary"
          document_type="appraisal_summary" doc_subcategory="valuation" doc_category="property_documents"
          generateEndpoint="generate-appraisal-summary" generateButton="Generate Summary" generateLabel="Appraisal Summary"
          uploadable={false} note="Only enabled if Full Appraisal Report exists" />
        <SubsectionHeader>Inspection</SubsectionHeader>
        <DocSlot label="Inspection Report" document_type="inspection_report" doc_subcategory="inspection"
          readOnly note="Read-only — populated by Inspectify webhook" />
        <SubsectionHeader>Title</SubsectionHeader>
        <DocSlot label="Title Commitment" document_type="title_commitment" doc_subcategory="title" />
        <DocSlot label="Title Insurance Policy (Lender's)" document_type="title_policy" doc_subcategory="title" />
        <SubsectionHeader>Insurance</SubsectionHeader>
        {/* HOI status pill — visible at-a-glance over the DocSlot.
            Green when the dec page is on file (documents row with
            document_type='hoi_declaration'); red when not. The
            "🛒 Shopping since <date>" sub-pill surfaces when the
            borrower clicked the Shop CTA in the closing-hero HOI
            modal but hasn't uploaded yet — distinguishes "borrower
            is mid-quote" from "borrower abandoned the task." */}
        {(() => {
          const hoiOnFile = (documents || []).some(d => d.document_type === 'hoi_declaration');
          const shoppingAt = user?.hoi_shop_clicked_at;
          return (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px',
              background: hoiOnFile ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${hoiOnFile ? '#bbf7d0' : '#fca5a5'}`,
              borderRadius: 8, marginBottom: 10, fontSize: 12, gap: 8, flexWrap: 'wrap',
            }}>
              <span style={{ fontWeight: 600, color: hoiOnFile ? '#166534' : '#991b1b' }}>
                {hoiOnFile ? '✓ HOI Declaration on file' : '✗ HOI not yet received'}
              </span>
              {shoppingAt && !hoiOnFile && (
                <span style={{ fontSize: 10, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>
                  🛒 Shopping since {new Date(shoppingAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
            </div>
          );
        })()}
        <DocSlot label="HOI Declaration Page" document_type="hoi_declaration" doc_subcategory="insurance"
          uploadable={false} readOnly
          note="Auto-populated when borrower uploads from their Documents page" />
        <DocSlot label="Flood Certificate" document_type="flood_cert" doc_subcategory="insurance" />
      </Section>

      {!isREDesk && (
      <>
      {/* ─── CLOSING PACKAGE ─── */}
      <Section title="Closing Package">
        <DocSlot label="Closing Package (full)" document_type="closing_package" doc_subcategory="closing" />
        <DocSlot label="Survey" document_type="survey" doc_subcategory="closing" />
      </Section>
      </>
      )}

      {/* SmartEstimate detail modal — preserved from prior layout. Modal
          content is identical; only the trigger location moved into the
          Disclosures section. */}
      {seModal && (() => {
        const d = seModal;
        const seFmt = v => { const n = parseFloat(v); return isNaN(n) || n === 0 ? null : '$' + n.toLocaleString(); };
        const seFmtAlways = v => { const n = parseFloat(v); return isNaN(n) ? '—' : '$' + n.toLocaleString(); };
        // The option's own rate and basis (seFeeTerms, from the server); "—" until read or when it cannot be.
        const progFeeRate = seFeeTerms && seFeeTerms.label ? seFeeTerms.label : '—';
        const purchasePrice = parseFloat(d.purchase_price || 0);
        const downPayment = parseFloat(d.down_payment || 0);
        const downPct = parseFloat(d.down_payment_pct || 0);
        const computedDown = downPayment || (downPct > 0 ? Math.round(purchasePrice * (downPct > 1 ? downPct / 100 : downPct)) : 0);
        const loanAmount = parseFloat(d.loan_amount || 0) !== purchasePrice ? parseFloat(d.loan_amount || 0) : (purchasePrice - computedDown);
        const closingCosts = parseFloat(d.closing_costs || 0);
        const programFee = parseFloat(d.program_fee || 0);
        const bdPoints = parseInt(d.buydown_points || 0);
        const bdCost = parseFloat(d.buydown_cost || 0);
        const totalCashToClose = computedDown + closingCosts + programFee + bdCost;
        const calcMonthly = () => {
          if (!loanAmount || !parseFloat(d.interest_rate || 0)) return null;
          const r = parseFloat(d.interest_rate);
          const mr = (r > 1 ? r / 100 : r) / 12;
          return Math.round(loanAmount * mr / (1 - Math.pow(1 + mr, -360)));
        };
        const monthlyPayment = parseFloat(d.monthly_payment) || calcMonthly();
        const seItem = (l, v) => v ? (
          <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{v}</div>
          </div>
        ) : null;
        const seSection = (title) => (
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f3f4f6' }}>{title}</div>
        );
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto', overflowX: 'hidden' }} onClick={() => setSeModal(null)}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '620px', width: '100%', margin: 'auto', boxSizing: 'border-box', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>SmartEstimate™</h2>
              {d.accepted_at && <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>Accepted {new Date(d.accepted_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>}
            </div>
            <button onClick={() => setSeModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#9ca3af', flexShrink: 0 }}>x</button>
          </div>
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#166534' }}>✓</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#166534' }}>SmartEstimate™ Accepted</span>
            </div>
            {d.selected_option && <span style={{ fontSize: '12px', background: '#acf44e', color: '#1a1a1a', padding: '3px 12px', borderRadius: '12px', fontWeight: '800' }}>Option {d.selected_option}</span>}
          </div>
          <div style={{ marginBottom: '24px' }}>
            {seSection('Loan Details')}
            <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {seItem('Purchase Price', seFmt(purchasePrice))}
              {seItem('Down Payment', computedDown > 0 ? seFmtAlways(computedDown) : (downPct > 0 ? (downPct > 1 ? downPct : downPct * 100) + '%' : '$0'))}
              {seItem('Loan Amount', seFmt(loanAmount))}
              {seItem('Interest Rate', d.interest_rate ? (parseFloat(d.interest_rate) > 1 ? parseFloat(d.interest_rate).toFixed(3) : (parseFloat(d.interest_rate) * 100).toFixed(3)) + '%' : null)}
              {seItem('Loan Term', '30-Year Fixed')}
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            {seSection('Program Details')}
            <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {seItem('Program Option', d.selected_option ? 'Option ' + d.selected_option : null)}
              <div data-testid="se-program-fee-rate" data-pct={seFeeTerms && seFeeTerms.programFeePct != null ? String(seFeeTerms.programFeePct) : ''} data-basis={(seFeeTerms && seFeeTerms.programFeeBasis) || ''}>
                {seItem('Program Fee Rate', progFeeRate)}
              </div>
              {seItem('Program Fee Amount', seFmt(d.program_fee))}
              {bdPoints > 0 && seItem('Rate Buydown', bdPoints + ' point' + (bdPoints > 1 ? 's' : '') + ' (' + (bdPoints * 0.25).toFixed(2) + '% reduction)')}
              {bdCost > 0 && seItem('Buydown Cost', '$' + Math.round(bdCost).toLocaleString())}
              {seItem('Closing Costs', seFmt(closingCosts))}
              {seItem('Total Cash to Close', seFmtAlways(totalCashToClose))}
              {seItem('Est. Monthly Payment', monthlyPayment ? '$' + monthlyPayment.toLocaleString() : null)}
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            {seSection('Terms & Conditions')}
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', fontSize: '12px', color: '#374151', lineHeight: '1.8', maxHeight: '260px', overflowY: 'auto' }}>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{DOORLY_PROGRAM_TERMS}</pre>
            </div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 14px', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#166534' }}>Electronically accepted by {d.borrower_name || 'borrower'}</div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>{d.accepted_at ? new Date(d.accepted_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</div>
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', textAlign: 'center', marginBottom: '16px' }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', lineHeight: '1.6' }}>Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity<br />8708 S Congress Ave, Ste E500-202, Austin, TX 78745</p>
          </div>
          <button onClick={() => setSeModal(null)} style={{ width: '100%', padding: '12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>Close</button>
        </div>
        </div>
        );
      })()}
    </div>
  );
};

// ─── Showing Credits Admin ────────────────────────────────────────────────────
const ShowingCreditsAdmin = ({ borrowerId, token }) => {
  const [credits, setCredits] = React.useState(null);
  const [showAdjust, setShowAdjust] = React.useState(false);
  const [adjustAmount, setAdjustAmount] = React.useState('');
  const [adjustReason, setAdjustReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const fetchCredits = React.useCallback(() => {
    axios.get(`${API}/api/admin/borrowers/${borrowerId}/showing-credits`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setCredits(r.data)).catch(() => {});
  }, [borrowerId, token]);

  React.useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const handleAdjust = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/borrowers/${borrowerId}/showing-credits/adjust`, {
        adjustment: parseInt(adjustAmount), reason: adjustReason,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowAdjust(false);
      setAdjustAmount('');
      setAdjustReason('');
      fetchCredits();
    } finally { setSaving(false); }
  };

  if (!credits) return null;

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 18px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Showing Credits</span>
          <div style={{ display: 'flex', gap: '5px' }}>
            {[1,2,3].map(i => (
              <span key={i} style={{
                width: '18px', height: '18px', borderRadius: '50%',
                background: i <= credits.credits_remaining ? '#acf44e' : '#e5e7eb',
                border: `2px solid ${i <= credits.credits_remaining ? '#86d832' : '#d1d5db'}`,
                display: 'inline-block',
              }} />
            ))}
          </div>
          <span style={{
            fontSize: '14px', fontWeight: '800',
            color: credits.credits_remaining === 0 ? '#ff082f' : credits.credits_remaining === 1 ? '#f5b832' : '#166534',
          }}>
            {credits.credits_remaining}/{credits.credits_total}
          </span>
          {credits.credits_remaining === 0 && (
            <span style={{ fontSize: '11px', background: '#fff0f3', color: '#ff082f', padding: '2px 7px', borderRadius: '4px', fontWeight: '600' }}>
              Paid showings only ($60)
            </span>
          )}
        </div>
        <button onClick={() => setShowAdjust(!showAdjust)}
          style={{ padding: '5px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>
          Adjust Credits
        </button>
      </div>
      <div style={{ fontSize: '12px', color: '#6b7280' }}>
        {credits.credits_used} showing{credits.credits_used !== 1 ? 's' : ''} confirmed · {credits.credits_remaining} credit{credits.credits_remaining !== 1 ? 's' : ''} remaining
      </div>
      {showAdjust && (
        <div style={{ marginTop: '12px', padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
            Adjust Credits (use negative to deduct)
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <input type="number" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="+1 or -1"
              style={{ width: '80px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
            <input type="text" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Reason (e.g. Courtesy credit)"
              style={{ flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={handleAdjust} disabled={!adjustAmount || saving}
              style={{ padding: '5px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowAdjust(false)}
              style={{ padding: '5px 10px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {credits.ledger && credits.ledger.length > 0 && (
        <details style={{ marginTop: '10px' }}>
          <summary style={{ fontSize: '12px', color: '#3a6ad9', cursor: 'pointer', userSelect: 'none' }}>
            View credit history ({credits.ledger.length} entries)
          </summary>
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {credits.ledger.map(entry => (
              <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '4px 0', borderBottom: '1px solid #f3f4f6', color: '#374151' }}>
                <span>{entry.description}</span>
                <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                  <span style={{ fontWeight: '700', color: entry.amount > 0 ? '#166534' : entry.amount < 0 ? '#ff082f' : '#374151' }}>
                    {entry.amount > 0 ? '+' : ''}{entry.amount}
                  </span>
                  <span style={{ color: '#9ca3af' }}>{'\u2192'} {entry.balance_after}</span>
                  <span style={{ color: '#9ca3af' }}>{new Date(entry.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

// ─── CollapsibleCard ──────────────────────────────────────────────────────────
const CollapsibleCard = ({ title, icon, children, defaultOpen = false, badge = null, accepted = false }) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const badgeBg = accepted ? '#acf44e' : '#eff6ff';
  const badgeColor = accepted ? '#1a1a1a' : '#3a6ad9';
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden' }}>
      <button type="button" onClick={() => setIsOpen(prev => !prev)}
        style={{ width: '100%', padding: '16px 20px', background: accepted ? '#f0fdf4' : isOpen ? '#f9fafb' : 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left', transition: 'background 0.15s' }}>
        {icon && <span style={{ fontSize: '16px' }}>{icon}</span>}
        <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827', flex: 1 }}>{title}</span>
        {badge && <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', background: badgeBg, color: badgeColor, marginRight: '8px' }}>{badge}</span>}
        <span style={{ fontSize: '16px', color: '#9ca3af', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', display: 'inline-block', userSelect: 'none' }}>{isOpen ? '▾' : '▸'}</span>
      </button>
      {isOpen && <div style={{ padding: '0 20px 20px', borderTop: '1px solid #f3f4f6' }}>{children}</div>}
    </div>
  );
};

// ─── DateEditButton ──────────────────────────────────────────────────────────
// DateEditButton removed — dates consolidated into Date Tracking tab

// ─── Main Component ───────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// LoanTeamCard — who owns this file, per role.
//
// ⚠️ THREE DISTINCT EMPTY STATES. Conflating any two is the failure this card
// was specified to avoid:
//
//   loading / error  → an ERROR state with a retry. Never "No team assigned":
//                      that would report a data fact from a network event.
//   team.length === 0→ "No team assigned" — NEVER assigned. Adversed and
//                      archived files were deliberately skipped by the backfill
//                      because inventing assigned_at = NOW() would fabricate
//                      history. A blank box reads as broken; this reads as
//                      deliberate, and says why.
//   seat.noHolder    → "Unassigned" — CONSIDERED, nobody available. The row
//                      exists with staff_user_id NULL.
//
// ⚠️ "UNASSIGNED" IS HALF OF EVERY CARD TODAY. account_manager and
// client_success have zero holders, so two of four seats show it on every
// current file. The copy therefore has to read as a STAFFING GAP rather than as
// a broken widget — hence the explicit "no one holds this role yet" line rather
// than a bare dash, and a muted amber rather than an error red. It is a fact
// about the org chart, not about this borrower.
// ─────────────────────────────────────────────────────────────────────────────
const ROLE_LABEL = {
  loan_officer: 'Loan Officer',
  contract_desk: 'Contract Desk',
  account_manager: 'Account Manager',
  client_success: 'Client Success',
};

function LoanTeamCard({ borrowerId, token, isAdversed, isArchived }) {
  const [state, setState] = React.useState({ status: 'loading', team: [], eligible: {}, roles: [] });
  const [editing, setEditing] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [history, setHistory] = React.useState(null);

  const load = React.useCallback(async () => {
    setState((p) => ({ ...p, status: 'loading' }));
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/team`,
        { headers: { Authorization: `Bearer ${token}` } });
      setState({ status: 'ok', team: r.data.team || [], eligible: r.data.eligible || {}, roles: r.data.roles || [] });
    } catch (e) {
      // ⚠️ NOT an empty team. A failed request is its own state.
      setState({ status: 'error', team: [], eligible: {}, roles: [], message: e?.response?.data?.error || e.message });
    }
  }, [borrowerId, token]);

  React.useEffect(() => { load(); }, [load]);

  const save = async (role, adminId) => {
    setSaving(true);
    try {
      // ⚠️ This CLOSES the open row and OPENS a new one server-side (see
      // utils/assignment.js reassign) — never an UPDATE, so history holds.
      await axios.patch(`${API}/api/admin/borrowers/${borrowerId}/assigned`,
        { admin_id: adminId === '' ? null : Number(adminId), role },
        { headers: { Authorization: `Bearer ${token}` } });
      setEditing(null);
      await load();
      if (history) await loadHistory();
    } catch (e) {
      alert('Could not reassign: ' + (e?.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/team/history`,
        { headers: { Authorization: `Bearer ${token}` } });
      setHistory(r.data.history || []);
    } catch (e) { setHistory([]); }
  };

  const box = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 };
  const h = { margin: '0 0 14px', fontSize: 13, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: '#6b7280' };

  if (state.status === 'loading') {
    return <div style={box}><h3 style={h}>Loan Team</h3><div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div></div>;
  }

  // ── STATE 3: fetch failure ────────────────────────────────────────────────
  if (state.status === 'error') {
    return (
      <div style={{ ...box, borderColor: '#fecaca', background: '#fef2f2' }}>
        <h3 style={h}>Loan Team</h3>
        <div style={{ fontSize: 13, color: '#991b1b', fontWeight: 600 }}>⚠ Couldn't load the team.</div>
        <div style={{ fontSize: 12, color: '#b91c1c', margin: '4px 0 10px' }}>{state.message}</div>
        <button onClick={load} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, background: '#fff', border: '1px solid #fca5a5', borderRadius: 8, cursor: 'pointer', color: '#991b1b' }}>Retry</button>
      </div>
    );
  }

  // ── STATE 1: never assigned ───────────────────────────────────────────────
  if (!state.team.length) {
    const why = isAdversed ? 'This file was adversed'
      : isArchived ? 'This file was archived'
      : 'This file has no assignments';
    return (
      <div style={box}>
        <h3 style={h}>Loan Team</h3>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>No team assigned</div>
        <div style={{ fontSize: 12.5, color: '#6b7280', margin: '6px 0 12px', lineHeight: 1.6 }}>
          {why} before team assignment existed, so it has no ownership history.
          Assigning now records today's date, not a historical fact.
        </div>
        <button disabled={saving} onClick={() => save('loan_officer', '')}
          style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700, background: '#111827', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
          Assign a team
        </button>
      </div>
    );
  }

  // ── STATE 2: seats, some unstaffed ────────────────────────────────────────
  const unstaffed = state.team.filter((t) => t.noHolder).length;
  return (
    <div style={box}>
      <h3 style={h}>Loan Team</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {state.team.map((seat) => {
          const opts = state.eligible[seat.role] || [];
          const isEditing = editing === seat.role;
          return (
            <div key={seat.role} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ width: 132, fontSize: 12.5, fontWeight: 700, color: '#6b7280', flexShrink: 0 }}>
                {ROLE_LABEL[seat.role] || seat.role}
              </div>
              {isEditing ? (
                <>
                  <select autoFocus defaultValue={seat.staffUserId || ''} disabled={saving}
                    onChange={(e) => save(seat.role, e.target.value)}
                    style={{ flex: 1, padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 7 }}>
                    <option value="">— Unassigned —</option>
                    {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  <button onClick={() => setEditing(null)} disabled={saving}
                    style={{ padding: '5px 10px', fontSize: 11.5, background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                </>
              ) : (
                <>
                  <div style={{ flex: 1, fontSize: 13.5 }}>
                    {seat.staffUserId ? (
                      <span style={{ color: '#111827', fontWeight: 600 }}>{seat.name}</span>
                    ) : (
                      // ⚠️ A STAFFING GAP, NOT A BROKEN CARD. Amber, not red, and
                      // it says WHY — two of four seats look like this on every
                      // file today, so a bare "—" would read as the card failing.
                      <>
                        <span style={{ color: '#b45309', fontWeight: 700 }}>Unassigned</span>
                        <span style={{ color: '#9ca3af', fontSize: 11.5, marginLeft: 8 }}>
                          no one holds this role yet
                        </span>
                      </>
                    )}
                  </div>
                  <button onClick={() => setEditing(seat.role)}
                    style={{ padding: '5px 11px', fontSize: 11.5, fontWeight: 700, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', color: '#374151' }}>
                    {seat.staffUserId ? 'Change' : 'Assign'}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {unstaffed > 0 && (
        // ⚠️ Names the cause once, at the card level, so nobody reads four
        // "Unassigned" rows as four separate problems with this borrower.
        <div style={{ marginTop: 12, padding: '9px 11px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e', lineHeight: 1.55 }}>
          {unstaffed} of {state.team.length} seats are unstaffed because no one currently holds
          {' '}{state.team.filter((t) => t.noHolder).map((t) => ROLE_LABEL[t.role] || t.role).join(' or ')}.
          {' '}That's a staffing gap across every file, not something specific to this borrower.
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button onClick={() => (history ? setHistory(null) : loadHistory())}
          style={{ padding: 0, fontSize: 11.5, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', color: '#3a6ad9' }}>
          {history ? 'Hide history' : 'Assignment history'}
        </button>
        {history && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#4b5563' }}>
            {history.length === 0 && <div style={{ color: '#9ca3af' }}>No history.</div>}
            {history.map((r, i) => (
              <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #f9fafb', display: 'flex', gap: 8 }}>
                <span style={{ width: 126, color: '#6b7280', flexShrink: 0 }}>{ROLE_LABEL[r.role] || r.role}</span>
                <span style={{ flex: 1 }}>{r.name || <em style={{ color: '#b45309' }}>unstaffed</em>}</span>
                <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {r.assignedAt ? new Date(r.assignedAt).toLocaleDateString() : '—'}
                  {r.active ? ' → now' : ` → ${r.unassignedAt ? new Date(r.unassignedAt).toLocaleDateString() : '?'} (${r.unassignedReason || 'closed'})`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminBorrowerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const { role: staffRole, isREDesk, isLO } = useStaffRole();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🔑 THE ESTIMATE RATES, SERVED (backend constants/feeSchedule ANNUAL_TAX_RATE / ANNUAL_HOI_RATE via GET /api/fee-schedule
  // leBasis) — the Loan Terms monthly payment never types a rate (2026-11-01: HOI 0.75%, was a typed 0.4%).
  const [estRates, setEstRates] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/fee-schedule`).then((r) => (r.ok ? r.json() : null)).then((j) => {
      const b = j && j.smartEstimate && j.smartEstimate.leBasis;
      if (b && Number.isFinite(Number(b.annualTaxRate)) && Number.isFinite(Number(b.annualHoiRate))) setEstRates({ tax: Number(b.annualTaxRate), hoi: Number(b.annualHoiRate) });
    }).catch(() => {});
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(searchParams.get('tab') || 'borrower_details');
  const setActiveTab = (tab) => { setActiveTabState(tab); setSearchParams({ tab }, { replace: true }); };

  // If a role-gated tab is selected by URL (?tab=loan_details on RE Desk
  // role), bounce back to borrower_details so the content area doesn't
  // render a blank pane. Backend gates are independent — this is a UX
  // guard, not a security boundary.
  useEffect(() => {
    const t = TABS.find(x => x.id === activeTab);
    if (t && t.gateRoles && !t.gateRoles.includes(staffRole)) {
      setActiveTab('borrower_details');
    }
  }, [activeTab, staffRole]); // eslint-disable-line react-hooks/exhaustive-deps
  const [modal, setModal] = useState(null); // 'move' | 'flag' | 'docs'
  const [teamMembers, setTeamMembers] = useState([]); // eslint-disable-line no-unused-vars

  // Notes tab
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [pinNote, setPinNote] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  // Email log
  const [emailLog, setEmailLog] = useState([]);

  // DocMagic LE/CD state
  const [dmDocs, setDmDocs] = useState({ le: null, cd: null });
  const [dmGenerating, setDmGenerating] = useState(null); // 'le' | 'cd' | null
  const [dmError, setDmError] = useState('');
  // Communication tab
  const [convMessages, setConvMessages] = useState([]);
  // ── 🚨 WHICH THREAD IS OPEN, BECAUSE A BORROWER CAN HAVE SEVERAL ───────────
  //
  // Jesse: "the Communication tab shows two unread that won't clear even after
  // I open the tab" and "I can't scroll up in the messages thread to view older
  // messages."
  //
  // 🔑 CATHY HAS THREE CONVERSATIONS: #88 "Support" (2 messages, 0 unread),
  // #91 "Document Upload Help" (7 messages, 4 unread) and #89 "Document Upload
  // Help" (1 message, 1 unread). This page did
  //   (data.conversations || []).find(c => c.owner_type === activeMsgOwner)
  // on a list ordered by last_message_at DESC, so it showed #88 and nothing
  // else. Eight of her ten messages were unreachable — that is the "cannot
  // scroll up" — and FIVE unread messages from her sat in threads that could
  // never be opened, so they could never be marked read.
  //
  // ⚠️ null MEANS "the most recent for this owner", so the default behaviour is
  // unchanged and a file with one thread never sees a picker.
  const [activeConvId, setActiveConvId] = useState(null);
  // 🔑 UNREAD CLEARED LOCALLY, BECAUSE THE SERVER ALREADY DID IT. Opening a
  // thread marks its borrower messages read server-side, but nothing refetched
  // the profile payload — so data.conversations kept the pre-read count and the
  // badge sat at 2 forever. Refetching from inside that effect would loop: the
  // effect depends on `data`. This records which threads this session has
  // opened and subtracts them at render.
  const [convReadLocally, setConvReadLocally] = useState([]);

  const [convLoading, setConvLoading] = useState(false);
  const msgEndRef = useRef(null);
  const [showCompose, setShowCompose] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  // Communication tab — which borrower's thread is currently open.
  // Defaults to 'primary' so single-borrower files behave unchanged.
  // On joint files the tab body renders a Primary / Co-Borrower
  // selector that flips this; the active-conversation lookups
  // (load, reply, send-new) all key off this value.
  const [activeMsgOwner, setActiveMsgOwner] = useState('primary');
  // 🔑 ONE RESOLVER FOR "WHICH THREAD AM I ACTING ON". The loader, the reply and
  // the compose each picked it themselves with
  // `.find(c => c.owner_type === activeMsgOwner)`, which returns the most recent
  // thread regardless of what is on screen. With a thread picker that is not a
  // cosmetic difference: a reply typed under "Document Upload Help" would have
  // posted into "Support". Three copies of a selection rule is how that happens.
  const activeConv = useCallback((d) => {
    const all = (d || data)?.conversations || [];
    const owned = all.filter(c => c.owner_type === activeMsgOwner);
    const pool = owned.length ? owned : all;
    return pool.find(c => String(c.id) === String(activeConvId)) || pool[0] || null;
  }, [data, activeMsgOwner, activeConvId]);
  // ⚠️ DECLARED AFTER activeMsgOwner, DELIBERATELY. It sat above it and eslint
  // reported `no-use-before-define` — which here is not a style note: a
  // useCallback dependency array is evaluated at the call site, so reading a
  // `const` from the temporal dead zone throws a ReferenceError and takes the
  // whole profile page with it.

  // Property wire editing


  // Needs tab
  const [needs, setNeeds] = useState([]); // eslint-disable-line no-unused-vars
  const [showAddNeed, setShowAddNeed] = useState(false); // eslint-disable-line no-unused-vars
  const [newNeed, setNewNeed] = useState({ need_type: 'document', title: '', description: '', priority: 'normal' }); // eslint-disable-line no-unused-vars

  // Liabilities (Financials tab)
  const [, setLiabilities] = useState([]);

  // Document viewer
  const [viewingDoc, setViewingDoc] = useState(null); // eslint-disable-line no-unused-vars
  const [showDocModal, setShowDocModal] = useState(false);

  // Review tab state
  const [, setReviewData] = useState(null);
  const [reviewSaving, setReviewSaving] = useState(null); // eslint-disable-line no-unused-vars
  const [atlasResult, setAtlasResult] = useState(null);
  const [flagModal, setFlagModal] = useState(null); // eslint-disable-line no-unused-vars
  // Credit pull state — handlers preserved for any future inline credit
  // surface; the live Credit File Review card was removed in fec3d8e.
  /* eslint-disable no-unused-vars */
  const [creditPulling, setCreditPulling] = useState(false);
  const [creditError, setCreditError] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);
  // Credit conditions
  const [creditConditions, setCreditConditions] = useState([]);
  const [conditionsGenerating, setConditionsGenerating] = useState(false);
  const [expandedCondition, setExpandedCondition] = useState(null);
  const [uploadingOnBehalf, setUploadingOnBehalf] = useState(null);
  /* eslint-enable no-unused-vars */

  // Financial Details tab state
  const [finIncome, setFinIncome] = useState([]);
  const [finAssets, setFinAssets] = useState([]);
  const [finLiabs, setFinLiabs] = useState([]);
  const [finGMI, setFinGMI] = useState(0);
  const [finTotalAssets, setFinTotalAssets] = useState(0);
  // ── 🔑 THE FIGURES READYCHECK HAS CALCULATED BUT NOBODY HAS CONFIRMED ──────
  //
  // Jesse: "surface the unconfirmed ReadyCheck figure on Financials".
  //
  // A ReadyCheck run stores its income calculation and the borrower's declared
  // assets, but applyReadyCheckIncome / applyReadyCheckAssets fire on the
  // borrower's Step-2 CONFIRM — so a file parked mid-Step-2 reads $0 here while
  // its numbers sit in the database. null means "not fetched / nothing pending".
  const [rcPending, setRcPending] = useState(null);
  const [finTotalLiab, setFinTotalLiab] = useState(0);
  const finLoadedRef = useRef(false);
  const [expandedIncomeId, setExpandedIncomeId] = useState(null);
  const [incomeForm, setIncomeForm] = useState({ borrower_label: '', borrower_type: 'primary', income_type: 'base_employment', employer_name: '', base_monthly: '', bonus_monthly: '', overtime_monthly: '', commission_monthly: '' });
  const [editIncomeForm, setEditIncomeForm] = useState(null);
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  // In-flight guard for the Asset Save buttons. Prevents a slow POST from
  // being re-fired by an impatient double-click — each click was inserting
  // a fresh asset_sources row (different local_id), creating spurious
  // duplicates. Reset on success or error.
  const [savingAsset, setSavingAsset] = useState(false);
  const [assetForm, setAssetForm] = useState({ asset_type: 'checking', institution_name: '', account_last4: '', current_balance: '', notes: '' });
  const [editAssetForm, setEditAssetForm] = useState(null);
  const [flagNote, setFlagNote] = useState(''); // eslint-disable-line no-unused-vars
  const [needTitle, setNeedTitle] = useState(''); // eslint-disable-line no-unused-vars
  const [needDesc, setNeedDesc] = useState(''); // eslint-disable-line no-unused-vars
  const [addingNeed, setAddingNeed] = useState(false); // eslint-disable-line no-unused-vars
  // AI Extractions — approvingExId state moved into AIExtractionsPanel
  // (inside ReviewModule) along with the JSX, since the AI Extracted
  // Data section is now embedded in the Underwriting Module rather
  // than rendered directly on the Review tab.
  const [aiExtractions, setAiExtractions] = useState([]);

  // AI Underwriter — Review tab analyze buttons (income/assets/housing).
  // Each section has independent loading + result state. uwNeeds is the
  // grouped additional_needs payload from /borrowers/:id/additional-needs;
  // uwNeedsBusy tracks the row-level patch in flight so the dropdown UI
  // can disable while it saves. uwManualForm is keyed by category for
  // the inline "Add Need" inputs.
  const [uwAnalysis, setUwAnalysis] = useState({ income: null, assets: null, housing: null });
  /* eslint-disable no-unused-vars */
  const [uwAnalysisRunAt, setUwAnalysisRunAt] = useState({ income: null, assets: null, housing: null });
  const [uwAnalyzing, setUwAnalyzing] = useState({ income: false, assets: false, housing: false });
  const [uwError, setUwError] = useState({ income: null, assets: null, housing: null });
  // Distinguishes "all docs over 5MB → friendly yellow banner" from
  // generic red errors. Per-section, set inside handleAnalyzeSection.
  const [uwOversizedAll, setUwOversizedAll] = useState({ income: null, assets: null, housing: null });
  const [uwToast, setUwToast] = useState(null); // { section, count } | null
  const [uwNeeds, setUwNeeds] = useState({ income: [], assets: [], housing: [], credit: [], other: [], uncategorized: [] });
  const [uwNeedsBusy, setUwNeedsBusy] = useState(null);
  // 🔑 THE LAST FAILED NEED ACTION, SO IT CAN BE SHOWN RATHER THAN SWALLOWED.
  // null when the last one succeeded. See handlePatchNeed.
  const [uwNeedError, setUwNeedError] = useState(null);
  // 🔑 WHERE THE LAST STAFF UPLOAD LANDED. On a joint file the needs card lists
  // both borrowers' needs, so the document does not necessarily belong to the
  // profile being viewed — and showing nothing is what made a successful upload
  // onto the wrong borrower indistinguishable from no upload at all.
  const [uwUploadResult, setUwUploadResult] = useState(null);
  const [uwUploadBusy, setUwUploadBusy] = useState(null);
  const [uwManualForm, setUwManualForm] = useState({ income: '', assets: '', housing: '' });
  // Apply-to-Financials state per section. applying = in-flight, applied =
  // last successful application (so the button can flip to ✓ Applied), error
  // = inline failure message rendered inside the recommendation sub-card.
  const [uwApplying, setUwApplying] = useState({ income: false, assets: false });
  const [uwApplied, setUwApplied] = useState({ income: null, assets: null });
  const [uwApplyError, setUwApplyError] = useState({ income: null, assets: null });
  /* eslint-enable no-unused-vars */

  // ── Co-borrower AI Underwriter state ────────────────────────────────────
  // Parallel mirror of the primary state above, scoped to coBorrower.id
  // when present. Income + Assets only — co-borrower housing is not in
  // scope here (the primary's housing analysis covers shared housing on
  // joint applications). All endpoints (analyze-section, /analysis,
  // /additional-needs, /financials) already key off the route :id, so
  // pointing the co-borrower handlers at coBorrower.id naturally
  // segregates rows in ai_analysis_results, additional_needs, and
  // borrower_income — no backend discriminator needed beyond the
  // borrower_type body param on /financials.
  /* eslint-disable no-unused-vars */
  const [coUwAnalysis, setCoUwAnalysis] = useState({ income: null, assets: null });
  const [coUwAnalysisRunAt, setCoUwAnalysisRunAt] = useState({ income: null, assets: null });
  const [coUwAnalyzing, setCoUwAnalyzing] = useState({ income: false, assets: false });
  const [coUwError, setCoUwError] = useState({ income: null, assets: null });
  const [coUwOversizedAll, setCoUwOversizedAll] = useState({ income: null, assets: null });
  const [coUwToast, setCoUwToast] = useState(null);
  const [coUwNeeds, setCoUwNeeds] = useState({ income: [], assets: [], housing: [], credit: [], other: [], uncategorized: [] });
  const [coUwNeedsBusy, setCoUwNeedsBusy] = useState(null);
  const [coUwManualForm, setCoUwManualForm] = useState({ income: '', assets: '' });
  const [coUwApplying, setCoUwApplying] = useState({ income: false, assets: false });
  const [coUwApplied, setCoUwApplied] = useState({ income: null, assets: null });
  const [coUwApplyError, setCoUwApplyError] = useState({ income: null, assets: null });
  /* eslint-enable no-unused-vars */

  // Offer generation
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerResult, setOfferResult] = useState(null);

  // PSA flow (Doorly A-B upload + Borrower B-C generate)
  const [doorlyPsaUploading, setDoorlyPsaUploading] = useState(false);
  const [doorlyPsaError, setDoorlyPsaError] = useState('');
  // Real Broker (reZEN) manual-create flow. Triggered from the Property
  // tab card; calls the same util the BoldSign A-B Completed webhook uses.
  const [creatingReZEN, setCreatingReZEN] = useState(false);
  const [rezenError, setRezenError] = useState('');
  const [borrowerPsaLoading, setBorrowerPsaLoading] = useState(false);
  const [borrowerPsaResult, setBorrowerPsaResult] = useState(null);
  const [borrowerPsaError, setBorrowerPsaError] = useState('');
  const [psaModalOpen, setPsaModalOpen] = useState(false);
  const [psaForm, setPsaForm] = useState({ doorly_purchase_price: '' });

  // Send Offer to Listing Agent
  const [sendOfferModalOpen, setSendOfferModalOpen] = useState(false);
  const [sendOfferForm, setSendOfferForm] = useState({ listing_agent_name: '', listing_agent_email: '', cc_email: '', comments_to_la: '' });
  const [improvingLaComments, setImprovingLaComments] = useState(false);
  const [sendOfferLoading, setSendOfferLoading] = useState(false);
  const [sendOfferError, setSendOfferError] = useState('');
  const [lastSentInfo, setLastSentInfo] = useState(null); // { email, at }

  // Manual Accept Offer — for cases where the listing agent uses their
  // own offer template / PSA and routes externally (no OfferReview /accept
  // event ever fires). Admin enters the accepted terms by hand; submit
  // POSTs to /manual-accept-offer which mirrors the side-effects of the
  // normal LA-accept flow.
  const todayYmd = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const [manualAcceptOpen, setManualAcceptOpen] = useState(false);
  const [manualAcceptForm, setManualAcceptForm] = useState({
    abPurchasePrice: '',
    propertyAddress: '',
    effectiveDate: todayYmd,
    closingDate: '',
    optionPeriodDays: '7',
    emdAmount: '',
    optionFee: '',
    hoaMonthly: '',
    listingAgentName: '',
    listingAgentEmail: '',
    notes: '',
    sellers: [{ name: '', email: '' }],
  });
  const [manualAcceptLoading, setManualAcceptLoading] = useState(false);
  const [manualAcceptError, setManualAcceptError] = useState('');
  // "View" of an already-accepted manual offer (Step 5 of the
  // Property Module). Holds the offer_listing_agent_comms row whose
  // data we're displaying — null = closed.
  const [acceptedDetailsRow, setAcceptedDetailsRow] = useState(null);

  // Edit Listing Agent (manual entry until MLS/IDX integration lands)
  const [editLaModalOpen, setEditLaModalOpen] = useState(false);
  const [editLaForm, setEditLaForm] = useState({ listing_agent_name: '', listing_agent_email: '', listing_agent_phone: '' });
  const [editLaSaving, setEditLaSaving] = useState(false);
  const [editLaError, setEditLaError] = useState('');

  // Resend "Offer Coming In" intro email (the heads-up email — NOT the
  // offer-with-PDF email). Stopgap manual fire while MLS/IDX integration
  // is pending; auto-fire path lives in homes.js after offer-request submit.
  // LA-email Resend state — used by the per-row Resend button on the
  // Listing Agent Communication Log card. handleResendIntroEmail fires
  // /api/admin/borrowers/:id/send-la-intro-email (the manual re-fire
  // path) which appends a new application_events row with the fresh
  // Resend id, then fetchOfferComms() refreshes the log.
  // Property Module Step 3 (Underwriting Review) — Nudge Loan Team
  // button state. Fires POST /api/admin/borrowers/:id/nudge-loan-team
  // which sends an internal alert + CCs the borrower's assigned admin.
  // Disabled for 1 hour after a successful nudge (server-side throttle).
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [nudgedAt, setNudgedAt] = useState(null);
  const [nudgeError, setNudgeError] = useState('');
  const [resendIntroLoading, setResendIntroLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [resendIntroResult, setResendIntroResult] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [resendIntroCc, setResendIntroCc] = useState('');

  // Edit Property Address (manual entry — propagates to option_periods,
  // offer_requests, seller_disclosures, double_close_packages, and
  // user_applications.form_data if the key exists).
  const [editPropertyOpen, setEditPropertyOpen] = useState(false);
  const [editPropertyValue, setEditPropertyValue] = useState('');
  const [editPropertySaving, setEditPropertySaving] = useState(false);
  const [editPropertyError, setEditPropertyError] = useState('');
  const [offerComms, setOfferComms] = useState([]);
  const [expandedComm, setExpandedComm] = useState(null);
  // Offer History on Step 4 — collapsed by default so the admin only
  // sees the most-recent send. Expanding reveals the prior rounds
  // (counter-offers, re-sends after listing-agent comments, etc).
  // One state shared by both render sites (the modal Step 4 and the
  // legacy inline Property tab block).
  const [offerHistoryExpanded, setOfferHistoryExpanded] = useState(false);
  const [resendingCommId, setResendingCommId] = useState(null);
  const [acceptedOfferAvailable, setAcceptedOfferAvailable] = useState(false);
  const [offerTerms, setOfferTerms] = useState({
    purchase_price: '',
    emd_percent: '1',
    is_new_construction: false,
    option_fee: '300',
    option_period_days: '7',
    close_days: '20',
    commission_pct: '3',
    ai_revision_prompt: '',
  });

  // Manual offer override (Property Module Step 3 panel). Lets the
  // admin override the HC recommended offer with a notes-required
  // justification — the override price then pre-populates Step 4's
  // purchase_price on the next visit (Step 4 useEffect below).
  const [offerOverride, setOfferOverride] = useState({ active: false, price: '', notes: '' });
  const [savingOverride, setSavingOverride] = useState(false);

  // HouseCanary / Disclosure state
  const [hcLoading, setHcLoading] = useState(false);
  const [disclosureData, setDisclosureData] = useState(null);
  const [showDisclosureForm, setShowDisclosureForm] = useState(false);
  const [disclosureForm, setDisclosureForm] = useState({ listing_agent_name: '', listing_agent_email: '', listing_agent_phone: '', cc_email: 'contracts@godoorly.com' });
  const [disclosureError, setDisclosureError] = useState('');
  const [disclosureSentInfo, setDisclosureSentInfo] = useState(null);
  const [disclosureLoading, setDisclosureLoading] = useState(false);

  // Settings cog
  const [showSettingsCog, setShowSettingsCog] = useState(false);
  const [showExportSub, setShowExportSub] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showAdverseModal, setShowAdverseModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  // Offer rejection modal — opens from the Property tab Offer Contract card
  // when the loan is at offer_submitted or offer_accepted. Two steps: pick
  // a reason, then confirm. POST to /api/admin/borrowers/:id/offer-rejected.
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectStep, setRejectStep] = useState(1);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDetail, setRejectDetail] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [auditLogEntries, setAuditLogEntries] = useState([]);
  const [adverseForm, setAdverseForm] = useState({
    // ECOA / FCRA-aligned reason codes per the Doorly spec. These map to
    // the labels rendered on the modal checkboxes; the array is sent to
    // POST /adverse as the canonical `reasons` payload.
    reason_credit: false,            // Credit score below minimum threshold
    reason_credit_history: false,    // Insufficient credit history / tradelines
    reason_dti: false,               // Excessive obligations in relation to income (DTI)
    reason_dero: false,              // Derogatory credit history (foreclosure, bankruptcy, etc.)
    reason_dero_seasoning: false,    // Derogatory credit history — insufficient seasoning
    reason_income: false,            // Unable to verify income
    reason_employment: false,        // Unable to verify employment
    reason_assets: false,            // Unable to verify assets
    reason_ltv: false,               // Insufficient down payment / reserves
    reason_property: false,          // Property did not meet program requirements
    reason_incomplete: false,        // Application incomplete — missing information
    reason_other: false,             // Other (requires written explanation)
    reason_other_text: '',
    notification_method: 'Both',
    notification_date: new Date().toISOString().slice(0, 10),
    showPreview: false,
    notes: '',
  });
  // Modal mode: 'deny' = adverse action (POST /adverse); 'cure' = Notice of
  // Incompleteness (POST /request-incomplete). Two genuinely separate instruments.
  const [adverseMode, setAdverseMode] = useState('deny');
  // ── 🚨 THE SECTION IS PART OF AN ITEM, AND IT STARTS EMPTY ────────────────
  //
  // 🚨 THE NOTICE AND THE CHECKLIST USED TO DISAGREE. This modal wrote
  // credit_conditions rows only; the borrower's step-5 checklist and the shell's
  // needs panel both render from additional_needs. So a borrower received a
  // notice itemising eight things, was told "upload them directly from your
  // dashboard, where each outstanding item is listed", and found none of them
  // there. The server now raises a need per item under the section chosen here.
  //
  // 🔑 EMPTY BY DEFAULT, DELIBERATELY. A default would be the code guessing where
  // a borrower should go to satisfy an item; the wrong section puts the upload
  // slot on a screen they never reach. Staff choose, or the send is refused.
  const [cureItems, setCureItems] = useState([{ title: '', description: '', section: '' }]);
  // ⚠️ FETCHED, NOT RETYPED. constants/cureSections.js is the one list, and the
  // need categories the server files items under come from it — a copy here
  // would be the third section system this codebase has had.
  const [cureSections, setCureSections] = useState([]);
  const [cureSubmitting, setCureSubmitting] = useState(false);
  const settingsCogRef = useRef(null);

  // Date Tracking state
  const [editingDateField, setEditingDateField] = useState(null);
  const [dateInputVal, setDateInputVal] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  // Option Period document URLs (LE / PSA) state REMOVED — the Option
  // Period Documents card on the Dates tab is gone. LE flow lives in
  // the offer-accepted overlay; PSA flow uses the native e-sign path
  // (PSASigningModal → /api/option-period/sign-bc-psa-canvas). The
  // option_periods.{le_url,psa_url} columns + PATCH /option-period-docs
  // endpoint remain on the backend as a rollback path but no UI
  // surface consumes them.

  // Closings tab — wire records (populated from /double-close/:userId/full)
  // wireRecords state removed — only consumer was the Down Payment Receipt
  // KPI, which now reads dcp.bc_emd_sent (kept in sync with step2_bc_complete).
  // verifyingWireId state removed alongside the wireRecords list in the
  // Closings tab — Mercury wire verification UI is now Finance > Wires only.

  // Closings tab state (double-close workflow)
  const [dcData, setDcData]                 = useState(null);
  // dcForm state removed — only consumer was the legacy Notes-card textarea
  // that's now replaced by ModuleNotes. setDcForm calls below are no-ops.
  const [, setTitleContacts]                = useState([]);
  const [dcLoading, setDcLoading]           = useState(false);
  // setDcSaving state removed — only writer was patchDc, also removed.
  const [dcPsaUploading, setDcPsaUploading] = useState(null); // 'ab' | 'bc' | null
  const [dcPkgSending, setDcPkgSending]     = useState(null); // 'ab' | 'bc' | null
  const [dcErr, setDcErr]                   = useState('');
  // Aggregated read-only context for the redesigned Closings tab — populated
  // from GET /api/admin/double-close/:userId/full alongside dcData.
  const [dcExtras, setDcExtras]             = useState({ optionPeriod: null, closingDisclosure: null, docmagicDocs: [], hasCoBorrower: false });
  // fundingModal + fundingSending state removed alongside the Closing Actions
  // section that opened it. Funding requests now flow through the Closing
  // Module overlay (Step 10).
  // E-closing link admin input — text field on the Closings tab. Saves to
  // users.eclosing_url via PATCH /api/admin/borrowers/:id/eclosing-url and
  // fires a borrower-facing notification email so they head back to sign.
  // fullPkgSending + titlePkgSending state removed alongside the Title
  // Requests section (Generate Closing Package + Resend Title Package
  // buttons). Resend now lives in Closing Module Step 1.

  // Schedule Closing modal — fired from the bottom of either side card.
  const [scheduleModal, setScheduleModal]       = useState({ open: false, side: null });
  const [scheduleForm, setScheduleForm]         = useState({ closing_date: '', closing_time: '', location: '5950 Sherry Lane, Suite 300, Dallas, Texas 75225', notes: '', eclosing_url: '' });
  const [scheduleSaving, setScheduleSaving]     = useState(false);
  // The CD waiting period for the closing-date picker (GET /cd-waiting-period): the earliest allowed date.
  const [cdWait, setCdWait] = useState(null);
  useEffect(() => {
    if (!scheduleModal.open) return undefined;
    let alive = true;
    axios.get(`${API}/api/admin/borrowers/${id}/cd-waiting-period`, { headers })
      .then((r) => { if (alive) setCdWait(r.data); }).catch(() => { if (alive) setCdWait(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleModal.open, id]);
  const [scheduleToast, setScheduleToast]       = useState(null);
  // Closing documents grid (B-C card 6-doc panel).
  const [closingDocs, setClosingDocs]           = useState({ documents: {}, types: {} });
  const [closingDocUploading, setClosingDocUploading] = useState(null); // doc_type or null
  // Documents panel collapses by default — admins typically only need it
  // open when they're actively uploading or reviewing a single doc.
  const [bcDocsExpanded, setBcDocsExpanded]     = useState(false);
  const [abDocsExpanded, setAbDocsExpanded]     = useState(false);
  // Title contact form modal — shared create/edit flow
  const [tcModalOpen, setTcModalOpen]       = useState(false);
  const [tcEditTarget, setTcEditTarget]     = useState(null); // null = create, object = edit

  // Listing-agent communications (Property tab)
  const [laComms, setLaComms]               = useState([]);
  const [laModalOpen, setLaModalOpen]       = useState(false);

  // Single source-of-truth for listing agent contact info — used by all
  // three forms on this page (disclosure request, A-B comms, send offer).
  // Priority: offer_requests (most current — pulled from live MLS at offer
  // time) → seller_disclosures (manually entered) → empty.
  // 3d-property commit 4: listingAgentInfo now includes data.linkedListing
  // .listingAgent.* as a tertiary fallback (after manual offerRequest +
  // disclosure). This is what seeds the EditListingAgentModal when admin
  // opens it from the MLS-source state — pre-fills the form with MLS
  // values so admin can review + save explicitly. Pre-fill is NOT a
  // persist; admin must hit Save in the modal for the values to land
  // on offer_requests.listing_agent_*.
  const _ll_agent = data?.linkedListing?.listingAgent || {};
  const listingAgentInfo = {
    name:   data?.offerRequest?.listing_agent_name   || disclosureData?.disclosure?.listing_agent_name   || _ll_agent.fullName || '',
    email:  data?.offerRequest?.listing_agent_email  || disclosureData?.disclosure?.listing_agent_email  || _ll_agent.email    || '',
    phone:  data?.offerRequest?.listing_agent_phone  || disclosureData?.disclosure?.listing_agent_phone  || _ll_agent.phone    || '',
    office: data?.offerRequest?.listing_office_name  || _ll_agent.office  || '',
  };
  // Divergence detection — admin saved values previously, but the live
  // MLS feed has different values today (agent changed brokerages,
  // typo corrected, office renamed, etc.). When ANY saved field
  // differs from its MLS counterpart, surface a 'MLS data has changed'
  // note on Step 1 so admin can choose to refresh from MLS.
  //
  // Only flags divergence when:
  //   1. Saved value is non-empty (admin actually committed to a value)
  //   2. MLS value is non-empty (linkedListing exists with this field)
  //   3. They differ (case-insensitive trimmed compare for name/office;
  //      lowercase compare for email; digits-only compare for phone
  //      since HAR ships '1+832-...' format and admin types '(832) ...')
  // Reserved for the new always-on Listing Agent card; same justification
  // as the resendIntro* hooks above. eslint-disable-next-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  const mlsAgentDiverged = (() => {
    if (!data?.linkedListing?.listingAgent) return null;
    const norm = (s) => String(s || '').trim().toLowerCase();
    const digits = (s) => String(s || '').replace(/\D/g, '');
    const fields = [];
    const compareName = (saved, mls) => saved && mls && norm(saved) !== norm(mls);
    const compareEmail = (saved, mls) => saved && mls && norm(saved) !== norm(mls);
    const comparePhone = (saved, mls) => {
      const ds = digits(saved), dm = digits(mls);
      if (!ds || !dm) return false;
      // Phone match if last-10-digit suffix matches (handles '1+' prefix variants)
      return ds.slice(-10) !== dm.slice(-10);
    };
    if (compareName(data?.offerRequest?.listing_agent_name,  _ll_agent.fullName)) fields.push('name');
    if (compareEmail(data?.offerRequest?.listing_agent_email, _ll_agent.email))    fields.push('email');
    if (comparePhone(data?.offerRequest?.listing_agent_phone, _ll_agent.phone))    fields.push('phone');
    if (compareName(data?.offerRequest?.listing_office_name,  _ll_agent.office))   fields.push('office');
    return fields.length ? fields : null;
  })();
  const [laLogExpanded, setLaLogExpanded]   = useState(false);

  // Property Module — 7-step wizard overlay on the Property tab.
  // Closed by default (legacy inline cards still render so RE desk can
  // edit any field directly). Opening it hides those inline cards and
  // surfaces SmartResume on the lowest incomplete step.
  const [showPropertyModule, setShowPropertyModule] = useState(false);

  // Closing Module — 10-step closing workflow overlay on the Closings tab.
  // Same shape as showPropertyModule: closed by default with a CTA button;
  // opening surfaces the full overlay with the current step body.
  const [showClosingModule, setShowClosingModule] = useState(false);
  // current_step from /api/admin/modules/closing/:id — drives the state-
  // aware CTA label on the Closings-tab button (▶ Start / ↺ Resume — Step N
  // of 10 / ✓ Complete). Fetched lazily when the Closings tab activates.
  const [closingModuleStep, setClosingModuleStep] = useState(null);

  // Review Module — 6-step underwriting wizard layered ABOVE the
  // existing Review tab content. Closed by default; opening it
  // collapses the new info cards (ReadyCheck summary, Documents)
  // but never removes the existing UWSnapshotCard / AI Underwriter /
  // ReviewBorrowerGroup grid below.
  const [showReviewModule, setShowReviewModule] = useState(() => !!searchParams.get('module'));
  // The Module step the guided tab's "Open in Underwriting Module" asked for (the guided step's work).
  // 🔑 ?module=<step> opens the Underwriting Module on that step (the Waiting on staff and fraud review queues link here,
  // Jesse 2026-10-26).
  const [moduleStepId, setModuleStepId] = useState(() => searchParams.get('module') || null);
  const handleNewConstruction = useCallback(async (value) => {
    try {
      await axios.post(
        `${API}/api/admin/borrowers/${id}/set-new-construction`,
        { is_new_construction: !!value },
        { headers }
      );
      // Caller is responsible for refreshing data; we don't fetchData()
      // here so the toggle stays responsive even if the parent is in
      // the middle of another load.
    } catch (err) {
      alert('Could not save new-construction flag: ' + (err.response?.data?.error || err.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchLaComms = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/api/admin/listing-agent-comms/${id}`, { headers });
      setLaComms(data?.communications || []);
    } catch { setLaComms([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    if (activeTab === 'property_details' && id) fetchLaComms();
  }, [activeTab, id, fetchLaComms]);

  const fetchDoubleClose = useCallback(async () => {
    if (!id) return;
    setDcLoading(true); setDcErr('');
    try {
      // Single round trip — /full returns dc package + option_periods +
      // closing_disclosures + wire_records + docmagic LE/CD + co-borrower flag.
      const [fullRes, tcRes] = await Promise.all([
        axios.get(`${API}/api/admin/double-close/${id}/full`, { headers }),
        axios.get(`${API}/api/admin/title-contacts`,          { headers }),
      ]);
      const pkg = fullRes.data?.package || null;
      const contacts = tcRes.data?.contacts || [];
      // Attach inspectionAppraisal onto the dc package so the Closings
      // tab card can read dcData?.inspectionAppraisal?.* without a
      // separate dcExtras lookup.
      const pkgWithIA = pkg ? { ...pkg, inspectionAppraisal: fullRes.data?.inspectionAppraisal || null } : null;
      setDcData(pkgWithIA);
      setTitleContacts(contacts);
      // setWireRecords removed — state is gone.
      setDcExtras({
        optionPeriod:      fullRes.data?.optionPeriod      || null,
        closingDisclosure: fullRes.data?.closingDisclosure || null,
        docmagicDocs:      fullRes.data?.docmagicDocs      || [],
        hasCoBorrower:     !!fullRes.data?.hasCoBorrower,
        // EMD context computed fresh server-side via getEmdContext —
        // single source of truth shared with the borrower OptionPeriod hero.
        emd:               fullRes.data?.emd               || null,
      });
      // setDcForm population removed — dcForm was only read by the legacy
      // Notes textarea, which is now ModuleNotes.
    } catch (e) {
      setDcErr(e.response?.data?.error || e.message);
    } finally {
      setDcLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab === 'closings' && id) fetchDoubleClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, fetchDoubleClose]);

  // Closing Module step probe — small/cheap read so the CTA can show an
  // accurate state-aware label (▶ Start / ↺ Resume — Step N / ✓ Complete)
  // before the overlay opens. The overlay itself does its own deeper fetch.
  // Re-runs whenever the overlay closes so the label reflects newly-saved
  // state without requiring a full profile reload.
  useEffect(() => {
    if (activeTab !== 'closings' || !id) return;
    let alive = true;
    axios.get(`${API}/api/admin/modules/closing/${id}`, { headers })
      .then(r => { if (alive) setClosingModuleStep(r.data?.current_step ?? null); })
      .catch(() => { if (alive) setClosingModuleStep(null); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, showClosingModule]);

  // One-shot retry — covers the race where the admin clicks Closings
  // immediately after generate-borrower-psa fires the auto-create in the
  // background. If dcData is still null 2s after the tab activates and
  // the initial fetch has settled, refetch once.
  useEffect(() => {
    if (activeTab !== 'closings' || !id || dcLoading || dcData) return;
    const t = setTimeout(() => { fetchDoubleClose(); }, 2000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, dcLoading, dcData]);

  // Closing-package documents grid (B-C card 6-doc panel).
  const fetchClosingDocs = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/api/admin/borrowers/${id}/closing-documents`, { headers });
      setClosingDocs({ documents: data?.documents || {}, types: data?.types || {} });
    } catch (_) { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // When the Closings tab opens, pull the doc grid so the B-C documents
  // panel renders immediately.
  useEffect(() => {
    if (activeTab === 'closings' && id) {
      fetchClosingDocs();
    }
  }, [activeTab, id, fetchClosingDocs]);

  // Refresh just the title_contacts list without touching the init-form draft.
  const refetchTitleContacts = useCallback(async () => {
    try {
      const { data: r } = await axios.get(`${API}/api/admin/title-contacts`, { headers });
      const list = r.contacts || [];
      setTitleContacts(list);
      return list;
    } catch { return []; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After the title-contact form modal saves, close the modal and refresh.
  // The Initialize-Double-Close form was removed when the Closings tab was
  // redesigned, so there's no draft to seed — just refresh the joined
  // contact fields on the existing double-close record if one exists.
  const onTitleContactSaved = async () => {
    await refetchTitleContacts();
    setTcModalOpen(false);
    setTcEditTarget(null);
    if (dcData) fetchDoubleClose();
  };

  useEffect(() => {
    const handler = (e) => { if (settingsCogRef.current && !settingsCogRef.current.contains(e.target)) { setShowSettingsCog(false); setShowExportSub(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Loan Terms edit mode
  const [loanTermsEditing, setLoanTermsEditing] = useState(false);
  const [loanTermsSaving, setLoanTermsSaving] = useState(false);
  const [loanTermsForm, setLoanTermsForm] = useState({ purchasePrice: '', dpPct: '', rate: '' });

  // SSN visibility
  const [ssnVisible, setSsnVisible] = useState(false);
  const [ssnFull, setSsnFull] = useState(null);
  const [ssnLoading, setSsnLoading] = useState(false);
  // SSN/ITIN inline edit state — for primary and co-borrower separately.
  // ssnEditing flips the read-row into an input form with a type
  // selector (SSN vs ITIN) and an inline save handler that PATCHes
  // /api/admin/borrowers/:id/profile (section=borrower). Unblocks the
  // Lisa-style cases where form_data.ssn was lost to the legacy
  // autosave-on-restore wipe (root-caused + patched in commit abb4729)
  // and any future variant of missing-ssn-on-file.
  const [ssnEditing, setSsnEditing] = useState(false);
  const [ssnEditType, setSsnEditType] = useState('ssn'); // 'ssn' | 'itin'
  const [ssnEditValue, setSsnEditValue] = useState('');
  const [ssnEditSaving, setSsnEditSaving] = useState(false);
  const [coSsnVisible, setCoSsnVisible] = useState(false);
  const [coSsnFull, setCoSsnFull] = useState(null);
  const [coSsnEditing, setCoSsnEditing] = useState(false);
  const [coSsnEditType, setCoSsnEditType] = useState('ssn');
  const [coSsnEditValue, setCoSsnEditValue] = useState('');
  const [coSsnEditSaving, setCoSsnEditSaving] = useState(false);
  const [coSsnLoading, setCoSsnLoading] = useState(false);
  const [dobVisible, setDobVisible] = useState(false);
  const [coDobVisible, setCoDobVisible] = useState(false);

  // Inline editing
  const [editSection, setEditSection] = useState(null); // 'borrower' | 'coborrower' | null
  const [editFields, setEditFields] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [showCoInvite, setShowCoInvite] = useState(false);
  const [coInviteForm, setCoInviteForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [coInviteSending, setCoInviteSending] = useState(false);

  // Referral partner
  const [referralPartner, setReferralPartner] = useState(null);
  // 🚨 A PARTNER SENT THEM AND THE ATTRIBUTION NEVER WROTE. Distinct from "no
  // partner": blank and nobody must not render the same, or a failed
  // compensation record is invisible. See the server note on referralPartner.
  const [partnerUnattributed, setPartnerUnattributed] = useState(false);
  // 🔑 ?panel=partner opens the Partner card's editor: the link on a submission_email_match staff alert
  // (backend utils/emailMatchAttribution), so "choose who to attribute" lands on the allow-listed override.
  const [showPartnerEdit, setShowPartnerEdit] = useState(() => searchParams.get('panel') === 'partner');
  const [partnersList, setPartnersList] = useState([]);

  // Document Review tab
  const [allDocuments, setAllDocuments] = useState([]);
  const [showReviewStatus, setShowReviewStatus] = useState(false); // eslint-disable-line no-unused-vars

  // Credit issues
  // eslint-disable-next-line no-unused-vars
  const [creditIssues, setCreditIssues] = useState([]);
  const [creditDismissed, setCreditDismissed] = useState([]); // eslint-disable-line no-unused-vars
  const [creditMeta, setCreditMeta] = useState(null); // eslint-disable-line no-unused-vars
  // A staff re-read of the stored report — display only (CreditRereadSnapshot).
  const [creditReread, setCreditReread] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [creditCounts, setCreditCounts] = useState({ total: 0, blocking: 0, significant: 0, warning: 0 });

  // Tab counts (badges)
  const [tabCounts, setTabCounts] = useState({ documentReview: 0, needs: 0, messages: 0 });
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false); // eslint-disable-line no-unused-vars
  const [showMapModal, setShowMapModal] = useState(false);

  // 🔑 GET /guided-view: the Review tab's header and the step-8 decision lock (the classic pricing and approval cards are gone).
  const [guidedView, setGuidedView] = useState(null);
  // 🔑 WHAT IS BLOCKING THE DECISION (hotfix 11-01, file #393): GET /blocking — the same source as the 30-minute stall
  // alert and Waiting on staff (backend utils/fileBlockers). The status card renders it; it never recomputes it.
  const [fileBlocking, setFileBlocking] = useState(null);
  const fetchData = useCallback(async () => {
    try {
      const [detail, team, gv, blk] = await Promise.all([
        axios.get(`${API}/api/admin/borrowers/${id}`, { headers }),
        axios.get(`${API}/api/admin/team`, { headers }).catch(() => ({ data: [] })),
        // 🔑 The shell's own state for each applicant (the Review-tab header; the guided locks) — Jesse, 2026-10-23.
        // 🚨 A failed read is an error state with Retry, never the classic tab (cleanup, Jesse 2026-10-28/29).
        axios.get(`${API}/api/admin/borrowers/${id}/guided-view`, { headers }).catch(() => ({ data: { error: true } })),
        axios.get(`${API}/api/admin/borrowers/${id}/blocking`, { headers }).catch(() => ({ data: null })),
      ]);
      setGuidedView(gv.data || null);
      setFileBlocking(blk.data || null);
      setData(detail.data);
      setReferralPartner(detail.data.referralPartner || null);
      setPartnerUnattributed(!!detail.data.partnerOriginUnattributed);
      setTeamMembers(Array.isArray(team.data) ? team.data : team.data?.members || []);
      // Init property analysis data from user record
      const u = detail.data.user;
      if (u) {
        // Pre-fill offer terms. Priority chain:
        //   1. offer_override_price — admin's explicit override on Step 3
        //   2. hc_recommended_offer — HouseCanary AVM × 0.90
        //   3. doorly_purchase_price — locked SmartEstimate baseline
        //   4. max_approval_amount  — fallback for borrowers pre-HC
        //
        // CRITICAL: only seed when prev.purchase_price is empty. fetchData
        // runs after every mutation (offer override save, status change,
        // etc.) and previously clobbered admin edits — most visibly the
        // Step 3 override flow, where the imperative setOfferTerms in
        // handleSaveOfferOverride got overwritten by this block when the
        // post-save fetchData fired. Same guard on emd_percent.
        const pp = u.offer_override_price || u.hc_recommended_offer || u.doorly_purchase_price || u.max_approval_amount || '';
        setOfferTerms(prev => ({
          ...prev,
          purchase_price: prev.purchase_price || (pp ? String(Math.round(pp)) : ''),
          // EMD now collected as a % of purchase price (default 1%). The
          // dollar amount is computed server-side in /generate-offer so
          // an admin overriding emd_percent automatically reflows EMD$
          // without a second input.
          emd_percent: prev.emd_percent || '1',
        }));
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('doorly_admin_token');
        localStorage.removeItem('doorly_admin_user');
        navigate('/admin/login');
        return;
      }
    }
    setLoading(false);
  }, [id, token, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const startLoanTermsEdit = () => {
    // Show the stage-aware price in the edit form (what the user sees displayed)
    let editPP = pp;
    const hasResale = user.smartestimate_accepted || HOME_SHOPPING_STAGES.includes(status);
    if (hasResale && pp > 0) {
      editPP = Math.round(pp);
    } else if (atlasLocked && Number(user.atlas_max_purchase_price || 0) > 0) {
      // Prefill the borrower-facing (B-C) price so the edit form matches the
      // Loan Terms card display (Atlas branch now applies the resale markup).
      // atlasLocked only — don't seed the editor from an unaccepted live sim.
      editPP = Math.round(Number(user.atlas_max_purchase_price));
    }
    const initRate = Number(user.approved_interest_rate) > 0
      ? Number(user.approved_interest_rate)
      : (Number(user.atlas_final_rate) > 0 ? Number(user.atlas_final_rate) * 100 : null);
    setLoanTermsForm({
      purchasePrice: editPP ? String(Math.round(editPP)) : '',
      dpPct: dp && editPP ? ((dp / editPP) * 100).toFixed(1) : '',
      rate: initRate ? String(initRate) : '',
    });
    setLoanTermsEditing(true);
  };

  const handleSaveLoanTerms = async () => {
    setLoanTermsSaving(true);
    try {
      const updates = {};
      const newPP = parseFloat(loanTermsForm.purchasePrice);
      const newDpPct = parseFloat(loanTermsForm.dpPct);
      const newRate = parseFloat(loanTermsForm.rate);

      if (!isNaN(newPP) && newPP > 0) {
        // If borrower has accepted SmartEstimate, the displayed price includes resale adjustment.
        // Strip the resale to save the base doorly_purchase_price.
        const hasResale = user.smartestimate_accepted || HOME_SHOPPING_STAGES.includes(status);
        if (hasResale) {
          updates.doorly_purchase_price = Math.round(newPP);
        } else {
          updates.doorly_purchase_price = newPP;
        }
      }
      if (!isNaN(newDpPct) && newDpPct >= 0 && newDpPct <= 100) {
        const basePP = !isNaN(newPP) && newPP > 0 ? newPP : pp;
        updates.down_payment = Math.round(basePP * (newDpPct / 100));
        updates.loan_amount = basePP - updates.down_payment;
        updates.down_payment_pct = newDpPct;
      }
      if (!isNaN(newRate) && newRate > 0 && newRate <= 30) updates.approved_interest_rate = newRate;

      if (Object.keys(updates).length === 0) { setLoanTermsEditing(false); setLoanTermsSaving(false); return; }

      await axios.put(`${API}/api/admin/users/${id}/smartestimate`, updates, { headers });
      setLoanTermsEditing(false);
      // Re-fetch and update data directly to ensure saved values display
      const detail = await axios.get(`${API}/api/admin/borrowers/${id}`, { headers });
      setData(detail.data);
    } catch (e) { alert('Error saving: ' + (e.response?.data?.error || e.message)); }
    finally { setLoanTermsSaving(false); }
  };

  const handleGenerateOffer = async () => {
    // Guard — never let an offer above the borrower's max approval through.
    // Mirrors the disabled state on the Generate button; this is the
    // belt-and-suspenders fallback in case the button got bypassed (e.g.
    // form auto-submit, dev tools).
    const maxApproval = Number(user?.atlas_max_purchase_price || user?.max_approval_amount || 0);
    const enteredPrice = parseFloat(offerTerms.purchase_price || 0);
    if (maxApproval > 0 && enteredPrice > maxApproval) {
      alert(`Purchase price $${enteredPrice.toLocaleString()} exceeds the borrower's max approval of $${maxApproval.toLocaleString()}. Please correct the price before generating.`);
      return;
    }
    setOfferLoading(true);
    setOfferResult(null);
    try {
      const body = { ...offerTerms };
      // Convert numeric strings
      ['purchase_price','emd_percent','option_fee','option_period_days','close_days','commission_pct','seller_credit'].forEach(k => {
        if (body[k]) body[k] = Number(body[k]);
      });
      const res = await axios.post(`${API}/api/admin/borrowers/${id}/generate-offer`, body, { headers });
      setOfferResult(res.data);
    } catch (e) {
      alert('Error generating offer: ' + (e.response?.data?.error || e.message));
    } finally { setOfferLoading(false); }
  };

  // ── PSA flow ──────────────────────────────────────────────────────────
  const handleUploadDoorlyPsa = async (file) => {
    if (!file) return;
    setDoorlyPsaUploading(true);
    setDoorlyPsaError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', 'doorly_psa');
      await axios.post(`${API}/api/admin/borrowers/${id}/upload-document`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      // Refresh documents list so the uploaded PSA renders
      await fetchData();
    } catch (e) {
      setDoorlyPsaError(e.response?.data?.error || 'Upload failed.');
    } finally { setDoorlyPsaUploading(false); }
  };

  // Manual reZEN create — used as a fallback when the auto-create from
  // the BoldSign A-B Completed webhook failed or when ops needs to retry
  // after fixing data on the user/listing-agent record.
  const handleManualReZENCreate = async () => {
    setCreatingReZEN(true);
    setRezenError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/borrowers/${id}/create-rezen-transaction`,
        {},
        { headers }
      );
      console.log('[reZEN] manual-create response:', res.data);
      await fetchData();
    } catch (e) {
      setRezenError(e.response?.data?.error || 'reZEN transaction creation failed.');
    } finally {
      setCreatingReZEN(false);
    }
  };

  const openBorrowerPsaModal = () => {
    // Prefill from the user record. 🚨 No resale adjustment unless the file stored one: the adjustment left the program
    // 2026-10-20, so a file with none gets 0 (price = Doorly's contract price), never the old 10% (HOTFIX 11-01, Jesse).
    // A retired-option file keeps the figure it signed.
    setPsaForm({
      doorly_purchase_price: user?.doorly_purchase_price ? String(Math.round(user.doorly_purchase_price)) : '',
    });
    setBorrowerPsaResult(null);
    setBorrowerPsaError('');
    setPsaModalOpen(true);
  };

  const handleGenerateBorrowerPsa = async () => {
    setBorrowerPsaLoading(true);
    setBorrowerPsaError('');
    try {
      const body = {
        doorly_purchase_price: Number(psaForm.doorly_purchase_price),
      };
      if (!body.doorly_purchase_price || body.doorly_purchase_price <= 0) {
        throw new Error('Doorly purchase price is required.');
      }
      const res = await axios.post(`${API}/api/admin/borrowers/${id}/generate-borrower-psa`, body, { headers });
      setBorrowerPsaResult(res.data);
      await fetchData();
    } catch (e) {
      setBorrowerPsaError(e.response?.data?.error || e.message || 'Failed to generate Borrower PSA.');
    } finally { setBorrowerPsaLoading(false); }
  };

  // ── Send Offer to Listing Agent ────────────────────────────────────────
  const fetchOfferComms = useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${id}/offer-comms`, { headers });
      setOfferComms(r.data?.comms || []);
      setAcceptedOfferAvailable(!!r.data?.accepted_offer_available);
    } catch (_) { /* silent — not critical */ }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'property_details' && id) fetchOfferComms();
  }, [activeTab, id, fetchOfferComms]);

  // ── A-B PSA package state — drives the Property Module Step 6
  // status pill (Pending Borrower Review → Seller Signatures →
  // Doorly Signature → Fully Executed). Lightweight parallel fetch
  // to the AbPsaInAppPanel sub-component (which owns its own state
  // for the Property tab card); shape is the same response from
  // /api/admin/borrowers/:id/ab-psa-state. Refetched on the same
  // trigger as offerComms.
  const [abPsaPackage, setAbPsaPackage] = useState(null);
  const fetchAbPsaPackage = useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${id}/ab-psa-state`, { headers });
      setAbPsaPackage(r.data || null);
    } catch (_) { /* silent — Step 6 falls back to documents.psa_ab_signed */ }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'property_details' && id) fetchAbPsaPackage();
  }, [activeTab, id, fetchAbPsaPackage]);

  // Manual A-B PSA upload state — Property Module Step 6 right card.
  const [abPsaUploading, setAbPsaUploading] = useState(false);
  const [abPsaUploadError, setAbPsaUploadError] = useState('');
  const abPsaFileInputRef = useRef(null);
  const handleAbPsaManualUpload = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setAbPsaUploadError('Only PDFs are supported for the executed A-B PSA.');
      return;
    }
    setAbPsaUploading(true);
    setAbPsaUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/admin/borrowers/${id}/upload-ab-psa-manual`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      await Promise.all([fetchData(), fetchAbPsaPackage()]);
    } catch (err) {
      setAbPsaUploadError(err?.response?.data?.error || err.message || 'Upload failed.');
    } finally {
      setAbPsaUploading(false);
      if (abPsaFileInputRef.current) abPsaFileInputRef.current.value = '';
    }
  };

  // ── Route A-B PSA — manual admin action (Property Module Step 6). Replaces
  // the old auto-fire on OfferAcceptedReview completion. Calls the idempotent
  // initiate endpoint; the button is unlocked only once the borrower has
  // completed their review (gated in the card UI on offer_review_completed_at).
  const [abPsaRouting, setAbPsaRouting] = useState(false);
  const [abPsaRouteError, setAbPsaRouteError] = useState('');
  const handleRouteAbPsa = async () => {
    if (abPsaRouting) return;
    setAbPsaRouting(true);
    setAbPsaRouteError('');
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/initiate-ab-psa-signing`, {}, { headers });
      await Promise.all([fetchData(), fetchAbPsaPackage()]);
    } catch (err) {
      setAbPsaRouteError(err?.response?.data?.error || err.message || 'Could not route the A-B PSA.');
    } finally {
      setAbPsaRouting(false);
    }
  };

  // Per-seller resend of the A-B PSA signing email. Invalidates the old
  // token, issues a fresh one, re-sends, and refetches so the new
  // "Sent" time + delivery pill reflect the resend. Keyed by seller email
  // so each row's button shows its own in-flight state.
  const [abPsaResending, setAbPsaResending] = useState({});
  const handleResendAbPsa = async (sellerEmail) => {
    if (!sellerEmail || abPsaResending[sellerEmail]) return;
    setAbPsaResending((m) => ({ ...m, [sellerEmail]: true }));
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/resend-ab-psa-token`, { email: sellerEmail }, { headers });
      await fetchAbPsaPackage();
    } catch (err) {
      alert(err?.response?.data?.error || err.message || 'Could not resend the signing email.');
    } finally {
      setAbPsaResending((m) => ({ ...m, [sellerEmail]: false }));
    }
  };

  // Resend the executive (Jesse) counter-sign "ready" email — routed only to
  // jesse@godoorly.com server-side.
  const [countersignResending, setCountersignResending] = useState(false);
  const handleResendCountersign = async () => {
    if (countersignResending) return;
    setCountersignResending(true);
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/resend-ab-psa-countersign-email`, {}, { headers });
      await fetchAbPsaPackage();
    } catch (err) {
      alert(err?.response?.data?.error || err.message || 'Could not resend the counter-sign email.');
    } finally {
      setCountersignResending(false);
    }
  };

  // B-C variant — Jesse's borrower-PSA countersign email. Used on the
  // Closings tab next to 'Jesse — pending countersignature' so admins
  // can re-fire when the original send didn't reach Jesse (Jane Smith
  // case: original send failed because the option_periods row was
  // missing, leaving the file stuck with no countersign token + no
  // delivered email). New endpoint regenerates the token, flips
  // psa_package_status, and re-sends via the canonical
  // sendBCPsaCountersign util.
  const [bcCountersignResending, setBcCountersignResending] = useState(false);
  const handleResendBcCountersign = async () => {
    if (bcCountersignResending) return;
    if (!window.confirm('Re-fire Jesse\'s B-C PSA countersign email? This generates a fresh 7-day signing token; any prior token on this file is invalidated.')) return;
    setBcCountersignResending(true);
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${id}/resend-bc-psa-countersign`, {}, { headers });
      alert(`✓ Countersign email re-sent. New token tail: …${r.data?.tokenTail || '(unknown)'}`);
      await fetchDoubleClose?.();
    } catch (err) {
      alert(err?.response?.data?.error || err.message || 'Could not resend the B-C PSA countersign email.');
    } finally {
      setBcCountersignResending(false);
    }
  };

  // Hydrate the override panel from persisted user data once it loads
  // (useState init runs before data fetch resolves). References
  // data?.user?.* not the destructured `user` alias — the destructured
  // binding lives at line ~5305, which is behind two early returns,
  // so accessing it at this point in the component body is a TDZ
  // ReferenceError. `data` is the top-level useState and is in scope
  // here.
  useEffect(() => {
    if (data?.user?.offer_override_price != null) {
      setOfferOverride({
        active: true,
        price: String(data.user.offer_override_price),
        notes: data.user.offer_override_notes || '',
      });
    }
  }, [data?.user?.offer_override_price]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveOfferOverride = async () => {
    if (!offerOverride.notes?.trim()) {
      alert('Notes are required when overriding the offer price.');
      return;
    }
    if (!(Number(offerOverride.price) > 0)) {
      alert('Enter a positive override price.');
      return;
    }
    setSavingOverride(true);
    try {
      await axios.post(
        `${API}/api/admin/borrowers/${id}/offer-override`,
        { price: offerOverride.price, notes: offerOverride.notes },
        { headers }
      );
      // Pre-populate Step 4's purchase_price right away so the admin
      // can flip to Step 4 and find it filled in.
      setOfferTerms(prev => ({ ...prev, purchase_price: String(offerOverride.price) }));
      await fetchData();
    } catch (err) {
      alert('Save failed: ' + (err?.response?.data?.error || err.message));
    } finally {
      setSavingOverride(false);
    }
  };

  // Step 4 pre-populate — purchase_price from override + new construction
  // from the Step 2 toggle. References data?.user / data?.offerRequest
  // (not the destructured aliases) for the same TDZ reason as the
  // hydrate effect above.
  //
  // The first effect is the LIVE MIRROR: while the override panel is
  // active and a positive price is in the input, Step 4's
  // purchase_price tracks it on every keystroke. Save is no longer
  // required for the pre-fill — Save persists the override + notes
  // for audit, but the visual mirror is immediate. Admins can still
  // override Step 4's value manually after unchecking the override
  // box (the effect short-circuits when active=false).
  //
  // The second effect is the PERSISTED FALLBACK: on a fresh page
  // load with no in-session override input, hydrate Step 4 from the
  // saved users.offer_override_price. Only fires when Step 4 is
  // currently empty so it never clobbers an in-progress admin edit.
  useEffect(() => {
    if (offerOverride.active && Number(offerOverride.price) > 0) {
      setOfferTerms(prev => ({ ...prev, purchase_price: String(offerOverride.price) }));
    }
  }, [offerOverride.active, offerOverride.price]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data?.user?.offer_override_price != null && !offerTerms.purchase_price) {
      setOfferTerms(prev => ({ ...prev, purchase_price: String(data.user.offer_override_price) }));
    }
  }, [data?.user?.offer_override_price]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data?.offerRequest?.is_new_construction === true && !offerTerms.is_new_construction) {
      setOfferTerms(prev => ({ ...prev, is_new_construction: true }));
    }
  }, [data?.offerRequest?.is_new_construction]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSendOfferModal = () => {
    setSendOfferForm({
      listing_agent_name:  listingAgentInfo.name  || '',
      listing_agent_email: listingAgentInfo.email || '',
      cc_email:            'contracts@godoorly.com',
      comments_to_la:      '',
    });
    setSendOfferError('');
    setSendOfferModalOpen(true);
  };

  // Pre-fill disclosure form when the admin opens it. Without this, the
  // form's initial useState({}) keeps the fields blank even after data
  // loads. This fires on every open so the latest LA contact wins.
  const openDisclosureFormPrefilled = () => {
    setDisclosureForm({
      listing_agent_name:  listingAgentInfo.name  || '',
      listing_agent_email: listingAgentInfo.email || '',
      listing_agent_phone: listingAgentInfo.phone || '',
      cc_email:            'contracts@godoorly.com',
    });
    setDisclosureError('');
    setShowDisclosureForm(true);
  };

  const handleImproveLaComments = async () => {
    const draft = (sendOfferForm.comments_to_la || '').trim();
    if (!draft) return;
    setImprovingLaComments(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/borrowers/${id}/improve-la-comments`,
        { text: draft },
        { headers }
      );
      const improved = r.data?.improved;
      if (improved) {
        setSendOfferForm((p) => ({ ...p, comments_to_la: improved }));
      }
    } catch (e) {
      alert('AI rewrite failed: ' + (e?.response?.data?.error || e.message));
    } finally {
      setImprovingLaComments(false);
    }
  };

  const handleSendOffer = async () => {
    if (!offerResult?.file_name) {
      setSendOfferError('No offer to send. Generate one first.');
      return;
    }
    if (!sendOfferForm.listing_agent_email) {
      setSendOfferError('Listing agent email is required.');
      return;
    }
    setSendOfferLoading(true);
    setSendOfferError('');
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${id}/send-offer`, {
        listing_agent_name:  sendOfferForm.listing_agent_name || null,
        listing_agent_email: sendOfferForm.listing_agent_email.trim(),
        offer_filename:      offerResult.file_name,
        cc_email:            sendOfferForm.cc_email?.trim() || null,
        // Optional notes from Doorly to the LA — surfaced inline in the
        // outbound email and on the OfferReview public page as
        // "Comments from Doorly". Empty string → null on the server.
        comments_to_la:      sendOfferForm.comments_to_la?.trim() || null,
        // Offer terms snapshot for Offer History subtext. offerResult.terms
        // is the canonical post-generate-offer payload from the backend.
        purchase_price:      offerResult?.terms?.purchase_price ?? null,
        earnest_money:       offerResult?.terms?.earnest_money ?? null,
        close_days:          offerResult?.terms?.close_days ?? null,
      }, { headers });
      // Backend returns { delivered: true, resendEmailId } on confirmed Resend
      // success. Anything else (delivered:false or no flag) is treated as a
      // failure and surfaced inline rather than dismissing the modal.
      if (r.data?.delivered === false) {
        setSendOfferError(r.data?.error || 'Email delivery failed. Try again or check Resend logs.');
        return;
      }
      setLastSentInfo({
        email: sendOfferForm.listing_agent_email.trim(),
        cc:    sendOfferForm.cc_email?.trim() || null,
        resendId: r.data?.resendEmailId || null,
        at:    new Date().toISOString(),
      });
      setSendOfferModalOpen(false);
      fetchOfferComms();
      // Refresh the full profile so users.application_status (→ offer_submitted)
      // + offer_requests propagate into the Property Module — without this the
      // module keeps stale loanStage, deriveCurrentStep stays on Step 4, and
      // the Next button won't advance after sending.
      await fetchData();
    } catch (e) {
      // Backend now returns 502 on Resend failure with the underlying error in
      // response.data.error — surface that verbatim instead of toast-success.
      const status = e.response?.status;
      const apiErr = e.response?.data?.error;
      if (status === 502) {
        setSendOfferError(`Delivery failed: ${apiErr || e.message}. The offer was NOT sent. Verify the listing agent's email or check the Resend dashboard.`);
      } else {
        setSendOfferError(apiErr || e.message || 'Failed to send offer.');
      }
    } finally { setSendOfferLoading(false); }
  };

  // Submit the Manual Accept Offer modal. Validates locally, POSTs to
  // /api/admin/borrowers/:id/manual-accept-offer which mirrors all the
  // side-effects of the regular OfferReview /accept handler.
  const handleManualAcceptOffer = async () => {
    if (manualAcceptLoading) return;
    setManualAcceptError('');
    const f = manualAcceptForm;
    if (!f.abPurchasePrice || Number(f.abPurchasePrice) <= 0) {
      setManualAcceptError('A-B Purchase Price is required.'); return;
    }
    if (!f.effectiveDate) { setManualAcceptError('Effective Date is required.'); return; }
    if (!f.closingDate)   { setManualAcceptError('Closing Date is required.'); return; }
    if (Date.parse(f.closingDate) <= Date.parse(f.effectiveDate)) {
      setManualAcceptError('Closing Date must be after Effective Date.'); return;
    }
    const sellers = (f.sellers || []).filter((s) => (s.name || '').trim() && (s.email || '').trim());
    if (sellers.length < 1) { setManualAcceptError('At least one seller (name + email) is required.'); return; }
    if (sellers.length > 4) { setManualAcceptError('Maximum of 4 sellers allowed.'); return; }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (sellers.some((s) => !emailRe.test(s.email.trim()))) {
      setManualAcceptError('All seller emails must be valid.'); return;
    }

    setManualAcceptLoading(true);
    try {
      const body = {
        abPurchasePrice:  Number(f.abPurchasePrice),
        // 🔑 The accepted property's address: required by the server when the file has no showing or
        // option-period address to take it from (the Loan Estimate cannot be issued without one).
        propertyAddress:  f.propertyAddress?.trim() || null,
        effectiveDate:    new Date(`${f.effectiveDate}T12:00:00`).toISOString(),
        closingDate:      new Date(`${f.closingDate}T12:00:00`).toISOString(),
        optionPeriodDays: Number(f.optionPeriodDays) || 7,
        emdAmount:        f.emdAmount ? Number(f.emdAmount) : null,
        optionFee:        f.optionFee ? Number(f.optionFee) : null,
        // 🔑 N+1 prerequisite 4: the listing's HOA dues as staff read them (0 = no HOA; blank = not known).
        hoaMonthly:       String(f.hoaMonthly ?? '').trim() === '' ? null : Number(f.hoaMonthly),
        listingAgentName: f.listingAgentName?.trim() || null,
        listingAgentEmail: f.listingAgentEmail?.trim() || null,
        notes:            f.notes?.trim() || null,
        sellers:          sellers.map((s) => ({ name: s.name.trim(), email: s.email.trim() })),
      };
      const r = await axios.post(`${API}/api/admin/borrowers/${id}/manual-accept-offer`, body, { headers });
      if (r.data?.ok) {
        setManualAcceptOpen(false);
        // Refresh page data so the new state propagates (banner flips,
        // contract pricing recomputes, offer history re-renders).
        await fetchData();
        try { await fetchOfferComms(); } catch (_) {}
      } else {
        setManualAcceptError(r.data?.error || 'Manual accept failed.');
      }
    } catch (e) {
      setManualAcceptError(e.response?.data?.error || e.message);
    } finally {
      setManualAcceptLoading(false);
    }
  };

  // Resend an existing offer comm row — re-fires the same PDF to the same
  // listing agent + CC. Creates a new offer_listing_agent_comms row with a
  // fresh invite_token (so the prior link stays valid for audit). Safe to
  // re-call: the borrower email and partner email are dedup'd by
  // user_id + property slug, and the stage advance is gated on current
  // status. Defaults CC to contracts@godoorly.com when the source row's
  // cc_email is null (older rows pre-CC-field).
  const handleResendOfferComm = async (comm) => {
    if (!comm?.offer_filename) return;
    setResendingCommId(comm.id);
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${id}/send-offer`, {
        listing_agent_name:  comm.listing_agent_name || null,
        listing_agent_email: comm.listing_agent_email,
        offer_filename:      comm.offer_filename,
        cc_email:            comm.cc_email || 'contracts@godoorly.com',
        // Carry the snapshot offer terms from the original comm row so
        // the resend creates a fresh row with the same price/EM/close
        // figures rather than null. Older rows pre-this-migration may
        // have null terms — pass through nulls; UI degrades gracefully.
        purchase_price:      comm.purchase_price ?? null,
        earnest_money:       comm.earnest_money ?? null,
        close_days:          comm.close_days ?? null,
      }, { headers });
      if (r.data?.delivered === false) {
        alert('Resend failed: ' + (r.data?.error || 'unknown'));
      } else {
        setLastSentInfo({
          email: comm.listing_agent_email,
          cc:    comm.cc_email || 'contracts@godoorly.com',
          resendId: r.data?.resendEmailId || null,
          at:    new Date().toISOString(),
        });
        fetchOfferComms();
      }
    } catch (e) {
      const status = e.response?.status;
      const apiErr = e.response?.data?.error;
      alert(status === 502
        ? `Resend failed: ${apiErr || e.message}. The offer was NOT sent.`
        : `Resend failed: ${apiErr || e.message}`);
    } finally { setResendingCommId(null); }
  };

  // Edit Listing Agent (manual entry — does NOT fire any email)
  const openEditLaModal = () => {
    setEditLaForm({
      listing_agent_name:  listingAgentInfo.name  || '',
      listing_agent_email: listingAgentInfo.email || '',
      listing_agent_phone: listingAgentInfo.phone || '',
    });
    setEditLaError('');
    setEditLaModalOpen(true);
  };
  const openEditPropertyModal = () => {
    // formData / optionPeriod / offerRequest are destructured from `data`
    // inside the render block, not in this handler's scope. Pull them
    // directly from `data` so the prefill matches what the KPI card shows.
    // Priority order matches the resolver below (L10315): option_period
    // wins post-acceptance, else latest offer_request (the active target),
    // else legacy form_data. formData.property_address is the original
    // application-form shopping preference — it doesn't auto-update when
    // the borrower submits a new offer request, so it would otherwise mask
    // the active offer address.
    const current = data?.optionPeriod?.property_address
      || data?.offerRequest?.address
      || data?.formData?.property_address
      || '';
    setEditPropertyValue(current);
    setEditPropertyError('');
    setEditPropertyOpen(true);
  };
  const handleSavePropertyAddress = async () => {
    const addr = editPropertyValue.trim();
    if (!addr) {
      setEditPropertyError('Property address is required.');
      return;
    }
    setEditPropertySaving(true);
    setEditPropertyError('');
    try {
      await axios.patch(`${API}/api/admin/borrowers/${id}/property-address`, {
        property_address: addr,
      }, { headers });
      setEditPropertyOpen(false);
      // Refresh the full borrower profile so option_periods, offer_requests,
      // disclosure data, and the Property KPI card all show the new address.
      await fetchData();
      fetchHCData();
      fetchOfferComms();
    } catch (e) {
      setEditPropertyError(e.response?.data?.error || e.message || 'Failed to update property address.');
    } finally { setEditPropertySaving(false); }
  };

  const handleNudgeLoanTeam = async () => {
    setNudgeLoading(true);
    setNudgeError('');
    try {
      const r = await axios.post(
        `${API}/api/admin/borrowers/${id}/nudge-loan-team`,
        {},
        { headers }
      );
      setNudgedAt(r.data?.nudgedAt || new Date().toISOString());
    } catch (e) {
      const status = e.response?.status;
      if (status === 429 && e.response?.data?.nextAllowedAt) {
        // Treat throttle as "already nudged" so the button reflects the
        // server-side cooldown — disables the button + shows the
        // re-enable time. Less alarming than a red error.
        setNudgedAt(e.response.data.nextAllowedAt);
      } else {
        setNudgeError(e.response?.data?.error || e.message || 'Could not send nudge.');
      }
    } finally {
      setNudgeLoading(false);
    }
  };

  const handleResendIntroEmail = async () => {
    setResendIntroLoading(true);
    setResendIntroResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${id}/send-la-intro-email`,
        { cc_email: resendIntroCc.trim() || null },
        { headers });
      setResendIntroResult({
        ok: true,
        message: `Sent to ${r.data?.sent_to || 'listing agent'}${r.data?.cc ? ' (cc ' + r.data.cc + ')' : ''}`,
        resendId: r.data?.resendEmailId || null,
        at: new Date().toISOString(),
      });
      // Refresh the Listing Agent Communication Log so the manual fire's
      // application_events row surfaces inline. Mirrors the post-fire
      // refresh pattern in handleResendOfferComm / handleSavePropertyAddress.
      fetchOfferComms();
    } catch (e) {
      const status = e.response?.status;
      const apiErr = e.response?.data?.error;
      const msg = status === 502
        ? `Delivery failed: ${apiErr || e.message}. The email was NOT sent — check Resend dashboard.`
        : (apiErr || e.message || 'Failed to send.');
      setResendIntroResult({ ok: false, message: msg, at: new Date().toISOString() });
    } finally { setResendIntroLoading(false); }
  };

  const handleSaveListingAgent = async () => {
    if (!editLaForm.listing_agent_email.trim()) {
      setEditLaError('Listing agent email is required.');
      return;
    }
    setEditLaSaving(true);
    setEditLaError('');
    try {
      await axios.patch(`${API}/api/admin/borrowers/${id}/listing-agent`, {
        listing_agent_name:  editLaForm.listing_agent_name.trim() || null,
        listing_agent_email: editLaForm.listing_agent_email.trim(),
        listing_agent_phone: editLaForm.listing_agent_phone.trim() || null,
      }, { headers });
      setEditLaModalOpen(false);
      // Refresh disclosure data so the KPI card picks up the new values, and
      // the full profile so the Property Module's deriveCurrentStep sees the
      // new listing-agent info (Step 1 → done) and the Next button advances.
      fetchHCData();
      await fetchData();
    } catch (e) {
      setEditLaError(e.response?.data?.error || e.message || 'Failed to update listing agent.');
    } finally { setEditLaSaving(false); }
  };

  // Fetch HouseCanary + disclosure data when Property tab active
  const fetchHCData = async () => {
    try {
      const [discRes] = await Promise.all([
        axios.get(`${API}/api/admin/borrowers/${id}/disclosure`, { headers }).catch(() => ({ data: {} })),
      ]);
      setDisclosureData(discRes.data || null);
    } catch {}
  };
  useEffect(() => {
    // Also pull on the Closings tab — listingAgentInfo falls through to
    // disclosureData when offer_requests has no LA fields populated, and
    // the A-B Transaction card relies on it. Without this, jumping
    // straight to Closings (without visiting Property first) shows a
    // blank Listing Agent name/email.
    if ((activeTab === 'property_details' || activeTab === 'closings') && id) fetchHCData();
  }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunHC = async () => {
    setHcLoading(true);
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/run-hc-analysis`, {}, { headers });
      // Poll for completion
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await axios.get(`${API}/api/admin/borrowers/${id}`, { headers });
          if (r.data?.user?.hc_analysis_status === 'complete' || r.data?.user?.hc_analysis_status === 'error' || r.data?.user?.hc_analysis_status === 'partial' || attempts > 20) {
            clearInterval(poll);
            setHcLoading(false);
            setData(r.data);
          }
        } catch { clearInterval(poll); setHcLoading(false); }
      }, 3000);
    } catch (e) { alert(e.response?.data?.error || e.message); setHcLoading(false); }
  };

  // (handleMIRateQuote + handleOrderMI removed in the MI-removal pass.
  //  Doorly's non-QM product doesn't bind MI; the National MI integration
  //  + the /mi-rate-quote and /mi-order endpoints were deleted in G1.)

  const handleRequestDisclosure = async () => {
    setDisclosureLoading(true);
    setDisclosureError('');
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${id}/request-disclosure`, disclosureForm, { headers });
      // Backend returns delivered:true with resendEmailId on confirmed Resend
      // success. Treat anything else as failure (defense-in-depth — server
      // should already 502, but a delivered:false 200 should also fail).
      if (r.data?.delivered === false) {
        setDisclosureError(r.data?.error || 'Email delivery failed.');
        return;
      }
      setDisclosureSentInfo({
        email: disclosureForm.listing_agent_email.trim(),
        cc: disclosureForm.cc_email?.trim() || null,
        resendId: r.data?.resendEmailId || null,
        at: new Date().toISOString(),
      });
      setShowDisclosureForm(false);
      fetchHCData();
    } catch (e) {
      const status = e.response?.status;
      const apiErr = e.response?.data?.error;
      if (status === 502) {
        setDisclosureError(`Delivery failed: ${apiErr || e.message}. The disclosure request was NOT sent. Verify the listing agent's email or check the Resend dashboard.`);
      } else {
        setDisclosureError(apiErr || e.message || 'Failed to send disclosure request.');
      }
    }
    setDisclosureLoading(false);
  };

  const handleAnalyzeDisclosure = async () => {
    setDisclosureLoading(true);
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/analyze-disclosure`, {}, { headers });
      fetchHCData();
      const r = await axios.get(`${API}/api/admin/borrowers/${id}`, { headers });
      setData(r.data);
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setDisclosureLoading(false);
  };

  // Admin manual upload of the disclosure PDF — parallel path to the
  // LA token flow. POSTs to /disclosure/upload which inserts/updates a
  // seller_disclosures row with status='received' + pdf_data, then
  // refreshes disclosureData so the wizard step flips to the received
  // state with View PDF / Analyze with AI on the same render.
  const handleUploadDisclosure = async (file) => {
    if (!file) return;
    setDisclosureLoading(true);
    setDisclosureError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/admin/borrowers/${id}/disclosure/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      await fetchHCData();
      setShowDisclosureForm(false);
    } catch (e) {
      setDisclosureError(e.response?.data?.error || e.message || 'Upload failed.');
    }
    setDisclosureLoading(false);
  };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/admin/partners/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPartnersList(r.data.partners || []))
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load all documents (primary + co-borrower) when Review or Documents tab activated
  useEffect(() => {
    if ((activeTab !== 'review' && activeTab !== 'documents') || !id) return;
    const coId = data?.loanConnection?.coborrower_id;
    const primaryName = `${data?.user?.first_name || ''} ${data?.user?.last_name || ''}`.trim();
    const coName = data?.coBorrower ? `${data.coBorrower.first_name || ''} ${data.coBorrower.last_name || ''}`.trim() : '';
    Promise.all([
      axios.get(`${API}/api/admin/borrowers/${id}/documents`, { headers }),
      coId ? axios.get(`${API}/api/admin/borrowers/${coId}/documents`, { headers }).catch(() => ({ data: { documents: [] } })) : Promise.resolve({ data: { documents: [] } }),
    ]).then(([pRes, cRes]) => {
      const primaryDocs = (pRes.data.documents || []).map(d => ({ ...d, owner_type: 'primary', owner_name: primaryName }));
      const coDocs = (cRes.data.documents || []).map(d => ({ ...d, owner_type: 'coborrower', owner_name: coName }));
      setAllDocuments([...primaryDocs, ...coDocs]);
    }).catch(() => {});
  }, [activeTab, id, data?.loanConnection?.coborrower_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Co-borrower credit-issues mirror — separate state so primary +
  // co-borrower flag lists don't clobber each other. Fetched in the
  // effect below alongside primary's load.
  const [coCreditIssues, setCoCreditIssues] = useState([]);

  // Load credit issues when Review or Financials tab activated.
  // Financials added so the two-column Credit Report card can render
  // a per-borrower flag list under each side's bureau tiles.
  useEffect(() => {
    if (!['review', 'financial_details'].includes(activeTab) || !id) return;
    axios.get(`${API}/api/admin/borrowers/${id}/credit-issues`, { headers })
      .then(r => {
        setCreditIssues(r.data.issues || []);
        setCreditDismissed(r.data.dismissed || []);
        setCreditMeta(r.data.pull_meta || null);
        setCreditReread(r.data.reread || null);
        setCreditCounts(r.data.counts || { total: 0, blocking: 0, significant: 0, warning: 0 });
      })
      .catch(() => {});
    const coId = data?.loanConnection?.coborrower_id || data?.coBorrower?.id;
    if (coId) {
      axios.get(`${API}/api/admin/borrowers/${coId}/credit-issues`, { headers })
        .then(r => setCoCreditIssues(r.data.issues || []))
        .catch(() => setCoCreditIssues([]));
    } else {
      setCoCreditIssues([]);
    }
  }, [activeTab, id, data?.loanConnection?.coborrower_id, data?.coBorrower?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab count badges — poll every 60s
  useEffect(() => {
    if (!id || !token) return;
    const fetchCounts = () => {
      axios.get(`${API}/api/admin/borrowers/${id}/tab-counts`, { headers })
        .then(r => setTabCounts(r.data))
        .catch(() => {});
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load notes when notes panel opened
  useEffect(() => {
    if (!showNotesPanel || !id) return;
    axios.get(`${API}/api/admin/borrowers/${id}/notes`, { headers })
      .then((r) => setNotes(r.data || []))
      .catch(() => {});
  }, [showNotesPanel, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Needs (used by needs form)
  const fetchNeeds = () => {
    if (!id) return;
    axios.get(`${API}/api/admin/borrowers/${id}/needs`, { headers })
      .then((r) => setNeeds(r.data || []))
      .catch(() => {});
  };

  // Load liabilities when Financials tab activated
  useEffect(() => {
    if (activeTab !== 'financial_details' || !id) return;
    axios.get(`${API}/api/admin/borrowers/${id}/liabilities`, { headers })
      .then((r) => { setLiabilities(r.data.liabilities || []); })
      .catch(() => {});
  }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handleAddNeed = async () => {
    if (!newNeed.title.trim()) return;
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/needs`, newNeed, { headers });
      setShowAddNeed(false);
      setNewNeed({ need_type: 'document', title: '', description: '', priority: 'normal' });
      fetchNeeds();
    } catch (_) {}
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || notesSaving) return;
    setNotesSaving(true);
    try {
      const res = await axios.post(`${API}/api/admin/borrowers/${id}/notes`, { note: newNote, is_pinned: pinNote }, { headers });
      setNotes((prev) => [res.data, ...prev]);
      setNewNote('');
      setPinNote(false);
    } catch (_) {}
    setNotesSaving(false);
  };

  const handlePinToggle = async (note) => {
    try {
      const res = await axios.patch(`${API}/api/admin/borrowers/${id}/notes/${note.id}`, { is_pinned: !note.is_pinned }, { headers });
      setNotes((prev) => prev.map((n) => n.id === note.id ? res.data : n).sort((a, b) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0) || new Date(b.created_at) - new Date(a.created_at)));
    } catch (_) {}
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/api/admin/borrowers/${id}/notes/${noteId}`, { headers });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (_) {}
  };

  // eslint-disable-next-line no-unused-vars
  const handleAcceptSection = async (sectionName) => {
    if (!data?.user?.id) return;
    setReviewSaving(sectionName);
    try {
      const r = await axios.post(`${API}/api/review/admin/${data.user.id}/section`,
        { section: sectionName, action: 'accept' }, { headers });
      setReviewData((prev) => ({ ...prev, sections: r.data.sections, overall_percent: r.data.overall_percent }));
      if (r.data.promoted) await fetchData();
    } catch (e) {
      // 🔑 Accepting assets needs a verified amount per account (backend utils/staffAssetConfirm, 2026-10-26) — entered in
      // the Underwriting Module; say so rather than doing nothing.
      if (e && e.response && e.response.status === 422) window.alert(`${e.response.data.error} Use the Underwriting Module's Assets step to enter them.`);
    }
    setReviewSaving(null);
  };

  // useCallback so handleFlagNeed (which lists this in its dep array) keeps a
  // stable reference across renders. Setters (setReviewSaving / setReviewData)
  // are stable; axios + API are module-level. Only `data` (state read inside)
  // matters for the dep array. eslint-disable matches the rest of the file's
  // useCallback pattern.
  const handleFlagSection = useCallback(async (sectionName, note) => {
    if (!data?.user?.id) return;
    setReviewSaving(sectionName);
    try {
      const r = await axios.post(`${API}/api/review/admin/${data.user.id}/section`,
        { section: sectionName, action: 'flag', note }, { headers });
      setReviewData((prev) => ({ ...prev, sections: r.data.sections, overall_percent: r.data.overall_percent }));
    } catch {}
    setReviewSaving(null);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // handleOrderCredit REMOVED along with POST /api/admin/borrowers/:id/pull-credit.
  // It was never bound to any control — declared, referenced only by a comment,
  // and its xactusPulling state carried an eslint no-unused-vars disable. A dead
  // handler that fires a hard credit inquiry is worse than no handler: it reads
  // as a supported path. Admin pulls now go through the single gated route,
  // POST /api/review/admin/:userId/xactus-pull, which requires a reason.

  // eslint-disable-next-line no-unused-vars
  const handleDismissCreditIssue = async (issueId, reason) => {
    try {
      await axios.patch(
        `${API}/api/admin/borrowers/${id}/credit-issues/${issueId}/dismiss`,
        { dismiss_reason: reason },
        { headers }
      );
      setCreditIssues(prev => prev.filter(i => i.id !== issueId));
      setCreditCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch {}
  };

  // Load messages + email log when Communication tab activated.
  // Joint-file safe: conversation lookup keys off activeMsgOwner so
  // flipping the Primary / Co-Borrower selector re-fetches the right
  // thread. Falls back to the first conversation when there's no
  // owner-matched thread (e.g. before either borrower has messaged).
  useEffect(() => {
    if (activeTab !== 'communication' || !data) return;
    // 🔑 THE OWNER'S THREADS, NOT JUST THE FIRST ONE. See activeConvId.
    const conv = activeConv(data);
    if (conv) {
      setConvLoading(true);
      axios.get(`${API}/api/admin/messages/threads/${conv.id}`, { headers })
        .then((r) => {
          setConvMessages(r.data.messages || []);
          // ⚠️ THE GET IS WHAT MARKS IT READ (admin.js UPDATEs is_read on this
          // conversation), so the local count is corrected only once the
          // request has actually succeeded.
          setConvReadLocally((prev) => (prev.includes(conv.id) ? prev : [...prev, conv.id]));
        })
        .catch(() => {})
        .finally(() => setConvLoading(false));
    } else {
      // No thread for either borrower yet — clear so the empty-state
      // text renders ("No message thread yet.") and Send Message
      // creates a new conversation.
      setConvMessages([]);
    }
    axios.get(`${API}/api/admin/borrowers/${id}/email-log`, { headers })
      .then(r => setEmailLog(r.data.emails || []))
      .catch(() => {});
  }, [activeTab, data, activeMsgOwner, activeConvId, activeConv]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = msgEndRef.current?.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [convMessages]);

  // 🔑 THE ONE STAFF COMPOSER (fp-format, Jesse 2026-11-01): payload = { body, format: 'rich', attachment_data?,
  // attachment_name? } from StaffComposer; the server decides plain vs formatted and checks the file
  // (utils/staffMessageCompose). Resolves true when sent; a refusal throws so the composer shows it and keeps the draft.
  const sendReply = async (payload) => {
    if (!data) return false;
    const conv = activeConv(data);
    if (!conv) return false;
    try {
      const res = await axios.post(`${API}/api/admin/messages/threads/${conv.id}/reply`, payload, { headers });
      setConvMessages((m) => [...m, res.data.message]);
      return true;
    } catch (e) {
      throw new Error(e.response?.data?.error || 'The message was not sent.');
    }
  };

  const handleExportMISMO = async () => {
    try {
      const response = await fetch(`${API}/api/admin/borrowers/${id}/export/mismo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert('Export failed: ' + (err.error || response.statusText));
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MISMO_${user?.loan_number || id}_${new Date().toISOString().slice(0, 10)}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // What was left out of the file (e.g. time at the current address), from the export route (2026-10-28).
      const warn = response.headers.get('X-Mismo-Warnings');
      if (warn) {
        let list = [];
        try { list = JSON.parse(decodeURIComponent(warn)); } catch (_) { list = []; }
        if (list.length) alert('MISMO exported, with gaps:\n\n' + list.join('\n'));
      }
    } catch (err) {
      alert('Export error: ' + err.message);
    }
  };

  // DocMagic — fetch current LE/CD state for this borrower (admin view)
  const fetchDmDocs = useCallback(async () => {
    if (!id) return;
    const next = { le: null, cd: null };
    for (const t of ['le', 'cd']) {
      try {
        // 🚨 The STAFF route — /api/disclosures/package is borrower-only and its 401 signed staff out (2026-10-24).
        const r = await axios.get(`${API}/api/admin/borrowers/${id}/disclosure-package/${t}`, { headers });
        next[t] = r.data.doc || null;
      } catch (_) { /* leave it empty; a staff route never 401s a signed-in staff user */ }
    }
    setDmDocs(next);
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'loan_details' && id) fetchDmDocs();
  }, [activeTab, id, fetchDmDocs]);

  const handleGenerateDm = async (docType) => {
    if (!user?.loan_number) {
      setDmError('Loan Number is required before generating documents.');
      return;
    }
    setDmGenerating(docType);
    setDmError('');
    try {
      await axios.post(`${API}/api/disclosures/generate/${id}/${docType}`, {}, { headers });
      await fetchDmDocs();
    } catch (err) {
      setDmError(err.response?.data?.error || err.message || 'Generation failed.');
    } finally {
      setDmGenerating(null);
    }
  };

  const fetchAIExtractions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/${id}/ai-extractions`, { headers });
      setAiExtractions(res.data.extractions || []);
    } catch (_) {}
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUwNeeds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/${id}/additional-needs`, { headers });
      setUwNeeds(res.data?.grouped || { income: [], assets: [], housing: [], credit: [], other: [], uncategorized: [] });
    } catch (_) { /* non-fatal — list just stays empty */ }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate previously-persisted analyses for this loan so the findings
  // panels are visible the moment the admin opens the Review tab — no
  // re-run required. Server returns { income|assets|housing: null | {analysis, run_at} }.
  const fetchStoredAnalyses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/${id}/analysis`, { headers });
      const data = res.data || {};
      setUwAnalysis({
        income:  data.income?.analysis  || null,
        assets:  data.assets?.analysis  || null,
        housing: data.housing?.analysis || null,
      });
      setUwAnalysisRunAt({
        income:  data.income?.run_at  || null,
        assets:  data.assets?.run_at  || null,
        housing: data.housing?.run_at || null,
      });
    } catch (_) { /* non-fatal — UI just stays empty until admin re-runs */ }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Map raw backend / Anthropic error strings to a single human-readable
  // sentence the admin can act on. Centralised here so the AnalyzeSection
  // panel just renders whatever string we set.
  const friendlyAnalyzeError = (err) => {
    const raw = String(err?.response?.data?.error || err?.message || '');
    if (/image exceeds 5\s?mb/i.test(raw)) {
      return 'One or more documents are too large to analyze (max 5MB per file). Oversized files were skipped.';
    }
    if (/invalid_request_error/i.test(raw)) {
      return 'There was a problem sending documents to the AI analyzer. Please try again.';
    }
    if (raw && !/internal server error/i.test(raw) && raw.length < 240) {
      return raw;
    }
    return 'Analysis failed. Please try again or contact support.';
  };

  // eslint-disable-next-line no-unused-vars
  const handleAnalyzeSection = useCallback(async (section) => {
    setUwError(prev => ({ ...prev, [section]: null }));
    setUwOversizedAll(prev => ({ ...prev, [section]: null }));
    setUwAnalyzing(prev => ({ ...prev, [section]: true }));
    setUwToast(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/borrowers/${id}/analyze-section`,
        { section },
        { headers }
      );
      setUwAnalysis(prev => ({ ...prev, [section]: res.data?.analysis || null }));
      setUwAnalysisRunAt(prev => ({ ...prev, [section]: res.data?.run_at || new Date().toISOString() }));
      setUwToast({ section, count: Number(res.data?.needs_created || 0), docs: Number(res.data?.docs_analyzed || 0) });
      fetchUwNeeds();
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === 'all_documents_oversized') {
        setUwOversizedAll(prev => ({
          ...prev,
          [section]: {
            message: err.response?.data?.message
              || 'All uploaded documents exceed the 5MB size limit. Please ask the borrower to upload compressed or smaller versions.',
            skipped: err.response?.data?.skipped_docs || [],
          },
        }));
      } else {
        setUwError(prev => ({ ...prev, [section]: friendlyAnalyzeError(err) }));
      }
    } finally {
      setUwAnalyzing(prev => ({ ...prev, [section]: false }));
    }
  }, [id, fetchUwNeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply AI Underwriter recommended figures to the canonical financials
  // tables (borrower_income / asset_sources). The PATCH /financials route
  // takes care of marking superseded rows non-current/non-eligible. After
  // success we refetch the borrower so the Property/Loan tab KPIs that
  // read user.gross_monthly_income & user.liquid_assets refresh, and we
  // invalidate finLoadedRef so a subsequent visit to the Financials tab
  // pulls fresh income/asset rows from the server.
  // eslint-disable-next-line no-unused-vars
  const handleApplyFinancials = useCallback(async (section) => {
    setUwApplyError(prev => ({ ...prev, [section]: null }));
    setUwApplying(prev => ({ ...prev, [section]: true }));
    try {
      const analysis = uwAnalysis[section];
      const body = {};
      if (section === 'income') {
        const rec = analysis?.recommended_monthly_income;
        if (!rec || rec.amount == null) throw new Error('No recommended income on this analysis.');
        body.monthly_income = Number(rec.amount);
        body.basis = rec.basis || null;
        body.confidence = rec.confidence || null;
      } else if (section === 'assets') {
        const rec = analysis?.recommended_assets;
        if (!rec) throw new Error('No recommended assets on this analysis.');
        body.recommended_assets = rec;
      }
      const res = await axios.patch(
        `${API}/api/admin/borrowers/${id}/financials`,
        body,
        { headers }
      );
      setUwApplied(prev => ({
        ...prev,
        [section]: section === 'income'
          ? { amount: res.data?.gross_monthly_income ?? body.monthly_income }
          : { total: res.data?.liquid_assets ?? 0 },
      }));
      finLoadedRef.current = false;
      await fetchData();
    } catch (err) {
      setUwApplyError(prev => ({
        ...prev,
        [section]: err.response?.data?.error || err.message || 'Apply failed.',
      }));
    } finally {
      setUwApplying(prev => ({ ...prev, [section]: false }));
    }
  }, [id, uwAnalysis, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handlePatchNeed = useCallback(async (needId, status, reason) => {
    setUwNeedsBusy(needId);
    setUwNeedError(null);
    try {
      // 🔑 A DISMISSAL CARRIES ITS REASON. The server refuses one without (min
      // 10 characters) — the reason is what makes the dismissal count as closed.
      await axios.patch(
        `${API}/api/admin/borrowers/${id}/additional-needs/${needId}`,
        reason ? { status, reason } : { status },
        { headers }
      );
      // Refresh BOTH caches:
      //   - fetchUwNeeds: separate uwNeeds state (Needs tab list)
      //   - fetchData:    main borrower payload (Documents & File Needs
      //                   card AND ReadyCheck card both read from
      //                   data.additionalNeeds / data.user.readycheck.openNeeds)
      // Without fetchData, dismissing on one card left the other card
      // showing the same row as still 'open'. They now stay in sync.
      await Promise.all([fetchUwNeeds(), fetchData()]);
    } catch (err) {
      // ── 🚨 THE SWALLOW IS WHY DISMISS LOOKED LIKE A DEAD BUTTON ────────────
      //
      // Jesse: "I tried to click 'dismiss' on one of Mario's needs … but the
      // button did not work."
      //
      // 🔑 IT WAS RETURNING 400 EVERY TIME. The endpoint's status whitelist had
      // no 'dismissed' entry, so every dismissal was rejected — and this catch
      // threw the message away. The old comment, "list refetch will reflect
      // server truth", was its own refutation: when the call FAILS there is no
      // new server truth, so the refetch redrew the unchanged list and the click
      // appeared to do nothing at all.
      //
      // ⚠️ A SWALLOWED WRITE IS WORSE THAN A FAILED ONE. A visible error gets
      // reported in minutes; a silent one gets re-clicked, then worked around,
      // and this one went unnoticed long enough that making dismissals the ONLY
      // thing that suppresses a re-ask landed on a button that never worked.
      const d = err?.response?.data;
      setUwNeedError({
        needId,
        message: d?.error || err?.message || 'The change could not be saved.',
        detail: d?.detail || (d?.received !== undefined ? `Sent: ${d.received}` : null),
      });
    } finally { setUwNeedsBusy(null); }
  }, [id, fetchUwNeeds, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  // null = not looked up yet; [] = looked up and nothing matched.
  const [coLinkCandidates, setCoLinkCandidates] = useState(null);
  const [coLinkBusy, setCoLinkBusy] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const handleAddManualNeed = useCallback(async (category) => {
    const text = String(uwManualForm[category] || '').trim();
    if (!text) return;
    try {
      await axios.post(
        `${API}/api/admin/borrowers/${id}/additional-needs`,
        { category, need_text: text },
        { headers }
      );
      setUwManualForm(prev => ({ ...prev, [category]: '' }));
      fetchUwNeeds();
    } catch (_) {}
  }, [id, uwManualForm, fetchUwNeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handleDeleteNeed = useCallback(async (needId) => {
    if (!window.confirm('Dismiss this need?')) return;
    setUwNeedsBusy(needId);
    try {
      await axios.delete(
        `${API}/api/admin/borrowers/${id}/additional-needs/${needId}`,
        { headers }
      );
      fetchUwNeeds();
    } catch (_) {}
    finally { setUwNeedsBusy(null); }
  }, [id, fetchUwNeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Co-borrower handlers ───────────────────────────────────────────────
  // Mirror the primary handlers, scoped to the co-borrower's user_id.
  // Resolved via data.loanConnection.coborrower_id (same shape the rest
  // of this file uses for co-borrower lookups). Each handler bails if
  // there's no co-borrower attached so the call sites can render
  // unconditionally without an outer guard.
  const coId = data?.loanConnection?.coborrower_id;

  const fetchCoUwNeeds = useCallback(async () => {
    if (!coId) return;
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/${coId}/additional-needs`, { headers });
      setCoUwNeeds(res.data?.grouped || { income: [], assets: [], housing: [], credit: [], other: [], uncategorized: [] });
    } catch (_) {}
  }, [coId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCoStoredAnalyses = useCallback(async () => {
    if (!coId) return;
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/${coId}/analysis`, { headers });
      const d = res.data || {};
      setCoUwAnalysis({
        income: d.income?.analysis || null,
        assets: d.assets?.analysis || null,
      });
      setCoUwAnalysisRunAt({
        income: d.income?.run_at || null,
        assets: d.assets?.run_at || null,
      });
    } catch (_) {}
  }, [coId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handleAnalyzeCoSection = useCallback(async (section) => {
    if (!coId) return;
    setCoUwError(prev => ({ ...prev, [section]: null }));
    setCoUwOversizedAll(prev => ({ ...prev, [section]: null }));
    setCoUwAnalyzing(prev => ({ ...prev, [section]: true }));
    setCoUwToast(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/borrowers/${coId}/analyze-section`,
        { section },
        { headers }
      );
      setCoUwAnalysis(prev => ({ ...prev, [section]: res.data?.analysis || null }));
      setCoUwAnalysisRunAt(prev => ({ ...prev, [section]: res.data?.run_at || new Date().toISOString() }));
      setCoUwToast({ section, count: Number(res.data?.needs_created || 0), docs: Number(res.data?.docs_analyzed || 0) });
      fetchCoUwNeeds();
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === 'all_documents_oversized') {
        setCoUwOversizedAll(prev => ({
          ...prev,
          [section]: {
            message: err.response?.data?.message
              || 'All uploaded documents exceed the 5MB size limit. Please ask the co-borrower to upload compressed or smaller versions.',
            skipped: err.response?.data?.skipped_docs || [],
          },
        }));
      } else {
        setCoUwError(prev => ({ ...prev, [section]: friendlyAnalyzeError(err) }));
      }
    } finally {
      setCoUwAnalyzing(prev => ({ ...prev, [section]: false }));
    }
  }, [coId, fetchCoUwNeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handlePatchCoNeed = useCallback(async (needId, status) => {
    if (!coId) return;
    setCoUwNeedsBusy(needId);
    try {
      await axios.patch(
        `${API}/api/admin/borrowers/${coId}/additional-needs/${needId}`,
        { status },
        { headers }
      );
      fetchCoUwNeeds();
    } catch (_) {} finally { setCoUwNeedsBusy(null); }
  }, [coId, fetchCoUwNeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handleAddCoManualNeed = useCallback(async (category) => {
    if (!coId) return;
    const text = String(coUwManualForm[category] || '').trim();
    if (!text) return;
    try {
      await axios.post(
        `${API}/api/admin/borrowers/${coId}/additional-needs`,
        { category, need_text: text },
        { headers }
      );
      setCoUwManualForm(prev => ({ ...prev, [category]: '' }));
      fetchCoUwNeeds();
    } catch (_) {}
  }, [coId, coUwManualForm, fetchCoUwNeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handleDeleteCoNeed = useCallback(async (needId) => {
    if (!coId) return;
    if (!window.confirm('Dismiss this need?')) return;
    setCoUwNeedsBusy(needId);
    try {
      await axios.delete(
        `${API}/api/admin/borrowers/${coId}/additional-needs/${needId}`,
        { headers }
      );
      fetchCoUwNeeds();
    } catch (_) {} finally { setCoUwNeedsBusy(null); }
  }, [coId, fetchCoUwNeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply-to-Financials for co-borrower. Passes borrower_type='coborrower'
  // so the backend writes the inserted borrower_income row with the
  // correct role flag instead of hardcoding 'primary'.
  // eslint-disable-next-line no-unused-vars
  const handleApplyCoFinancials = useCallback(async (section) => {
    if (!coId) return;
    setCoUwApplyError(prev => ({ ...prev, [section]: null }));
    setCoUwApplying(prev => ({ ...prev, [section]: true }));
    try {
      const analysis = coUwAnalysis[section];
      const body = { borrower_type: 'coborrower' };
      if (section === 'income') {
        const rec = analysis?.recommended_monthly_income;
        if (!rec || rec.amount == null) throw new Error('No recommended income on this analysis.');
        body.monthly_income = Number(rec.amount);
        body.basis = rec.basis || null;
        body.confidence = rec.confidence || null;
      } else if (section === 'assets') {
        const rec = analysis?.recommended_assets;
        if (!rec) throw new Error('No recommended assets on this analysis.');
        body.recommended_assets = rec;
      }
      const res = await axios.patch(
        `${API}/api/admin/borrowers/${coId}/financials`,
        body,
        { headers }
      );
      setCoUwApplied(prev => ({
        ...prev,
        [section]: section === 'income'
          ? { amount: res.data?.gross_monthly_income ?? body.monthly_income }
          : { total: res.data?.liquid_assets ?? 0 },
      }));
      finLoadedRef.current = false;
      await fetchData();
    } catch (err) {
      setCoUwApplyError(prev => ({
        ...prev,
        [section]: err.response?.data?.error || err.message || 'Apply failed.',
      }));
    } finally {
      setCoUwApplying(prev => ({ ...prev, [section]: false }));
    }
  }, [coId, coUwAnalysis, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-need Flag — reuses the section-level flag flow under the hood.
  // Pre-fills the admin_note with the need's title + description so the
  // borrower sees the AI-generated request verbatim, then admin can edit
  // before sending. The downstream review_sections row + borrower-response
  // panel + approve / re-flag buttons all keep working unchanged.
  // eslint-disable-next-line no-unused-vars
  const handleFlagNeed = useCallback((sectionId, need) => {
    const defaultNote = need?.description
      ? `${need.title}\n\n${need.description}`
      : (need?.title || '');
    const finalNote = window.prompt(
      'Flag note (sent to borrower). You can edit before sending:',
      defaultNote
    );
    if (finalNote == null) return; // admin cancelled
    if (!finalNote.trim()) return;
    return handleFlagSection(sectionId, finalNote.trim());
  }, [handleFlagSection]);

  useEffect(() => {
    if (activeTab === 'review' && id) {
      fetchAIExtractions();
      fetchStoredAnalyses();
      // Hydrate co-borrower analyses too so the Review tab opens with
      // both panels pre-populated. Cheap GET that no-ops when no co-id.
      fetchCoStoredAnalyses();
    }
  }, [activeTab, id, coId]); // eslint-disable-line react-hooks/exhaustive-deps

  // uwNeeds powers the Review tab roll-up AND the Loan tab "Needs" card,
  // so fetch on either tab. (Cheap + idempotent — just a single GET.)
  useEffect(() => {
    if ((activeTab === 'review' || activeTab === 'loan_details') && id) {
      fetchUwNeeds();
      fetchCoUwNeeds();
    }
  }, [activeTab, id, coId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch credit history when financial tab loads
  useEffect(() => {
    if (activeTab !== 'financial_details' || !id) return;
    axios.get(`${API}/api/admin/borrowers/${id}/credit-history`, { headers })
      .then(r => setCreditHistory(r.data.history || [])).catch(() => {});
  }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch credit conditions when review tab loads
  useEffect(() => {
    if (activeTab !== 'review' || !id) return;
    axios.get(`${API}/api/admin/borrowers/${id}/credit-conditions`, { headers })
      .then(r => setCreditConditions(r.data.conditions || [])).catch(() => {});
  }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  const handleGenerateConditions = async () => {
    setConditionsGenerating(true);
    try {
      await axios.post(`${API}/api/admin/borrowers/${id}/generate-credit-conditions`, {}, { headers });
      const r = await axios.get(`${API}/api/admin/borrowers/${id}/credit-conditions`, { headers });
      setCreditConditions(r.data.conditions || []);
    } catch (e) { alert(e.response?.data?.error || 'Could not generate conditions'); }
    setConditionsGenerating(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleConditionAction = async (condId, action) => {
    try {
      await axios.patch(`${API}/api/admin/borrowers/${id}/credit-conditions/${condId}`, { action }, { headers });
      const r = await axios.get(`${API}/api/admin/borrowers/${id}/credit-conditions`, { headers });
      setCreditConditions(r.data.conditions || []);
    } catch {}
  };

  // Fetch financial details when tab is activated (primary + co-borrower)
  // ── 🔑 "CALCULATED BUT NOT CONFIRMED" IS A THIRD STATE ─────────────────────
  //
  // Jesse: "surface the unconfirmed ReadyCheck figure on Financials".
  //
  // This tab shows what has been WRITTEN. A ReadyCheck run calculates its
  // figures and stores them, but applyReadyCheckIncome / applyReadyCheckAssets
  // fire on the borrower's Step-2 confirm — so a file parked mid-Step-2 reads $0
  // while its numbers sit in ai_analysis_results and users.asset_sources.
  //
  // ⚠️ IT IS RENDERED AS A PREVIEW, NEVER AS THE FIGURE. The number stays grey
  // and the headline stays $0, because every downstream calculation on this page
  // — Back DTI, reserves, the Loan Terms card — reads the WRITTEN column. A
  // pending figure promoted into the headline would make the DTI beside it
  // disagree with the income above it, and the borrower's ratification is a real
  // gate rather than a rendering detail.
  //
  // ⚠️ AND A DISPUTE IS SAID OUT LOUD. readycheck_income_disputed means the
  // borrower looked at this number and said it was wrong — "awaiting
  // confirmation" would be a misleading way to describe that.
  const renderRcPending = (kind) => {
    const p = rcPending && rcPending[kind];
    if (!p || p.amount == null || Number(p.amount) <= 0) return null;
    if (p.confirmedAt) return null;
    return (
      <div style={{ fontSize: 10.5, color: '#92400e', marginTop: 3, lineHeight: 1.35 }}>
        <span style={{ fontWeight: 700 }}>
          ReadyCheck calculated {fmt$(p.amount)}{kind === 'income' ? '/mo' : ''}
        </span>
        {p.disputed
          ? ' \u2014 the borrower disputed this figure on Step 2; resolve the dispute to apply it.'
          : ' \u2014 awaiting the borrower\u2019s confirmation on ReadyCheck Step 2. Not applied yet, so it is not in the DTI below.'}
        {kind === 'assets' && Array.isArray(p.giftGaps) && p.giftGaps.length > 0 && (
          <span>
            {' '}({p.giftGaps.length} gift source{p.giftGaps.length === 1 ? '' : 's'} excluded
            pending donor documents.)
          </span>
        )}
      </div>
    );
  };
  useEffect(() => {
    if (activeTab !== 'financial_details' || !id) return;
    const coId = data?.loanConnection?.coborrower_id;
    Promise.all([
      axios.get(`${API}/api/admin/borrowers/${id}/income`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/api/admin/borrowers/${id}/assets`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/api/admin/borrowers/${id}/liabilities`, { headers }).catch(() => ({ data: {} })),
      coId ? axios.get(`${API}/api/admin/borrowers/${coId}/income`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
      coId ? axios.get(`${API}/api/admin/borrowers/${coId}/assets`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
      coId ? axios.get(`${API}/api/admin/borrowers/${coId}/liabilities`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
      // ⚠️ ITS OWN REQUEST, AND ITS OWN .catch. A pending-figure preview is a
      // convenience; it must never be the reason the Financials tab fails to
      // render the figures that ARE written.
      axios.get(`${API}/api/admin/borrowers/${id}/readycheck-pending-figures`, { headers })
        .catch(() => ({ data: null })),
    ]).then(([iR, aR, lR, coIR, coAR, coLR, rcR]) => {
      setRcPending(rcR?.data || null);
      const coName = data?.coBorrower ? `${data.coBorrower.first_name} ${data.coBorrower.last_name}` : 'Co-Borrower';
      const primaryInc = (iR.data?.income || []).map(i => ({ ...i, borrower_type: i.borrower_type || 'primary' }));
      const coInc = (coIR.data?.income || []).map(i => ({ ...i, borrower_type: 'coborrower', borrower_label: coName }));
      const inc = [...primaryInc, ...coInc];
      setFinIncome(inc); setFinGMI((iR.data?.totalGMI || 0) + (coIR.data?.totalGMI || 0));
      // Read from data?.user — `user` is destructured below the early
      // returns (loading / !data), so on the mount-render where this effect
      // first fires it's in the temporal dead zone. Optional chaining does
      // NOT rescue you from TDZ; the closure throws ReferenceError between
      // setFinIncome and setFinAssets, leaving Assets+Liabilities empty.
      const primName = `${data?.user?.first_name || ''} ${data?.user?.last_name || ''}`.trim() || 'Primary';
      const primaryAssets = (aR.data?.assets || []).map(a => ({ ...a, owner_type: a.owner_type || 'primary', owner_name: primName }));
      const coAssets = (coAR.data?.assets || []).map(a => ({ ...a, owner_type: 'coborrower', owner_name: coName }));
      setFinAssets([...primaryAssets, ...coAssets]); setFinTotalAssets((aR.data?.totalAssets || 0) + (coAR.data?.totalAssets || 0));
      const primaryLiabs = (lR.data?.liabilities || []).map(l => ({ ...l, owner_type: l.owner_type || 'primary' }));
      const coLiabs = (coLR?.data?.liabilities || []).map(l => ({ ...l, owner_type: 'coborrower', owner_name: coName }));
      setFinLiabs([...primaryLiabs, ...coLiabs]);
      // Backend handler returns the total under summary.totalMonthlyPayments;
      // the older shape used totalMonthly. Read either so the header total
      // surfaces correctly regardless of which handler responded.
      const liabTotal = (lR.data?.summary?.totalMonthlyPayments ?? lR.data?.totalMonthly ?? 0)
                      + (coLR?.data?.summary?.totalMonthlyPayments ?? coLR?.data?.totalMonthly ?? 0);
      setFinTotalLiab(liabTotal);
      // ── 🚨 THE PREFILL ONLY EVER RAN FOR THE PRIMARY ──────────────────────
      //
      // Jesse: "is also only showing Cathy's income, not Mario's. They should
      // each have their own income and asset rows on the Financials tab."
      //
      // 🚨 THE FETCH ABOVE ALREADY ASKS FOR BOTH BORROWERS. What did not was
      // this: the prefill posted to `id` and re-read `id`, and the guard was
      // `inc.length === 0` — the COMBINED list. So a co-borrower with no
      // borrower_income rows got none created, and worse, once the primary had
      // any income at all the combined list was non-empty and the prefill never
      // fired for anybody. Mario's income could not appear because nothing ever
      // populated it.
      //
      // ⚠️ GUARDED PER BORROWER, because that is the question. "Does this
      // borrower have income rows" is answerable about one person at a time, and
      // the combined count answers it about neither.
      // ── 🚨 THE TOTAL COMES FROM THE SERVER, NOT FROM A GUESSED FIELD NAME ──
      //
      // Jesse: "the Financials tab shows $0 income and $0 assets for Cathy."
      //
      // 🚨 THE $0 INCOME WAS MINE, from the commit that fixed the co-borrower
      // prefill. I recomputed the header with
      // `Number(i.monthly_amount || i.amount)` — and borrower_income has NEITHER
      // column. Its figures are base_monthly / bonus_monthly /
      // commission_monthly / overtime_monthly / other_monthly, summed into
      // monthly_total, and GET /income returns `totalGMI` already computed from
      // monthly_total and filtered on is_current. My sum returned 0 for every
      // row, so setFinGMI(0) zeroed a header that had been right.
      //
      // ⚠️ AND IT FIRED ON CATHY BECAUSE MARIO HAS NO INCOME ROWS. The condition
      // is "either borrower is empty", so her correct total was recomputed — and
      // destroyed — in order to prefill his.
      //
      // 🔑 SO THE RESPONSE IS CARRIED, NOT THE ROWS ALONE. The server owns
      // is_current and the monthly_total rollup; re-deriving either on the client
      // is a second definition of the number, which is the shape this codebase
      // keeps finding.
      const prefill = async (who) => {
        await axios.post(`${API}/api/admin/borrowers/${who}/income/prefill`, {}, { headers })
          .catch(() => {});
        const r2 = await axios.get(`${API}/api/admin/borrowers/${who}/income`, { headers })
          .catch(() => ({ data: {} }));
        return { rows: r2.data?.income || [], total: Number(r2.data?.totalGMI) || 0 };
      };
      if (primaryInc.length === 0 || (coId && coInc.length === 0)) {
        (async () => {
          // ⚠️ THE UNPREFILLED SIDE KEEPS ITS ORIGINAL RESPONSE TOTAL, which is
          // the whole point — nothing recomputes a figure the server already sent.
          const p2 = primaryInc.length === 0
            ? await prefill(id)
            : { rows: primaryInc, total: Number(iR.data?.totalGMI) || 0 };
          const c2 = (coId && coInc.length === 0)
            ? await prefill(coId)
            : { rows: coInc, total: Number(coIR.data?.totalGMI) || 0 };
          const pTagged = p2.rows.map(i => ({ ...i, borrower_type: i.borrower_type || 'primary' }));
          const cTagged = c2.rows.map(i => ({ ...i, borrower_type: 'coborrower', borrower_label: coName }));
          setFinIncome([...pTagged, ...cTagged]);
          setFinGMI(p2.total + c2.total);
        })();
      }
    }).catch(err => { console.error('[Financials] effect failed:', err); });
  }, [activeTab, id, data?.loanConnection?.coborrower_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveDate = async (fieldName, value) => {
    setSavingDate(true);
    try {
      await axios.patch(`${API}/api/admin/borrowers/${id}/dates`, { [fieldName]: value || null }, { headers });
      setEditingDateField(null);
      fetchData();
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setSavingDate(false); }
  };

  // handlePullCredit and handleOrderCredit are both gone. Admin credit pulls
  // now go through ONE gated route — POST /api/review/admin/:userId/xactus-pull
  // — which requires a reason, attributes to the acting admin, and records a
  // credit_authorization_log row. There is no admin pull control in this file.
  // The co-borrower path inside the Financials credit card still has its own
  // inline handler calling setCreditPulling/setCreditError, so those state
  // setters remain in use.

  const handleArchiveBorrower = async () => {
    setShowArchiveConfirm(false);
    try {
      const res = await axios.patch(`${API}/api/admin/borrowers/${id}/archive`, {}, { headers });
      if (res.data.success) { alert(`${user.first_name} ${user.last_name} has been archived.`); navigate('/admin/borrowers'); }
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
  };

  // Canonical reason-code label map — drives both the checkbox UI and
  // the `reasons[]` payload posted to /adverse. Order matches the spec.
  const ADVERSE_REASON_CATALOG = [
    ['reason_credit',           'Credit score below minimum threshold'],
    ['reason_credit_history',   'Insufficient credit history / tradelines'],
    ['reason_dti',              'Excessive obligations in relation to income (DTI)'],
    ['reason_dero',             'Derogatory credit history (foreclosure, bankruptcy, etc.)'],
    ['reason_dero_seasoning',   'Derogatory credit history — insufficient seasoning'],
    ['reason_income',           'Unable to verify income'],
    ['reason_employment',       'Unable to verify employment'],
    ['reason_assets',           'Unable to verify assets'],
    ['reason_ltv',              'Insufficient down payment / reserves'],
    ['reason_property',         'Property did not meet program requirements'],
    // reason_incomplete intentionally REMOVED from the deny catalog: an incomplete
    // application is a different Reg B instrument (Notice of Incompleteness / cure),
    // not an adverse-action denial. Use the "Request missing items (cure)" mode.
    ['reason_other',            'Other (requires written explanation)'],
  ];
  const adverseSelectedReasons = ADVERSE_REASON_CATALOG
    .filter(([k]) => adverseForm[k] && k !== 'reason_other')
    .map(([, label]) => label);
  const adverseHasOther = !!adverseForm.reason_other;
  const adverseHasAnyReason = adverseSelectedReasons.length > 0 || (adverseHasOther && adverseForm.reason_other_text.trim());

  const handleSubmitAdverse = async () => {
    if (!adverseHasAnyReason) { alert('Select at least one adverse reason.'); return; }
    if (adverseHasOther && !adverseForm.reason_other_text.trim()) {
      alert('Please describe the "Other" reason.'); return;
    }
    try {
      // Resolve qualifying FICO (lower of middle scores across borrowers)
      // for the audit payload + downstream FCRA disclosure logic.
      const primaryFico = Number(user?.fico_score_actual || user?.credit_score || 0) || null;
      const coFico      = Number(coBorrower?.fico_score_actual || coBorrower?.credit_score || 0) || null;
      const qualifyingFico = (primaryFico && coFico) ? Math.min(primaryFico, coFico) : (primaryFico || coFico);
      const payload = {
        // New canonical shape — backend prefers this.
        reasons: adverseSelectedReasons,
        otherText: adverseHasOther ? adverseForm.reason_other_text.trim() : '',
        notification_method: adverseForm.notification_method,
        qualifying_fico: qualifyingFico,
        credit_bureau: user?.fico_score_source || 'Xactus',
        // Legacy fields kept for back-compat — backend will accept either.
        ...adverseForm,
      };
      const res = await axios.post(`${API}/api/admin/borrowers/${id}/adverse`, payload, { headers });
      if (res.data.success) { setShowAdverseModal(false); alert('Adverse Action notice issued and sent to borrower.'); fetchData(); }
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
  };

  // CURE path — Notice of Incompleteness. Posts the itemized missing items to the
  // dedicated endpoint, which keeps the borrower in the funnel (docs_needed) and
  // NEVER sets the adverse flags. Borrower email is gated server-side (off until
  // counsel approves the notice language) — this only prepares + stores it.
  const handleSubmitCure = async () => {
    const items = cureItems
      .map((it) => ({ title: (it.title || '').trim(), description: (it.description || '').trim(),
        section: (it.section || '').trim() }))
      .filter((it) => it.title);
    if (!items.length) { alert('Add at least one missing item.'); return; }
    // 🚨 CHECKED HERE AND AGAIN ON THE SERVER. The client check is for the
    // staffer's benefit — it names the row — and the server's is the one that
    // decides, because a client check is a convenience and not a control.
    const missing = items.filter((it) => !it.section);
    if (missing.length) {
      alert(`Choose a section for every item so the borrower can find it. Missing on: `
        + `${missing.map((it) => `"${it.title}"`).join(', ')}`);
      return;
    }
    try {
      setCureSubmitting(true);
      const res = await axios.post(`${API}/api/admin/borrowers/${id}/request-incomplete`, { items }, { headers });
      if (res.data.success) {
        setShowAdverseModal(false);
        setCureItems([{ title: '', description: '', section: '' }]);
        alert(
          `Notice of Incompleteness prepared. ${res.data.items} item(s) requested; borrower is now in docs_needed with a cure deadline of ${String(res.data.cure_deadline).split('T')[0]}.` +
          (res.data.email_sent ? ' The borrower was emailed.' : ' Borrower email is OFF (send disabled pending counsel review) — the PDF is stored and ops was alerted.')
        );
        fetchData();
      }
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setCureSubmitting(false); }
  };

  const handleOpenAuditLog = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/${id}/audit-log`, { headers });
      setAuditLogEntries(res.data.entries || []);
      setShowAuditLogModal(true);
    } catch (err) { alert('Could not load audit log: ' + err.message); }
  };

  const handleExportAuditCSV = () => {
    if (!auditLogEntries.length) return;
    const csv = [['Timestamp', 'Event', 'Description', 'Actor'],
      ...auditLogEntries.map(e => [new Date(e.created_at).toLocaleString(), e.event_type || '', (e.description || '').replace(/,/g, ';'), e.actor || ''])
    ].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${user?.loan_number || id}_audit_log.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // 🔑 The same composer payload as sendReply above (fp-format).
  const handleSendMessage = async (payload) => {
    setMessageSending(true);
    try {
      const conv = activeConv(data);
      if (conv) {
        // Reply to existing thread
        const res = await axios.post(`${API}/api/admin/messages/threads/${conv.id}/reply`, payload, { headers });
        setConvMessages((m) => [...m, res.data.message]);
      } else {
        // Create new conversation — target the currently-selected borrower
        // so admin can kick off a thread with the co-borrower from the
        // loan file (otherwise they'd have to wait for the co to message
        // first to get a thread at all).
        const targetUserId = activeMsgOwner === 'coborrower' && data?.coBorrower?.id
          ? data.coBorrower.id
          : id;
        await axios.post(`${API}/api/admin/messages/send`, { ...payload, user_id: targetUserId }, { headers });
        await fetchData();
      }
      setMessageSent(true);
      setShowCompose(false);
      setTimeout(() => setMessageSent(false), 3000);
      setMessageSending(false);
      return true;
    } catch (e) {
      setMessageSending(false);
      throw new Error('Error sending message: ' + (e.response?.data?.error || e.message));
    }
  };

  // saveDates removed — dates consolidated into Date Tracking tab with auto-calculate

  const fetchSsn = async () => {
    if (ssnFull) { setSsnVisible(true); return; }
    setSsnLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${id}/ssn`, { headers });
      setSsnFull(r.data.ssn);
      setSsnVisible(true);
    } catch {}
    setSsnLoading(false);
  };

  // Save handler for the inline SSN/ITIN editor — primary side. Sends
  // through the existing PATCH /borrowers/:id/profile endpoint with
  // section='borrower'. Sets has_ssn alongside the value so downstream
  // reads (form_data.ssn || form_data.itin guards) pick the right
  // field. PatchOnlySendOne policy: when type='ssn' we send ssn +
  // has_ssn='yes' (don't blank itin, so a borrower with both on file
  // keeps the non-active value untouched); same in reverse for itin.
  // Refetches the borrower payload on success so the masked display
  // refreshes inline.
  const saveSsnEdit = async () => {
    const digits = String(ssnEditValue || '').replace(/\D/g, '');
    if (digits.length !== 9) {
      alert('Please enter a valid 9-digit number.');
      return;
    }
    const formatted = `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5,9)}`;
    setSsnEditSaving(true);
    try {
      const fields = ssnEditType === 'itin'
        ? { itin: formatted, has_ssn: 'no' }
        : { ssn:  formatted, has_ssn: 'yes' };
      await axios.patch(
        `${API}/api/admin/borrowers/${id}/profile`,
        { section: 'borrower', fields },
        { headers }
      );
      // Reset local read-state for the eyeball so the next reveal
      // refetches the new value (otherwise ssnFull holds the prior
      // SSN and the eyeball would show stale).
      setSsnFull(null);
      setSsnVisible(false);
      setSsnEditing(false);
      setSsnEditValue('');
      await fetchData();
    } catch (err) {
      alert('Could not save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSsnEditSaving(false);
    }
  };

  // Co-borrower mirror of saveSsnEdit. Routes through the co-borrower's
  // own user_applications row (PATCH endpoint key off :id, so we hit
  // /borrowers/:coId/profile here).
  const saveCoSsnEdit = async () => {
    const coId = data?.loanConnection?.coborrower_id || data?.coBorrower?.id;
    if (!coId) { alert('No co-borrower on this file.'); return; }
    const digits = String(coSsnEditValue || '').replace(/\D/g, '');
    if (digits.length !== 9) {
      alert('Please enter a valid 9-digit number.');
      return;
    }
    const formatted = `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5,9)}`;
    setCoSsnEditSaving(true);
    try {
      const fields = coSsnEditType === 'itin'
        ? { itin: formatted, has_ssn: 'no' }
        : { ssn:  formatted, has_ssn: 'yes' };
      await axios.patch(
        `${API}/api/admin/borrowers/${coId}/profile`,
        { section: 'borrower', fields },
        { headers }
      );
      setCoSsnFull(null);
      setCoSsnVisible(false);
      setCoSsnEditing(false);
      setCoSsnEditValue('');
      await fetchData();
    } catch (err) {
      alert('Could not save: ' + (err.response?.data?.error || err.message));
    } finally {
      setCoSsnEditSaving(false);
    }
  };

  const fetchCoSsn = async () => {
    if (coSsnFull) { setCoSsnVisible(true); return; }
    const coId = data?.loanConnection?.coborrower_id || data?.coBorrower?.id;
    if (!coId) return;
    setCoSsnLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/borrowers/${coId}/ssn`, { headers });
      setCoSsnFull(r.data.ssn);
      setCoSsnVisible(true);
    } catch {}
    setCoSsnLoading(false);
  };

  const startEdit = (section) => {
    const fd = data?.formData || {};
    const u = data?.user || {};
    const co = data?.coBorrower || {};
    if (section === 'borrower') {
      setEditFields({
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        email: u.email || '',
        phone: fd.phone || u.phone || '',
        employment_types: incomeTypesOf(fd),
      });
    } else if (section === 'coborrower') {
      setEditFields({
        first_name: co.first_name || '',
        last_name: co.last_name || '',
        email: co.email || '',
        phone: co.phone || '',
        employment_types: incomeTypesOf(co),
        // ⚠️ SEEDED, NOT BLANK. An unseeded select renders "not set" for a
        // borrower who HAS a path, and saving the form would then clear it —
        // a silent downgrade to the state this control exists to fix.
        self_employed_path: co.self_employed_path || '',
      });
    }
    setEditSection(section);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const targetId = editSection === 'coborrower' ? coBorrower?.id : id;
      await axios.patch(`${API}/api/admin/borrowers/${targetId}/profile`, { section: editSection === 'coborrower' ? 'borrower' : editSection, fields: editFields }, { headers });
      await fetchData();
      setEditSection(null);
    } catch (e) { alert('Error saving: ' + (e.response?.data?.error || e.message)); }
    setEditSaving(false);
  };

  if (loading) return (
    <AdminLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#9ca3af', fontSize: '14px' }}>Loading…</div>
    </AdminLayout>
  );
  // ── 🚨 NEVER A DEAD "Borrower not found" (Jesse, 2026-11-02) ────────────────────────────────────────────────
  // A broker submission's id reached this page as a borrower id ("Open borrower file" on submission #11). A
  // submission reference — /admin/borrowers/bs-11 or ?submission=11 — forwards to the submission view, which itself
  // forwards here once the borrower registers. Anything else says what may have happened and where to look.
  if (!data) {
    const subRef = (String(id || '').match(/^bs-(\d+)$/i) || [])[1] || searchParams.get('submission');
    if (subRef && /^\d+$/.test(String(subRef))) {
      return <Navigate to={`/admin/partners/submissions/${Number(subRef)}`} replace />;
    }
    return (
      <AdminLayout>
        <div data-testid="borrower-missing" style={{ padding: '32px', maxWidth: 640 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>No borrower file #{String(id)}</h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
            There is no borrower with this id that you can open. If this came from a partner&apos;s submission, the
            borrower may not have registered yet — their submission is on Partner Referrals until they do.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => navigate('/admin/partners/referrals')}
              style={{ padding: '8px 14px', borderRadius: 8, background: '#3a6ad9', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Partner Referrals →
            </button>
            <button type="button" onClick={() => navigate('/admin/borrowers')}
              style={{ padding: '8px 14px', borderRadius: 8, background: '#f3f4f6', color: '#374151', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Borrower Pipeline
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const { user, formData = {}, documents = [], additionalNeeds = [], idVerification, loanConnection, coBorrower, tasks = [], conditions = [], loxConditions = [], miscDocs = [], conversations = [], events = [], optionPeriod, offerRequest, assignedAdmin, reviewSections = [], reviewComplete = false, keyDates, closingDisclosure } = data; // eslint-disable-line no-unused-vars

  // Hydrate Atlas result from DB when no in-memory result exists
  // Hydrate the Review tab from the atlas_* columns only when pricing is LOCKED
  // (accepted + applied). "Price Loan" is a dryRun that persists nothing, so the
  // only thing in these columns is the last ACCEPTED pricing — but gate on the
  // lock explicitly so a reload can never surface an unaccepted simulation.
  if (!atlasResult && user.atlas_price_locked && user.atlas_priced_at && user.atlas_final_rate) {
    const inputs = (() => { try { return typeof user.atlas_pricing_inputs === 'string' ? JSON.parse(user.atlas_pricing_inputs) : (user.atlas_pricing_inputs || {}); } catch { return {}; } })();
    const notes = (() => { try { return typeof user.atlas_eligibility_notes === 'string' ? JSON.parse(user.atlas_eligibility_notes) : (user.atlas_eligibility_notes || {}); } catch { return {}; } })();
    // (PMI hydration removed — non-QM loans can't bind MI. The
    //  pmiRequired / monthlyPmi / pmiAnnualRate / miCoverage fields on
    //  the rehydrated Atlas result are gone; the JSX consumers were
    //  cleaned up in the same pass.)
    // Derive DP / closing / CTC from the PERSISTED atlas figures so the hydrated
    // Review card reconciles with Max Purchase Price + Loan Amount. Down payment
    // = borrower (B-C) price (base × the file's resale factor — 1.00 for a new file) − loan, NOT the stale user.down_payment
    // (which is a borrower-facing column that only updates on Accept). Closing =
    // cash-to-close − DP. Falls back to the borrower-facing DP + 3.5% estimate
    // only for legacy rows missing atlas_max_loan_amount / atlas_cash_to_close.
    const _hyBase = parseFloat(user.atlas_max_purchase_price || 0);
    const _hyLoan = parseFloat(user.atlas_max_loan_amount || 0);
    const _hyDP = (_hyBase > 0 && _hyLoan > 0)
      ? Math.max(0, Math.round(_hyBase) - _hyLoan)
      : parseFloat(user.down_payment || 0);
    const _hyCTC = user.atlas_cash_to_close != null
      ? parseFloat(user.atlas_cash_to_close)
      : _hyDP + Math.round(_hyBase * 0.035);
    const _hyClosing = Math.max(0, Math.round(_hyCTC - _hyDP));
    setAtlasResult({
      finalRate: parseFloat(user.atlas_final_rate),
      maxPurchasePrice: parseFloat(user.atlas_max_purchase_price || 0),
      maxLoanAmount: parseFloat(user.atlas_max_loan_amount || 0),
      appliedMaxLTV: parseFloat(user.atlas_ltv || 0),
      actualDTI: parseFloat(user.atlas_dti || 0),
      monthlyPayment: parseFloat(user.atlas_monthly_payment || 0),
      primeRate: parseFloat(user.atlas_prime_rate || 0),
      baseRate: parseFloat(user.atlas_base_rate || 0),
      ficoAdjustment: parseFloat(user.atlas_fico_adjustment || 0),
      loanAmountAdj: parseFloat(user.atlas_loan_amount_adjustment || 0),
      dtiAdjustment: parseFloat(user.atlas_dti_adjustment || 0),
      otherAdjustments: parseFloat(user.atlas_other_adjustments || 0),
      eligible: user.atlas_eligibility_status === 'eligible',
      issues: notes.issues || [],
      pricedAt: user.atlas_priced_at,
      inputs,
      ficoBand: null, loanBand: null, dtiBand: null,
      // Why max buying power is capped — 'cash_to_close' | 'dti' | 'loan_tier'
      // (null for target-price runs). Hydrated so the Review tab's cap tile
      // shows on reload, not just right after a fresh price-loan call.
      priceCapReason: user.atlas_price_cap_reason || null,
      downPayment: _hyDP,
      closingCosts: _hyClosing,
      cashToClose: _hyCTC,
      assetsShortfall: 0,
      // Reserves — hydrate from DB when present so the Reserves card shows
      // without requiring a re-price.
      reservesRequired: user.atlas_reserves_required != null ? parseFloat(user.atlas_reserves_required) : null,
      totalReserveMonths: user.atlas_reserves_months != null ? parseInt(user.atlas_reserves_months, 10) : null,
      reservesSatisfied: user.atlas_reserves_satisfied,
      reservesShortfall: user.atlas_reserves_shortfall != null ? parseFloat(user.atlas_reserves_shortfall) : null,
      overlayTriggers: (() => {
        try {
          const t = user.atlas_overlay_triggers;
          if (Array.isArray(t)) return t;
          if (typeof t === 'string') return JSON.parse(t);
          return [];
        } catch { return []; }
      })(),
      assetsAfterClose: user.atlas_assets_after_close != null ? parseFloat(user.atlas_assets_after_close) : null,
    });
  }

  const status = user.application_status || 'not_started';
  const statusMeta = STATUS_META[status] || { label: status.replace(/_/g,' '), bg: '#f9fafb', color: '#6b7280' };
  // New construction borrowers skip the option period — under_contract is
  // their distinct phase that warrants the NC tag on the badge. Once they
  // advance to 'closing' the badge collapses back to the standard "Closing"
  // label so it reads the same as a resale closing (matches the dashboard
  // hero, which now routes both NC and resale closings to HeroClosing).
  const isNewConstruction = !!offerRequest?.is_new_construction;
  const stageLabel = isNewConstruction && status === 'under_contract'
    ? 'New Construction — Under Contract'
    : statusMeta.label;
  const currentStep = getCurrentStep(status);
  const days = daysInStage(user.status_updated_at);

  // Next action logic
  let nextAction = null;
  const missingDocs = additionalNeeds.filter(isAwaitingBorrower);
  // 🚨 A BLOCKED FILE IS NEVER "On Track" (hotfix 11-01 → push B C3, Jesse 2026-10-31, file #393): the headline is the
  // file's FIRST blocker from GET /blocking (backend utils/fileBlockers — ranked staff → borrower → third party, built on
  // utils/decisionHolds and Waiting on staff), with its owner and the action that clears it. Never re-ranked here.
  const blk = fileBlocking && !fileBlocking.unreadable ? fileBlocking : null;
  const blkLead = blk && blk.state && blk.state !== 'none' ? blk : null;
  const openBlocking = () => {
    if (!blkLead) return;
    if (blkLead.link === 'request_docs') { setModal('docs'); return; }
    if (!blkLead.module) return;
    setActiveTab('review'); setModuleStepId(blkLead.module === 'misc' ? 'documents' : blkLead.module); setShowReviewModule(true);
  };
  const blockingAction = blkLead ? { icon: '⏳', text: blkLead.headline, owner: blkLead.owner || 'Doorly staff', state: blkLead.state,
    action: blkLead.link === 'request_docs' ? (blkLead.action || 'Request Docs') : blkLead.module && blkLead.action ? `${blkLead.moduleLabel || blkLead.module} → ${blkLead.action}` : null,
    actionFn: openBlocking, more: Math.max(0, (blkLead.items || []).length - 1), color: '#d97706', bg: '#fffbeb' } : null;
  if (blockingAction) {
    nextAction = blockingAction;
  } else if (!blk && missingDocs.length > 0) {
    // Only when GET /blocking could not be read: the borrower's requests are otherwise in its ranked list.
    nextAction = { icon: '⚠️', text: `${missingDocs.length} document${missingDocs.length > 1 ? 's' : ''} missing`, owner: 'Borrower', action: 'Request Docs', actionFn: () => setModal('docs'), color: '#d97706', bg: '#fffbeb' };
  } else if (idVerification && ['failed','pending'].includes(idVerification.status) && !user.identity_verified) {
    nextAction = { icon: '⚠️', text: 'Identity verification incomplete', owner: 'Borrower', action: 'Send Reminder', actionFn: () => {}, color: '#d97706', bg: '#fffbeb' };
  } else if (status === 'approved' && !user.smartestimate_accepted) {
    nextAction = { icon: '⚠️', text: 'Awaiting SmartEstimate™ acceptance', owner: 'Borrower', action: 'Send Reminder', actionFn: () => {}, color: '#d97706', bg: '#fffbeb' };
  } else if (status === 'home_shopping' && !offerRequest) {
    nextAction = { icon: '⚠️', text: 'Borrower is shopping — no offer submitted yet', owner: 'Borrower', action: 'View Homes', actionFn: () => {}, color: '#3a6ad9', bg: '#edf1fb' };
  } else if (status === 'offer_requested') {
    nextAction = { icon: '🏡', text: 'Offer requested — review property and generate offer', owner: 'Ops', action: 'Go to Property →', actionFn: () => setActiveTab('property_details'), color: '#f5b832', bg: '#fff7ed' };
  } else if (status === 'offer_submitted') {
    nextAction = { icon: '✉️', text: 'Offer sent to listing agent — awaiting response', owner: 'Listing Agent', action: 'View Property →', actionFn: () => setActiveTab('property_details'), color: '#b45309', bg: '#fffbeb' };
  } else if (status === 'offer_accepted') {
    // Two-phase next action during offer_accepted:
    //   - Until the borrower completes the 6-screen offer-review flow
    //     (LE / Borrower Rights / Services Disclosure / Confirm /
    //     Intent to Proceed / Confirm), surface that we're waiting on
    //     them. offer_review_completed_at is the canonical signal.
    //   - Once review is done, the next thing ops actually has to do is
    //     route the A-B PSA for signatures via the Closings tab.
    if (user.offer_review_completed_at) {
      nextAction = { icon: '📝', text: 'Borrower Review Completed — Route Doorly PSA to Sellers', owner: 'Ops', action: 'Go to Property →', actionFn: () => setActiveTab('property_details'), color: '#3a6ad9', bg: '#eff3fe' };
    } else {
      nextAction = { icon: '⏳', text: 'Waiting on Borrower to complete Review', owner: 'Borrower', action: null, color: '#d97706', bg: '#fffbeb' };
    }
  }
  if (!nextAction) {
    nextAction = { icon: '✅', text: 'On Track — No immediate action needed', owner: 'System', action: null, color: '#acf44e', bg: '#f0fde4' };
  }

  // KPI calculations — use Atlas values when priced, otherwise est. purchase price + 9% rate
  const atlasLocked = user.atlas_price_locked;
  const requestedPP = Number(formData.purchase_price || user.est_purchase_price || user.lead_purchase_price || 0);
  const pp = Number(user.doorly_purchase_price != null ? user.doorly_purchase_price : requestedPP);
  const dp = Number(user.down_payment != null ? user.down_payment : (formData.down_payment || user.lead_down_payment || 0));
  const la = Number(user.loan_amount != null ? user.loan_amount : (formData.loan_amount || (pp - dp) || 0));
  // Combined income/debt (includes co-borrower if linked). Two paths
  // can update income — admin Apply (writes borrower_income → finGMI
  // refreshes on /income endpoint) and Phase 2 confirmation (writes
  // users.gross_monthly_income → confirmedGmi refreshes on borrower-
  // detail endpoint). Each fetch has its own cache window, so they
  // can disagree mid-session. DTI gets the FRESHER of the two so it
  // updates the moment either path lands, instead of waiting for the
  // borrower-detail refetch. Fallback chain: finGMI (live) →
  // confirmedGmi (user.gross_monthly_income) → AI Phase 1 candidate.
  const confirmedGmi = Number(user.combined_gmi || user.gross_monthly_income || 0);
  // 🚨 "5,388" IS THE RIGHT ANSWER AND Number() MAKES IT NaN — see
  // utils/parseModelNumber.js. Cathy's run returned a thousands-separated
  // string and this read produced nothing at all.
  const aiPhase1Income = parseModelAmount(
    user?.readycheck?.latestAnalysis?.incomeAssessment?.qualifyingMonthly) || 0;
  const gmi = finGMI > 0 ? finGMI : (confirmedGmi > 0 ? confirmedGmi : aiPhase1Income);
  const gmiIsPending = finGMI <= 0 && confirmedGmi <= 0 && aiPhase1Income > 0;
  const debt = Number(user.combined_debt || user.total_monthly_debt || 0);
  // Estimated monthly PITI: P&I (30yr amort) + taxes (1.8%) + insurance (0.5%)
  // Committed rate only — never atlasResult (the in-session Price Loan sim, which
  // a dryRun persists nothing for). Uses atlas_final_rate once pricing is Accepted
  // (locked), else the borrower's approved rate. Keeps header/KPI estimates from
  // moving on an unaccepted re-price.
  const estRate = atlasLocked
    ? Number(user.atlas_final_rate || (user.approved_interest_rate ? user.approved_interest_rate / 100 : 0.09))
    : (user.approved_interest_rate ? user.approved_interest_rate / 100 : 0.09);
  const mr = estRate / 12;
  const estPI = la > 0 && mr > 0 ? la * (mr * Math.pow(1 + mr, 360)) / (Math.pow(1 + mr, 360) - 1) : 0;
  const estTaxIns = pp * (0.018 + 0.005) / 12;
  const estPITI = estPI + estTaxIns;
  const monthlyIncome = gmi;
  const frontDti = monthlyIncome > 0 ? ((estPITI / monthlyIncome) * 100).toFixed(1) : null;
  const backDti = monthlyIncome > 0 ? (((debt + estPITI) / monthlyIncome) * 100).toFixed(1) : null;


const btnStyle = { padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', whiteSpace: 'nowrap' };
  const primaryBtn = { ...btnStyle, backgroundColor: '#3a6ad9', color: '#fff', border: 'none' };

  // ── 🔑 READ-ONLY FOR A SCOPED ROLE ────────────────────────────────────────
  //
  // Jesse: "they should have access to view all tabs as read only (no edit
  // access)". The server enforces it absolutely — every non-GET under
  // /borrowers/:id is refused for this role by middleware/borrowerDataGuard, so
  // nothing on this page can change the file. This is the half that tells the
  // person holding the mouse.
  const readOnlyFile = isBorrowerViewReadOnly(currentStaffRole());

  return (
    <AdminLayout>
      {readOnlyFile && (
        <>
          {/* ⚠️ FORM FIELDS ONLY, NOT EVERY BUTTON. A blanket pointer-events
              rule would also kill "download document", "view PDF" and the tab
              bar — all of which are READS this role is explicitly granted, and
              breaking them to prevent writes the server already refuses would
              trade one broken page for another. Inputs, selects and textareas
              are unambiguous: there is no read-only reason to type in one. */}
          <style>{`
            .admin-bp-readonly input:not([type="search"]),
            .admin-bp-readonly textarea,
            .admin-bp-readonly select {
              pointer-events: none !important;
              background: #f9fafb !important;
              color: #6b7280 !important;
            }
          `}</style>
          {/* 🔑 SAID ONCE, AT THE TOP, AND NOT DISMISSIBLE. A rep who does not
              know the file is read-only reads a failed save as the product
              being broken. */}
          <div className="admin-content-pad" style={{ padding: '10px 0', background: '#eff6ff',
            borderBottom: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: 12.5, color: '#1e40af', fontWeight: 600 }}>
              Read-only — this borrower was referred by one of your partners.
              <span style={{ fontWeight: 400, color: '#3b5fa4' }}>
                {' '}You can see every tab. Changes are made by the team working the file.
              </span>
            </div>
          </div>
        </>
      )}
      {/* ⚠️ THE MARKER WRAPS THE WHOLE PAGE. Classing the sticky header alone —
          which is what the first attempt did — left every field below it live,
          and the rule would have read as applied while doing nothing. */}
      <div className={readOnlyFile ? 'admin-bp-readonly' : undefined}>
      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <div className="admin-content-pad admin-bp-sticky-header" style={{ position: 'sticky', top: 'calc(64px + env(safe-area-inset-top, 0px))', zIndex: 200, backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', paddingTop: '12px', paddingBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        {/* Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/admin/borrowers')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            ← Borrowers
          </button>
          <span style={{ color: '#e5e7eb' }}>|</span>
          <div>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>
              {user.first_name} {user.last_name}
              {coBorrower && <span style={{ color: '#6b7280' }}> &amp; {coBorrower.first_name} {coBorrower.last_name}</span>}
            </span>
            <span style={{ fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace', marginLeft: '10px' }}>{user.loan_number || '—'}</span>
          </div>
          <span style={{ fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '9999px', backgroundColor: statusMeta.bg, color: statusMeta.color, whiteSpace: 'nowrap', border: `1px solid ${statusMeta.color}22` }}>
            {stageLabel}
          </span>
          {days > 0 && <span style={{ fontSize: '11px', color: '#9ca3af' }}>{days}d in stage</span>}
        </div>
        {/* Referral Partner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Referral Partner:</span>
          {referralPartner ? (
            <>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#3a6ad9' }}>
                {referralPartner.partner_name}
              </span>
              <span style={{
                fontSize: '10px', fontWeight: '600', padding: '2px 6px', borderRadius: '9999px',
                backgroundColor: referralPartner.partner_type === 'loan_officer' ? '#edf1fb' : '#eff6ff',
                color: referralPartner.partner_type === 'loan_officer' ? '#3a6ad9' : '#3a6ad9',
              }}>
                {referralPartner.partner_type === 'loan_officer' ? 'LO' : 'Realtor'}
              </span>
            </>
          ) : partnerUnattributed ? (
            /* 🚨 THE STATE THAT USED TO READ "No referral partner". A borrower
               with a converted broker submission demonstrably came from a
               partner; showing the same words as a direct sign-up hid a
               compensation record that failed to write. Amber, and it names the
               repair. */
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>
              Attribution missing &mdash; a broker sent this borrower
              <span style={{ fontWeight: 400, color: '#b45309' }}>
                {' '}(run repairPartnerAttribution)
              </span>
            </span>
          ) : (
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>No referral partner</span>
          )}
          <button
            onClick={() => setShowPartnerEdit(v => !v)}
            style={{ marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '13px', padding: '2px 4px' }}
            title="Edit referral partner"
          >✏️</button>
        </div>
        <MloOfRecordLine userId={user.id} token={token} />
        {showPartnerEdit && (
          <PartnerReassignPanel
            userId={user.id} token={token} partnersList={partnersList}
            currentPartnerId={user.referral_partner_id || (referralPartner && referralPartner.partner_id) || null}
            onSaved={(u) => {
              setReferralPartner(u && u.referral_partner_id
                ? { partner_id: u.referral_partner_id, partner_name: u.referral_partner_name, partner_type: u.referral_partner_type }
                : null);
              setShowPartnerEdit(false);
            }}
            onCancel={() => setShowPartnerEdit(false)}
          />
        )}
        {/* Right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div ref={settingsCogRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button onClick={() => { setShowSettingsCog(p => !p); setShowExportSub(false); }}
              style={{ ...btnStyle, fontSize: '17px', padding: '7px 12px' }} title="Settings">⚙️</button>
            {showSettingsCog && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '180px', zIndex: 500, overflow: 'visible' }}>
                <button onClick={() => { setShowSettingsCog(false); setShowArchiveConfirm(true); }}
                  style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  📦 Archive
                </button>
                <button onClick={async () => {
                  setShowSettingsCog(false); setShowAdverseModal(true);
                  // 🔑 LOADED ON OPEN rather than at mount: this modal is opened
                  // rarely and the list is small, so fetching it here keeps the
                  // profile page's first paint free of a request nobody needs.
                  try {
                    const r = await axios.get(`${API}/api/admin/cure-sections`, { headers });
                    setCureSections(r.data?.sections || []);
                  } catch (_) { setCureSections([]); }
                }}
                  style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  ⛔ Adverse
                </button>
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowExportSub(p => !p)}
                    style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>📤 Export</span>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>▶</span>
                  </button>
                  {showExportSub && (
                    <div style={{ position: 'absolute', top: 0, right: 'calc(100% + 4px)', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '160px', zIndex: 501, overflow: 'hidden' }}>
                      <button onClick={() => { setShowSettingsCog(false); setShowExportSub(false); handleExportMISMO(); }}
                        style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        📄 MISMO 3.4 File
                      </button>
                      <button onClick={() => { setShowSettingsCog(false); setShowExportSub(false); handleOpenAuditLog(); }}
                        style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        📋 Audit Log
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowNotesPanel(!showNotesPanel)}
            style={{ position: 'relative', padding: '8px 12px', background: showNotesPanel ? '#3a6ad9' : '#f3f4f6', color: showNotesPanel ? '#ffffff' : '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            title="Notes"
          >
            💬
            {notes.length > 0 && (
              <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#3a6ad9', color: '#fff', borderRadius: '10px', padding: '1px 5px', fontSize: '10px', fontWeight: '700', minWidth: '16px', textAlign: 'center' }}>
                {notes.length}
              </span>
            )}
          </button>
          <button style={primaryBtn} onClick={() => setModal('move')}>Move Stage ▾</button>
        </div>
      </div>

      {/* ── Next Action Card ───────────────────────────────────────────────── */}
      <div className="admin-content-pad" style={{ paddingTop: '16px', paddingBottom: 0 }}>
        <div data-testid="next-action-card" data-state={nextAction.state || ''} style={{ borderRadius: '10px', padding: '14px 18px', backgroundColor: nextAction.bg, borderLeft: `4px solid ${nextAction.color}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '16px' }}>{nextAction.icon}</span>
            <div>
              <span data-testid="next-action-text" style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{nextAction.text}</span>
              <span data-testid="next-action-owner" style={{ fontSize: '12px', color: '#6b7280', marginLeft: '10px' }}>Owner: {nextAction.owner}</span>
              {nextAction.more > 0 && (
                <span data-testid="next-action-more" style={{ fontSize: '12px', color: '#6b7280', marginLeft: '10px' }}>+{nextAction.more} more on the Review tab</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>In this stage for {days}d</span>
            {nextAction.action && (
              <button data-testid="next-action-button" onClick={nextAction.actionFn} style={{ ...btnStyle, backgroundColor: nextAction.color, color: '#fff', border: 'none', fontSize: '12px' }}>{nextAction.action}</button>
            )}
          </div>
        </div>
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <div className="admin-content-pad" style={{ paddingTop: '16px', paddingBottom: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
        {[
          ...(() => {
            // Stage-aware purchase price resolver
            // Committed pricing ONLY — do not read atlasResult (the in-session
            // "Price Loan" simulation). Price Loan is a dryRun that persists
            // nothing, so these KPI cards must reflect atlas_max_purchase_price
            // (written only on "Accept Pricing — Apply to Loan File", which
            // locks). So re-pricing a loan no longer moves the KPI cards until
            // the admin accepts.
            const maxApproval = Number(user.atlas_max_purchase_price || user.max_approval_amount || 0);
            const hasPricing = atlasLocked;
            // The "C" (Contract) badge fires once the listing agent has
            // accepted the offer — server.js's offer-accept handler
            // writes contract_purchase_price + flags purchase_price_
            // updated_at_offer at that point. POST_ACCEPTANCE_STAGES
            // covers option_period onward; offer_accepted is the gate
            // before that, so we include it explicitly here.
            const contractPriced = POST_ACCEPTANCE_STAGES.includes(status) ||
                                   status === 'offer_accepted' ||
                                   !!user.purchase_price_updated_at_offer;
            let kpiPP, ppLabel, ppBadge;
            if (contractPriced) {
              // contract_purchase_price is the post-acceptance B-C price.
              // When it's null (offer accepted before that column existed,
              // or admin price edits drifted it), fall back to the same
              // resale-adjusted B-C math the home-shopping branch below
              // uses — NOT raw doorly_purchase_price, which is the A-B
              // (Doorly→seller) price and ignores the resale markup.
              const dpriceFB = Number(user.doorly_purchase_price || 0);
              const bcFromDoorly = dpriceFB > 0 ? Math.round(dpriceFB) : 0;
              kpiPP = Number(user.contract_purchase_price) || bcFromDoorly || pp || maxApproval;
              ppLabel = 'Purchase Price'; ppBadge = 'C';
            } else if (user.smartestimate_accepted || HOME_SHOPPING_STAGES.includes(status)) {
              const dprice = Number(user.doorly_purchase_price || 0);
              kpiPP = dprice > 0 ? Math.round(dprice) : (maxApproval || pp);
              ppLabel = 'Purchase Price'; ppBadge = 'S';
            } else if (hasPricing && maxApproval > 0) {
              // Purchase Price on the KPI cards is the BORROWER (B-C) price =
              // Doorly base × (1 + resale), like the Loan tab — so PP reconciles
              // with Down Payment + Loan Amount (which derive from kpiPP). Atlas's
              // base ("Max Purchase Price") stays on the Review tab + the borrower-
              // facing SmartEstimate row 1 as the list price to shop for.
              kpiPP = Math.round(maxApproval);
              ppLabel = 'Purchase Price'; ppBadge = 'A';
            } else {
              kpiPP = pp || requestedPP;
              ppLabel = 'Purchase Price'; ppBadge = 'R';
            }
            const kpiDpPct = Number(user.down_payment_pct || 0) || (pp > 0 && dp > 0 ? (dp / pp) * 100 : 0);
            const kpiDP = kpiDpPct > 0 ? Math.round(kpiPP * kpiDpPct / 100) : dp;
            const kpiLA = kpiPP - kpiDP;
            const kpiMr = estRate / 12;
            const kpiPI = kpiLA > 0 && kpiMr > 0 ? kpiLA * (kpiMr * Math.pow(1 + kpiMr, 360)) / (Math.pow(1 + kpiMr, 360) - 1) : 0;
            const kpiTaxIns = kpiPP * (0.018 + 0.005) / 12;
            const kpiPITI = kpiPI + kpiTaxIns;
            const kpiFrontDti = monthlyIncome > 0 ? ((kpiPITI / monthlyIncome) * 100).toFixed(1) : null;
            const kpiBackDti = monthlyIncome > 0 ? (((debt + kpiPITI) / monthlyIncome) * 100).toFixed(1) : null;
            const badgeColors = { C: '#3a6ad9', S: '#111827', A: '#acf44e', R: '#e5e7eb' };
            const badgeTextColors = { C: '#fff', S: '#acf44e', A: '#1a1a1a', R: '#6b7280' };
            // Pill renderer — reused for PP / Down Payment / Loan Amount.
            // Tooltip on the C badge surfaces the origin AND distinguishes
            // between two distinct lock states:
            //   - purchase_price_updated_at_offer set (post offer-accept) —
            //     contract_purchase_price has been computed from doorly +
            //     resale adjustment but is still mutable until A-B execution.
            //   - purchase_price_locked = TRUE (post A-B PSA full execution) —
            //     contract_purchase_price is now the canonical immutable
            //     borrower purchase price for the rest of the loan lifecycle.
            const ppTooltip = ppBadge === 'C'
              ? (user.purchase_price_locked
                  ? 'Contract Price — locked at A-B PSA execution (Borrower Purchase Price)'
                  : 'Updated at offer acceptance — from the Doorly purchase price')
              : undefined;
            const renderBadge = (badge) => (
              <span title={badge === 'C' ? ppTooltip : undefined}
                    style={{ fontSize: '9px', fontWeight: '700', background: badgeColors[badge], color: badgeTextColors[badge], padding: '1px 5px', borderRadius: '3px', marginLeft: '4px', verticalAlign: 'middle' }}>
                {badge}
              </span>
            );
            const dpDisplay = kpiDP > 0 ? `${fmt$(kpiDP)} (${kpiPP > 0 ? ((kpiDP/kpiPP)*100).toFixed(0) : 0}%)` : '—';
            // Purchase Price is the only KPI that carries a source badge.
            // Down Payment / Loan Amount derive deterministically from it,
            // so adding badges to those cards just creates visual noise.
            return [
              { label: ppLabel, value: fmt$(kpiPP), node: <span>{fmt$(kpiPP)} {renderBadge(ppBadge)}</span> },
              { label: 'Down Payment', value: dpDisplay },
              { label: 'Loan Amount',  value: kpiLA > 0 ? fmt$(kpiLA) : '—' },
              { label: 'DTI (Front/Back)', value: kpiFrontDti ? `${kpiFrontDti}% / ${kpiBackDti}%` : '—' },
            ];
          })(),
          { label: 'Credit Score', node: <CreditScoreDisplay borrower={{ ...user, credit_score_range: formData.credit_score_range }} coBorrower={coBorrower} /> },
          { label: 'Interest Rate', value: (atlasResult?.finalRate || user.atlas_final_rate) ? fmtPct((atlasResult?.finalRate || parseFloat(user.atlas_final_rate)) * 100) : user.approved_interest_rate ? fmtPct(user.approved_interest_rate) : '—' },
          // 🚨 NO RESALE ADJUSTMENT TILE (hotfix 11-01, Jesse): the resale adjustment was removed from the program
          // 2026-10-20. The stored column stays (retired-option files keep what they signed); the header no longer shows it.
        ].map((k) => (
          <div key={k.label} style={{ ...card, padding: '12px 14px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{k.label}</div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{k.node || k.value}</div>
          </div>
        ))}
      </div>

      {/* ── Progress Tracker ── hidden on mobile (too wide for a phone) ────── */}
      <div className="admin-content-pad admin-hide-mobile" style={{ paddingTop: '16px', paddingBottom: 0 }}>
        <div style={{ ...card, padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {STAGE_STEPS.map((step, i) => {
              const done = i < currentStep;
              const curr = i === currentStep;
              const color = done ? '#acf44e' : curr ? '#3a6ad9' : '#e5e7eb';
              return (
                <React.Fragment key={step.label}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flex: '0 0 auto' }}>
                    <div style={{ width: '24px', height: '24px', borderRadius: '9999px', backgroundColor: done ? '#acf44e' : curr ? '#3a6ad9' : '#f9fafb', border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: done ? '#1a1a1a' : curr ? '#fff' : '#9ca3af', fontWeight: '700', position: 'relative' }}>
                      {done ? '✓' : curr ? <div style={{ width: '8px', height: '8px', borderRadius: '9999px', backgroundColor: '#fff' }} /> : ''}
                    </div>
                    <span style={{ fontSize: '9px', fontWeight: curr ? '700' : '400', color: curr ? '#3a6ad9' : done ? '#acf44e' : '#9ca3af', whiteSpace: 'nowrap' }}>{step.label}</span>
                  </div>
                  {i < STAGE_STEPS.length - 1 && (
                    <div style={{ flex: 1, height: '2px', backgroundColor: done ? '#acf44e' : '#e5e7eb', margin: '0 4px', marginBottom: '16px' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Showing Credits — visible for home_shopping / offer_requested */}
      {(status === 'home_shopping' || status === 'offer_requested') && (
        <div className="admin-content-pad" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <ShowingCreditsAdmin borrowerId={user.id} token={token} />
        </div>
      )}

      {/* ── Body: Tabs ────────────────────────────────────────────────────── */}
      <div className="admin-mobile-pad" style={{ padding: '16px 28px 32px' }}>
        <div style={{ minWidth: 0 }}>
          {/* Tab nav — raised-card look. Active tab's white bottom border + -2px
              margin makes it sit on top of the container's 2px border so it
              appears flush with the panel below. */}
          <div className="admin-tabs-bar" style={{
            display: 'flex',
            gap: 4,
            borderBottom: '2px solid #e5e7eb',
            marginBottom: 24,
            paddingBottom: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}>
            {TABS.filter(t => {
              if (t.gateStages && !t.gateStages.includes(user?.application_status)) return false;
              if (t.gateRoles  && !t.gateRoles.includes(staffRole)) return false;
              return true;
            }).map((t) => {
              const count = t.countKey ? (tabCounts[t.countKey] || 0) : 0;
              const isActive = activeTab === t.id;
              const baseStyle = {
                padding: '10px 20px',
                fontSize: 14,
                border: '1px solid #e5e7eb',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              };
              const tabStyle = isActive ? {
                ...baseStyle,
                fontWeight: 600,
                color: '#3a6ad9',
                background: '#ffffff',
                borderBottom: '2px solid #ffffff',
                marginBottom: -2,
              } : {
                ...baseStyle,
                fontWeight: 500,
                color: '#6b7280',
                background: '#f9fafb',
                borderBottom: 'none',
                transition: 'all 0.15s ease',
              };
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabStyle}>
                  {t.label}
                  {count > 0 && (
                    <span style={{ backgroundColor: '#ff082f', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 9999, minWidth: 16, textAlign: 'center' }}>{count > 99 ? '99+' : count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── TAB 1: Borrower Details ──────────────────────────────────── */}
          {activeTab === 'borrower_details' && (
            <>
              {/* Reg B joint-credit-intent status (read-only; renders only when
                  this borrower is part of a co-borrower pairing). */}
              <JointIntentAdminCard borrowerId={id} token={token} />
              <GuidedFlowReopenCard borrowerId={id} token={token} />
              <TestAccountCard borrowerId={id} token={token} />
              <ApplicationEditsCard borrowerId={id} token={token} />
              {/* Deadline-flagged banner — set by utils/deadlineChecker
                  when the option period expires with outstanding overdue
                  tasks. Full-width above the two-column card grid so it's
                  the first thing the admin sees on this tab. */}
              {user?.deadline_flagged && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <span style={{ fontSize: '18px' }}>🚩</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '14px' }}>
                      This file has been flagged for overdue tasks
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      Flagged {user.deadline_flagged_at
                        ? new Date(user.deadline_flagged_at).toLocaleDateString()
                        : '—'}
                      {Array.isArray(user.deadline_flag_reasons) && user.deadline_flag_reasons.length > 0
                        && ` · Overdue: ${user.deadline_flag_reasons.join(', ')}`}
                    </div>
                  </div>
                </div>
              )}

            {/* ReadyCheck status card moved to the Review tab so the
                phase tiles + dispute resolution sit alongside the
                Underwriting Module CTA where ops actually work the
                file. See {activeTab === 'review'} branch. */}

            <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Borrower Info */}
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>Primary Borrower</h3>
                  {editSection !== 'borrower'
                    ? <button onClick={() => startEdit('borrower')} style={{ padding: '5px 14px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>Edit</button>
                    : <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => setEditSection(null)} style={{ padding: '5px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Cancel</button>
                        <button onClick={saveEdit} disabled={editSaving} style={{ padding: '5px 14px', background: editSaving ? '#e5e7eb' : '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: editSaving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>{editSaving ? 'Saving...' : 'Save'}</button>
                      </div>
                  }
                </div>
                {editSection === 'borrower' ? (
                  <>
                    {[
                      { k: 'first_name', label: 'First Name' },
                      { k: 'last_name', label: 'Last Name' },
                      { k: 'email', label: 'Email' },
                      { k: 'phone', label: 'Phone' },
                    ].map(f => (
                      <div key={f.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{f.label}</span>
                        <input value={editFields[f.k] || ''} onChange={e => setEditFields(p => ({ ...p, [f.k]: e.target.value }))}
                          style={{ fontSize: '12px', border: '1.5px solid #3a6ad9', borderRadius: '6px', padding: '5px 8px', width: '55%', outline: 'none', background: '#f8faff' }} />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <InfoRow label="User ID" value={user.id} />
                    <InfoRow label="Full Name" value={`${user.first_name} ${user.last_name}`} />
                    <InfoRow label="Email" value={user.email} />
                    <InfoRow label="Phone" value={formData.phone || user.phone} />
                  </>
                )}
                {/* DOB + SSN are PII the Real Estate Desk role doesn't need —
                    they coordinate showings/offers, not credit decisions.
                    Backend /api/admin/borrowers/:id/ssn endpoint is the
                    authoritative gate; hiding the toggles is the UX layer. */}
                {!isREDesk && (<>
                {/* DOB with eyeball toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>DOB</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '500', color: '#111827' }}>
                      {dobVisible
                        ? (formData.date_of_birth
                            ? new Date(formData.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                            : '—')
                        : '**/**/****'}
                    </span>
                    <button
                      onClick={() => setDobVisible(!dobVisible)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#9ca3af', padding: '0', lineHeight: 1 }}
                      title={dobVisible ? 'Hide date of birth' : 'Show date of birth'}
                    >
                      {dobVisible ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                {/* SSN/ITIN — read mode shows the masked digits + eyeball,
                    with a pencil to flip into edit mode. Edit mode swaps
                    the row body into a type-select + input + Save/Cancel.
                    Unblocks ops on borrowers whose form_data.ssn ended up
                    missing (legacy autosave-on-restore wipe + any future
                    case). Saves through PATCH /borrowers/:id/profile and
                    refetches the borrower payload so the masked display
                    refreshes inline. */}
                {ssnEditing ? (
                  <div style={{ padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>Set SSN / ITIN</span>
                      <button onClick={() => { setSsnEditing(false); setSsnEditValue(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6b7280' }}>
                        Cancel
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select
                        value={ssnEditType}
                        onChange={(e) => setSsnEditType(e.target.value)}
                        style={{ fontSize: 12, padding: '6px 8px', border: '1.5px solid #3a6ad9', borderRadius: 6, background: '#f8faff' }}
                      >
                        <option value="ssn">SSN</option>
                        <option value="itin">ITIN</option>
                      </select>
                      <input
                        type="text"
                        value={ssnEditValue}
                        onChange={(e) => {
                          const d = e.target.value.replace(/\D/g, '').slice(0, 9);
                          const formatted = d.length <= 3 ? d
                            : d.length <= 5 ? `${d.slice(0,3)}-${d.slice(3)}`
                            : `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,9)}`;
                          setSsnEditValue(formatted);
                        }}
                        placeholder="XXX-XX-XXXX"
                        autoFocus
                        style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', padding: '6px 10px', border: '1.5px solid #3a6ad9', borderRadius: 6, background: '#f8faff' }}
                      />
                      <button
                        onClick={saveSsnEdit}
                        disabled={ssnEditSaving || ssnEditValue.replace(/\D/g, '').length !== 9}
                        style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 6, border: 'none', background: ssnEditSaving || ssnEditValue.replace(/\D/g, '').length !== 9 ? '#e5e7eb' : '#acf44e', color: ssnEditSaving || ssnEditValue.replace(/\D/g, '').length !== 9 ? '#9ca3af' : '#1a1a1a', cursor: ssnEditSaving ? 'not-allowed' : 'pointer' }}
                      >
                        {ssnEditSaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>SSN/ITIN</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#111827', fontFamily: 'monospace' }}>
                        {ssnVisible ? (ssnFull || formData.id_number || '—') : (formData.id_number || '•••-••-••••')}
                      </span>
                      <button
                        onClick={ssnVisible ? () => setSsnVisible(false) : fetchSsn}
                        disabled={ssnLoading}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#9ca3af', padding: '0', lineHeight: 1 }}
                        title={ssnVisible ? 'Hide SSN' : 'Show full SSN'}
                      >
                        {ssnLoading ? '…' : ssnVisible ? '🙈' : '👁'}
                      </button>
                      <button
                        onClick={() => { setSsnEditing(true); setSsnEditType(formData.id_type === 'ITIN' ? 'itin' : 'ssn'); setSsnEditValue(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#3a6ad9', padding: '0 0 0 2px', lineHeight: 1 }}
                        title={formData.id_number ? 'Edit SSN/ITIN' : 'Add SSN/ITIN'}
                      >
                        ✎
                      </button>
                    </div>
                  </div>
                )}
                </>)}
                <InfoRow label="Citizenship" value={{ us_citizen: 'U.S. Citizen', permanent_resident: 'Permanent Resident Alien', non_permanent_resident: 'Non-Permanent Resident Alien', itin: 'ITIN' }[formData.citizenship_status] || formData.citizenship_status || null} />
                <InfoRow label="Marital Status" value={formData.marital_status} />
                {editSection === 'borrower'
                  ? <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Employment</span>
                      <IncomeTypePicker value={editFields.employment_types} onChange={(next) => setEditFields((f) => ({ ...f, employment_types: next }))} />
                    </div>
                  : <InfoRow label="Employment" value={incomeTypeLabel(formData)} />
                }
                <InfoRow label="Identity" value={identityAdminLabel(user, idVerification, documents)} />
              </div>

              {/* Co-Borrower card */}
              {coBorrower ? (
                <div style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>Co-Borrower</h3>
                    {editSection !== 'coborrower'
                      ? <button onClick={() => startEdit('coborrower')} style={{ padding: '5px 14px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>Edit</button>
                      : <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => setEditSection(null)} style={{ padding: '5px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Cancel</button>
                          <button onClick={saveEdit} disabled={editSaving} style={{ padding: '5px 14px', background: editSaving ? '#e5e7eb' : '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: editSaving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>{editSaving ? 'Saving...' : 'Save'}</button>
                        </div>
                    }
                  </div>
                  {editSection === 'coborrower' ? (
                    <>
                      {[
                        { k: 'first_name', label: 'First Name' },
                        { k: 'last_name', label: 'Last Name' },
                        { k: 'email', label: 'Email' },
                        { k: 'phone', label: 'Phone' },
                      ].map(f => (
                        <div key={f.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>{f.label}</span>
                          <input value={editFields[f.k] || ''} onChange={e => setEditFields(p => ({ ...p, [f.k]: e.target.value }))}
                            style={{ fontSize: '12px', border: '1.5px solid #3a6ad9', borderRadius: '6px', padding: '5px 8px', width: '55%', outline: 'none', background: '#f8faff' }} />
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Employment</span>
                        <IncomeTypePicker value={editFields.employment_types} onChange={(next) => setEditFields((p) => ({ ...p, employment_types: next }))} />
                      </div>
                      {/* ── 🚨 THE INCOME PATH, WHICH HAD NO STAFF CONTROL ─────
                          Jesse: "set Mario's self-employed path and have her try
                          again."

                          🔑 ONLY THE BORROWER COULD SET IT — the picker lives on
                          their own Verification page and admin.js never touched
                          the column. So a staff member working the file had no
                          way to record it, ReadyCheck kept reading
                          `path=unknown`, and the prompt's rule for that ("treat
                          as bank-stmt by default") is what put BOTH a 1040 and a
                          YTD P&L on his needs list — two full-doc documents for a
                          borrower with no full-doc path.

                          ⚠️ THE FOUR OPTIONS AND THEIR WORDING COME FROM THE
                          BORROWER'S OWN PICKER (Verification.js pathOptions), so
                          staff and borrower are choosing from the same list and
                          mean the same thing by it. */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb', gap: 8 }}>
                        {/* ⚠️ "1099 / SE" IN THE LABEL, because Doorly's guideline
                            treats them as one category ("INCOME — SELF-EMPLOYED /
                            1099") and a staff member looking at a 1099 borrower
                            should not wonder whether this field is theirs — which
                            is exactly why Mario's was never set. */}
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Income path (1099 / SE)</span>
                        <select value={editFields.self_employed_path || ''} onChange={e => setEditFields(p => ({ ...p, self_employed_path: e.target.value }))} style={{ fontSize: '12px', border: '1.5px solid #3a6ad9', borderRadius: '6px', padding: '5px 8px', background: '#f8faff', maxWidth: '62%' }}>
                          <option value="">— not set</option>
                          {/* 🔑 THE SAME LIST THE BORROWER PICKS FROM — see
                              constants/readycheckPaths. */}
                          {RC_PATH_OPTIONS.map((o) => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <InfoRow label="User ID" value={coBorrower.id} />
                      <InfoRow label="Full Name" value={`${coBorrower.first_name} ${coBorrower.last_name}`} />
                      <InfoRow label="Email" value={coBorrower.email} />
                      <InfoRow label="Phone" value={coBorrower.phone || loanConnection?.coborrower_phone} />
                    </>
                  )}
                  {!isREDesk && (<>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>DOB</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#111827' }}>
                        {coDobVisible ? (coBorrower.dob_month && coBorrower.dob_day ? `${coBorrower.dob_month}/${coBorrower.dob_day}/${coBorrower.dob_year}` : coBorrower.date_of_birth ? new Date(coBorrower.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—') : '**/**/****'}
                      </span>
                      <button onClick={() => setCoDobVisible(!coDobVisible)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#9ca3af', padding: '0', lineHeight: 1 }}>{coDobVisible ? '🙈' : '👁'}</button>
                    </div>
                  </div>
                  {/* Co-borrower SSN/ITIN edit — mirror of the primary
                      pattern above; uses saveCoSsnEdit which routes
                      through PATCH /borrowers/:coId/profile. */}
                  {coSsnEditing ? (
                    <div style={{ padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>Set SSN / ITIN</span>
                        <button onClick={() => { setCoSsnEditing(false); setCoSsnEditValue(''); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6b7280' }}>
                          Cancel
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select
                          value={coSsnEditType}
                          onChange={(e) => setCoSsnEditType(e.target.value)}
                          style={{ fontSize: 12, padding: '6px 8px', border: '1.5px solid #3a6ad9', borderRadius: 6, background: '#f8faff' }}
                        >
                          <option value="ssn">SSN</option>
                          <option value="itin">ITIN</option>
                        </select>
                        <input
                          type="text"
                          value={coSsnEditValue}
                          onChange={(e) => {
                            const d = e.target.value.replace(/\D/g, '').slice(0, 9);
                            const formatted = d.length <= 3 ? d
                              : d.length <= 5 ? `${d.slice(0,3)}-${d.slice(3)}`
                              : `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,9)}`;
                            setCoSsnEditValue(formatted);
                          }}
                          placeholder="XXX-XX-XXXX"
                          autoFocus
                          style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', padding: '6px 10px', border: '1.5px solid #3a6ad9', borderRadius: 6, background: '#f8faff' }}
                        />
                        <button
                          onClick={saveCoSsnEdit}
                          disabled={coSsnEditSaving || coSsnEditValue.replace(/\D/g, '').length !== 9}
                          style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 6, border: 'none', background: coSsnEditSaving || coSsnEditValue.replace(/\D/g, '').length !== 9 ? '#e5e7eb' : '#acf44e', color: coSsnEditSaving || coSsnEditValue.replace(/\D/g, '').length !== 9 ? '#9ca3af' : '#1a1a1a', cursor: coSsnEditSaving ? 'not-allowed' : 'pointer' }}
                        >
                          {coSsnEditSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>SSN/ITIN</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '500', color: '#111827', fontFamily: 'monospace' }}>
                          {coSsnVisible ? (coSsnFull || coBorrower.id_number || '—') : (coBorrower.id_number || '•••-••-••••')}
                        </span>
                        <button
                          onClick={coSsnVisible ? () => setCoSsnVisible(false) : fetchCoSsn}
                          disabled={coSsnLoading}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#9ca3af', padding: '0', lineHeight: 1 }}
                          title={coSsnVisible ? 'Hide SSN' : 'Show full SSN'}
                        >
                          {coSsnLoading ? '…' : coSsnVisible ? '🙈' : '👁'}
                        </button>
                        <button
                          onClick={() => { setCoSsnEditing(true); setCoSsnEditType(coBorrower.id_type === 'ITIN' ? 'itin' : 'ssn'); setCoSsnEditValue(''); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#3a6ad9', padding: '0 0 0 2px', lineHeight: 1 }}
                          title={coBorrower.id_number ? 'Edit SSN/ITIN' : 'Add SSN/ITIN'}
                        >
                          ✎
                        </button>
                      </div>
                    </div>
                  )}
                  </>)}
                  <InfoRow label="Citizenship" value={{ us_citizen: 'U.S. Citizen', permanent_resident: 'Permanent Resident', non_permanent_resident: 'Non-Permanent Resident', itin: 'ITIN' }[coBorrower.citizenship_status || coBorrower.citizenship] || coBorrower.citizenship_status || coBorrower.citizenship} />
                  <InfoRow label="Marital Status" value={coBorrower.marital_status} />
                  <InfoRow label="Employment" value={incomeTypeLabel(coBorrower)} />
                  <InfoRow label="Identity" value={identityAdminLabel(coBorrower, coBorrower.idVerification || null, documents.filter((d) => d.owner_type === 'coborrower'))} />
                  {/* Link Status / Application Status / Verification rows
                      removed so the Co-Borrower card mirrors the Primary
                      Borrower card. Link / application / verification
                      state surfaces on the ReadyCheck status card and
                      Review tab anyway; duplicating it here cluttered
                      the at-a-glance profile read. */}
                </div>
              ) : (
                <div style={card}>
                  {showCoInvite ? (
                    <div>
                      <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Invite Co-Borrower</h3>
                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        {[
                          { k: 'first_name', label: 'First Name', ph: 'Jane' },
                          { k: 'last_name', label: 'Last Name', ph: 'Doe' },
                          { k: 'email', label: 'Email *', ph: 'coborrower@email.com' },
                          { k: 'phone', label: 'Phone', ph: '(555) 000-0000' },
                        ].map(f => (
                          <div key={f.k}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '3px' }}>{f.label}</label>
                            <input value={coInviteForm[f.k]} onChange={e => setCoInviteForm(p => ({ ...p, [f.k]: e.target.value }))} placeholder={f.ph}
                              style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={async () => {
                          if (!coInviteForm.email.trim()) { alert('Email is required.'); return; }
                          setCoInviteSending(true);
                          try {
                            await axios.post(`${API}/api/admin/borrowers/${id}/invite-coborrower`, coInviteForm, { headers });
                            setShowCoInvite(false);
                            setCoInviteForm({ first_name: '', last_name: '', email: '', phone: '' });
                            fetchData();
                          } catch (e) { alert(e.response?.data?.error || 'Could not send invite.'); }
                          setCoInviteSending(false);
                        }} disabled={coInviteSending || !coInviteForm.email.trim()}
                          style={{ padding: '8px 18px', background: coInviteSending || !coInviteForm.email.trim() ? '#e5e7eb' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                          {coInviteSending ? 'Sending...' : 'Send Invite'}
                        </button>
                        <button onClick={() => setShowCoInvite(false)}
                          style={{ padding: '8px 14px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : loanConnection && loanConnection.status === 'pending' ? (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>Co-Borrower Invited</h3>
                        <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', background: '#fff7ed', color: '#7a5200', border: '1px solid #fed7aa' }}>Pending</span>
                      </div>
                      <InfoRow label="Name" value={`${loanConnection.coborrower_first_name || ''} ${loanConnection.coborrower_last_name || ''}`.trim() || '—'} />
                      <InfoRow label="Email" value={loanConnection.coborrower_email} />
                      <InfoRow label="Invited" value={loanConnection.created_at ? new Date(loanConnection.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
                      {loanConnection.invite_expires_at && (
                        <InfoRow label="Expires" value={new Date(loanConnection.invite_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
                      )}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API}/api/admin/borrowers/${id}/invite-coborrower`, {
                              first_name: loanConnection.coborrower_first_name,
                              last_name: loanConnection.coborrower_last_name,
                              email: loanConnection.coborrower_email,
                              phone: loanConnection.coborrower_phone,
                            }, { headers });
                            alert('Invite resent!');
                            fetchData();
                          } catch (e) { alert(e.response?.data?.error || 'Could not resend invite.'); }
                        }} style={{ padding: '8px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                          Resend Invite
                        </button>
                        <button onClick={() => setShowCoInvite(true)}
                          style={{ padding: '8px 14px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: '600' }}>
                          New Invite
                        </button>
                      </div>

                      {/* ── 🚨 THEY MAY HAVE ALREADY REGISTERED ────────────────
                          Four reports of "still invite pending" came from one
                          unwritten column: the co-borrower made an account and
                          the link never landed, so the file showed Pending, the
                          application details were blank and they were absent
                          from the Review tab. Registration links it now; the
                          ones stranded before that had NO path in the product —
                          only a shell script, which is not a fix for something
                          a staff member is looking straight at.

                          ⚠️ THE SERVER FINDS THE CANDIDATES. This only renders
                          what it returned and sends back an id it supplied. */}
                      {coLinkCandidates === null ? (
                        <button
                          type="button"
                          onClick={async () => {
                            setCoLinkBusy(true);
                            try {
                              const r = await axios.get(
                                `${API}/api/admin/borrowers/${id}/coborrower/candidates`, { headers });
                              setCoLinkCandidates(r.data?.candidates || []);
                            } catch (e) {
                              alert(e.response?.data?.error || 'Could not look up matching accounts.');
                            } finally { setCoLinkBusy(false); }
                          }}
                          disabled={coLinkBusy}
                          style={{ marginTop: 12, padding: '7px 12px', background: 'none',
                            border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
                            fontSize: 12, color: '#374151', fontWeight: 600 }}>
                          {coLinkBusy ? 'Checking…' : 'Already registered? Find their account'}
                        </button>
                      ) : coLinkCandidates.length === 0 ? (
                        <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                          No registered account matches this invite — by email or by name.
                          They have most likely not signed up yet, so Resend Invite is the next step.
                        </div>
                      ) : (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                            Registered account{coLinkCandidates.length > 1 ? 's' : ''} matching this invite
                          </div>
                          {coLinkCandidates.map((c) => (
                            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                              flexWrap: 'wrap', padding: '8px 10px', marginBottom: 6,
                              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                              <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflowWrap: 'anywhere' }}>
                                  {`${c.first_name || ''} ${c.last_name || ''}`.trim() || '(no name)'}
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280', overflowWrap: 'anywhere' }}>{c.email}</div>
                              </div>
                              {/* 🚨 A NAME MATCH IS WEAKER EVIDENCE AND SAYS SO. The
                                  email on the invite is the thing staff typed; a
                                  different address means somebody has to look. */}
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px',
                                borderRadius: 9999, whiteSpace: 'nowrap',
                                background: c.match === 'email' ? '#f0fdf4' : '#fff7ed',
                                color: c.match === 'email' ? '#166534' : '#7a5200',
                                border: `1px solid ${c.match === 'email' ? '#bbf7d0' : '#fed7aa'}` }}>
                                {c.match === 'email' ? 'email matches' : 'NAME only — check first'}
                              </span>
                              <button
                                type="button"
                                disabled={coLinkBusy}
                                onClick={async () => {
                                  if (!window.confirm(
                                    `Link ${c.first_name} ${c.last_name} <${c.email}> as the co-borrower on this file?`
                                    + (c.match === 'email' ? '' : '\n\nTHE EMAIL DOES NOT MATCH THE INVITE. '
                                      + 'Check this is the right person before continuing.'))) return;
                                  setCoLinkBusy(true);
                                  try {
                                    await axios.post(`${API}/api/admin/borrowers/${id}/coborrower/link`,
                                      { user_id: c.id }, { headers });
                                    setCoLinkCandidates(null);
                                    await fetchData();
                                  } catch (e) {
                                    alert(e.response?.data?.error || 'Could not link that account.');
                                  } finally { setCoLinkBusy(false); }
                                }}
                                style={{ padding: '6px 12px', background: coLinkBusy ? '#e5e7eb' : '#3a6ad9',
                                  color: coLinkBusy ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8,
                                  cursor: coLinkBusy ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                                {coLinkBusy ? 'Linking…' : 'Link account'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                      <button onClick={() => setShowCoInvite(true)}
                        style={{ padding: '10px 24px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                        + Invite a Co-Borrower
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Tasks */}
              {tasks.length > 0 && (
                <div style={{ ...card, gridColumn: '1 / -1' }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tasks ({tasks.length})</h3>
                  {tasks.map((t) => (
                    <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', border: '1px solid #f3f4f6', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#111827' }}>{t.title}</span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '9999px', backgroundColor: t.priority === 'urgent' ? '#fff0f3' : '#f9fafb', color: t.priority === 'urgent' ? '#ff082f' : '#6b7280' }}>{t.priority}</span>
                        <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '9999px', backgroundColor: t.status === 'completed' ? '#f0fde4' : '#fffbeb', color: t.status === 'completed' ? '#acf44e' : '#d97706' }}>{t.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Application Details — mirrors ViewApplicationModal */}
              <div style={{ ...card, gridColumn: '1 / -1' }}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Application Details</h3>

                {/* ── Personal Information ── */}
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', borderBottom: '1px solid #f3f4f6', paddingBottom: '6px' }}>Personal Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 24px', marginBottom: '20px' }}>
                  {/* First-Time Buyer dropped — no longer collected on the
                      current ApplicationForm. ReadyCheck uses housing history
                      (VOR / VOM / mortgage statements) to derive the same
                      signal more accurately. */}
                  {[
                    { label: 'Housing Status', value: { own: 'Own (with mortgage)', rent: 'Rent', rent_free: 'Rent Free / Live with Family' }[formData.housing_status] || formData.housing_status },
                    { label: 'Time at Address', value: currentDurationText(formData) },
                    { label: 'Citizenship', value: { us_citizen: 'U.S. Citizen', permanent_resident: 'Permanent Resident Alien', non_permanent_resident: 'Non-Permanent Resident Alien', itin: 'ITIN' }[formData.citizenship_status || formData.citizenship] || formData.citizenship_status || formData.citizenship },
                    { label: 'Marital Status', value: formData.marital_status },
                  ].map(({ label, value }) => <InfoRow key={label} label={label} value={value} />)}
                  {(formData.address_street || formData.address_city) && (
                    <InfoRow wide label="Current Address" value={[formData.address_street, formData.address_city, formData.address_state, formData.address_zip].filter(Boolean).join(', ')} />
                  )}
                </div>

                {/* ── Loan Information ── */}
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', borderBottom: '1px solid #f3f4f6', paddingBottom: '6px' }}>Loan Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 24px', marginBottom: '20px' }}>
                  {[
                    { label: 'Loan Type', value: formData.loan_type === 'purchase' ? 'Purchase' : formData.loan_type === 'refinance' ? 'Refinance' : formData.loan_type },
                    { label: 'Property Type', value: { 'single-family': 'Single Family', condo: 'Condo / Townhome', 'multi-family': 'Multi-Family (2–4 units)', manufactured: 'Manufactured / Mobile' }[formData.property_type] || formData.property_type },
                    { label: 'Purchase Price', value: formData.purchase_price ? '$' + Number(formData.purchase_price).toLocaleString() : null },
                    { label: 'Down Payment', value: formData.down_payment ? '$' + Number(formData.down_payment).toLocaleString() : null },
                    { label: 'Credit Score Range', value: formData.credit_score_range ? formData.credit_score_range.replace(/_/g, '-') : '—' },
                    { label: 'Timeline', value: formData.timeline },
                    { label: 'Lead Source', value: (() => { const r = formData.heard_about || user.heard_about || user.lead_source; if (!r) return null; return r.replace('google_search','Google Search').replace('ai_search','AI Search').replace('social_media','Social Media').replace('real_estate_agent','Real Estate Agent').replace('referral','Referral').replace('other','Other').replace(/_/g,' '); })() },
                  ].map(({ label, value, node }) => <InfoRow key={label} label={label} value={node || value} />)}
                </div>

                {/* ── Employment Type (the only employment field still
                    collected on the current ApplicationForm) ──
                    The legacy Employment section captured employer name,
                    job title, phone, start date, work schedule — plus a
                    Previous Employer block — and a self-reported Income
                    breakdown. All of that lived on the Financial Profile
                    step which was deleted; the values now come from
                    ReadyCheck's verified docs (pay stubs, W-2s, bank
                    statements) instead. Showing stale form_data fields
                    here misled ops into thinking the application captured
                    employer details that aren't there. What stays: the
                    employment_types multiselect (W-2 + 1099 + Self-Employed
                    + Retired + Military) which routes ReadyCheck doc
                    requirements. Rendered as a comma-joined pretty
                    string per borrower; co-borrower column appears
                    side-by-side on joint files. */}
                {(() => {
                  const empLabel = (raw) => {
                    if (!raw) return null;
                    const types = Array.isArray(raw) ? raw : String(raw).split(',');
                    const pretty = {
                      w2: 'W-2',
                      W2: 'W-2',
                      self_employed: 'Self-Employed',
                      'Self-Employed': 'Self-Employed',
                      '1099': '1099',
                      retired: 'Retired',
                      Retired: 'Retired',
                      military: 'Military',
                      Military: 'Military',
                    };
                    return types.map(t => pretty[String(t).trim()] || String(t).trim()).filter(Boolean).join(' + ');
                  };
                  const pEmp  = empLabel(formData.employment_types || formData.employment_type);
                  const cEmp  = coBorrower ? empLabel(coBorrower.employment_types || coBorrower.employment_type) : null;
                  return (
                    <>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', borderBottom: '1px solid #f3f4f6', paddingBottom: '6px' }}>Employment Type</div>
                      <div style={{ display: 'grid', gridTemplateColumns: coBorrower ? '1fr 1fr' : '1fr', gap: '0 24px', marginBottom: '20px' }}>
                        <InfoRow label={coBorrower ? `${user.first_name} (Primary)` : 'Borrower'} value={pEmp} />
                        {coBorrower && <InfoRow label={`${coBorrower.first_name} (Co-Borrower)`} value={cEmp} />}
                      </div>
                    </>
                  );
                })()}

                {/* ── Declarations (side-by-side when co-borrower on file) ──
                    Always renders both columns on joint files, even when
                    the co-borrower hasn't answered yet — admin needs to
                    see at-a-glance whether responses are missing on the
                    co side instead of having the column vanish entirely.
                    Unanswered cells render the same "—" badge the
                    primary side uses for nulls, so the empty state reads
                    as "pending" rather than a layout bug. */}
                {(() => {
                  const decl = formData.declarations || {};
                  const cbDecl = coBorrower?.declarations || {};
                  const hasCb = !!coBorrower;
                  const primaryAnswered = Object.keys(decl).length > 0;
                  const coAnswered      = Object.keys(cbDecl).length > 0;
                  const DeclBadge = ({ val }) => val === null || val === undefined
                    ? <span style={{ padding: '1px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', background: '#f3f4f6', color: '#9ca3af' }}>—</span>
                    : <span style={{ padding: '1px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', background: val ? '#fee2e2' : '#dcfce7', color: val ? '#cc0627' : '#15803d' }}>{val ? 'YES' : 'NO'}</span>;
                  return (
                    <>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', borderBottom: '1px solid #f3f4f6', paddingBottom: '6px' }}>Declarations</div>
                      {(primaryAnswered || hasCb) ? (
                      <div style={{ border: '1px solid #f3f4f6', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
                        {/* Column headers — always shown on joint files,
                            with a small "Pending" caption under either
                            name when that side hasn't completed the
                            declarations step. */}
                        {hasCb && (
                          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                            <span style={{ flex: 1, fontSize: '11px', fontWeight: '700', color: '#6b7280' }}>Question</span>
                            <div style={{ width: 100, textAlign: 'center' }}>
                              <div style={{ fontSize: '10px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>{user.first_name}</div>
                              {!primaryAnswered && <div style={{ fontSize: '9px', color: '#9ca3af', fontStyle: 'italic', marginTop: 2 }}>Pending</div>}
                            </div>
                            <div style={{ width: 100, textAlign: 'center' }}>
                              <div style={{ fontSize: '10px', fontWeight: '700', color: '#3a6ad9', textTransform: 'uppercase' }}>{coBorrower.first_name}</div>
                              {!coAnswered && <div style={{ fontSize: '9px', color: '#9ca3af', fontStyle: 'italic', marginTop: 2 }}>Pending</div>}
                            </div>
                          </div>
                        )}
                        {Object.entries(DECL_LABELS).map(([key, label]) => (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f9fafb', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: '#374151', flex: 1, lineHeight: '1.4' }}>{label}</span>
                            <div style={{ width: hasCb ? 100 : 'auto', textAlign: 'center' }}><DeclBadge val={decl[key]} /></div>
                            {hasCb && <div style={{ width: 100, textAlign: 'center' }}><DeclBadge val={cbDecl[key]} /></div>}
                          </div>
                        ))}
                      </div>
                      ) : (
                      <div style={{ padding: '12px 14px', background: '#f9fafb', borderRadius: '8px', fontSize: '13px', color: '#9ca3af', marginBottom: '20px' }}>Declarations not yet completed by borrower.</div>
                      )}
                    </>
                  );
                })()}


                {/* ── Demographic information (URLA Section 8) — STATUS ONLY (Jesse, 2026-10-27) ──
                    Each applicant answers their own on step 3. Staff see "Answered" or "Chose not to answer" per
                    applicant, never the answers: the server sends demographics_status only
                    (backend utils/urlaDemographics.toStaffView). The values print on the 1003 and go in the MISMO export. */}
                {(() => {
                  const primary = formData.demographics_status || null;
                  const co = coBorrower ? (coBorrower.demographics_status || null) : null;
                  if (!primary && !co) return null;
                  const cell = (v) => (v ? v : 'Not answered yet');
                  return (
                    <div data-testid="admin-profile-demographics">
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', borderBottom: '1px solid #f3f4f6', paddingBottom: '6px' }}>Demographic information (URLA Section 8)</div>
                      <div style={{ border: '1px solid #f3f4f6', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
                        {[{ name: user.first_name, v: primary }, ...(coBorrower ? [{ name: coBorrower.first_name, v: co }] : [])].map((r) => (
                          <div key={r.name || 'p'} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f9fafb' }}>
                            <span style={{ flex: 1, fontSize: '13px', color: '#374151', lineHeight: '1.4' }}>{r.name}</span>
                            <span style={{ fontSize: '13px', fontWeight: '500', color: r.v ? '#111827' : '#9ca3af' }}>{cell(r.v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Lender History ── */}
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', borderBottom: '1px solid #f3f4f6', paddingBottom: '6px' }}>Lender History</div>
                {(() => {
                  const answered = user.applied_traditional_lender === true || user.applied_traditional_lender === false;
                  const appliedYes = user.applied_traditional_lender === true;
                  const reasons = Array.isArray(user.traditional_lender_reason) ? user.traditional_lender_reason : [];
                  const answeredAt = user.traditional_lender_question_answered_at
                    ? new Date(user.traditional_lender_question_answered_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                    : null;
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 24px', marginBottom: '20px' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <InfoRow label="Applied with Traditional Lender" value={
                            answered ? (appliedYes ? 'Yes' : 'No') : <span style={{ color: '#9ca3af' }}>Not answered</span>
                          } />
                          {answered && !appliedYes && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 9999, background: '#ffdd86', color: '#7a5200', textTransform: 'uppercase', letterSpacing: 0.3, marginLeft: 4 }}>
                              Recommended to seek traditional options
                            </span>
                          )}
                        </div>
                      </div>
                      <InfoRow label="Reason for Choosing Doorly" value={
                        !answered ? <span style={{ color: '#9ca3af' }}>Not answered</span>
                        : !appliedYes ? 'N/A'
                        : reasons.length > 0 ? reasons.join(', ')
                        : <span style={{ color: '#9ca3af' }}>Not answered</span>
                      } />
                      {user.traditional_lender_reason_other && (
                        <InfoRow label="Other Reason" value={user.traditional_lender_reason_other} />
                      )}
                      {answeredAt && <InfoRow label="Answered At" value={answeredAt} />}
                    </div>
                  );
                })()}

                {/* ── Consent ── */}
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', borderBottom: '1px solid #f3f4f6', paddingBottom: '6px' }}>Authorization & Consent</div>
                {formData.consent_log?.length > 0 ? (
                  formData.consent_log.map((c, i) => (
                    <div key={i} style={{ border: '1px solid #f3f4f6', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px', fontSize: '12px' }}>
                      <div style={{ fontWeight: '600', color: '#111827', marginBottom: '2px' }}>{c.title || 'Authorization & Consent'}</div>
                      {c.timestamp && <div style={{ color: '#6b7280', marginTop: '4px' }}>✅ Agreed — {new Date(c.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
                    </div>
                  ))
                ) : formData.econsent_accepted_at ? (
                  <div style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: '#f0fde4', border: '1px solid #d4f9a0', fontSize: '12px', color: '#374151' }}>
                    ✅ E-Consent accepted: {new Date(formData.econsent_accepted_at).toLocaleString()}
                  </div>
                ) : (
                  <div style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: '#f9fafb', fontSize: '12px', color: '#9ca3af' }}>
                    Consent given at application submission.
                  </div>
                )}
              </div>

              {/* Inspectify status — read-only summary of the borrower's
                  inspection + appraisal order. Only renders when an order
                  exists. Complete-Scheduling button covers the case where
                  the borrower didn't finish on the Inspectify-hosted flow.
                  Report buttons render once the webhook stamps a URL. */}
              {(optionPeriod?.inspectify_order_id || optionPeriod?.inspectify_order_error) && (
                <div style={card}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    Inspection & Appraisal (Inspectify)
                    {optionPeriod.inspectify_order_id && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#f3f4f6', color: '#6b7280', textTransform: 'uppercase' }}>Order #{optionPeriod.inspectify_order_id}</span>
                    )}
                  </h3>
                  {optionPeriod.inspection_status === 'not_applicable' && (
                    <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                      New Construction — Appraisal Only (no third-party inspection)
                    </div>
                  )}
                  {optionPeriod.inspectify_order_error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#991b1b', lineHeight: 1.5 }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Inspectify Order Failed</div>
                      <div style={{ wordBreak: 'break-word' }}>{optionPeriod.inspectify_order_error}</div>
                      {optionPeriod.inspectify_order_error_at && (
                        <div style={{ marginTop: 4, color: '#b91c1c', fontSize: 11 }}>
                          {new Date(optionPeriod.inspectify_order_error_at).toLocaleString()}
                        </div>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/api/admin/borrowers/${user.id}/retry-inspectify-order`, {}, { headers: { Authorization: `Bearer ${token}` } });
                            await fetchData();
                          } catch (e) {
                            alert(`Retry failed: ${e.response?.data?.error || e.message}`);
                          }
                        }}
                        style={{ marginTop: 8, background: '#991b1b', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        Retry Order
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div style={{ padding: '10px 12px', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Inspection</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: optionPeriod.inspection_status === 'not_applicable' ? '#9ca3af' : '#111827' }}>
                        {optionPeriod.inspection_status === 'not_applicable'
                          ? 'Not Applicable'
                          : optionPeriod.inspection_completed_at
                          ? `✓ Completed ${new Date(optionPeriod.inspection_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : optionPeriod.inspection_scheduled_at
                            ? `Scheduled ${new Date(optionPeriod.inspection_scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' })}`
                            : 'Awaiting scheduling'}
                      </div>
                      {optionPeriod.inspection_report_url && (
                        <button onClick={() => window.open(optionPeriod.inspection_report_url, '_blank')} type="button"
                          style={{ marginTop: 8, padding: '5px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          View Inspection Report
                        </button>
                      )}
                    </div>
                    <div style={{ padding: '10px 12px', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Appraisal</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {optionPeriod.appraisal_completed_at
                          ? `✓ Completed ${new Date(optionPeriod.appraisal_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : optionPeriod.appraisal_scheduled_at
                            ? `Scheduled ${new Date(optionPeriod.appraisal_scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' })}`
                            : 'Awaiting scheduling'}
                      </div>
                      {optionPeriod.appraisal_report_url && (
                        <button onClick={() => window.open(optionPeriod.appraisal_report_url, '_blank')} type="button"
                          style={{ marginTop: 8, padding: '5px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          View Appraisal Report
                        </button>
                      )}
                    </div>
                  </div>
                  {optionPeriod.inspectify_collect_url && !((optionPeriod.inspection_scheduled_at || optionPeriod.inspection_status === 'not_applicable') && optionPeriod.appraisal_scheduled_at) && (
                    <div style={{ padding: '10px 12px', background: '#f0f4ff', border: '1px solid #bfdbfe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ fontSize: 12, color: '#1e3a8a' }}>
                        Borrower hasn't completed scheduling yet. Send them back to Inspectify if they need a hand.
                      </div>
                      <button onClick={() => window.open(optionPeriod.inspectify_collect_url, '_blank')} type="button"
                        style={{ padding: '6px 14px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                        Open Scheduling →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
          )}

          {/* ── TAB: Closings ───────────────────────────────────────────── */}
          {activeTab === 'closings' && (() => {
            // Closing Module CTA + overlay — same shape as the Property
            // Module integration on the Property tab. Click button →
            // full-screen overlay; ✕ in the wizard header closes it.
            // The legacy double-close UI below stays available as the
            // edit surface; the module is the workflow chrome on top.
            // State-aware label — mirrors PropertyModule's moduleResumeLabel.
            // Falls back to ▶ Start when current_step hasn't loaded yet.
            const cmComplete = closingModuleStep === 'complete';
            const cmLabel = cmComplete
              ? '✓ Closing Module Complete — Review'
              : typeof closingModuleStep === 'number' && closingModuleStep > 1
                ? `↺ Resume Closing Module — Step ${closingModuleStep} of ${CLOSING_MODULE_STEPS.length}`
                : '▶ Start Closing Module';
            // Right-aligned, normal-size button — matches the visual weight
            // of every other tab-level action button instead of the prior
            // full-width 14px-padding billboard.
            const ClosingModuleSlot = !showClosingModule ? (
              <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button
                  onClick={() => setShowClosingModule(true)}
                  style={{
                    background: cmComplete ? '#f0fdf4' : '#3a6ad9',
                    color:      cmComplete ? '#166534' : '#fff',
                    border:     cmComplete ? '1px solid #bbf7d0' : 'none',
                    borderRadius: 8, padding: '8px 16px',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {cmLabel}
                </button>
              </div>
              {/* 🔑 AFM v2 pre-closing checks (VOE, business existence, desk review, second appraisal). */}
              <PreClosingChecksPanel userId={id} />
              </>
            ) : (
              // Full-screen overlay — backdrop is non-dismissive (matches
              // PropertyModule pattern). Close path is the ✕ in the wizard
              // header. zIndex 10000 matches the modal stack convention.
              <div style={{
                position: 'fixed', inset: 0, zIndex: 10000,
                background: 'rgba(0,0,0,0.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
              }}>
                <div style={{
                  width: '100%', maxWidth: 960, maxHeight: '90vh',
                  overflowY: 'auto', borderRadius: 12,
                  boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                  background: '#ffffff',
                }}>
                  <ClosingModule
                    borrower={{
                      id: user.id,
                      first_name: user.first_name,
                      last_name: user.last_name,
                      loan_number: user.loan_number,
                      date_closed: user.date_closed,
                    }}
                    onClose={() => setShowClosingModule(false)}
                  />
                </div>
              </div>
            );

            // Loading: dcLoading flips on every fetch — show a placeholder so
            // the layout doesn't flash empty. Once the consolidated /full
            // endpoint resolves, dcData is either populated (manager view) or
            // null (no record — show the redirect-to-Property message).
            if (dcLoading && !dcData) {
              return <>{ClosingModuleSlot}<div style={{ ...card, color: '#9ca3af', fontSize: 13 }}>Loading…</div></>;
            }
            if (!dcData) {
              return (
                <>
                  {ClosingModuleSlot}
                  <div style={{ ...card, textAlign: 'center', padding: 40, color: '#6b7280' }}>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#374151' }}>Setting up closing package…</p>
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#9ca3af' }}>If this persists, refresh the page.</p>
                  </div>
                </>
              );
            }

            // ── Helpers ──────────────────────────────────────────────────
            const borrowerFullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '';
            const fmt$ = (v) => {
              if (v === null || v === undefined || v === '') return '—';
              const n = Number(v);
              if (!isFinite(n)) return '—';
              return '$' + n.toLocaleString();
            };
            // fmtDateLong removed — only caller was the funding modal.
            const fmtDateShort = (v) => {
              if (!v) return '—';
              const s = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10);
              const [y, m, d] = s.split('-').map(Number);
              return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            };
            const fmtTS = (v) => v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
            const daysFromToday = (v) => {
              if (!v) return null;
              const s = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10);
              const [y, m, d] = s.split('-').map(Number);
              const target = new Date(y, m - 1, d);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              return Math.ceil((target - today) / 86400000);
            };

            const op    = dcExtras.optionPeriod;
            const cd    = dcExtras.closingDisclosure;
            const hasCo = dcExtras.hasCoBorrower;

            // patchDc removed — only caller was the legacy Notes textarea's
            // onBlur autosave, now replaced by ModuleNotes (which has its own
            // persistence). Future field edits should call /api/admin/
            // double-close/:id directly with axios.patch.

            const uploadPsa = (side) => async (e) => {
              const file = e.target.files?.[0];
              if (!file || !dcData) return;
              setDcPsaUploading(side); setDcErr('');
              try {
                const reader = new FileReader();
                const base64 = await new Promise((resolve, reject) => {
                  reader.onload = () => {
                    const s = String(reader.result || '');
                    resolve(s.includes(',') ? s.split(',')[1] : s);
                  };
                  reader.onerror = () => reject(reader.error);
                  reader.readAsDataURL(file);
                });
                await axios.post(`${API}/api/admin/double-close/${dcData.id}/upload-psa`, {
                  side,
                  filename:      file.name,
                  original_name: file.name,
                  mime_type:     file.type || 'application/pdf',
                  file_size:     file.size,
                  file_data:     base64,
                }, { headers });
                await fetchDoubleClose();
              } catch (err) {
                setDcErr(err.response?.data?.error || err.message);
              } finally {
                setDcPsaUploading(null);
                e.target.value = '';
              }
            };

            // ── 🚨 THE ONLY PATH THAT SENDS CLOSING INSTRUCTIONS ────────────
            //
            // This button was removed with the Title Requests section and left
            // no replacement, so POST /closing-package-{ab|bc} sat wired,
            // persisting and emailing, with nothing able to call it. The
            // generator ran once, on a test file, and never reached a title
            // company — while a hand-made Word document did.
            //
            // ⚠️ A GENERATOR NOBODY CAN TRIGGER IS WORSE THAN NO GENERATOR: it
            // reads as coverage. Every guard on that document — the §D.1
            // recording branches, the deed types, the wire preflight — is worth
            // nothing until an admin can press this.
            //
            // 🔑 THE ROUTE ALREADY DOES THE WHOLE JOB. It generates, attaches
            // the PDF as the first attachment, persists the identical bytes to
            // documents.closing_instructions, and stamps
            // double_close_packages.{side}_closing_package_sent_at. This adds a
            // caller and nothing else — deliberately, so there is one path.
            //
            // Title-package resend (a different email, cover sheet only) lives
            // at Closing Module Step 1 → 'Resend Title Open Emails'.
            const sendClosingPackage = (side) => async () => {
              if (!dcData) return;
              const label = side.toUpperCase();
              if (!window.confirm(
                `Send the ${label} closing package to the title company?\n\n`
                + 'This emails the escrow agent the generated Closing Instructions '
                + 'plus the file documents, and stamps the package as sent.'
              )) return;
              setDcPkgSending(side); setDcErr('');
              try {
                await axios.post(
                  `${API}/api/admin/double-close/${dcData.id}/closing-package-${side}`,
                  {}, { headers }
                );
                await fetchDoubleClose();
              } catch (err) {
                // ⚠️ SURFACE THE PREFLIGHT'S REASONS VERBATIM. It refuses with a
                // list of exactly what is unresolved and how to resolve it;
                // collapsing that to "failed to send" throws away the answer.
                setDcErr(err.response?.data?.error || err.message);
              } finally {
                setDcPkgSending(null);
              }
            };

            // Rendered per side so an A-B-only or B-C-only send is possible —
            // the route and the generator are both side-aware, and a dual
            // button would hide which leg actually went.
            const ClosingPackageButton = ({ side }) => {
              const sentAt = dcData?.[`${side}_closing_package_sent_at`];
              const busy = dcPkgSending === side;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={sendClosingPackage(side)}
                    disabled={busy || !!dcPkgSending}
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: '1px solid #3a6ad9',
                      background: busy ? '#9ca3af' : '#3a6ad9', color: '#fff',
                      fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer',
                    }}
                  >
                    {busy ? 'Sending…' : `Send ${side.toUpperCase()} Closing Package`}
                  </button>
                  {/* 🔑 THE STAMP IS THE EVIDENCE THE DOCUMENT WENT THROUGH THE
                      SYSTEM. It has never been set on a real file — see
                      docs/follow-ups.md. Surfacing it here is what makes the
                      difference visible at the moment somebody would otherwise
                      reach for a Word document. */}
                  <span style={{ fontSize: 12, color: sentAt ? '#2d6a0a' : '#6b7280' }}>
                    {sentAt ? `Sent ${fmtTS(sentAt)}` : 'Not sent'}
                  </span>
                </div>
              );
            };

            const openDocBlob = async (docId) => {
              try {
                const resp = await axios.get(`${API}/api/admin/borrowers/${id}/documents/${docId}/view`, { headers, responseType: 'blob' });
                const url = URL.createObjectURL(resp.data);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) {
                alert('Unable to open PDF: ' + (e.response?.data?.error || e.message));
              }
            };

            // ── KPI row ──────────────────────────────────────────────────
            const opEnd = user.date_option_period_end;
            const opDays = daysFromToday(opEnd);
            // Number value renders in Doorly blue regardless of urgency;
            // gray-dash only when no option-period end date is set.
            let opValue, opColor;
            if (opEnd == null) { opValue = '—'; opColor = '#9ca3af'; }
            else if (opDays < 0) { opValue = '0 — Expired'; opColor = '#3a6ad9'; }
            else { opValue = String(opDays); opColor = '#3a6ad9'; }

            // ITEM 2 — closing_date fix. dcData.closing_date is the legacy
            // double_close_packages column; user.date_closing is the canonical
            // value populated by /offer-review/:token/accept, the dates-tab
            // PATCH, and the Schedule Closing modal. Prefer users column so
            // the KPI reflects the most recent write regardless of which
            // table got the update.
            const closingDate = user.date_closing || dcData.closing_date;
            const closingDays = daysFromToday(closingDate);
            const closingSub  = closingDate
              ? (closingDays === 0 ? 'Today' : closingDays > 0 ? `In ${closingDays} day${closingDays === 1 ? '' : 's'}` : `${Math.abs(closingDays)} day${Math.abs(closingDays) === 1 ? '' : 's'} ago`)
              : '—';

            // EMD = A-B only. There is no B-C EMD — the B-C side is the
            // borrower's down payment, tracked in its own KPI below.
            const abEmdSent   = !!dcData.ab_emd_sent;
            const abEmdSentAt = dcData.ab_emd_sent_at;

            // Down payment status reads dcp.bc_emd_sent (legacy column name
            // repurposed for "down payment received" in the post-d3aee9e
            // model). Backend keeps it in sync with closing_module_steps.
            // step2_bc_complete via the '2:mark_bc_received' action handler.
            const dpReceived   = !!dcData.bc_emd_sent;
            const dpReceivedAt = dcData.bc_emd_sent_at;

            // Generic KPI cards — Title Company is rendered separately below
            // because it carries the inline escrow officer edit + Message
            // button which don't fit the simple value/sub shape.
            const kpis = [
              { label: 'Option Period Remaining', value: opValue, valueColor: opColor, sub: opEnd ? fmtDateShort(opEnd) : 'Not set' },
              { label: 'Closing Date',             value: closingDate ? fmtDateShort(closingDate) : '—', valueColor: '#111827', sub: closingSub },
              { label: 'EMD Status',
                value: abEmdSent ? 'Sent' : 'Pending',
                valueColor: abEmdSent ? '#2d6a0a' : '#7a5200',
                sub: abEmdSent
                  ? (abEmdSentAt ? `Sent ${fmtDateShort(abEmdSentAt)}` : 'Sent')
                  : 'Awaiting EMD wire' },
              { label: 'Down Payment Receipt',
                value: dpReceived ? 'Received' : 'Pending',
                valueColor: dpReceived ? '#2d6a0a' : '#7a5200',
                sub: dpReceived
                  ? (dpReceivedAt ? `Received ${fmtDateShort(dpReceivedAt)}` : 'Received')
                  : 'Awaiting borrower wire' },
            ];

            // ── Side card render helper ──────────────────────────────────
            // Defined as a plain function (not a component) so React doesn't
            // remount its children on each parent render — same reasoning as
            // the prior renderTransactionCard inline pattern.
            const sideCard = (side) => {
              const isAb = side === 'ab';
              const purchasePrice = isAb ? dcData.ab_purchase_price : dcData.bc_purchase_price;
              // EMD computed-fresh server-side by utils/emdCalc.js. The
              // headline "EMD Amount" displays the GROSS figure (1% of
              // purchase price) — that's the total earnest-money obligation.
              // The $500 offer-fee credit applies to the borrower's net
              // out-of-pocket cash and is reflected in Cash to Close, not
              // here. dcData.{ab,bc}_emd_amount columns are no longer read
              // for display.
              // EMD computation — server > persisted > 1%-fallback. Used by
              // the read-only EMD/Down Payment card. Variables only needed
              // by removed sections (offerFeePaid, emdIsHeuristic, cashToClose,
              // closingFundsAmount, fundsReceived/At, packageSent, fullPkgSent,
              // fundingAt/Conf/Approved, closingNoticeSent, isSendingFull/Title)
              // were dropped — kept only what the surviving JSX still reads.
              const emdCtx        = dcExtras?.emd || null;
              const psaPriceForSide = isAb ? (emdCtx?.abPurchasePrice ?? dcData.ab_purchase_price) : (emdCtx?.bcPurchasePrice ?? dcData.bc_purchase_price);
              const fallbackEmd  = psaPriceForSide ? Math.round(Number(psaPriceForSide) * 0.01) : null;
              const storedEmdRaw = isAb ? dcData.ab_emd_amount : dcData.bc_emd_amount;
              const persistedEmd = storedEmdRaw != null && Number(storedEmdRaw) > 0 ? Number(storedEmdRaw) : null;
              const serverEmd    = isAb ? emdCtx?.abEmdTotal : emdCtx?.bcEmdTotal;
              const emdAmount    = (serverEmd ?? persistedEmd) ?? fallbackEmd;
              const emdSent      = isAb ? !!dcData.ab_emd_sent  : !!dcData.bc_emd_sent;
              const emdSentAt    = isAb ? dcData.ab_emd_sent_at : dcData.bc_emd_sent_at;
              // Down Payment derives from Loan Terms (basePrice − loanAmount).
              // B-C only; A-B is null.
              const ltDpPct       = Number(user?.down_payment_pct) || 10;
              const ltBasePrice   = Number(user?.contract_purchase_price || user?.doorly_purchase_price || 0);
              const ltLoanAmount  = ltBasePrice > 0 ? Math.round(ltBasePrice * (1 - ltDpPct / 100)) : 0;
              const ltDownPayment = ltBasePrice > 0 ? ltBasePrice - ltLoanAmount : 0;
              const downPaymentAmount = !isAb
                ? (ltDownPayment > 0 ? ltDownPayment : null)
                : null;
              const psaDocId     = isAb ? dcData.ab_psa_document_id : dcData.bc_psa_document_id;
              const isUploading  = dcPsaUploading === side;
              const accent       = isAb ? '#3a6ad9' : '#acf44e';

              // PSA status — A-B mirrors the simple Uploaded/Not uploaded
              // binary it always had. B-C now reflects the full signing
              // state machine: not generated → generated → primary signed
              // (waiting on co-buyer) → all borrowers signed (waiting on
              // Jesse to countersign) → fully executed. Color rises with
              // urgency: gray for inert, blue for "needs the other side",
              // amber for "waiting on us", green for done, red on failure.
              const psaStatus = (() => {
                if (isAb) {
                  if (psaDocId) return { label: 'Uploaded', color: '#2d6a0a', bg: '#f4fee8', icon: '✓' };
                  return { label: 'Not uploaded', color: '#7a5200', bg: '#fff7ed', icon: '○' };
                }
                const pkgStatus = op?.psa_package_status;
                if (pkgStatus === 'declined' || pkgStatus === 'expired' || pkgStatus === 'revoked') {
                  return { label: pkgStatus.charAt(0).toUpperCase() + pkgStatus.slice(1), color: '#ff082f', bg: '#fef2f2', icon: '⚠' };
                }
                if (op?.psa_signed_doorly_at || pkgStatus === 'completed') {
                  return { label: `Fully Executed${op?.psa_signed_doorly_at ? ` — ${fmtTS(op.psa_signed_doorly_at)}` : ''}`, color: '#16a34a', bg: '#f0fdf4', icon: '✓' };
                }
                const bcSignedFull = !!op?.psa_signed_primary_at && (!hasCo || !!op?.psa_signed_coborrower_at);
                if (bcSignedFull || pkgStatus === 'awaiting_doorly_countersign') {
                  return { label: 'Waiting on Jesse to Countersign', color: '#f59e0b', bg: '#fffbeb', icon: '⏳' };
                }
                if (op?.psa_signed_primary_at && hasCo && !op?.psa_signed_coborrower_at) {
                  return { label: 'Waiting on Co-Buyer Signature', color: '#f59e0b', bg: '#fffbeb', icon: '⏳' };
                }
                if (op?.psa_signed_coborrower_at && !op?.psa_signed_primary_at) {
                  return { label: 'Waiting on Buyer Signature', color: '#3a6ad9', bg: '#eff6ff', icon: '→' };
                }
                if (psaDocId) {
                  return { label: 'Sent to Borrower — Awaiting Signature', color: '#3a6ad9', bg: '#eff6ff', icon: '→' };
                }
                return { label: 'Not generated', color: '#9ca3af', bg: '#f9fafb', icon: '○' };
              })();

              const titleFileOpenedAt = isAb ? user?.ab_title_file_opened_at : user?.bc_title_file_opened_at;
              return (
                <div style={{ ...card, padding: 18, borderLeft: `4px solid ${accent}` }}>
                  {/* Header */}
                  <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#111827' }}>
                        {isAb ? 'A-B Transaction' : 'B-C Transaction'}
                      </h4>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {isAb ? 'Seller → Go Doorly, LLC' : `Go Doorly, LLC → ${borrowerFullName || 'Borrower'}`}
                      </div>
                    </div>
                    {titleFileOpenedAt && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 9999, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap' }}>
                        ✓ Title File Opened
                      </div>
                    )}
                  </div>

                  {/* Section: Transaction Details. ITEM 6a: A-B side now
                      shows Listing Agent (sourced from offer_requests via
                      listingAgentInfo) instead of seller name+email — those
                      come from the SimplyRETS feed and are read-only here.
                      ITEM 6c: "Doorly Entity" label is "Buyer" on the A-B
                      side (Doorly is buying from the seller). */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Transaction Details</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>{isAb ? 'A-B' : 'B-C'} Purchase Price</span>
                        <span style={{ fontWeight: 700, color: '#111827' }}>{fmt$(purchasePrice)}</span>
                      </div>
                      {isAb ? (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ color: '#6b7280' }}>Listing Agent</span>
                            <span style={{ fontWeight: 600, color: '#111827', textAlign: 'right' }}>
                              {listingAgentInfo.name || '—'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ color: '#6b7280' }}>Listing Agent Email</span>
                            <span style={{ fontSize: 12, color: '#374151', textAlign: 'right' }}>{listingAgentInfo.email || '—'}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#6b7280' }}>Buyer</span>
                            <span style={{ fontWeight: 600, color: '#111827' }}>{dcData.doorly_entity || 'Go Doorly, LLC'}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ color: '#6b7280' }}>Buyer</span>
                            <span style={{ fontWeight: 600, color: '#111827', textAlign: 'right' }}>
                              {borrowerFullName || '—'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ color: '#6b7280' }}>Buyer Email</span>
                            <span style={{ fontSize: 12, color: '#374151', textAlign: 'right' }}>{user.email || '—'}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#6b7280' }}>Seller</span>
                            <span style={{ fontWeight: 600, color: '#111827' }}>{dcData.doorly_entity || 'Go Doorly, LLC'}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Section: EMD (A-B) / Down Payment (B-C) — read-only.
                      Interactive {ab,bc}_emd_sent toggle removed; status is
                      now derived from the EMD wire's send state in the
                      Closing Module (Step 2 card). The Mercury wireRecords
                      verification list moved out of this card too — pending
                      a follow-up surface in Finance > Wires. */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                      {isAb ? 'EMD' : 'Down Payment'}
                    </div>
                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#6b7280' }}>Amount</span>
                        <span style={{ fontWeight: 600, color: '#111827' }}>
                          {fmt$(isAb ? emdAmount : downPaymentAmount)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#6b7280' }}>Status</span>
                        <span style={{ fontWeight: 600, color: emdSent ? '#166534' : '#854d0e' }}>
                          {emdSent ? (isAb ? 'Sent' : 'Received') : 'Pending'}
                        </span>
                      </div>
                      {emdSent && emdSentAt && (
                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                          {fmtTS(emdSentAt)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Closing Funds + Estimated Cash to Close sections removed.
                      Both carried funds_received toggles that flipped
                      dcp.{ab,bc}_funds_received — the Closing Module's Step 8
                      (Cash to Close) now owns that status transition via the
                      closing_module_steps.step8_complete + cd.funds_received
                      gates. Manual flip on the legacy dc columns is gone. */}

                  {/* Section: PSA. Both sides: View + Replace only. The
                      borrower PSA is auto-mirrored from the Property tab when
                      generated/uploaded there, so there's no Upload button on
                      the closings card — admin uploads on the Property tab.
                      Status indicator (Uploaded / Not generated) renders inline
                      with the buttons but styled as muted text, not a button. */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>PSA</div>
                    {(() => {
                      // The View button should surface the most-progressed
                      // copy of the contract: fully executed if Jesse has
                      // countersigned, else borrower-signed if all borrowers
                      // signed, else the pristine generated PSA. Falls back
                      // to psaDocId (dc.bc_psa_document_id) when no signed
                      // copy is on file. A-B side ignores this — it never
                      // produces borrower_psa_signed / _fully_executed rows,
                      // so it always uses psaDocId.
                      let viewDocId = psaDocId;
                      if (!isAb && Array.isArray(documents)) {
                        const exec = documents.find(d => d.document_type === 'borrower_psa_fully_executed');
                        const signed = documents.find(d => d.document_type === 'borrower_psa_signed');
                        if (exec?.id)        viewDocId = exec.id;
                        else if (signed?.id) viewDocId = signed.id;
                      }
                      return (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button onClick={() => viewDocId && openDocBlob(viewDocId)} type="button" disabled={!viewDocId}
                        style={{ padding: '6px 12px', background: viewDocId ? '#3a6ad9' : '#e5e7eb', color: viewDocId ? '#fff' : '#9ca3af', border: 'none', borderRadius: 7, cursor: viewDocId ? 'pointer' : 'not-allowed', fontSize: 11, fontWeight: 700 }}>
                        View
                      </button>
                      <label style={{ display: 'inline-block' }}>
                        <input type="file" accept="application/pdf" onChange={uploadPsa(side)} disabled={isUploading} style={{ display: 'none' }} />
                        <span style={{ display: 'inline-block', padding: '6px 12px', background: '#111827', color: '#acf44e', borderRadius: 7, cursor: isUploading ? 'wait' : 'pointer', fontSize: 11, fontWeight: 700 }}>
                          {isUploading ? 'Uploading…' : 'Replace'}
                        </span>
                      </label>
                      {/* Inline status — icon + label, right-aligned in the
                          button row. Color shifts to match urgency. */}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: psaStatus.color, marginLeft: 'auto' }}>
                        <span aria-hidden>{psaStatus.icon}</span>
                        <span>{psaStatus.label}</span>
                      </span>
                    </div>
                      );
                    })()}
                    {/* 🚨 THE RESTORED TRIGGER. Until this existed, the only way
                        a title company received closing instructions was a
                        hand-made Word document outside the system. */}
                    <ClosingPackageButton side={side} />
                    {/* Signing timeline (B-C only). Appears below the inline
                        status the moment any party has signed and persists
                        through full execution. Pending parties show a muted
                        bullet so the reader sees the whole chain at a
                        glance. */}
                    {!isAb && (op?.psa_signed_primary_at || op?.psa_signed_coborrower_at || op?.psa_signed_doorly_at) && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#6b7280' }}>
                        {op.psa_signed_primary_at ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{ color: '#16a34a' }}>✓</span>
                            <span>Buyer signed {fmtTS(op.psa_signed_primary_at)}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{ color: '#9ca3af' }}>○</span>
                            <span style={{ color: '#9ca3af' }}>Buyer — pending signature</span>
                          </div>
                        )}
                        {hasCo && (
                          op.psa_signed_coborrower_at ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <span style={{ color: '#16a34a' }}>✓</span>
                              <span>Co-Buyer signed {fmtTS(op.psa_signed_coborrower_at)}</span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <span style={{ color: '#9ca3af' }}>○</span>
                              <span style={{ color: '#9ca3af' }}>Co-Buyer — pending signature</span>
                            </div>
                          )
                        )}
                        {op.psa_signed_doorly_at ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{ color: '#16a34a' }}>✓</span>
                            <span>Jesse countersigned {fmtTS(op.psa_signed_doorly_at)}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ color: '#9ca3af' }}>○</span>
                            <span style={{ color: '#9ca3af' }}>Jesse — pending countersignature</span>
                            <button
                              type="button"
                              onClick={handleResendBcCountersign}
                              disabled={bcCountersignResending}
                              style={{ marginLeft: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#3a6ad9', background: bcCountersignResending ? '#f3f4f6' : '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, cursor: bcCountersignResending ? 'default' : 'pointer' }}
                              title="Re-fire Jesse's countersign email (generates a fresh 7-day token)"
                            >
                              {bcCountersignResending ? 'Sending…' : '↻ Resend Countersign'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {!isAb && (() => {
                    // ITEM 7b/c — "Loan Documents" renamed to "Documents",
                    // backed by the 6-card closing-documents grid keyed by
                    // document_type (inspection_report, appraisal_report,
                    // hoi_declaration, promissory_note, deed_of_trust,
                    // closing_package). Backend POST/GET endpoints handle
                    // upload + replace + view.
                    const DOC_ORDER = [
                      ['inspection_report', 'Inspection Report'],
                      ['appraisal_report',  'Appraisal Report'],
                      ['hoi_declaration',   'HOI Declaration Page'],
                      ['promissory_note',   'Promissory Note'],
                      ['deed_of_trust',     'Deed of Trust'],
                      ['closing_package',   'Closing Package'],
                    ];
                    const uploadClosingDoc = (docType) => async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setClosingDocUploading(docType);
                      try {
                        const reader = new FileReader();
                        const base64 = await new Promise((resolve, reject) => {
                          reader.onload = () => {
                            const s = String(reader.result || '');
                            resolve(s.includes(',') ? s.split(',')[1] : s);
                          };
                          reader.onerror = () => reject(reader.error);
                          reader.readAsDataURL(file);
                        });
                        await axios.post(`${API}/api/admin/borrowers/${id}/closing-documents/${docType}/upload`, {
                          filename:      file.name,
                          original_name: file.name,
                          mime_type:     file.type || 'application/pdf',
                          file_data:     base64,
                        }, { headers });
                        await fetchClosingDocs();
                      } catch (err) {
                        alert('Upload failed: ' + (err.response?.data?.error || err.message));
                      } finally {
                        setClosingDocUploading(null);
                        e.target.value = '';
                      }
                    };
                    const viewClosingDoc = async (docType) => {
                      try {
                        const resp = await axios.get(`${API}/api/admin/borrowers/${id}/closing-documents/${docType}/view`, { headers, responseType: 'blob' });
                        const url = URL.createObjectURL(resp.data);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (err) {
                        alert('Unable to open: ' + (err.response?.data?.error || err.message));
                      }
                    };
                    // Count uploaded docs for the collapsed-state badge so
                    // admins can see at a glance how many of the 6 are in.
                    const uploadedCount = DOC_ORDER.filter(([k]) => !!closingDocs.documents?.[k]).length;
                    return (
                      <div style={{ marginBottom: 14 }}>
                        <button type="button" onClick={() => setBcDocsExpanded((v) => !v)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: 0, marginBottom: bcDocsExpanded ? 8 : 0, background: 'none', border: 'none', cursor: 'pointer' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Documents</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 9999, background: uploadedCount === DOC_ORDER.length ? '#f0fdf4' : '#f3f4f6', color: uploadedCount === DOC_ORDER.length ? '#15803d' : '#6b7280' }}>{uploadedCount}/{DOC_ORDER.length}</span>
                            <span style={{ fontSize: 14, color: '#9ca3af', transform: bcDocsExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s' }}>›</span>
                          </span>
                        </button>
                        {bcDocsExpanded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {DOC_ORDER.map(([key, label]) => {
                            const doc = closingDocs.documents?.[key];
                            const uploaded = !!doc;
                            const uploading = closingDocUploading === key;
                            return (
                              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ color: '#374151', fontWeight: 600 }}>{label}</div>
                                  <div style={{ fontSize: 10, color: uploaded ? '#2d6a0a' : '#9ca3af', marginTop: 2 }}>
                                    {uploaded
                                      ? `Uploaded — ${doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}`
                                      : 'Not Uploaded'}
                                  </div>
                                </div>
                                {uploaded ? (
                                  <>
                                    <button onClick={() => viewClosingDoc(key)} type="button"
                                      style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>View</button>
                                    <label style={{ display: 'inline-block' }}>
                                      <input type="file" accept="application/pdf" onChange={uploadClosingDoc(key)} disabled={uploading} style={{ display: 'none' }} />
                                      <span style={{ display: 'inline-block', padding: '4px 10px', background: '#111827', color: '#acf44e', borderRadius: 5, cursor: uploading ? 'wait' : 'pointer', fontSize: 10, fontWeight: 700 }}>
                                        {uploading ? '…' : 'Replace'}
                                      </span>
                                    </label>
                                  </>
                                ) : (
                                  <label style={{ display: 'inline-block' }}>
                                    <input type="file" accept="application/pdf" onChange={uploadClosingDoc(key)} disabled={uploading} style={{ display: 'none' }} />
                                    <span style={{ display: 'inline-block', padding: '4px 10px', background: '#3a6ad9', color: '#fff', borderRadius: 5, cursor: uploading ? 'wait' : 'pointer', fontSize: 10, fontWeight: 700 }}>
                                      {uploading ? 'Uploading…' : 'Upload'}
                                    </span>
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        )}
                        {bcDocsExpanded && (() => {
                          // ── LE / CD slot rows ──────────────────────────
                          // Pull the most-recent loan_estimate /
                          // closing_disclosure rows from the documents
                          // table (versioned via storeLE / storeCD) and
                          // surface a real View button. Falls back to a
                          // "Not yet generated" hint when no row exists.
                          const docsArr = Array.isArray(documents) ? documents : [];
                          const latestByType = (type) => {
                            const matching = docsArr
                              .filter((d) => d.document_type === type)
                              .slice()
                              .sort((a, b) => new Date(b.upload_date || 0) - new Date(a.upload_date || 0));
                            return matching[0] || null;
                          };
                          const versionDocs = [
                            ['loan_estimate',     'Loan Estimate'],
                            ['closing_disclosure','Closing Disclosure'],
                          ];
                          const adminToken = localStorage.getItem('doorly_admin_token');
                          const viewVersionedDoc = async (doc) => {
                            try {
                              const r = await axios.get(`${API}/api/admin/borrowers/${id}/documents/${doc.id}/view`, {
                                headers: { Authorization: `Bearer ${adminToken}` },
                                responseType: 'blob',
                              });
                              const url = URL.createObjectURL(r.data);
                              window.open(url, '_blank');
                              setTimeout(() => URL.revokeObjectURL(url), 60000);
                            } catch (err) {
                              alert('Unable to open: ' + (err.response?.data?.error || err.message));
                            }
                          };
                          return (
                            <>
                              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {versionDocs.map(([type, label]) => {
                                  const doc = latestByType(type);
                                  return (
                                    <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12 }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ color: '#374151', fontWeight: 600 }}>{label}</div>
                                        <div style={{ fontSize: 10, color: doc ? '#2d6a0a' : '#9ca3af', marginTop: 2 }}>
                                          {doc
                                            ? `${(doc.original_name || label + '.pdf').replace(/\.pdf$/i, '')} — ${doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}`
                                            : 'Not yet generated'}
                                        </div>
                                      </div>
                                      {doc && (
                                        <button onClick={() => viewVersionedDoc(doc)} type="button"
                                          style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>View</button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Read-only signed-status hint preserved
                                  for admins watching the signing flow. */}
                              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#6b7280' }}>
                                {op?.le_signed_primary_at && <div>LE signed {fmtTS(op.le_signed_primary_at)}</div>}
                                {cd?.cd_signed && <div>CD signed {fmtTS(cd.cd_signed_at)}</div>}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* A-B Documents — narrower set than B-C: inspection +
                      appraisal reports only, since those are the artifacts
                      that flow back to the seller / listing-side packet.
                      Reads the same closingDocs.documents map the B-C card
                      uses, so an Inspectify-uploaded report shows on both. */}
                  {isAb && (() => {
                    const AB_DOCS = [
                      ['inspection_report', 'Inspection Report'],
                      ['appraisal_report',  'Appraisal Report'],
                    ];
                    const viewAbDoc = async (docType) => {
                      try {
                        const resp = await axios.get(`${API}/api/admin/borrowers/${id}/closing-documents/${docType}/view`, { headers, responseType: 'blob' });
                        const url = URL.createObjectURL(resp.data);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (err) {
                        alert('Unable to open: ' + (err.response?.data?.error || err.message));
                      }
                    };
                    const abUploadedCount = AB_DOCS.filter(([k]) => !!closingDocs.documents?.[k]).length;
                    return (
                      <div style={{ marginBottom: 14 }}>
                        <button type="button" onClick={() => setAbDocsExpanded((v) => !v)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: 0, marginBottom: abDocsExpanded ? 8 : 0, background: 'none', border: 'none', cursor: 'pointer' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Documents</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 9999, background: abUploadedCount === AB_DOCS.length ? '#f0fdf4' : '#f3f4f6', color: abUploadedCount === AB_DOCS.length ? '#15803d' : '#6b7280' }}>{abUploadedCount}/{AB_DOCS.length}</span>
                            <span style={{ fontSize: 14, color: '#9ca3af', transform: abDocsExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s' }}>›</span>
                          </span>
                        </button>
                        {abDocsExpanded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {AB_DOCS.map(([key, label]) => {
                            const doc = closingDocs.documents?.[key];
                            return (
                              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ color: '#374151', fontWeight: 600 }}>{label}</div>
                                  <div style={{ fontSize: 10, color: doc ? '#2d6a0a' : '#9ca3af', marginTop: 2 }}>
                                    {doc
                                      ? `Uploaded — ${doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}`
                                      : 'Pending'}
                                  </div>
                                </div>
                                {doc && (
                                  <button onClick={() => viewAbDoc(key)} type="button"
                                    style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>View</button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Title Requests + Closing Actions sections removed.
                      Replaced by Closing Module overlay actions:
                        - Resend Title Open Emails  → Step 1
                        - Generate Closing Package  → (no replacement yet;
                          if needed, surface a dedicated admin action)
                        - Request Funding (A-B/B-C) → Step 10
                        - Schedule Closing          → Step 9 (Clear to Close) */}

                </div>
              );
            };

            return (
              <div>
                {ClosingModuleSlot}
                {dcErr && <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', color: '#ff082f', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{dcErr}</div>}

                {/* Row 1 — KPI strip. ITEM 1: full width, 5 equal columns
                    matching the section grid below. The 4 generic cards
                    render from the kpis array; Title Company is bespoke. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10, marginBottom: 16, width: '100%' }}>
                  {kpis.map((k) => (
                    <div key={k.label} style={{ ...card, padding: '12px 14px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{k.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: k.valueColor || '#111827', marginBottom: 2 }}>{k.value}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{k.sub}</div>
                    </div>
                  ))}
                  {/* Appraisal Status KPI — replaces the former Title Company
                      card. Sourced from option_periods.appraisal_status,
                      flattened onto dcData.inspectionAppraisal by /full. */}
                  <div style={{ ...card, padding: '12px 14px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Appraisal</div>
                    {(() => {
                      const raw = String(dcData?.inspectionAppraisal?.appraisal_status || '').toLowerCase();
                      const MAP = {
                        '':          { label: 'Not Ordered',     bg: '#f3f4f6', color: '#6b7280' },
                        not_ordered: { label: 'Not Ordered',     bg: '#f3f4f6', color: '#6b7280' },
                        unpaid:      { label: 'Not Ordered',     bg: '#f3f4f6', color: '#6b7280' },
                        ordered:     { label: 'Ordered',         bg: '#ffdd86', color: '#7a5200' },
                        paid:        { label: 'Ordered',         bg: '#ffdd86', color: '#7a5200' },
                        scheduled:   { label: 'Scheduled',       bg: '#ffdd86', color: '#7a5200' },
                        complete:    { label: 'Completed',       bg: '#acf44e', color: '#1a4d00' },
                        completed:   { label: 'Completed',       bg: '#acf44e', color: '#1a4d00' },
                        received:    { label: 'Report Received', bg: '#acf44e', color: '#1a4d00' },
                        waived:      { label: 'Waived',          bg: '#f3f4f6', color: '#6b7280' },
                      };
                      const s = MAP[raw] || MAP[''];
                      return (
                        <span style={{ display: 'inline-block', fontSize: 13, fontWeight: 800, padding: '4px 12px', borderRadius: 9999, background: s.bg, color: s.color }}>
                          {s.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Message BluePrint — opens the BluePrint portal transaction
                    (B-C order, else A-B). Replaces the former title-company
                    messaging card. Disabled until files open. */}
                {(() => {
                  const orderId = dcData?.blueprint_bc_order_id || dcData?.blueprint_ab_order_id || null;
                  const base = {
                    display: 'inline-block', padding: '10px 18px', borderRadius: 8,
                    fontSize: 13, fontWeight: 700, textDecoration: 'none',
                  };
                  return (
                    <div style={{ marginBottom: 16 }}>
                      {orderId ? (
                        <a href={`https://app.blueprinttitle.com/transactions/${orderId}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ ...base, background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', cursor: 'pointer' }}>
                          Message BluePrint Title →
                        </a>
                      ) : (
                        <span title="BluePrint files not yet opened"
                          style={{ ...base, background: '#fff', color: '#9ca3af', border: '1px solid #e5e7eb', cursor: 'not-allowed' }}>
                          Message BluePrint Title →
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Row 2 — A-B and B-C side-by-side */}
                <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  {sideCard('ab')}
                  {sideCard('bc')}
                </div>

                {/* Row 2.5 — Inspection & Appraisal status. Sits between
                    the side cards and Notes. Sourced from the option_periods
                    Inspectify columns flattened into dcData.inspectionAppraisal
                    by the /full endpoint. Status precedence per side:
                    completed → scheduled → ordered → not_ordered. */}
                <div style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: 24,
                  marginBottom: 20,
                }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: 15, fontWeight: 700, color: '#111827' }}>
                    Inspection & Appraisal
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    {/* Inspection */}
                    <div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Home Inspection
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Status:</span>
                        <span style={{
                          background: dcData?.inspectionAppraisal?.inspection_completed_at ? '#acf44e' :
                                      dcData?.inspectionAppraisal?.inspection_scheduled_at ? '#ffdd86' :
                                      dcData?.inspectionAppraisal?.inspection_appraisal_ordered ? '#c5d3f5' : '#f3f4f6',
                          color:      dcData?.inspectionAppraisal?.inspection_completed_at ? '#111827' :
                                      dcData?.inspectionAppraisal?.inspection_scheduled_at ? '#7a5200' :
                                      dcData?.inspectionAppraisal?.inspection_appraisal_ordered ? '#1e3a8a' : '#6b7280',
                          fontSize: 12, fontWeight: 700,
                          padding: '3px 10px', borderRadius: 4,
                        }}>
                          {dcData?.inspectionAppraisal?.inspection_completed_at ? 'Completed' :
                           dcData?.inspectionAppraisal?.inspection_scheduled_at ? 'Scheduled' :
                           dcData?.inspectionAppraisal?.inspection_appraisal_ordered ? 'Ordered' : 'Not Ordered'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Date:</span>
                        <span style={{ fontSize: 13, color: '#111827' }}>
                          {dcData?.inspectionAppraisal?.inspection_completed_at
                            ? new Date(dcData.inspectionAppraisal.inspection_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : dcData?.inspectionAppraisal?.inspection_scheduled_at
                              ? new Date(dcData.inspectionAppraisal.inspection_scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : '—'}
                        </span>
                      </div>
                      {dcData?.inspectionAppraisal?.inspection_report_url && (
                        <button
                          onClick={() => window.open(dcData.inspectionAppraisal.inspection_report_url, '_blank')}
                          type="button"
                          style={{ marginTop: 10, background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          View Report
                        </button>
                      )}
                    </div>

                    {/* Appraisal */}
                    <div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Traditional Appraisal
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Status:</span>
                        <span style={{
                          background: dcData?.inspectionAppraisal?.appraisal_completed_at ? '#acf44e' :
                                      dcData?.inspectionAppraisal?.appraisal_scheduled_at ? '#ffdd86' :
                                      dcData?.inspectionAppraisal?.inspection_appraisal_ordered ? '#c5d3f5' : '#f3f4f6',
                          color:      dcData?.inspectionAppraisal?.appraisal_completed_at ? '#111827' :
                                      dcData?.inspectionAppraisal?.appraisal_scheduled_at ? '#7a5200' :
                                      dcData?.inspectionAppraisal?.inspection_appraisal_ordered ? '#1e3a8a' : '#6b7280',
                          fontSize: 12, fontWeight: 700,
                          padding: '3px 10px', borderRadius: 4,
                        }}>
                          {dcData?.inspectionAppraisal?.appraisal_completed_at ? 'Completed' :
                           dcData?.inspectionAppraisal?.appraisal_scheduled_at ? 'Scheduled' :
                           dcData?.inspectionAppraisal?.inspection_appraisal_ordered ? 'Ordered' : 'Not Ordered'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Date:</span>
                        <span style={{ fontSize: 13, color: '#111827' }}>
                          {dcData?.inspectionAppraisal?.appraisal_completed_at
                            ? new Date(dcData.inspectionAppraisal.appraisal_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : dcData?.inspectionAppraisal?.appraisal_scheduled_at
                              ? new Date(dcData.inspectionAppraisal.appraisal_scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : '—'}
                        </span>
                      </div>
                      {dcData?.inspectionAppraisal?.appraisal_report_url && (
                        <button
                          onClick={() => window.open(dcData.inspectionAppraisal.appraisal_report_url, '_blank')}
                          type="button"
                          style={{ marginTop: 10, background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          View Report
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Inspectify order pill + open-scheduling shortcut */}
                  {dcData?.inspectionAppraisal?.inspectify_order_id && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>
                        Inspectify Order #{dcData.inspectionAppraisal.inspectify_order_id}
                      </span>
                      {dcData?.inspectionAppraisal?.inspectify_collect_url && (
                        <button
                          onClick={() => window.open(dcData.inspectionAppraisal.inspectify_collect_url, '_blank')}
                          type="button"
                          style={{ background: 'none', border: '1px solid #3a6ad9', color: '#3a6ad9', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Open in Inspectify →
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Row 3 — Notes. ModuleNotes lives inside the card; the
                    legacy free-floating textarea bound to dcp.notes was
                    removed in favor of the threaded ModuleNotes (same edit
                    surface as Property + Underwriting tabs). */}
                <div style={{ ...card, marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#111827' }}>Notes</h4>
                  <ModuleNotes moduleType="closing" borrowerId={id} />
                </div>


                {/* Schedule Closing modal (Item 8). */}
                {scheduleModal.open && (
                  <div onClick={() => !scheduleSaving && setScheduleModal({ open: false, side: null })}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div onClick={(e) => e.stopPropagation()}
                      style={{ background: '#fff', borderRadius: 14, maxWidth: 520, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
                      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>Schedule Closing</h3>
                      </div>
                      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>Closing Date</label>
                          <input type="date" value={scheduleForm.closing_date} onChange={(e) => setScheduleForm(p => ({ ...p, closing_date: e.target.value }))}
                            min={cdWait && cdWait.earliest ? cdWait.earliest : undefined} data-testid="schedule-closing-date"
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                          {/* 🔑 THE CD WAITING PERIOD (§1026.19(f)(1)(ii)(A)): the server refuses an earlier date too. */}
                          {cdWait && cdWait.cdIssued && (
                            <div data-testid="schedule-closing-cd-wait" style={{ marginTop: 4, fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>
                              {cdWait.earliest
                                ? `Earliest closing: ${cdWait.earliest} (three business days after the borrower received the Closing Disclosure; Saturdays count, Sundays and federal holidays don't).`
                                : 'The borrower\'s receipt of the Closing Disclosure is not on record yet, so no closing date can be confirmed.'}
                            </div>
                          )}
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>Closing Time</label>
                          <input type="time" value={scheduleForm.closing_time} onChange={(e) => setScheduleForm(p => ({ ...p, closing_time: e.target.value }))}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>Closing Location</label>
                          <input type="text" value={scheduleForm.location} onChange={(e) => setScheduleForm(p => ({ ...p, location: e.target.value }))}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>E-Closing Link (optional)</label>
                          <input type="url" value={scheduleForm.eclosing_url} onChange={(e) => setScheduleForm(p => ({ ...p, eclosing_url: e.target.value }))}
                            placeholder="https://title-company.example/sign/..."
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                          <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af', lineHeight: 1.4 }}>
                            {scheduleModal.side === 'ab'
                              ? 'Pasting the title-company link here fires the Doorly Signatory notification and send notification to begin closing.'
                              : 'Pasting the title-company link here fires the borrower notification and unlocks the Begin E-Closing button on their dashboard.'}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
                          <textarea value={scheduleForm.notes} onChange={(e) => setScheduleForm(p => ({ ...p, notes: e.target.value }))} rows={3}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button onClick={() => setScheduleModal({ open: false, side: null })} type="button" disabled={scheduleSaving}
                            style={{ padding: '10px 18px', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            Cancel
                          </button>
                          <button onClick={async () => {
                            if (!scheduleForm.closing_date) { alert('Closing date is required.'); return; }
                            setScheduleSaving(true);
                            try {
                              await axios.post(`${API}/api/admin/borrowers/${id}/schedule-closing`, scheduleForm, { headers });
                              // Side-aware e-closing link save. A-B
                              // writes to dc.ab_eclosing_url and emails
                              // Jesse (the Doorly Signatory). B-C writes
                              // to users.eclosing_url and fires the
                              // borrower + co-borrower + partner emails.
                              // No-op when the value hasn't changed.
                              const desiredEclosing = (scheduleForm.eclosing_url || '').trim();
                              const currentEclosing = (scheduleModal.side === 'ab'
                                ? (dcData?.ab_eclosing_url || '')
                                : (user?.eclosing_url || '')
                              ).trim();
                              if (desiredEclosing !== currentEclosing) {
                                await axios.patch(`${API}/api/admin/borrowers/${id}/eclosing-url`,
                                  { eclosing_url: desiredEclosing || null, side: scheduleModal.side }, { headers })
                                  .catch((eclosingErr) => {
                                    console.error('[schedule-closing] eclosing-url PATCH failed:', eclosingErr.response?.data?.error || eclosingErr.message);
                                  });
                              }
                              setScheduleModal({ open: false, side: null });
                              setScheduleToast(`Closing scheduled for ${scheduleForm.closing_date}`);
                              setTimeout(() => setScheduleToast(null), 4000);
                              await fetchDoubleClose();
                              await fetchData();
                            } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
                            finally { setScheduleSaving(false); }
                          }} type="button" disabled={scheduleSaving}
                            style={{ padding: '10px 22px', background: scheduleSaving ? '#e5e7eb' : '#3a6ad9', color: scheduleSaving ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: scheduleSaving ? 'wait' : 'pointer' }}>
                            {scheduleSaving ? 'Saving…' : 'Confirm & Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {scheduleToast && (
                  <div style={{ position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', background: '#111827', color: '#acf44e', borderRadius: 8, fontSize: 13, fontWeight: 700, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 9999 }}>
                    ✓ {scheduleToast}
                  </div>
                )}

                {/* CtcChecklistSection removed — the gate matrix lived here
                    pre-Closing-Module and surfaced the same step state the
                    Closing Module overlay now owns. No drop-in replacement;
                    the overlay's step progression is the source of truth. */}

                {/* Closing Module Notes block consolidated into the Row 3
                    Notes card above — was previously duplicated here. */}

                {/* SmartStart card — renders only if borrower is enrolled.
                    Always rendered (regardless of stage) so the admin can
                    track progress at any point post-enrollment, not just
                    once the loan reaches closing. */}
                <SmartStartAdminCard userId={id} headers={headers} />
              </div>
            );
          })()}

          {/* Title contact form modal (rendered outside the tab-content branches
              so it works whether the tab body is the init form or the manager). */}
          <TitleContactFormModal
            open={tcModalOpen}
            initial={tcEditTarget}
            headers={headers}
            onClose={() => { setTcModalOpen(false); setTcEditTarget(null); }}
            onSaved={onTitleContactSaved}
          />

          {/* ── TAB 2: Loan Details ─────────────────────────────────────── */}
          {activeTab === 'loan_details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* ─── Disclosure figures that are placeholders ───
                  🚨 cdMath has been recording every defaulted disclosure figure
                  in `usedDefaults` for months, correctly and completely, into a
                  response nothing rendered. This is the reader — and it goes
                  FIRST on this tab, above the loan figures themselves, because
                  a number and the fact that the number is invented are not
                  separate concerns.

                  The launch gate lives in docs/launch-gates.md; a gate that
                  lives only in a markdown file nobody re-reads is the same
                  complaint in a different hat. This puts it in front of whoever
                  opens the borrower. */}
              <DisclosureDefaultsCard borrowerId={id} token={token} />

              {/* ─── Loan Team ───
                  Who owns this file, per role. Three distinct empty states —
                  never assigned / considered-but-unstaffed / fetch failure —
                  because collapsing any two of them is how an adversed file
                  ends up looking broken rather than deliberate. */}
              <LoanTeamCard
                borrowerId={id}
                token={token}
                isAdversed={!!user?.is_adversed}
                isArchived={!!user?.is_archived}
              />
              {/* 🔑 REAPPLY on a declined file: the date, and staff opening it early (2026-10-22). */}
              {/* The card asks the server whether the file is declined (any route) and renders nothing otherwise. */}
              <ReapplyEarlyCard borrowerId={id} token={token} />

              {/* ─── Placeholder-figure visibility banner ───
                  When loan_docs.le_compliance_snapshot.usedDefaults is a
                  non-empty array, the LE was built using one or more
                  hardcoded placeholder figures (titleSettlement,
                  recordingFee, monthlyTax rate, etc. — see
                  backend/utils/cdMath.js) instead of real vendor/title
                  quotes. The amber banner makes that fact visible at the
                  top of the Loan tab so it's actionable BEFORE the
                  borrower signs — placeholder figures that don't match
                  the final CD create tolerance-cure exposure under
                  Reg Z 1026.19(e)(3). Read-only here; the future entry
                  surface is where the figures actually get replaced.
                  null + [] both produce nothing. */}
              {Array.isArray(data?.le_used_defaults) && data.le_used_defaults.length > 0 && (
                <div style={{
                  background: '#ffdd86',
                  border: '1px solid #d97706',
                  borderLeft: '4px solid #d97706',
                  borderRadius: 8,
                  padding: '12px 16px',
                  color: '#111827',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>
                    ⚠ This LE was issued using {data.le_used_defaults.length} placeholder figure{data.le_used_defaults.length === 1 ? '' : 's'}, not quoted amounts
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    Fields on the frozen LE snapshot that came from hardcoded defaults in <code style={{ background: 'rgba(17,24,39,0.08)', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>cdMath.js</code> instead of real vendor/title quotes:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {data.le_used_defaults.map((field) => (
                      <span key={field} style={{
                        background: '#111827',
                        color: '#ffdd86',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontFamily: 'monospace',
                        fontSize: 11,
                        fontWeight: 600,
                      }}>{field}</span>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, fontStyle: 'italic', color: '#111827', opacity: 0.85 }}>
                    Real title / vendor figures should replace these before the borrower signs.
                    Placeholders that don't match the final CD create tolerance-cure exposure
                    under Reg Z 1026.19(e)(3) (zero-bucket recording fees, 10%-aggregate title services).
                  </div>
                </div>
              )}

              {/* ─── DocMagic Loan Documents ─── */}
              <div style={{ ...card, padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Loan Documents</h3>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>LE / CD</span>
                </div>
                {!user?.loan_number && (
                  <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#9f1239' }}>
                    ⚠ Loan Number missing. A loan number is required to generate documents. Save the borrower's loan number first.
                  </div>
                )}
                {dmError && (
                  <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#ff082f' }}>
                    {dmError}
                  </div>
                )}

                {(() => {
                  // Pull the most-recent loan_estimate / closing_disclosure
                  // PDFs from the documents-table mirror so the View button
                  // can open the actual versioned file (storeLE / storeCD
                  // write a row here on every successful regeneration).
                  const docsArr = Array.isArray(documents) ? documents : [];
                  const latestVersionedByType = (type) => {
                    const matches = docsArr
                      .filter((d) => d.document_type === type)
                      .slice()
                      .sort((a, b) => new Date(b.upload_date || 0) - new Date(a.upload_date || 0));
                    return matches[0] || null;
                  };
                  const adminToken = localStorage.getItem('doorly_admin_token');
                  const viewVersionedDoc = async (vd) => {
                    if (!vd?.id) return;
                    try {
                      const r = await axios.get(`${API}/api/admin/borrowers/${id}/documents/${vd.id}/view`, {
                        headers: { Authorization: `Bearer ${adminToken}` },
                        responseType: 'blob',
                      });
                      const url = URL.createObjectURL(r.data);
                      window.open(url, '_blank');
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    } catch (err) {
                      alert('Unable to open: ' + (err.response?.data?.error || err.message));
                    }
                  };
                  return (
                    <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {['le', 'cd'].map(t => {
                        const doc = dmDocs[t];
                        // ── Signed-state override ────────────────────
                        // The DocMagic package_status only transitions
                        // to 'completed' when the package's signing webhook
                        // fires. Borrower-side signing flows that don't
                        // go through DocMagic write the signed-state
                        // signals on the borrower row / closing_disclosures
                        // row instead — the package_status stays at
                        // 'generated' and the card mislabels the doc as
                        // unsigned. Surface the real signed state from
                        // the canonical signing-source columns so the
                        // LE card mirrors the CD card and the borrower
                        // documents page.
                        let status = doc?.package_status;
                        let signedAt = doc?.all_signed_at;
                        let primarySignedAt = doc?.primary_signed_at;
                        let coSignedAt = doc?.coborrower_signed_at;
                        let signerName = null;
                        if (t === 'le' && user?.intent_to_proceed_signed_document_id) {
                          status = 'completed';
                          signedAt = signedAt || user.intent_to_proceed_at || null;
                          primarySignedAt = primarySignedAt || user.intent_to_proceed_at || null;
                          // signerName intentionally left null — the
                          // 'Signed by <name>' line was redundant on the
                          // LE card; the Signed pill + timestamp rows
                          // already convey the state cleanly. CD branch
                          // below keeps the signer name (paired CD signing
                          // workflow still uses it as a confirmation
                          // anchor on the closing-side review).
                        }
                        if (t === 'cd' && closingDisclosure?.cd_signed_document_id) {
                          status = 'completed';
                          signedAt = signedAt || closingDisclosure.cd_signed_at || null;
                          primarySignedAt = primarySignedAt || closingDisclosure.cd_signed_at || null;
                          signerName = closingDisclosure.cd_signed_typed_name || null;
                        }
                        const statusCfg = {
                          pending:    { bg: '#f3f4f6', color: '#374151', label: 'Pending' },
                          generated:  { bg: '#dcfce7', color: '#166534', label: 'Generated' },
                          created:    { bg: '#ffdd86', color: '#7a5200', label: 'Awaiting Signature' },
                          completed:  { bg: '#acf44e', color: '#1a1a1a', label: 'Signed' },
                          declined:   { bg: '#fecdd3', color: '#9f1239', label: 'Declined' },
                          error:      { bg: '#fecdd3', color: '#9f1239', label: 'Error' },
                        }[status] || null;
                        const label = t === 'le' ? 'Loan Estimate' : 'Closing Disclosure';
                        const versionType = t === 'le' ? 'loan_estimate' : 'closing_disclosure';
                        // When the LE was signed through the borrower
                        // ITP flow, the canonical signed PDF lives at
                        // intent_to_proceed_signed_document_id (a
                        // separate doc row of type itp_signed_le, not
                        // loan_estimate). Resolve View to that doc so
                        // the admin opens the same signed PDF the
                        // borrower sees — mirrors the LE card on the
                        // Documents tab > Disclosures section.
                        let versionedDoc = latestVersionedByType(versionType);
                        if (t === 'le' && user?.intent_to_proceed_signed_document_id) {
                          const signedDoc = docsArr.find((d) => d.id === user.intent_to_proceed_signed_document_id);
                          if (signedDoc) versionedDoc = signedDoc;
                        }
                        if (t === 'cd' && closingDisclosure?.cd_signed_document_id) {
                          const signedDoc = docsArr.find((d) => d.id === closingDisclosure.cd_signed_document_id);
                          if (signedDoc) versionedDoc = signedDoc;
                        }
                        const gen = dmGenerating === t;
                        const genTs = doc?.generated_at;
                        const canView = !!versionedDoc;
                        return (
                          <div key={t} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>{label}</div>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                              {statusCfg ? (
                                <span style={{ background: statusCfg.bg, color: statusCfg.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 9999 }}>
                                  {statusCfg.label}
                                </span>
                              ) : (
                                <span style={{ fontSize: 11, color: '#9ca3af' }}>Not yet generated</span>
                              )}
                              {genTs && (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>
                                  {new Date(genTs).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                            {primarySignedAt && (
                              <div style={{ fontSize: 11, color: '#6b7280' }}>Primary signed: {new Date(primarySignedAt).toLocaleString()}</div>
                            )}
                            {coSignedAt && (
                              <div style={{ fontSize: 11, color: '#6b7280' }}>Co-borrower signed: {new Date(coSignedAt).toLocaleString()}</div>
                            )}
                            {signedAt && (
                              <div style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>Fully signed: {new Date(signedAt).toLocaleString()}</div>
                            )}
                            {signerName && (
                              <div style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>Signed by {signerName}</div>
                            )}
                            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <button
                                onClick={() => handleGenerateDm(t)}
                                disabled={gen || !user?.loan_number}
                                style={{
                                  padding: '8px 12px',
                                  background: gen || !user?.loan_number ? '#e5e7eb' : '#3a6ad9',
                                  color: gen || !user?.loan_number ? '#9ca3af' : '#fff',
                                  border: 'none', borderRadius: 8,
                                  cursor: gen || !user?.loan_number ? 'not-allowed' : 'pointer',
                                  fontSize: 13, fontWeight: 700,
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                }}>
                                {gen ? (
                                  <>
                                    <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} />
                                    Generating…
                                  </>
                                ) : (doc ? 'Regenerate' : 'Generate')}
                              </button>
                              <button
                                onClick={() => viewVersionedDoc(versionedDoc)}
                                disabled={!canView}
                                title={canView ? 'Open the latest version in a new tab' : 'Generate the document first'}
                                style={{
                                  padding: '8px 12px',
                                  background: canView ? '#fff' : '#f3f4f6',
                                  color: canView ? '#3a6ad9' : '#9ca3af',
                                  border: `1px solid ${canView ? '#bfcff8' : '#e5e7eb'}`,
                                  borderRadius: 8,
                                  cursor: canView ? 'pointer' : 'not-allowed',
                                  fontSize: 13, fontWeight: 700,
                                }}>
                                View
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>

              <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Loan Terms */}
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Loan Terms</h3>
                  {/* Loan Officer is view-only on this card — they can
                      reference the locked terms (purchase price, rate,
                      down payment) and regenerate the LE downstream,
                      but can't mutate the underlying pricing. Hiding
                      the Edit button is sufficient since the inputs
                      only render in editing mode. Backend write
                      gating, if needed, would go on
                      PUT /api/admin/users/:id. */}
                  {!loanTermsEditing ? (
                    !isLO && (
                      <button onClick={startLoanTermsEdit}
                        style={{ padding: '5px 14px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                        Edit
                      </button>
                    )
                  ) : (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => setLoanTermsEditing(false)}
                        style={{ padding: '5px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                        Cancel
                      </button>
                      <button onClick={handleSaveLoanTerms} disabled={loanTermsSaving}
                        style={{ padding: '5px 14px', background: loanTermsSaving ? '#e5e7eb' : '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: loanTermsSaving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>
                        {loanTermsSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Purchase Price — stage-aware source chain with C/S/A/R badge.
                    C = Contract (offer-accepted contract_purchase_price)
                    S = SmartEstimate-stretched (doorly_purchase_price × (1 + resale_adjustment))
                    A = Atlas (atlas_max_purchase_price / max_approval_amount)
                    R = Requested (formData / lead requested price)
                    The C branch fires from offer_accepted onward; the same
                    contractPriced gate the KPI bar uses, so the two views
                    stay in sync.
                    Edit-mode preserved. */}
                {(() => {
                  const ltContractPriced = POST_ACCEPTANCE_STAGES.includes(status) ||
                                           status === 'offer_accepted' ||
                                           !!user.purchase_price_updated_at_offer;
                  let ltPP, ltBadge;
                  if (ltContractPriced) {
                    // contract_purchase_price is the post-acceptance B-C
                    // price. When null (legacy offers / admin pricing
                    // drift), apply the resale adjustment to doorly_-
                    // purchase_price so the row shows the B-C contract
                    // figure — NOT the raw A-B price. Same fallback as
                    // the basePrice resolver below + the KPI bar above.
                    const cpp = Number(user.contract_purchase_price || 0);
                    if (cpp > 0) { ltPP = cpp; }
                    else {
                      const dprice = Number(user.doorly_purchase_price || 0);
                      ltPP = dprice > 0 ? Math.round(dprice) : pp;
                    }
                    ltBadge = 'C';
                  } else if (user.smartestimate_accepted || HOME_SHOPPING_STAGES.includes(status)) {
                    const dprice = Number(user.doorly_purchase_price || 0);
                    ltPP = dprice > 0 ? Math.round(dprice) : pp; ltBadge = 'S';
                  } else if (atlasLocked) {
                    // Gated on atlasLocked (accepted + applied) ONLY — an unaccepted
                    // live simulation (atlasResult) must NOT change the loan file.
                    // Borrower-facing (B-C) price like the C/S branches: apply the
                    // resale markup so PP / Down Payment / Loan Amount match what
                    // Atlas computed on the borrower price (base × (1 + resale)).
                    const aprice = Number(user.atlas_max_purchase_price || user.max_approval_amount || 0);
                    ltPP = aprice > 0 ? Math.round(aprice) : pp; ltBadge = 'A';
                  } else {
                    ltPP = requestedPP || pp; ltBadge = 'R';
                  }
                  const ltBadgeColors = { C: '#3a6ad9', S: '#111827', A: '#acf44e', R: '#e5e7eb' };
                  const ltBadgeText   = { C: '#fff',    S: '#acf44e', A: '#1a1a1a', R: '#6b7280' };
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Purchase Price</span>
                      {loanTermsEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '12px', color: '#374151' }}>$</span>
                          <input type="text" value={loanTermsForm.purchasePrice}
                            onChange={e => setLoanTermsForm(f => ({ ...f, purchasePrice: e.target.value.replace(/[^0-9.]/g, '') }))}
                            style={{ width: '110px', padding: '5px 8px', border: '1.5px solid #3a6ad9', borderRadius: '6px', fontSize: '13px', textAlign: 'right', outline: 'none', background: '#f8faff' }} />
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', fontWeight: '500', color: ltPP ? '#111827' : '#9ca3af' }}>
                          {ltPP ? fmt$(ltPP) : '—'}
                          {ltPP > 0 && (
                            <span style={{ fontSize: '9px', fontWeight: '700', background: ltBadgeColors[ltBadge], color: ltBadgeText[ltBadge], padding: '1px 5px', borderRadius: '3px', marginLeft: '5px', verticalAlign: 'middle' }}>
                              {ltBadge}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Computed rows: Loan Amount, Down Payment, LTV, Monthly Payment.
                    Once the borrower has accepted a SmartEstimate, the loan
                    math is based on the borrower-facing purchase price
                    (doorly_purchase_price × (1 + resale_adjustment/100)) so
                    Loan Amount and Down Payment line up with the figures
                    shown on the borrower's accepted SmartEstimate. Same
                    chain as the Purchase Price row above. */}
                {(() => {
                  const ddDpPct = Number(user.down_payment_pct || 0);
                  const editDpPct = loanTermsEditing
                    ? parseFloat(loanTermsForm.dpPct)
                    : ddDpPct;
                  const dpPctSafe = Number.isFinite(editDpPct) && editDpPct >= 0 && editDpPct <= 100
                    ? editDpPct
                    : 0;
                  // basePrice mirrors the Purchase Price row's resolution
                  // chain (C/S/A/R) so the rest of the card reads as a
                  // single coherent loan picture. Same offer_accepted +
                  // purchase_price_updated_at_offer fallback the badge
                  // logic uses above.
                  const ltContractPriced = POST_ACCEPTANCE_STAGES.includes(status) ||
                                           status === 'offer_accepted' ||
                                           !!user.purchase_price_updated_at_offer;
                  const basePrice = (() => {
                    if (ltContractPriced) {
                      // contract_purchase_price is the post-acceptance
                      // B-C price. When it's null (offer accepted pre-
                      // column or admin price edits drifted it), fall
                      // back to the resale-adjusted B-C math — NOT raw
                      // doorly_purchase_price, which is the A-B
                      // (Doorly→seller) price and ignores the resale
                      // markup. Loan Terms here drives the Down Payment
                      // / Loan Amount on this card AND the Closings tab,
                      // so the two surfaces must agree.
                      const cpp = Number(user.contract_purchase_price || 0);
                      if (cpp > 0) return cpp;
                      const dprice = Number(user.doorly_purchase_price || 0);
                      return dprice > 0 ? Math.round(dprice) : 0;
                    }
                    if (user.smartestimate_accepted || HOME_SHOPPING_STAGES.includes(status)) {
                      const dprice = Number(user.doorly_purchase_price || 0);
                      return dprice > 0 ? Math.round(dprice) : 0;
                    }
                    if (atlasLocked) {
                      // atlasLocked (accepted + applied) ONLY — a live sim must not
                      // drive the loan file. Borrower-facing (B-C) price like the
                      // C/S branches: apply the resale markup so Down Payment / Loan
                      // Amount match Atlas's borrower-price figures.
                      const aprice = Number(user.atlas_max_purchase_price || user.max_approval_amount || 0);
                      return aprice > 0 ? Math.round(aprice) : 0;
                    }
                    return Number(user.doorly_purchase_price || 0);
                  })();

                  // Loan Amount = basePrice × (1 − dp_pct/100). Down Payment = basePrice − LA.
                  const loanAmount = basePrice > 0 ? Math.round(basePrice * (1 - dpPctSafe / 100)) : 0;
                  const downPayment = basePrice > 0 ? Math.round(basePrice - loanAmount) : 0;
                  // LTV = 1 − dp_pct/100, formatted with one decimal.
                  const ltvValue = basePrice > 0 ? (100 - dpPctSafe) : null;

                  // Each non-Purchase-Price row keeps an empty <span> on the
                  // left so the flex layout (justify-content: space-between)
                  // keeps values right-aligned. Label text is hidden but the
                  // structural element is preserved.
                  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' };
                  const labelStyle = { fontSize: '12px', color: '#6b7280' };
                  const valStyle = (truthy) => ({ fontSize: '12px', fontWeight: '500', color: truthy ? '#111827' : '#9ca3af' });

                  return (
                    <>
                      {/* Loan Amount */}
                      <div style={rowStyle}>
                        <span style={labelStyle}>Loan Amount</span>
                        <span style={valStyle(loanAmount > 0)}>
                          {loanAmount > 0 ? fmt$(loanAmount) : '—'}
                        </span>
                      </div>

                      {/* Down Payment — combined "$X,XXX (X%)". Edit-mode hosts
                          the dp_pct input (the standalone "Down Payment %" row
                          was removed; this row is now its only entry point). */}
                      <div style={rowStyle}>
                        <span style={labelStyle}>Down Payment</span>
                        {loanTermsEditing ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: '#374151' }}>{downPayment > 0 ? fmt$(downPayment) : '—'}</span>
                            <input type="number" value={loanTermsForm.dpPct}
                              onChange={e => setLoanTermsForm(f => ({ ...f, dpPct: e.target.value }))}
                              step="0.5" min="0" max="100"
                              style={{ width: '64px', padding: '5px 8px', border: '1.5px solid #3a6ad9', borderRadius: '6px', fontSize: '13px', textAlign: 'right', outline: 'none', background: '#f8faff' }} />
                            <span style={{ fontSize: '12px', color: '#374151' }}>%</span>
                          </div>
                        ) : (
                          <span style={valStyle(downPayment > 0)}>
                            {downPayment > 0
                              ? `${fmt$(downPayment)} (${dpPctSafe.toFixed(0)}%)`
                              : '—'}
                          </span>
                        )}
                      </div>

                      {/* LTV — 100 − dp_pct, one decimal. */}
                      <div style={rowStyle}>
                        <span style={labelStyle}>LTV</span>
                        <span style={valStyle(ltvValue != null)}>
                          {ltvValue != null ? fmtPct(ltvValue) : '—'}
                        </span>
                      </div>
                    </>
                  );
                })()}

                {/* Approved Rate — falls back to atlas_final_rate (stored as decimal) when approved_interest_rate hasn't been synced yet */}
                {(() => {
                  const displayedRate = Number(user.approved_interest_rate) > 0
                    ? Number(user.approved_interest_rate)
                    : (Number(user.atlas_final_rate) > 0 ? Number(user.atlas_final_rate) * 100 : null);
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Approved Rate</span>
                      {loanTermsEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input type="number" value={loanTermsForm.rate}
                            onChange={e => setLoanTermsForm(f => ({ ...f, rate: e.target.value }))}
                            step="0.125" min="0.1" max="30"
                            style={{ width: '72px', padding: '5px 8px', border: '1.5px solid #3a6ad9', borderRadius: '6px', fontSize: '13px', textAlign: 'right', outline: 'none', background: '#f8faff' }} />
                          <span style={{ fontSize: '12px', color: '#374151' }}>%</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', fontWeight: '500', color: displayedRate ? '#111827' : '#9ca3af' }}>{displayedRate ? fmtPct(displayedRate) : '—'}</span>
                      )}
                    </div>
                  );
                })()}

                {/* Monthly Payment — full PITI when SmartEstimate accepted
                    so this matches the figure on the borrower's signed
                    SmartEstimate. Pre-acceptance, fall back to P&I only
                    (no purchase price yet to estimate taxes/insurance from).
                    Tax/HOI rates are SERVED (backend constants/feeSchedule, the
                    rates cdMath uses), never typed here, so the
                    Loan Terms card's Monthly Payment matches the figure
                    cdMath produces for the LE PDF and the CD PDF. The
                    prior 1.6% tax + flat $150 HOI used the older
                    mismoGenerator (DocMagic LE) rules-of-thumb and
                    diverged from the native LE/CD math by ~$50/mo on a
                    $300K loan. */}
                {(() => {
                  const ddDpPct = Number(user.down_payment_pct || 0);
                  const dpPctSafe = Number.isFinite(ddDpPct) && ddDpPct >= 0 && ddDpPct <= 100 ? ddDpPct : 0;
                  const basePrice = (() => {
                    if (POST_ACCEPTANCE_STAGES.includes(status)) {
                      return Number(user.contract_purchase_price || user.doorly_purchase_price || 0);
                    }
                    if (user.smartestimate_accepted || HOME_SHOPPING_STAGES.includes(status)) {
                      const dprice = Number(user.doorly_purchase_price || 0);
                      return dprice > 0 ? Math.round(dprice) : 0;
                    }
                    if (atlasLocked) {
                      // atlasLocked (accepted + applied) ONLY — a live sim must not
                      // drive the loan file. Borrower-facing (B-C) price like the
                      // C/S branches: apply the resale markup so Down Payment / Loan
                      // Amount match Atlas's borrower-price figures.
                      const aprice = Number(user.atlas_max_purchase_price || user.max_approval_amount || 0);
                      return aprice > 0 ? Math.round(aprice) : 0;
                    }
                    return Number(user.doorly_purchase_price || 0);
                  })();
                  const loanAmount = basePrice > 0 ? Math.round(basePrice * (1 - dpPctSafe / 100)) : 0;
                  const ratePct = Number(user.approved_interest_rate) > 0
                    ? Number(user.approved_interest_rate)
                    : (Number(user.atlas_final_rate) > 0 ? Number(user.atlas_final_rate) * 100 : 0);
                  // PMI removed — non-QM loans can't bind MI. Monthly
                  // Payment is now P&I + escrow only; the inline
                  // pmiCalculator fallback table is gone with the rest
                  // of the MI logic.
                  let monthlyPayment = null;
                  if (loanAmount > 0 && ratePct > 0) {
                    const r = ratePct / 100 / 12;
                    const n = 360;
                    const factor = Math.pow(1 + r, n);
                    const pi = loanAmount * (r * factor) / (factor - 1);
                    if (Number.isFinite(pi)) {
                      const showPiti = user.smartestimate_accepted || HOME_SHOPPING_STAGES.includes(status) || POST_ACCEPTANCE_STAGES.includes(status);
                      // The served estimate rates (feeSchedule, = cdMath's escrow rates). Both feed monthly; sum into integer dollars.
                      const escrow = showPiti && basePrice > 0 && estRates
                        ? Math.round((basePrice * estRates.tax) / 12) + Math.round((basePrice * estRates.hoi) / 12)
                        : 0;
                      monthlyPayment = Math.round(pi) + escrow;
                    }
                  }
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f9fafb' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Monthly Payment</span>
                      <span style={{ fontSize: '12px', fontWeight: '500', color: monthlyPayment != null ? '#111827' : '#9ca3af' }}>
                        {monthlyPayment != null ? fmt$(monthlyPayment) : '—'}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Needs — rolled up from Review tab. Anything still here
                  is blocking the all_conditions_cleared CTC gate. Sits
                  as the second cell of the 2-col grid (immediately to
                  the right of Loan Terms) so admins see open conditions
                  next to the file's pricing instead of having to scroll
                  past National MI / DTI / Timeline to find them. */}
              {(() => {
                // 🔑 THE SAME "OPEN" THE CTC GATE USES (constants/needStatus.js):
                // a dismissal counts only with a staff reason on record, so this
                // card and all_conditions_cleared can no longer disagree.
                const allNeeds = [
                  ...(uwNeeds.income || []).map((n) => ({ ...n, _cat: 'Income' })),
                  ...(uwNeeds.assets || []).map((n) => ({ ...n, _cat: 'Assets' })),
                  ...(uwNeeds.housing || []).map((n) => ({ ...n, _cat: 'Housing' })),
                  ...(uwNeeds.credit || []).map((n) => ({ ...n, _cat: 'Credit' })),
                  ...(uwNeeds.other || []).map((n) => ({ ...n, _cat: 'Other' })),
                  ...(uwNeeds.uncategorized || []).map((n) => ({ ...n, _cat: 'Misc' })),
                ];
                const openNeeds = allNeeds.filter(isOpenNeed);
                const openCount = openNeeds.length;
                return (
                  <div style={card}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Needs ({openCount} open)
                      </h3>
                      <button onClick={() => setActiveTab('review')}
                        style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                        Manage in Review →
                      </button>
                    </div>
                    <div style={{
                      padding: '8px 10px',
                      borderRadius: '6px',
                      background: openCount === 0 ? '#f0fde4' : '#fffbeb',
                      border: `1px solid ${openCount === 0 ? '#bbf7d0' : '#fde68a'}`,
                      fontSize: '11px',
                      color: openCount === 0 ? '#166534' : '#7a5200',
                      marginBottom: '10px',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                      <span>{openCount === 0 ? '✓' : '⚠️'}</span>
                      <span>
                        {openCount === 0
                          ? 'All needs cleared — Clear-to-Close gate satisfied.'
                          : `${openCount} open need${openCount === 1 ? '' : 's'} blocking Clear to Close.`}
                      </span>
                    </div>
                    {openCount === 0 ? (
                      <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>No open needs.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {openNeeds.map((n) => (
                          <div key={n.id} style={{
                            padding: '8px 10px',
                            borderRadius: '8px',
                            background: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderLeft: '3px solid #f59e0b',
                            fontSize: '12px',
                            color: '#374151',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: n.description ? '4px' : 0 }}>
                              <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', background: '#f3f4f6', color: '#374151', flexShrink: 0 }}>{n._cat}</span>
                              <span style={{ fontWeight: '600', color: '#111827', flex: 1 }}>{n.title}</span>
                              <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', background: '#fff', color: '#7a5200', border: '1px solid #fde68a', textTransform: 'capitalize', flexShrink: 0 }}>{n.status || 'pending'}</span>
                            </div>
                            {n.description && (
                              <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.4 }}>{n.description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* National MI card removed — non-QM loans can't bind MI.
                  The /mi-rate-quote + /mi-order endpoints + the
                  national_mi_* columns were deleted in G1. */}

              {/* DTI & Credit */}
              <div style={card}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>DTI & Credit</h3>
                <InfoRow label="Front DTI" value={frontDti ? fmtPct(frontDti) : null} />
                <InfoRow label="Back DTI" value={backDti ? fmtPct(backDti) : null} />
                <InfoRow label="Credit Score" value={<CreditScoreDisplay borrower={{ ...user, credit_score_range: formData.credit_score_range }} coBorrower={coBorrower} />} />
                <InfoRow
                  label="Monthly Income"
                  value={gmi > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {fmt$(gmi)}/mo
                      {gmiIsPending && (
                        <span
                          title="The borrower hasn't completed Phase 2 (income confirmation) on the ReadyCheck wizard yet. Shown here is the AI's Phase 1 calculation; the canonical users.gross_monthly_income field is still $0."
                          style={{
                            fontSize: 9, fontWeight: 700,
                            padding: '1px 6px', borderRadius: 4,
                            background: '#fffbeb', color: '#92400e',
                            border: '1px solid #fde68a',
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}
                        >
                          AI · Pending Confirmation
                        </span>
                      )}
                    </span>
                  ) : null}
                />
                <InfoRow label="Monthly Debt" value={debt > 0 ? fmt$(debt) + '/mo' : null} />
              </div>

              {/* Application Timeline */}
              <div style={card}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Application Timeline</h3>
                <InfoRow label="Account Created" value={user.created_at ? new Date(user.created_at).toLocaleDateString() : null} />
                <InfoRow label="Last Status Update" value={user.status_updated_at ? new Date(user.status_updated_at).toLocaleDateString() : null} />
                <InfoRow label="Days in Current Stage" value={`${days}d`} />
                <InfoRow label="Current Stage" value={stageLabel} />
                {/* SmartEstimate acceptance — derive from the explicit
                    column OR the application_status being past
                    home_shopping. Stages in HOME_SHOPPING_STAGES /
                    POST_ACCEPTANCE_STAGES can only be reached after
                    the borrower accepted a SmartEstimate (the stage
                    advance gates on it), so a missing column flag on a
                    borrower in those stages is stale data, not a real
                    'not accepted' signal. The Loan Terms card has
                    already used this OR-with-stage pattern; this row
                    just brings the Timeline card in line. */}
                <InfoRow label="SmartEstimate™ Accepted"
                  value={(user.smartestimate_accepted ||
                          HOME_SHOPPING_STAGES.includes(status) ||
                          POST_ACCEPTANCE_STAGES.includes(status))
                    ? '✅ Yes'
                    : '— No'} />
                <InfoRow label="Identity Verified" value={identityAdminLabel(user, idVerification, documents)} />
                {/* Plaid Income + Argyle Employment rows removed —
                    those integrations are surfaced on the Financials
                    tab where the underlying connection state lives;
                    duplicating them here added noise without surfacing
                    anything actionable. */}
              </div>

              </div>
            </div>
          )}

          {/* ── TAB: Review ─────────────────────────────────────────────── */}
          {activeTab === 'review' && (() => {
            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>

              {/* 🔑 THE REVIEW TAB (Jesse, 2026-10-24 — the approved redesign; no phase banner, phase tiles, Re-run or
                  separate Open Items list). The classic tab is gone (cleanup Phase 2). */}
              {guidedView && !guidedView.error ? (
                (() => {
                  // 🔑 THE STRIPPED-BACK TAB + THE UNDERWRITING MODULE (Jesse, 2026-10-24). The tab shows the step, the
                  // strip, the current step's open items and a compact decision card; everything else is in the Module:
                  // its section steps (identity, income, assets, housing, credit, misc — ReviewStepBody, as for classic
                  // files) plus, for a guided file, the fraud panels on Identity, steps 5–6 (re-check, figures, disputes,
                  // override) on Income, letter dismissal and the stored-report re-read on Credit, and three more steps:
                  // Documents and needs (the documents card: upload, re-tag, dismiss), Decision (re-price, uphold /
                  // overturn, notices) and Consents (consents and signatures with time, IP and user agent).
                  const hasYesDeclaration = Object.values(formData.declarations || {}).some((v) => v === true)
                    || Object.values(coBorrower?.declarations || {}).some((v) => v === true);
                  const hasMisc = !!(formData.requires_bankruptcy_lox || formData.requires_foreclosure_lox || formData.requires_employment_gap_lox
                    || formData.requires_mortgage_default_lox || formData.housing_status === 'rent_free' || hasYesDeclaration
                    || (additionalNeeds || []).some((n) => n.category === 'misc' || n.category === 'miscellaneous')
                    // 🔑 …and whenever a misc / co_misc review row exists (push C item 1): a pending Misc step is open, so it shows.
                    || (reviewSections || []).some((r) => ['misc', 'co_misc'].includes(String(r.section || '').toLowerCase())));
                  const gm = (part) => <GuidedModuleActions part={part} borrowerId={id} token={token} onChanged={fetchData} loxConditions={loxConditions} />;
                  const guided = {
                    // 🔑 THE APPROVED REDESIGN (Jesse, 2026-10-26): each step shows its own flags (the step that owns the
                    // flag's document); Identity keeps only identity flags; Income and Assets are the guided steps
                    // (figures, the breakdown, Accept / Change); the old steps 5–6 card is split onto Docs (re-check),
                    // Income / Assets (disputes) and Credit (debts).
                    stepTop: {
                      // 🔑 Each step's own open requests, explanations and letters sit on it (Jesse, 2026-10-26 — utils/moduleItems).
                      identity: ({ flags, items, reload }) => (<>
                        <IdentityRecordRow user={user} idVerification={idVerification} documents={documents} />
                        <StepItems API={API} borrowerId={id} headers={headers} items={items} module="identity" onChanged={async () => { await reload(); await fetchData(); }} />
                        <FlagList API={API} borrowerId={id} headers={headers} flags={flags} module="identity" onChanged={reload} />
                      </>),
                      income: ({ flags, items, reload }) => (
                        <GuidedIncomeStep API={API} borrowerId={id} headers={headers} flags={flags} itemsOpen={(items.acceptCounts && items.acceptCounts.income) || 0}
                          onChanged={async () => { await reload(); await fetchData(); }} dispute={gm('dispute-income')}
                          comparison={<BankStatementComparison API={API} borrowerId={id} headers={headers} />}
                          items={<StepItems API={API} borrowerId={id} headers={headers} items={items} module="income" onChanged={async () => { await reload(); await fetchData(); }} />} />
                      ),
                      assets: ({ flags, items, reload }) => (
                        <GuidedAssetsStep API={API} borrowerId={id} headers={headers} flags={flags} itemsOpen={(items.acceptCounts && items.acceptCounts.assets) || 0}
                          onChanged={async () => { await reload(); await fetchData(); }} dispute={gm('dispute-assets')}
                          items={<StepItems API={API} borrowerId={id} headers={headers} items={items} module="assets" onChanged={async () => { await reload(); await fetchData(); }} />} />
                      ),
                      housing: ({ items, reload }) => (<>
                        <RentalPropertiesStaff API={API} borrowerId={id} headers={headers} onChanged={async () => { await reload(); await fetchData(); }} />
                        <RentHistoryTable API={API} borrowerId={id} headers={headers} onChanged={async () => { await reload(); await fetchData(); }} />
                        <StepItems API={API} borrowerId={id} headers={headers} items={items} module="housing" onChanged={async () => { await reload(); await fetchData(); }} />
                      </>
                      ),
                      credit: ({ flags, items, reload }) => (<>
                        <StepItems API={API} borrowerId={id} headers={headers} items={items} module="credit" onChanged={async () => { await reload(); await fetchData(); }} />
                        <FlagList API={API} borrowerId={id} headers={headers} flags={flags} module="credit" onChanged={reload} />
                        {/* 🔑 LOX Level 2 (push C item 6): the staff-only credit flags, each with its AFM v2 cite. */}
                        <CreditRuleFlags API={API} borrowerId={id} headers={headers} />
                        {gm('letters')}
                        {gm('debts')}
                        <RereadStoredReport borrowerId={user?.id || id} headers={headers} api={API} onDone={fetchData} />
                        {coBorrower?.id && <RereadStoredReport borrowerId={coBorrower.id} headers={headers} api={API} onDone={fetchData} />}
                      </>),
                      // 🔑 Misc lists its own open items too (push C item 1 — utils/moduleItems MODULES includes misc).
                      misc: ({ items, reload }) => (
                        <StepItems API={API} borrowerId={id} headers={headers} items={items} module="misc" onChanged={async () => { await reload(); await fetchData(); }} />
                      ),
                      // 🔑 The last step, "1003 and summary" (Jesse, 2026-10-27 — routes/urla1003): the 1003 and MISMO against the
                      // decision, the 1003 versions and the UW summary; Accept is required before an offer (utils/fileReview).
                      urla: ({ reload, goTo }) => (
                        <Guided1003Step API={API} borrowerId={id} headers={headers} goTo={goTo} onChanged={async () => { await reload(); await fetchData(); }} />
                      ),
                    },
                    // 🔑 Docs is not a step (Jesse, 2026-10-26): "All documents" on every step, the re-check beside it.
                    allDocuments: <DocumentsReadOnly documents={documents} API={API} borrowerId={id} headers={headers} onUploaded={fetchData} />,
                    documentCount: (documents || []).filter((d) => !/__(replaced|unplaced|removed)$/.test(String(d.document_type || ''))).length,
                    recheck: gm('recheck-inline'),
                    extraSteps: [
                      { id: 'decision', label: 'Decision', shortLabel: 'Decision', node: gm('decision') },
                      { id: 'consents', label: 'Consents', shortLabel: 'Consents', node: gm('consents') },
                      { id: 'urla', label: '1003 and summary', shortLabel: '1003 and summary', node: null, statusUrl: `${API}/api/admin/borrowers/${id}/1003-step?regenerate=0` },
                    ],
                  };
                  return (<>
                    {showReviewModule && (
                      <div data-testid="uw-module-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(17,24,39,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                        <div style={{ width: '100%', maxWidth: 1100, maxHeight: '90vh', overflowY: 'auto', borderRadius: 16, boxShadow: '0 25px 60px rgba(0,0,0,0.4)', background: '#ffffff' }}>
                          <ReviewModule user={user} coBorrower={coBorrower} formData={formData} reviewSections={reviewSections} documents={documents}
                            additionalNeeds={additionalNeeds} conditions={conditions} loxConditions={loxConditions} hasMisc={hasMisc} loanConnection={loanConnection}
                            headers={headers} API={API} id={id} onSectionUpdated={fetchData} onClose={() => setShowReviewModule(false)}
                            aiExtractions={aiExtractions} onExtractionChanged={() => { fetchAIExtractions(); fetchData(); }} idVerification={idVerification}
                            guided={guided} initialStepId={moduleStepId} />
                        </div>
                      </div>
                    )}
                    <GuidedReviewTab borrowerId={id} token={token} onChanged={fetchData} user={user} fraudFlags={data?.fraudFlags} idVerification={idVerification} refreshKey={showReviewModule ? 1 : 0}
                      onOpenModule={(stepId) => { setModuleStepId(stepId); setShowReviewModule(true); }} />
                  </>);
                })()
              ) : guidedView?.error ? (
                // 🚨 A FAILED /guided-view IS AN ERROR STATE, NEVER AN EMPTY OR OTHER TAB (cleanup Phase 2 — the classic
                // Review tab it used to fall back to is gone).
                <div data-testid="review-tab-load-error" role="alert" style={{ ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>The Review tab couldn't be loaded just now.</div>
                  <button type="button" data-testid="review-tab-retry" onClick={() => fetchData()}
                    style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>
                    Retry
                  </button>
                </div>
              ) : (
                <div data-testid="review-tab-loading" style={{ ...card, fontSize: '13px', color: '#374151' }}>Loading…</div>
              )}

              {/* Underwriting module notes — same thread as the Underwriting
                  Modules dashboard card. */}
              <div style={card}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Notes</h3>
                <ModuleNotes moduleType="underwriting" borrowerId={id} />
              </div>
            </div>
            );
          })()}

          {/* ── TAB 5: Financials ───────────────────────────────────────── */}
          {activeTab === 'financial_details' && (() => {
            const reloadFin = () => {
              finLoadedRef.current = false;
              const coId = data?.loanConnection?.coborrower_id;
              const coName = data?.coBorrower ? `${data.coBorrower.first_name} ${data.coBorrower.last_name}` : 'Co-Borrower';
              Promise.all([
                axios.get(`${API}/api/admin/borrowers/${id}/income`, { headers }).catch(() => ({ data: {} })),
                axios.get(`${API}/api/admin/borrowers/${id}/assets`, { headers }).catch(() => ({ data: {} })),
                axios.get(`${API}/api/admin/borrowers/${id}/liabilities`, { headers }).catch(() => ({ data: {} })),
                coId ? axios.get(`${API}/api/admin/borrowers/${coId}/income`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
                coId ? axios.get(`${API}/api/admin/borrowers/${coId}/assets`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
                coId ? axios.get(`${API}/api/admin/borrowers/${coId}/liabilities`, { headers }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
              ]).then(([iR, aR, lR, coIR, coAR, coLR]) => {
                const primaryInc = (iR.data?.income || []).map(i => ({ ...i, borrower_type: i.borrower_type || 'primary' }));
                const coInc = (coIR.data?.income || []).map(i => ({ ...i, borrower_type: 'coborrower', borrower_label: coName }));
                setFinIncome([...primaryInc, ...coInc]); setFinGMI((iR.data?.totalGMI || 0) + (coIR.data?.totalGMI || 0));
                const primaryAssets = (aR.data?.assets || []).map(a => ({ ...a, owner_type: a.owner_type || 'primary', owner_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Primary' }));
                const coAssets = (coAR.data?.assets || []).map(a => ({ ...a, owner_type: 'coborrower', owner_name: coName }));
                setFinAssets([...primaryAssets, ...coAssets]); setFinTotalAssets((aR.data?.totalAssets || 0) + (coAR.data?.totalAssets || 0));
                const primaryLiabs = (lR.data?.liabilities || []).map(l => ({ ...l, owner_type: l.owner_type || 'primary' }));
                const coLiabs = (coLR?.data?.liabilities || []).map(l => ({ ...l, owner_type: 'coborrower', owner_name: coName }));
                setFinLiabs([...primaryLiabs, ...coLiabs]);
                const liabTotal = (lR.data?.summary?.totalMonthlyPayments ?? lR.data?.totalMonthly ?? 0)
                                + (coLR?.data?.summary?.totalMonthlyPayments ?? coLR?.data?.totalMonthly ?? 0);
                setFinTotalLiab(liabTotal);
              });
            };

            const fmt$ = v => v ? '$' + parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '$0.00';
            const INCOME_LABELS = { base_employment:'Employment', schedule_c:'Schedule C', '1099':'1099', bank_statement:'Bank Statement', w2_only:'W-2 Only' };
            const LIAB_LABELS = { mortgage:'Mortgage', auto:'Auto', student:'Student', credit_card:'Credit Card', installment:'Installment', other:'Other' };

            // Contract Price Breakdown — visible once the A-B PSA fully
            // executes and purchase_price_locked flips TRUE. Sourced from
            // existing columns: doorly_purchase_price (A-B price),
            // resale_adjustment (percent), contract_purchase_price (B-C
            // price). The resale-adjustment dollar amount is computed as
            // contract − doorly (avoids a second multiply on the wire).
            const lockedDoorly  = Number(user.doorly_purchase_price) || 0;
            const lockedBorrower = Number(user.contract_purchase_price) || 0;

            const fmtDateLocked = (v) => {
              if (!v) return '';
              try { return new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
              catch (_) { return ''; }
            };

            return (
            <div>
              {/* Contract Price Breakdown — gated on purchase_price_locked
                  so it only renders post A-B PSA execution. Read-only summary
                  of how the canonical borrower purchase price was derived. */}
              {user.purchase_price_locked && lockedBorrower > 0 && (
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 16,
                  fontSize: 13,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Contract Price Breakdown
                    <span style={{
                      background: '#3a6ad9', color: '#fff',
                      borderRadius: 4, padding: '1px 7px',
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.03em',
                    }}>C</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#374151' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Doorly Purchase Price (A-B)</span>
                      <span>{fmt$(lockedDoorly)}</span>
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontWeight: 700, borderTop: '1px solid #bfdbfe',
                      paddingTop: 6, marginTop: 4, color: '#111827',
                    }}>
                      <span>Borrower Contract Price (B-C)</span>
                      <span>{fmt$(lockedBorrower)}</span>
                    </div>
                  </div>
                  {user.purchase_price_locked_at && (
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                      Locked at A-B PSA execution · {fmtDateLocked(user.purchase_price_locked_at)}
                    </div>
                  )}
                </div>
              )}

              {/* Financial KPI Bar */}
              <div className="admin-two-col-grid" style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {(() => {
                  // Financials KPI 'Gross Monthly Income' reads finGMI from
                  // borrower_income rows summed by the /income endpoint.
                  // That stays $0 until the admin clicks 'Apply' on the AI's
                  // recommended income (which inserts a borrower_income
                  // row) OR the borrower confirms Phase 2 on the wizard.
                  // To match the Overview-tab behavior, fall through to
                  // the AI's Phase 1 qualifyingMonthly with a pending pill
                  // when nothing's been applied yet. Admin sees the AI's
                  // number immediately instead of a misleading $0.
                  // 🚨 TOLERANT OF THE MODEL'S FORMATTING — see
                  // utils/parseModelNumber.js. Number("5,388") is NaN, which
                  // made displayGmi NaN and the KPI read $0.
                  // ⚠️ THIS EXACT LINE APPEARS TWICE IN THIS FILE. Both were
                  // wrong in the same way; a fix to one would have left the
                  // other reading $0 on whichever surface rendered it.
                  const aiPhase1 = parseModelAmount(
                    user?.readycheck?.latestAnalysis?.incomeAssessment?.qualifyingMonthly) || 0;
                  const displayGmi = finGMI > 0 ? finGMI : aiPhase1;
                  const pending = finGMI <= 0 && aiPhase1 > 0;
                  return (
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>Gross Monthly Income</span>
                        {pending && (
                          <span
                            title="The AI's Phase 1 calculation is shown. Click 'Apply' on the ReadyCheck card OR the borrower must confirm Phase 2 to write this into the borrower_income table."
                            style={{
                              fontSize: 9, fontWeight: 700,
                              padding: '1px 5px', borderRadius: 3,
                              background: '#fffbeb', color: '#92400e',
                              border: '1px solid #fde68a',
                              letterSpacing: 0.3,
                            }}
                          >
                            AI · PENDING
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '800', color: pending ? '#92400e' : (displayGmi > 0 ? '#acf44e' : '#9ca3af') }}>{fmt$(displayGmi)}</div>
                      {/* ── 🚨 TWO PATHS WRITE THIS, AND THE EARLIER COPY NAMED
                             ONLY ONE ──────────────────────────────────────────
                          Jesse: "a few readycheck runs ago, there was an income
                          listed on the Financials tab. and this was before I
                          applied anything from the Review tab (which still is
                          not even possible to do)."

                          🔑 HE IS RIGHT, AND THE FIRST VERSION OF THIS MESSAGE
                          WAS WRONG. readycheckFinancials.applyReadyCheckIncome
                          reads the ReadyCheck run's OWN output
                          (ai_analysis_results, section='readycheck',
                          incomeAssessment.qualifyingMonthly) and writes a
                          borrower_income row plus users.gross_monthly_income.
                          So a ReadyCheck run absolutely can populate this tab
                          with nothing applied from the Review tab.

                          ⚠️ BUT IT FIRES ON THE CONFIRM, NOT ON THE RUN. The
                          call sites are POST /readycheck/confirm-income,
                          /confirm-assets (and the retired /reprice) — requireAuth,
                          the borrower. The staff DoorlyDesk trigger
                          (POST /borrowers/:id/trigger-readycheck) mints a
                          borrower token and runs the ANALYSIS only; it never
                          confirms. So a file parked mid-Step-2 has its figures
                          computed and stored and still reads $0 here.

                          🚨 SENDING STAFF TO THE REVIEW TAB IN THAT STATE IS A
                          WRONG INSTRUCTION — the blocker is the borrower's
                          confirm, and the Review-tab Apply is a separate,
                          additional path that needs its own analysis run. */}
                      {!displayGmi && finIncome.length === 0 && (
                        <div style={{ fontSize: 10.5, color: '#92400e', marginTop: 2, lineHeight: 1.35 }}>
                          No income rows yet. These are written when the borrower confirms the
                          calculated income on ReadyCheck Step 2 — or by Apply to Financials after
                          running the Income analysis on the Review tab.
                        </div>
                      )}
                      {!displayGmi && renderRcPending('income')}
                    </div>
                  );
                })()}
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Monthly Debt</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#ff082f' }}>{fmt$(finTotalLiab || user.total_monthly_debt || 0)}</div>
                </div>
                <div>
                  {/* ── 🚨 $0 AND "NOTHING APPLIED YET" ARE DIFFERENT THINGS ──────
                      Jesse: "the Financials tab is showing $0 under Assets … I am
                      so confused why there are so many breaks" — and then: "it
                      may be waiting on the admin to apply it". That was right.

                      🔑 THIS TAB SHOWS WHAT HAS BEEN WRITTEN, NOT WHAT THE AI
                      CALCULATED — and TWO paths write it. The Review tab's
                      Apply-to-Financials CTA (PATCH /financials) is one. The
                      other is the borrower's own ReadyCheck confirm:
                      /confirm-assets calls applyReadyCheckAssets, which sums
                      their declared sources into users.liquid_assets. Both are
                      real, and before EITHER has happened a genuine $0 and an
                      unstarted file are the same number on screen.

                      ⚠️ THE STAFF RUN IS NOT ONE OF THEM. trigger-readycheck
                      runs the analysis and stops; the writeback hangs off the
                      confirm, which only the borrower can do.

                      ⚠️ SO THE EMPTY CASE SAYS WHICH IT IS. A bare $0 next to a
                      file with twenty-one documents reads as a bug, and it cost
                      a long diagnosis to establish it was not one. */}
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Total Assets</div>
                  {(() => {
                    const blobTotal = (() => { try { const a = Array.isArray(user.asset_sources) ? user.asset_sources : JSON.parse(user.asset_sources || '[]'); return a.reduce((s, x) => s + (Number(x.amount) || 0), 0); } catch { return 0; } })();
                    const shown = finTotalAssets || blobTotal;
                    if (shown > 0) {
                      return <div style={{ fontSize: '20px', fontWeight: '800', color: '#3a6ad9' }}>{fmt$(shown)}</div>;
                    }
                    return (
                      <>
                        <div style={{ fontSize: '20px', fontWeight: '800', color: '#9ca3af' }}>{fmt$(0)}</div>
                        <div style={{ fontSize: 10.5, color: '#92400e', marginTop: 2, lineHeight: 1.35 }}>
                          {finAssets.length === 0
                            ? 'No asset rows yet. These are written when the borrower confirms their assets on '
                              + 'ReadyCheck Step 2 — or by Apply to Financials after running the Assets analysis '
                              + 'on the Review tab.'
                            : 'Rows on file carry no amount — open one and add a balance.'}
                        </div>
                        {renderRcPending('assets')}
                      </>
                    );
                  })()}
                </div>
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Back DTI</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: backDti && Number(backDti) > 43 ? '#ff082f' : '#acf44e' }}>
                    {backDti ? backDti + '%' : '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Qualifying FICO</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#3a6ad9' }}>{(() => {
                    // Compute middle from the three bureau scores when all
                    // three are present — that's the authoritative middle FICO.
                    // Fall back to fico_score_actual; never trust credit_score
                    // alone since it can drift from the canonical middle.
                    const bureaus = [user.xactus_equifax_score, user.xactus_experian_score, user.xactus_transunion_score].map(Number).filter(Boolean);
                    if (bureaus.length === 3) return bureaus.sort((a, b) => a - b)[1];
                    return user.fico_score_actual || user.credit_score || '—';
                  })()}</div>
                </div>
              </div>

              {/* Credit Report — Primary + Co-Borrower side-by-side.
                  Each column reads scores from its own source: primary
                  from user.xactus_*, co-borrower from coBorrower.xactus_*
                  with a fallback to the denormalized user.coborrower_*_*
                  columns (written by xactus.js on joint pulls AND by the
                  manual Lisa-style data entry path). When there's no
                  co-borrower the layout collapses back to a single column.
                  Read-only — pull action lives on the Review tab's Credit
                  File Review section once unlocked. */}
              {(() => {
                // Build a renderer that takes a column-scope object and
                // emits bureau tiles + middle + on-file flag chips +
                // analyzer-issue list. Keeps the JSX flat instead of
                // duplicating the entire markup twice.
                const renderCreditColumn = ({ title, eq, ex, tu, ficoActual, bankruptcy, foreclosure, collections, tradelines, issues, pulledAt, source }) => {
                  const bureaus = [eq, ex, tu].map(Number).filter(Boolean);
                  const mid = bureaus.length === 3 ? bureaus.sort((a, b) => a - b)[1] : (ficoActual || null);
                  const scoreColor = mid >= 700 ? '#2d6a0a' : mid >= 620 ? '#7a5200' : '#ff082f';
                  const scoreLabel = mid >= 700 ? 'Strong' : mid >= 620 ? 'Eligible' : mid >= 500 ? 'Marginal' : 'Review Required';
                  const hasAny = mid || eq || ex || tu;
                  return (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</div>
                        {pulledAt && (
                          <div style={{ fontSize: 10, color: '#9ca3af' }}>
                            {new Date(pulledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {source || 'Xactus'}
                          </div>
                        )}
                      </div>
                      {!hasAny ? (
                        <div style={{ padding: '14px', background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                          No credit pull on file
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                            {[
                              { label: 'Equifax',    value: eq, color: '#d32f2f' },
                              { label: 'Experian',   value: ex, color: '#1565c0' },
                              { label: 'TransUnion', value: tu, color: '#2e7d32' },
                            ].map(b => (
                              <div key={b.label} style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: 10, textAlign: 'center' }}>
                                <div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{b.label}</div>
                                <div style={{ fontSize: 20, fontWeight: 900, color: b.value ? b.color : '#d1d5db' }}>{b.value || '—'}</div>
                              </div>
                            ))}
                          </div>
                          {mid && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f0f4ff', border: '1px solid #c5d3f5', borderRadius: 8, marginBottom: 10 }}>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Qualifying (Middle)</div>
                                <div style={{ fontSize: 22, fontWeight: 900, color: '#3a6ad9' }}>{mid}</div>
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 16, background: scoreColor + '20', color: scoreColor, border: `1px solid ${scoreColor}40`, whiteSpace: 'nowrap' }}>{scoreLabel}</span>
                            </div>
                          )}
                          {(bankruptcy || foreclosure || (collections && collections > 0)) && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                              {bankruptcy   && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#fff0f3', color: '#ff082f', border: '1px solid #ffc5ca' }}>Bankruptcy</span>}
                              {foreclosure  && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#fff0f3', color: '#ff082f', border: '1px solid #ffc5ca' }}>Foreclosure</span>}
                              {collections > 0 && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#fffbef', color: '#7a5200', border: '1px solid #ffe4a0' }}>{collections} Collection{collections !== 1 ? 's' : ''}</span>}
                            </div>
                          )}
                          {tradelines > 0 && (
                            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>{tradelines} tradeline{tradelines !== 1 ? 's' : ''} imported</div>
                          )}
                          {issues.length > 0 && (
                            <div style={{ marginTop: 6, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Flags ({issues.length})</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {issues.slice(0, 10).map((iss, i) => {
                                  const sev = iss.severity || 'medium';
                                  const sevColor = sev === 'high' || sev === 'blocking' ? '#ff082f' : sev === 'medium' || sev === 'significant' ? '#7a5200' : '#6b7280';
                                  const sevBg    = sev === 'high' || sev === 'blocking' ? '#fff0f3' : sev === 'medium' || sev === 'significant' ? '#fffbef' : '#f9fafb';
                                  return (
                                    <div key={iss.id || i} style={{ padding: '6px 8px', background: sevBg, borderLeft: `3px solid ${sevColor}`, borderRadius: 4, fontSize: 11, color: '#374151' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                        <span style={{ fontSize: 9, fontWeight: 800, color: sevColor, textTransform: 'uppercase' }}>{sev}</span>
                                        <span style={{ fontWeight: 700, color: '#111827' }}>{iss.title || iss.type || iss.event_type || 'Flag'}</span>
                                      </div>
                                      <div style={{ color: '#4b5563', lineHeight: 1.4 }}>{iss.message || iss.description || ''}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                };

                const primaryEq = user.xactus_equifax_score;
                const primaryEx = user.xactus_experian_score;
                const primaryTu = user.xactus_transunion_score;
                const primaryHasAny = primaryEq || primaryEx || primaryTu || user.fico_score_actual || user.credit_score;
                // Co-borrower: prefer their own users row (Craig's fico_score_actual etc),
                // fall back to the denormalized columns on the primary's row that
                // xactus.js + manual entry write for joint pulls.
                const coEq = coBorrower?.xactus_equifax_score    || user.coborrower_xactus_equifax_score;
                const coEx = coBorrower?.xactus_experian_score   || user.coborrower_xactus_experian_score;
                const coTu = coBorrower?.xactus_transunion_score || user.coborrower_xactus_transunion_score;
                const coFico = coBorrower?.fico_score_actual || user.coborrower_fico_score;
                const coHasAny = !!(coEq || coEx || coTu || coFico);

                return (
                  <div style={{ ...card, marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>Credit Report</h3>
                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                          {/* 🚨 "PULLED" IS WRONG ON A REISSUED FILE, and it printed
                              an internal marker as the agency. xactus_pull_status
                              'received' means Doorly holds a report it did NOT pull —
                              saying "pulled" sends staff looking for an inquiry that
                              does not exist. credit_furnished_by carries the reseller's
                              real name; fico_score_source is a provenance marker like
                              'xactus_manual' and was never an agency name. */}
                          {user.date_credit_pulled
                            ? (user.xactus_pull_status === 'received'
                                ? `Received ${new Date(user.credit_received_at || user.date_credit_pulled).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} from ${user.credit_furnished_by || 'the broker\u2019s reseller'}${user.credit_original_pulled_at ? ` · originally pulled ${new Date(user.credit_original_pulled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''} · no Doorly inquiry`
                                : `Last pulled ${new Date(user.date_credit_pulled).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${user.credit_furnished_by || user.fico_score_source || 'Xactus'}`)
                            : 'Not yet pulled — credit pulls happen from the Review tab once Credit File Review is unlocked.'}
                        </p>
                      </div>
                    </div>
                    {creditError && (
                      <div style={{ padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '10px', marginBottom: '14px', fontSize: '13px', color: '#ff082f', fontWeight: '600' }}>{creditError}</div>
                    )}

                    {/* ── 🚨 THE CREDIT PATH AND WHY ──────────────────────────
                        Staff had no answer to "why did this borrower get an
                        inquiry when their broker already pulled" — the decision
                        was a console.log and a fall-through, recorded nowhere.
                        Shown only on files that HAVE a path, so it does not add
                        an empty row to the 33 borrowers who never had a broker. */}
                    {/* ── 🚨 THREE STATES, NOT TWO ─────────────────────────
                        This block used to ask "is it reissue?" and render
                        everything else as NEW PULL. 'blocked' then read as
                        "Doorly obtains its own report" on a file where Doorly
                        may not obtain anything — the borrower has not signed
                        their authorization, and a new pull is itself credit use.
                        A staff member reading that would wait for a report that
                        is never coming. */}
                    {user.credit_path && (
                      <div style={{
                        padding: '10px 12px', borderRadius: 10, marginBottom: 14, fontSize: 12.5,
                        background: user.credit_path === 'reissue' ? '#f0fdf4'
                          : user.credit_path === 'blocked' ? '#fef2f2' : '#fffbeb',
                        border: `1px solid ${user.credit_path === 'reissue' ? '#bbf7d0'
                          : user.credit_path === 'blocked' ? '#fecaca' : '#fde68a'}`,
                        color: user.credit_path === 'reissue' ? '#166534'
                          : user.credit_path === 'blocked' ? '#991b1b' : '#92400e',
                      }}>
                        <div style={{ fontWeight: 800, marginBottom: 2 }}>
                          {user.credit_path === 'reissue'
                            ? 'Credit path: REISSUE — no Doorly inquiry'
                            : user.credit_path === 'blocked'
                              ? 'Credit path: BLOCKED — no credit may be pulled, reissued or used'
                              : 'Credit path: NEW PULL — Doorly obtains its own report'}
                        </div>
                        {/* ⚠️ THE REASON CODE, NOT JUST THE VERDICT. A path with no
                            reason tells staff what happened and nothing about why,
                            which is the question they are always actually asking. */}
                        <div style={{ opacity: 0.9 }}>{user.credit_path_reason}</div>
                        {user.credit_furnished_by && (
                          <div style={{ marginTop: 3, opacity: 0.85 }}>
                            Reseller on file: {user.credit_furnished_by}
                          </div>
                        )}
                      </div>
                    )}

                    {(primaryHasAny || coHasAny) && (
                      <div style={{ display: 'grid', gridTemplateColumns: coBorrower ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 12 }}>
                        {renderCreditColumn({
                          title:        `${user.first_name || 'Primary'}${coBorrower ? '' : ' Borrower'}`,
                          eq:           primaryEq,
                          ex:           primaryEx,
                          tu:           primaryTu,
                          ficoActual:   user.fico_score_actual || user.credit_score,
                          bankruptcy:   user.xactus_bankruptcy_flag,
                          foreclosure:  user.xactus_foreclosure_flag,
                          collections:  user.xactus_collections_count,
                          tradelines:   user.xactus_tradeline_count,
                          issues:       creditIssues,
                          pulledAt:     user.date_credit_pulled,
                          source:       user.fico_score_source,
                        })}
                        {coBorrower && renderCreditColumn({
                          title:        `${coBorrower.first_name || 'Co'}-Borrower`,
                          eq:           coEq,
                          ex:           coEx,
                          tu:           coTu,
                          ficoActual:   coFico,
                          // Strictly per-borrower. Collections, bankruptcy,
                          // foreclosure, tradeline count, and the flag list
                          // come from the co-borrower's own row + their own
                          // credit_report_events. Empty here when the joint
                          // pull's writer only persisted these on the primary
                          // side — see backend/utils/xactus.js. Per-borrower
                          // population needs the co-borrower's slice from the
                          // pull response stored on their users row.
                          bankruptcy:   coBorrower.xactus_bankruptcy_flag,
                          foreclosure:  coBorrower.xactus_foreclosure_flag,
                          collections:  coBorrower.xactus_collections_count,
                          tradelines:   coBorrower.xactus_tradeline_count,
                          issues:       coCreditIssues,
                          pulledAt:     coBorrower.date_credit_pulled,
                          source:       coBorrower.fico_score_source,
                        })}
                      </div>
                    )}

                    <CreditRereadSnapshot reread={creditReread} />

                    {creditHistory.length > 0 && (
                      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '14px', marginTop: '14px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Pull History</div>
                        {creditHistory.slice(0, 3).map(pull => (
                          <div key={pull.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            <span>{new Date(pull.pulled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                            <span>Mid: <strong style={{ color: '#111827' }}>{pull.middle_score || '—'}</strong></span>
                            <span style={{ color: pull.pull_status === 'complete' ? '#2d6a0a' : '#ff082f' }}>{pull.pull_status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── ASSETS ── */}
              {(() => {
                const borrowerAssets = finAssets.length > 0 ? finAssets : (() => { try { return Array.isArray(user.asset_sources) ? user.asset_sources : JSON.parse(user.asset_sources || '[]'); } catch { return []; } })();
                const totalBorrowerAssets = finAssets.length > 0 ? finTotalAssets : borrowerAssets.reduce((s, a) => s + (Number(a.amount || a.current_balance) || 0), 0);
                const ASSET_TYPE_LABELS = { checking: 'Checking', savings: 'Savings', investment: 'Investment', retirement: 'Retirement', 'Personal Funds': 'Personal Funds', 'Gift Funds': 'Gift Funds', 'Retirement Account (401k/IRA)': 'Retirement', 'Investment Account': 'Investment', 'Sale of Property': 'Sale of Property', other: 'Other', Other: 'Other' };
                const inputSt = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
                const labelSt = { fontSize: '10px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' };
                return (
                  <div style={{ ...card, marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>Assets</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#3a6ad9' }}>{fmt$(totalBorrowerAssets)}</span>
                        <button onClick={() => setExpandedAssetId('new')}
                          style={{ padding: '5px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>+ Asset</button>
                      </div>
                    </div>

                    {/* Add new asset form */}
                    {expandedAssetId === 'new' && (
                      <div style={{ border: '1px solid #3a6ad9', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: '#fafbff' }}>
                        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                          <div>
                            <label style={labelSt}>Account Type</label>
                            <select value={assetForm.asset_type} onChange={e => setAssetForm(p => ({ ...p, asset_type: e.target.value }))} style={inputSt}>
                              <option value="checking">Checking</option><option value="savings">Savings</option><option value="investment">Investment</option><option value="retirement">Retirement</option><option value="other">Other</option>
                            </select>
                          </div>
                          <div><label style={labelSt}>Institution</label><input value={assetForm.institution_name} onChange={e => setAssetForm(p => ({ ...p, institution_name: e.target.value }))} placeholder="Bank name" style={inputSt} /></div>
                          <div><label style={labelSt}>Last 4 Digits</label><input value={assetForm.account_last4} onChange={e => setAssetForm(p => ({ ...p, account_last4: e.target.value.replace(/\D/g, '').slice(0, 4) }))} placeholder="1234" maxLength={4} style={inputSt} /></div>
                        </div>
                        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                          <div><label style={labelSt}>Balance ($)</label><input type="number" value={assetForm.current_balance} onChange={e => setAssetForm(p => ({ ...p, current_balance: e.target.value }))} placeholder="0.00" style={inputSt} /></div>
                          <div><label style={labelSt}>Notes</label><input value={assetForm.notes} onChange={e => setAssetForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" style={inputSt} /></div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button onClick={() => { setExpandedAssetId(null); setAssetForm({ asset_type: 'checking', institution_name: '', account_last4: '', current_balance: '', notes: '' }); }}
                            style={{ padding: '7px 16px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Cancel</button>
                          <button disabled={savingAsset} onClick={async () => {
                            if (savingAsset) return;
                            setSavingAsset(true);
                            try {
                              await axios.post(`${API}/api/admin/borrowers/${id}/assets`, assetForm, { headers });
                              setExpandedAssetId(null); setAssetForm({ asset_type: 'checking', institution_name: '', account_last4: '', current_balance: '', notes: '' });
                              reloadFin(); fetchData();
                            } catch (err) {
                              alert('Failed to save asset: ' + (err.response?.data?.error || err.message));
                            } finally {
                              setSavingAsset(false);
                            }
                          }} style={{ padding: '7px 16px', background: savingAsset ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: savingAsset ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>{savingAsset ? 'Saving…' : 'Save'}</button>
                        </div>
                      </div>
                    )}

                    {borrowerAssets.length === 0 && expandedAssetId !== 'new' ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', background: '#f9fafb', borderRadius: '8px' }}>No assets added yet.</div>
                    ) : borrowerAssets.length > 0 && (
                      <div style={{ border: '1px solid #f3f4f6', borderRadius: '8px', overflow: 'hidden' }}>
                        {borrowerAssets.map((a, idx) => {
                          const assetId = a.id || idx;
                          const bal = Number(a.amount || a.current_balance) || 0;
                          // Excluded rows — set when AI Apply pushes a new
                          // recommendation; rendered greyed out with an
                          // Include toggle so admins can bring them back.
                          const excluded = a.include_in_closing === false;
                          return (
                            <div key={assetId}>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '10px 14px',
                                borderBottom: (expandedAssetId === assetId || idx < borrowerAssets.length - 1) ? '1px solid #f3f4f6' : 'none',
                                cursor: a.id ? 'pointer' : 'default',
                                background: expandedAssetId === assetId ? '#f9fafb' : (excluded ? '#fafafa' : '#fff'),
                                opacity: excluded ? 0.55 : 1,
                              }}
                                onClick={() => a.id && setExpandedAssetId(expandedAssetId === assetId ? null : assetId)}>
                                {a.id && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: expandedAssetId === assetId ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}><polyline points="9 18 15 12 9 6"/></svg>}
                                <div style={{ flex: 1 }}>
                                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827', textDecoration: excluded ? 'line-through' : 'none' }}>{ASSET_TYPE_LABELS[a.asset_type || a.source_type] || a.asset_type || a.source_type || 'Asset'}</span>
                                  {(a.institution_name || a.source_name) && <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '6px' }}>{a.institution_name || a.source_name}</span>}
                                  {a.account_last4 && <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '4px' }}>····{a.account_last4}</span>}
                                  {a.donor_name && <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '4px' }}>from {a.donor_name}</span>}
                                  {a.owner_type === 'coborrower' && <span style={{ fontSize: '10px', fontWeight: '700', color: '#3a6ad9', background: '#eff6ff', padding: '1px 6px', borderRadius: '4px', marginLeft: '6px' }}>{a.owner_name || 'Co-Borrower'}</span>}
                                  {excluded && <span style={{ fontSize: '10px', fontWeight: '700', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', padding: '1px 6px', borderRadius: '4px', marginLeft: '6px' }}>EXCLUDED</span>}
                                </div>
                                {excluded && a.id && (
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    axios.patch(`${API}/api/admin/borrowers/${id}/assets/${assetId}/include`, { include: true }, { headers })
                                      .then(() => { reloadFin(); fetchData(); })
                                      .catch(err => alert('Failed to include asset: ' + (err.response?.data?.error || err.message)));
                                  }} type="button"
                                    style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfcff8', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                                    Include
                                  </button>
                                )}
                                <span style={{ fontSize: '14px', fontWeight: '800', color: excluded ? '#9ca3af' : '#111827', fontFamily: 'monospace', textDecoration: excluded ? 'line-through' : 'none' }}>{fmt$(bal)}</span>
                              </div>
                              {/* Expanded edit */}
                              {expandedAssetId === assetId && a.id && (() => {
                                if (!editAssetForm || editAssetForm._id !== assetId) {
                                  setEditAssetForm({ _id: assetId, asset_type: a.asset_type || a.source_type || 'checking', institution_name: a.institution_name || a.source_name || '', account_last4: a.account_last4 || '', current_balance: a.current_balance || a.amount || '', notes: a.notes || '' });
                                }
                                const ed = editAssetForm?._id === assetId ? editAssetForm : a;
                                return (
                                  <div style={{ padding: '14px 14px 14px 38px', background: '#f9fafb', borderBottom: idx < borrowerAssets.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                    <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                      <div><label style={labelSt}>Account Type</label>
                                        <select value={ed.asset_type} onChange={e => setEditAssetForm(p => ({ ...p, asset_type: e.target.value }))} style={inputSt}>
                                          <option value="checking">Checking</option><option value="savings">Savings</option><option value="investment">Investment</option><option value="retirement">Retirement</option><option value="other">Other</option>
                                        </select></div>
                                      <div><label style={labelSt}>Institution</label><input value={ed.institution_name || ''} onChange={e => setEditAssetForm(p => ({ ...p, institution_name: e.target.value }))} style={inputSt} /></div>
                                      <div><label style={labelSt}>Last 4</label><input value={ed.account_last4 || ''} onChange={e => setEditAssetForm(p => ({ ...p, account_last4: e.target.value.replace(/\D/g, '').slice(0, 4) }))} maxLength={4} style={inputSt} /></div>
                                    </div>
                                    <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                      <div><label style={labelSt}>Balance ($)</label><input type="number" value={ed.current_balance || ''} onChange={e => setEditAssetForm(p => ({ ...p, current_balance: e.target.value }))} style={inputSt} /></div>
                                      <div><label style={labelSt}>Notes</label><input value={ed.notes || ''} onChange={e => setEditAssetForm(p => ({ ...p, notes: e.target.value }))} style={inputSt} /></div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                                      <button onClick={() => { if (window.confirm('Delete this asset?')) { axios.delete(`${API}/api/admin/borrowers/${id}/assets/${assetId}`, { headers }).then(() => { setExpandedAssetId(null); reloadFin(); fetchData(); }).catch(err => alert('Failed to delete asset: ' + (err.response?.data?.error || err.message))); } }}
                                        style={{ padding: '7px 14px', background: '#fff0f3', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Delete</button>
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => setExpandedAssetId(null)} style={{ padding: '7px 16px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Cancel</button>
                                        <button disabled={savingAsset} onClick={async () => {
                                          if (savingAsset) return;
                                          setSavingAsset(true);
                                          try {
                                            await axios.patch(`${API}/api/admin/borrowers/${id}/assets/${assetId}`, editAssetForm, { headers });
                                            setExpandedAssetId(null); reloadFin(); fetchData();
                                          } catch (err) {
                                            alert('Failed to save asset: ' + (err.response?.data?.error || err.message));
                                          } finally {
                                            setSavingAsset(false);
                                          }
                                        }}
                                          style={{ padding: '7px 16px', background: savingAsset ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: savingAsset ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>{savingAsset ? 'Saving…' : 'Save Changes'}</button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#f9fafb', borderTop: '1px solid #f3f4f6' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Total Assets</span>
                          <span style={{ fontSize: '15px', fontWeight: '900', color: '#111827', fontFamily: 'monospace' }}>{fmt$(totalBorrowerAssets)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── INCOME ── */}
              <div style={{ ...card, marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>Monthly Income</h3>
                  <button onClick={() => setExpandedIncomeId('new')}
                    style={{ padding: '5px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>+ Income</button>
                </div>

                {/* Add new income form */}
                {expandedIncomeId === 'new' && (() => {
                  const [f, setF] = [incomeForm, setIncomeForm];
                  const inputSt = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
                  const labelSt = { fontSize: '10px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' };
                  return (
                    <div style={{ border: '1px solid #3a6ad9', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: '#fafbff' }}>
                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        <div>
                          <label style={labelSt}>Borrower</label>
                          <select value={f.borrower_type} onChange={e => setF(p => ({ ...p, borrower_type: e.target.value, borrower_label: e.target.value === 'coborrower' ? (coBorrower ? `${coBorrower.first_name} ${coBorrower.last_name}` : 'Co-Borrower') : `${user.first_name} ${user.last_name}` }))} style={inputSt}>
                            <option value="primary">{user.first_name} {user.last_name} (Primary)</option>
                            {coBorrower && <option value="coborrower">{coBorrower.first_name} {coBorrower.last_name} (Co-Borrower)</option>}
                          </select>
                        </div>
                        <div>
                          <label style={labelSt}>Income Type</label>
                          <select value={f.income_type} onChange={e => setF(p => ({ ...p, income_type: e.target.value }))} style={inputSt}>
                            <option value="base_employment">Employment</option>
                            <option value="1099">1099</option>
                            <option value="schedule_c">Schedule C</option>
                            <option value="bank_statement">Bank Statement</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelSt}>Employer</label>
                          <input value={f.employer_name || ''} onChange={e => setF(p => ({ ...p, employer_name: e.target.value }))} placeholder="Employer name" style={inputSt} />
                        </div>
                      </div>
                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                        <div><label style={labelSt}>Base $/mo</label><input type="number" value={f.base_monthly || ''} onChange={e => setF(p => ({ ...p, base_monthly: e.target.value }))} placeholder="0" style={inputSt} /></div>
                        <div><label style={labelSt}>Bonus $/mo</label><input type="number" value={f.bonus_monthly || ''} onChange={e => setF(p => ({ ...p, bonus_monthly: e.target.value }))} placeholder="0" style={inputSt} /></div>
                        <div><label style={labelSt}>OT $/mo</label><input type="number" value={f.overtime_monthly || ''} onChange={e => setF(p => ({ ...p, overtime_monthly: e.target.value }))} placeholder="0" style={inputSt} /></div>
                        <div><label style={labelSt}>Comm $/mo</label><input type="number" value={f.commission_monthly || ''} onChange={e => setF(p => ({ ...p, commission_monthly: e.target.value }))} placeholder="0" style={inputSt} /></div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setExpandedIncomeId(null); setIncomeForm({ borrower_label: `${user.first_name} ${user.last_name}`, borrower_type: 'primary', income_type: 'base_employment', employer_name: '', base_monthly: '', bonus_monthly: '', overtime_monthly: '', commission_monthly: '' }); }}
                          style={{ padding: '7px 16px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Cancel</button>
                        <button onClick={async () => {
                          const uid = incomeForm.borrower_type === 'coborrower' && coBorrower ? coBorrower.id : id;
                          await axios.post(`${API}/api/admin/borrowers/${uid}/income`, incomeForm, { headers });
                          setExpandedIncomeId(null); setIncomeForm({ borrower_label: `${user.first_name} ${user.last_name}`, borrower_type: 'primary', income_type: 'base_employment', employer_name: '', base_monthly: '', bonus_monthly: '', overtime_monthly: '', commission_monthly: '' });
                          reloadFin();
                        }} style={{ padding: '7px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>Save</button>
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  // AI-pending callout — when the income TOTAL (finGMI) is
                  // \$0 but ReadyCheck has produced a qualifyingMonthly
                  // figure, surface it here so the empty state isn't a
                  // dead end. Gated on finGMI not finIncome.length —
                  // borrowers can have stub rows with \$0 amounts (e.g.
                  // Luc's auto-inserted schedule_c row id=23 with all
                  // zero monthly columns) that make .length === 1 but
                  // still represent zero applied income. Goes away once
                  // admin Apply lands a real amount that pushes finGMI > 0.
                  // 🚨 TOLERANT OF THE MODEL'S FORMATTING — see
                  // utils/parseModelNumber.js. Number("5,388") is NaN, which
                  // made displayGmi NaN and the KPI read $0.
                  // ⚠️ THIS EXACT LINE APPEARS TWICE IN THIS FILE. Both were
                  // wrong in the same way; a fix to one would have left the
                  // other reading $0 on whichever surface rendered it.
                  const aiPhase1 = parseModelAmount(
                    user?.readycheck?.latestAnalysis?.incomeAssessment?.qualifyingMonthly) || 0;
                  const showAiCallout = finGMI <= 0 && aiPhase1 > 0;
                  if (!showAiCallout) return null;
                  return (
                    <div style={{
                      padding: '12px 14px',
                      borderRadius: '8px',
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderLeft: '4px solid #f59e0b',
                      marginBottom: '12px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 12, flexWrap: 'wrap',
                    }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>
                          ReadyCheck AI — Pending Application
                        </div>
                        <div style={{ fontSize: 14, color: '#78350f', fontWeight: 600 }}>
                          AI calculated <strong>{fmt$(aiPhase1)}/mo</strong> from the uploaded docs.
                          {' '}Not yet written to the income table — click <strong>Apply</strong> on the ReadyCheck card (Review tab) to make it canonical.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTab('review')}
                        style={{
                          fontSize: 11, fontWeight: 700,
                          padding: '6px 14px',
                          background: '#3a6ad9', color: '#fff',
                          border: 'none', borderRadius: 6,
                          cursor: 'pointer', flexShrink: 0,
                          letterSpacing: 0.3,
                        }}
                      >
                        Go to Review Tab →
                      </button>
                    </div>
                  );
                })()}
                {finIncome.length === 0 && expandedIncomeId !== 'new' ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', background: '#f9fafb', borderRadius: '8px' }}>No income sources added yet.</div>
                ) : (
                  <div style={{ border: '1px solid #f3f4f6', borderRadius: '8px', overflow: 'hidden' }}>
                    {finIncome.map((inc, idx) => {
                      // Excluded — set when AI Apply pushes a recommendation;
                      // rendered greyed out with an Include toggle.
                      const incExcluded = inc.is_current === false;
                      return (
                      <div key={inc.id}>
                        {/* Row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: (expandedIncomeId === inc.id || idx < finIncome.length-1) ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', background: expandedIncomeId === inc.id ? '#f9fafb' : (incExcluded ? '#fafafa' : '#fff'), opacity: incExcluded ? 0.55 : 1 }}
                          onClick={() => setExpandedIncomeId(expandedIncomeId === inc.id ? null : inc.id)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: expandedIncomeId === inc.id ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}><polyline points="9 18 15 12 9 6"/></svg>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: inc.borrower_type === 'coborrower' ? '#3a6ad9' : '#111827', textDecoration: incExcluded ? 'line-through' : 'none' }}>
                              {inc.borrower_label}
                              <span style={{ color: '#9ca3af', fontWeight: '400', marginLeft: '6px' }}>{INCOME_LABELS[inc.income_type] || inc.income_type}</span>
                              {inc.employer_name && <span style={{ color: '#6b7280', fontWeight: '400' }}> at {inc.employer_name}</span>}
                              {incExcluded && <span style={{ fontSize: '10px', fontWeight: '700', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', padding: '1px 6px', borderRadius: '4px', marginLeft: '6px' }}>EXCLUDED</span>}
                            </div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                              Base: {fmt$(inc.base_monthly)}
                              {parseFloat(inc.bonus_monthly)>0 && ` + Bonus: ${fmt$(inc.bonus_monthly)}`}
                              {parseFloat(inc.overtime_monthly)>0 && ` + OT: ${fmt$(inc.overtime_monthly)}`}
                              {parseFloat(inc.commission_monthly)>0 && ` + Comm: ${fmt$(inc.commission_monthly)}`}
                            </div>
                          </div>
                          {inc.is_current && <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: '#edf1fb', color: '#3a6ad9' }}>CURRENT</span>}
                          {incExcluded && (
                            <button onClick={(e) => {
                              e.stopPropagation();
                              axios.patch(`${API}/api/admin/borrowers/${inc.user_id || id}/income/${inc.id}/include`, { include: true }, { headers })
                                .then(() => reloadFin())
                                .catch(err => alert('Failed to include income: ' + (err.response?.data?.error || err.message)));
                            }} type="button"
                              style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfcff8', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                              Include
                            </button>
                          )}
                          <span style={{ fontSize: '14px', fontWeight: '800', color: incExcluded ? '#9ca3af' : '#111827', fontFamily: 'monospace', textDecoration: incExcluded ? 'line-through' : 'none' }}>{fmt$(inc.monthly_total)}/mo</span>
                        </div>
                        {/* Expanded edit */}
                        {expandedIncomeId === inc.id && (() => {
                          const inputSt = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
                          const labelSt = { fontSize: '10px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' };
                          // Initialize edit form on first expand
                          if (!editIncomeForm || editIncomeForm._id !== inc.id) {
                            setEditIncomeForm({ _id: inc.id, employer_name: inc.employer_name || '', position_title: inc.position_title || '', income_type: inc.income_type || 'base_employment', base_monthly: inc.base_monthly || '', bonus_monthly: inc.bonus_monthly || '', overtime_monthly: inc.overtime_monthly || '', commission_monthly: inc.commission_monthly || '', other_monthly: inc.other_monthly || '' });
                          }
                          const ed = editIncomeForm?._id === inc.id ? editIncomeForm : inc;
                          return (
                            <div style={{ padding: '14px 14px 14px 38px', background: '#f9fafb', borderBottom: idx < finIncome.length-1 ? '1px solid #f3f4f6' : 'none' }}>
                              <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                <div><label style={labelSt}>Income Type</label>
                                  <select value={ed.income_type} onChange={e => setEditIncomeForm(p => ({ ...p, income_type: e.target.value }))} style={inputSt}>
                                    <option value="base_employment">Employment</option><option value="1099">1099</option><option value="schedule_c">Schedule C</option><option value="bank_statement">Bank Statement</option>
                                  </select></div>
                                <div><label style={labelSt}>Employer</label><input value={ed.employer_name || ''} onChange={e => setEditIncomeForm(p => ({ ...p, employer_name: e.target.value }))} style={inputSt} /></div>
                                <div><label style={labelSt}>Position</label><input value={ed.position_title || ''} onChange={e => setEditIncomeForm(p => ({ ...p, position_title: e.target.value }))} style={inputSt} /></div>
                              </div>
                              <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                <div><label style={labelSt}>Base $/mo</label><input type="number" value={ed.base_monthly || ''} onChange={e => setEditIncomeForm(p => ({ ...p, base_monthly: e.target.value }))} style={inputSt} /></div>
                                <div><label style={labelSt}>Bonus $/mo</label><input type="number" value={ed.bonus_monthly || ''} onChange={e => setEditIncomeForm(p => ({ ...p, bonus_monthly: e.target.value }))} style={inputSt} /></div>
                                <div><label style={labelSt}>OT $/mo</label><input type="number" value={ed.overtime_monthly || ''} onChange={e => setEditIncomeForm(p => ({ ...p, overtime_monthly: e.target.value }))} style={inputSt} /></div>
                                <div><label style={labelSt}>Comm $/mo</label><input type="number" value={ed.commission_monthly || ''} onChange={e => setEditIncomeForm(p => ({ ...p, commission_monthly: e.target.value }))} style={inputSt} /></div>
                                <div><label style={labelSt}>Other $/mo</label><input type="number" value={ed.other_monthly || ''} onChange={e => setEditIncomeForm(p => ({ ...p, other_monthly: e.target.value }))} style={inputSt} /></div>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                                <button onClick={() => { if (window.confirm('Delete this income source?')) { axios.delete(`${API}/api/admin/borrowers/${inc.user_id || id}/income/${inc.id}`, { headers }).then(() => { setExpandedIncomeId(null); reloadFin(); }); } }}
                                  style={{ padding: '7px 14px', background: '#fff0f3', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Delete</button>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button onClick={() => setExpandedIncomeId(null)} style={{ padding: '7px 16px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Cancel</button>
                                  <button onClick={async () => { await axios.patch(`${API}/api/admin/borrowers/${inc.user_id || id}/income/${inc.id}`, editIncomeForm, { headers }); setExpandedIncomeId(null); reloadFin(); }}
                                    style={{ padding: '7px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>Save Changes</button>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>


              {/* ── LIABILITIES ── */}
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>Monthly Liabilities</h3>
                  <button onClick={() => {
                    const name = prompt('Creditor name:');
                    const pmt = prompt('Monthly payment ($):');
                    if (!name || !pmt) return;
                    const type = prompt('Type (auto/mortgage/student/credit_card/installment/other):') || 'other';
                    axios.post(`${API}/api/admin/borrowers/${id}/liabilities`, { creditor_name: name, account_type: type, monthly_payment: parseFloat(pmt) || 0 }, { headers }).then(() => reloadFin());
                  }} style={{ padding: '5px 12px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>+ Liability</button>
                </div>
                {finLiabs.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', background: '#f9fafb', borderRadius: '8px' }}>No liabilities. Will auto-import from credit pull.</div>
                ) : (
                  <div style={{ border: '1px solid #f3f4f6', borderRadius: '8px', overflow: 'hidden' }}>
                    {finLiabs.map((l, idx) => (
                      <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: idx < finLiabs.length-1 ? '1px solid #f3f4f6' : 'none', opacity: l.exclude_from_dti ? 0.6 : 1 }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{l.creditor_name || 'Liability'}</span>
                          <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '6px' }}>{LIAB_LABELS[l.account_type] || l.account_type}</span>
                          {l.exclude_from_dti && <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', background: '#f4fee8', color: '#2d6a0a', marginLeft: '6px' }}>EXCLUDED</span>}
                          {l.outstanding_balance > 0 && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>Balance: ${parseFloat(l.outstanding_balance).toLocaleString()}</div>}
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: l.exclude_from_dti ? '#9ca3af' : '#111827', fontFamily: 'monospace' }}>{fmt$(l.monthly_payment)}/mo</span>
                        <button onClick={() => { axios.patch(`${API}/api/admin/borrowers/${id}/liabilities/${l.id}`, { exclude_from_dti: !l.exclude_from_dti }, { headers }).then(() => reloadFin()); }}
                          style={{ padding: '3px 8px', background: l.exclude_from_dti ? '#e5e7eb' : '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: '600', color: '#374151' }}>
                          {l.exclude_from_dti ? 'Include' : 'Exclude'}
                        </button>
                        <button onClick={() => { if (window.confirm('Delete?')) axios.delete(`${API}/api/admin/borrowers/${id}/liabilities/${l.id}`, { headers }).then(() => reloadFin()); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ff082f', fontSize: '16px' }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* ── TAB 6: Property ─────────────────────────────────────────── */}
          {activeTab === 'property_details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Underwriting-review gate moved into the Property Module's
                  Step 5 (Generate Offer) — the Generate Offer PDF button
                  itself disables with a tooltip when !reviewComplete. RE
                  desk can now work all earlier steps (listing agent,
                  disclosure, valuation, offer details) before underwriting
                  is fully accepted.

                  Threshold: < 4 means Application / Verification /
                  Approval / Shopping show the mini Shopping Preferences
                  card. Offer (step 4 — offer_requested) onward shows
                  the full Property Module + cards. The previous < 5
                  cutoff was off-by-one — it left borrowers in
                  offer_requested stuck on the mini card even though
                  they had a specific property in flight (the whole
                  point of the module). */}
              {currentStep < 4 ? (
                <div style={card}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Shopping Preferences</h3>
                  <InfoRow label="Target ZIPs" value={formData.target_zips || formData.preferred_zip} />
                  {/* ── 🚨 THE ZIPs WE COULD NOT CONFIRM ───────────────────────
                      The reference data behind the service-area check is a
                      static USPS snapshot that cannot resolve four ZIPs — two
                      of them, 75036 (Frisco) and 75072 (McKinney), real and
                      populated. Those used to be DROPPED at submit: the
                      borrower was told the team would check, and nothing
                      reached anybody.

                      🔑 NO QUEUE AND NO NEW SURFACE. It renders in Shopping
                      Preferences, beside the ZIPs it belongs with, because
                      that is where staff already read them. It is not a task
                      and does not claim anyone has been assigned — it is the
                      ZIP, visible, so a human can answer it when they get
                      here. */}
                  {formData.target_zips_unconfirmed ? (
                    <InfoRow
                      label="ZIPs needing a manual check"
                      value={`${formData.target_zips_unconfirmed} — not in the reference data; confirm against the property address`}
                    />
                  ) : null}
                  <InfoRow label="Budget" value={fmt$(pp)} />
                  <InfoRow label="Property Type" value={formData.property_type} />
                </div>
              ) : (
                <>
                  {/* ─── Property Module CTA + wizard render ──────────────
                      SmartResume opens on the lowest incomplete step (or 7
                      for a fully-complete loan, so admin can review). When
                      the module is open the legacy inline cards below
                      collapse — admin uses the wizard as the canonical
                      surface. Closing the module re-exposes the inline
                      cards as the "advanced view". */}
                  {/* Standalone always-on Listing Agent card was removed —
                      duplicated the existing KPI-strip Listing Agent card
                      below. That KPI card has been updated to include the
                      brokerage/office line + the same offer_requests →
                      disclosure → MLS fallback chain that powered this
                      duplicate, so MLS-derived contacts now surface in
                      the KPI strip without a second card. */}

                  {(() => {
                    const moduleDerivedStep = derivePropertyModuleStep({
                      user, offerRequest, disclosureData,
                      documents, loanStage: user?.application_status, optionPeriod,
                      abPsa: abPsaPackage?.package
                        ? { hasPackage: true, status: abPsaPackage.package.status, allSignedAt: abPsaPackage.package.all_signed_at }
                        : { hasPackage: false },
                      // New gate — must accept every review section before
                      // the offer module advances past Step 4.
                      reviewComplete,
                    });
                    const moduleComplete = moduleDerivedStep === 'complete';
                    const moduleResumeLabel = moduleComplete
                      ? '✓ Property Module Complete — Review'
                      : moduleDerivedStep > 1
                      ? `↺ Resume Property Module — Step ${moduleDerivedStep} of ${PROPERTY_MODULE_STEPS.length}`
                      : '▶ Start Property Module';
                    return !showPropertyModule ? (
                      // Right-aligned, normal-size button (matches the
                      // Closing Module CTA convention). Full-width billboard
                      // dropped — too visually heavy for a tab-level action.
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                        <button
                          onClick={() => setShowPropertyModule(true)}
                          style={{
                            background: moduleComplete ? '#f0fdf4' : '#3a6ad9',
                            color: moduleComplete ? '#166534' : '#fff',
                            border: moduleComplete ? '1px solid #bbf7d0' : 'none',
                            borderRadius: 8, padding: '8px 16px',
                            fontSize: 13, fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {moduleResumeLabel}
                        </button>
                      </div>
                    ) : (
                      // Full-screen overlay — covers nav, tabs, and the
                      // entire admin profile. Backdrop is non-dismissive
                      // by design (no onClick on the outer div): the
                      // Property Module is a multi-step workflow and
                      // accidental backdrop clicks would lose progress.
                      // Close path is the explicit ✕ inside the wizard
                      // header (wired via the existing onClose prop
                      // below). zIndex 10000 matches the LOX / KYC modal
                      // patterns over in Verification.js.
                      <div style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 10000,
                        background: 'rgba(0,0,0,0.65)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 24,
                      }}>
                        <div style={{
                          width: '100%',
                          maxWidth: 960,
                          maxHeight: '90vh',
                          overflowY: 'auto',
                          borderRadius: 12,
                          boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                          background: '#ffffff',
                        }}>
                        <PropertyModule
                          user={user}
                          offerRequest={offerRequest}
                          disclosureData={disclosureData}
                          documents={documents}
                          loanStage={user?.application_status}
                          optionPeriod={optionPeriod}
                          abPsa={abPsaPackage?.package
                            ? { hasPackage: true, status: abPsaPackage.package.status, allSignedAt: abPsaPackage.package.all_signed_at }
                            : { hasPackage: false }}
                          reviewComplete={reviewComplete}
                          propertyAddress={
                            optionPeriod?.property_address
                            || offerRequest?.address
                            || formData.property_address
                          }
                          onClose={() => setShowPropertyModule(false)}
                          renderStep={(stepNum) => {
                            // Step bodies — each delegates back to the
                            // inline editing surface. The wizard is the
                            // navigation + status overlay; the inline
                            // cards stay the actual edit surface in this
                            // pass so we don't dual-render hundreds of
                            // lines of stateful JSX. A follow-up commit
                            // will fold the editors into the steps once
                            // the surface is stable in production.
                            const baseCardStyle = {
                              padding: 16,
                              borderRadius: 10,
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              fontSize: 13,
                              color: '#374151',
                              lineHeight: 1.6,
                            };
                            // ── linkedListing (3d-property commit 3) ───────────────
                            // AnyProp listing tied to this borrower's offer, attached
                            // by the borrower-detail endpoint (3d-property commit 2).
                            // Single source for Step 1 + Step 4 pre-fill. Three states:
                            //   · null         → off-MLS deal or no listing_id (banner
                            //                    in commit 5 explains; manual entry only)
                            //   · live_anyprop → fresh from AnyProp; use as fallback
                            //                    BELOW manual offerRequest/disclosure
                            //                    values so admin's explicit entry wins.
                            //   · cached_saved_homes → snapshot from saved_homes; same
                            //                    use as live but with a 'cached' banner
                            //                    in commit 5.
                            // pickWithSource(...) picks the first non-empty value AND
                            // tags where it came from so the UI can render an
                            // 'MLS-sourced' pill when the displayed value originated
                            // from linkedListing (not from manual entry).
                            const linkedListing = data?.linkedListing || null;
                            // 3d-property commit 5: county source-of-truth chain.
                            // Prefer the MLS-supplied CountyOrParish over the
                            // HouseCanary-derived hc_property_county when both exist.
                            // Forward-compat — no UI surface today, but exposed so
                            // future Valuation / Tax cards bind to one resolved
                            // value with the right priority.
                            // eslint-disable-next-line no-unused-vars
                            const resolvedCounty = linkedListing?.county || user?.hc_property_county || null;
                            // 3d-property commit 5: state-of-listing banner content.
                            // Three branches matching the linkedListing tri-state:
                            //   · null                 → off-MLS / no listing_id
                            //   · cached_saved_homes   → live AnyProp unavailable
                            //                            (using the saved snapshot)
                            //   · live_anyprop / other → no banner (happy path)
                            // Renders at the top of EVERY step body so admin sees the
                            // listing-data context regardless of where SmartResume
                            // jumped to.
                            const listingStateBanner = (() => {
                              if (!linkedListing) {
                                return {
                                  bg:     '#f9fafb',
                                  border: '#e5e7eb',
                                  accent: '#6b7280',
                                  title:  '📋 Off-MLS deal',
                                  body:   'This deal was submitted off-MLS — no listing data to pre-fill. Continue with manual entry on each step.',
                                };
                              }
                              if (linkedListing.source === 'cached_saved_homes') {
                                return {
                                  bg:     '#fffbeb',
                                  border: '#fde68a',
                                  accent: '#d97706',
                                  title:  '⚠ Showing cached listing data',
                                  body:   'Live MLS unavailable for this listing (likely went off-market). Values shown were captured when the borrower saved/requested the home. Refresh the page to retry the live MLS pull.',
                                };
                              }
                              return null;   // live_anyprop → happy path, no banner
                            })();
                            const banner = listingStateBanner ? (
                              <div style={{
                                marginBottom: 16, padding: '12px 14px',
                                background: listingStateBanner.bg,
                                border: `1px solid ${listingStateBanner.border}`,
                                borderLeft: `4px solid ${listingStateBanner.accent}`,
                                borderRadius: 8,
                                fontSize: 12, color: '#374151', lineHeight: 1.5,
                              }}>
                                <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                                  {listingStateBanner.title}
                                </div>
                                <div>{listingStateBanner.body}</div>
                              </div>
                            ) : null;
                            // eslint-disable-next-line no-unused-vars
                            const pickWithSource = (manual, disclosure, mlsValue) => {
                              if (manual)     return { value: manual,     source: 'manual' };
                              if (disclosure) return { value: disclosure, source: 'disclosure' };
                              if (mlsValue)   return { value: mlsValue,   source: 'mls' };
                              return { value: '', source: null };
                            };
                            // Reusable MLS-source pill (also used by Step 4 Listing Data
                            // card). Brand: amber #ffdd86 background, dark #111827 text
                            // — the only legible combo for that color per brand rules.
                            const mlsPill = (
                              <span style={{
                                display: 'inline-block', marginLeft: 6, padding: '1px 6px',
                                fontSize: 9, fontWeight: 700, borderRadius: 4,
                                background: '#ffdd86', color: '#111827',
                                textTransform: 'uppercase', letterSpacing: 0.04,
                                verticalAlign: 'middle',
                              }}>MLS</span>
                            );
                            // The legacy reviewBlockerCopy block (inline
                            // "Underwriting review not complete" banner shown
                            // at the top of the Offer Details step) has been
                            // removed. UW review is now its own step (3),
                            // making the dependency explicit in the wizard
                            // chrome instead of buried as a banner on a later
                            // step. The dedicated stepNum === 3 block below
                            // surfaces the gate + a deep link to the Review
                            // tab.

                            // Step 1 (was 2 pre-refactor) — Seller's Disclosure.
                            if (stepNum === 1) {
                              const isNc = !!offerRequest?.is_new_construction;
                              const disc = disclosureData?.disclosure;
                              const discStatus = disc?.status;
                              const discReceived = discStatus === 'submitted' || discStatus === 'received';
                              const discRequested = discStatus === 'requested';
                              const analysis = disclosureData?.analysis;

                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  {/* New construction shortcut */}
                                  <div style={baseCardStyle}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={isNc}
                                        onChange={(e) => { handleNewConstruction(e.target.checked).then(() => fetchData()); }}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                      />
                                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                        New construction — seller's disclosure not required
                                      </span>
                                    </label>
                                    {isNc && (
                                      <div style={{ marginTop: 10, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 13 }}>
                                        ✓ New construction confirmed — Step 2 complete.
                                      </div>
                                    )}
                                  </div>

                                  {/* Live disclosure surface — hidden when new construction is checked */}
                                  {!isNc && (
                                    <div style={baseCardStyle}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
                                        <div>
                                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Seller's Disclosure</div>
                                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Request from listing agent or upload manually.</div>
                                        </div>
                                        {!discReceived && (
                                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            <button type="button"
                                              onClick={() => showDisclosureForm ? setShowDisclosureForm(false) : openDisclosureFormPrefilled()}
                                              style={{ padding: '6px 12px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                                              {showDisclosureForm ? 'Cancel' : 'Request from Agent'}
                                            </button>
                                            <label style={{ display: 'inline-block', padding: '6px 12px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 7, cursor: disclosureLoading ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700, opacity: disclosureLoading ? 0.6 : 1 }}>
                                              Upload Manually
                                              <input type="file" accept="application/pdf"
                                                disabled={disclosureLoading}
                                                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadDisclosure(f); e.target.value = ''; }}
                                                style={{ display: 'none' }} />
                                            </label>
                                          </div>
                                        )}
                                      </div>

                                      {/* Request form (only when "Request from Agent" is toggled on) */}
                                      {showDisclosureForm && !discReceived && (
                                        <div style={{ background: '#ffffff', borderRadius: 10, padding: 12, marginBottom: 10, border: '1px solid #e5e7eb' }}>
                                          {disclosureError && (
                                            <div style={{ marginBottom: 10, padding: '8px 10px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>
                                              {disclosureError}
                                            </div>
                                          )}
                                          <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                            {[
                                              { key: 'listing_agent_name',  label: 'Agent Name',    placeholder: 'Jane Smith' },
                                              { key: 'listing_agent_email', label: 'Agent Email *', placeholder: 'agent@brokerage.com' },
                                              { key: 'listing_agent_phone', label: 'Agent Phone',   placeholder: '(512) 000-0000' },
                                            ].map(f => (
                                              <div key={f.key}>
                                                <label style={{ fontSize: 10, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{f.label}</label>
                                                <input value={disclosureForm[f.key]}
                                                  onChange={e => setDisclosureForm(p => ({ ...p, [f.key]: e.target.value }))}
                                                  placeholder={f.placeholder}
                                                  style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                                              </div>
                                            ))}
                                          </div>
                                          <div style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 10, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>CC (optional)</label>
                                            <input value={disclosureForm.cc_email}
                                              onChange={e => setDisclosureForm(p => ({ ...p, cc_email: e.target.value }))}
                                              placeholder="contracts@godoorly.com, broker@brokerage.com"
                                              style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                                          </div>
                                          <button onClick={handleRequestDisclosure}
                                            disabled={!disclosureForm.listing_agent_email || disclosureLoading}
                                            style={{ padding: '8px 16px', background: disclosureForm.listing_agent_email && !disclosureLoading ? '#3a6ad9' : '#e5e7eb', color: disclosureForm.listing_agent_email && !disclosureLoading ? '#fff' : '#9ca3af', border: 'none', borderRadius: 8, cursor: disclosureForm.listing_agent_email && !disclosureLoading ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 12 }}>
                                            {disclosureLoading ? 'Sending…' : 'Send Request'}
                                          </button>
                                        </div>
                                      )}

                                      {/* Transient send-confirmation banner */}
                                      {disclosureSentInfo && !showDisclosureForm && !discReceived && (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#f4fee8', border: '1px solid #acf44e', borderRadius: 8, fontSize: 12, color: '#2d6a0a' }}>
                                          ✓ Disclosure request sent to <strong>{disclosureSentInfo.email}</strong>
                                          {disclosureSentInfo.cc && <> (cc {disclosureSentInfo.cc})</>} at {new Date(disclosureSentInfo.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                          {disclosureSentInfo.resendId && <div style={{ fontSize: 10, color: '#5a7a30', marginTop: 2 }}>Resend ID: <code>{disclosureSentInfo.resendId}</code></div>}
                                        </div>
                                      )}

                                      {/* Persistent status pill — only when status is actually
                                          requested or received. Hides the 'not_requested' stub
                                          rows the Listing Agent edit modal creates. */}
                                      {disc && discStatus !== 'not_requested' && (discRequested || discReceived) && (
                                        <div style={{ padding: '10px 12px', background: discReceived ? '#f4fee8' : '#fffbef', border: `1px solid ${discReceived ? '#d4f99a' : '#ffe4a0'}`, borderRadius: 10 }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <div style={{ minWidth: 0 }}>
                                              <div style={{ fontSize: 13, fontWeight: 700, color: discReceived ? '#2d6a0a' : '#7a5200' }}>
                                                {discReceived ? '✓ Disclosure Received' : '⏳ Disclosure Requested'}
                                              </div>
                                              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{disc.listing_agent_email}</div>
                                            </div>
                                            {discReceived && (
                                              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                                <button
                                                  onClick={async () => {
                                                    try {
                                                      const resp = await axios.get(`${API}/api/admin/borrowers/${id}/disclosure/pdf`, { headers, responseType: 'blob' });
                                                      const url = URL.createObjectURL(resp.data);
                                                      window.open(url, '_blank');
                                                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                                                    } catch (e) {
                                                      alert('Unable to open PDF: ' + (e.response?.data?.error || e.message));
                                                    }
                                                  }}
                                                  style={{ padding: '6px 10px', background: '#fff', color: '#3a6ad9', border: '1.5px solid #3a6ad9', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                                                  View PDF
                                                </button>
                                                <button onClick={handleAnalyzeDisclosure}
                                                  disabled={disclosureLoading}
                                                  style={{ padding: '6px 12px', background: disclosureLoading ? '#e5e7eb' : '#3a6ad9', color: disclosureLoading ? '#9ca3af' : '#fff', border: 'none', borderRadius: 7, cursor: disclosureLoading ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700 }}>
                                                  {disclosureLoading ? 'Analyzing…' : (analysis ? 'Re-Analyze' : 'Analyze with AI')}
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          {disc.pdf_filename && (
                                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                              <span>📄</span>
                                              <span style={{ fontWeight: 500 }}>{disc.pdf_filename}</span>
                                              {disc.submitted_at && (
                                                <span style={{ color: '#9ca3af' }}>· uploaded {new Date(disc.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* AI analysis flags */}
                                      {analysis && (() => {
                                        const flags = typeof analysis.flags === 'string' ? JSON.parse(analysis.flags || '[]') : (analysis.flags || []);
                                        const disqualifiers = flags.filter(f => f.found && f.severity === 'disqualifier');
                                        const warnings = flags.filter(f => f.found && f.severity === 'warning');
                                        return (
                                          <div style={{ marginTop: 12 }}>
                                            <div style={{ padding: '10px 12px', background: disqualifiers.length ? '#fff0f3' : warnings.length ? '#fffbef' : '#f4fee8', border: `1px solid ${disqualifiers.length ? '#ffc5ca' : warnings.length ? '#ffe4a0' : '#d4f99a'}`, borderRadius: 10, marginBottom: 8 }}>
                                              <div style={{ fontSize: 13, fontWeight: 800, color: disqualifiers.length ? '#ff082f' : warnings.length ? '#7a5200' : '#2d6a0a', marginBottom: 4 }}>
                                                {disqualifiers.length ? 'Property Disqualified' : warnings.length ? 'Review Required' : 'Disclosure Clear'}
                                              </div>
                                              <div style={{ fontSize: 11, color: '#6b7280' }}>{analysis.disqualifier_count} disqualifying · {analysis.warning_count} warning(s)</div>
                                            </div>
                                            {[...disqualifiers, ...warnings].map(flag => (
                                              <div key={flag.key} style={{ padding: '8px 10px', background: flag.severity === 'disqualifier' ? '#fff0f3' : '#fffbef', border: `1px solid ${flag.severity === 'disqualifier' ? '#ffc5ca' : '#ffe4a0'}`, borderRadius: 8, marginBottom: 6 }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{flag.label}</div>
                                                {flag.excerpt && <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>"{flag.excerpt}"</div>}
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            // Step 2 (was 3 pre-refactor) — Valuation.
                            if (stepNum === 2) {
                              const ran = !!user?.hc_avm_value;
                              const isRunning = hcLoading || user?.hc_analysis_status === 'running';
                              const hcError   = user?.hc_analysis_status === 'error';
                              const hcEmpty   = (user?.hc_analysis_status === 'partial' || user?.hc_analysis_status === 'complete')
                                              && user?.hc_analyzed_at && !user?.hc_avm_value;

                              const kpis = ran ? [
                                {
                                  label: 'AVM Value',
                                  value: '$' + Math.round(user.hc_avm_value).toLocaleString(),
                                  color: '#3a6ad9', bold: true,
                                  help: "HouseCanary's Automated Valuation Model estimate of the property's current market value. Calculated from recent comparable sales, property characteristics (size, age, lot), and local market trends. Statistical estimate — not a formal appraisal.",
                                },
                                {
                                  label: 'Recommended Offer',
                                  value: user.hc_recommended_offer ? '$' + Math.round(user.hc_recommended_offer).toLocaleString() : '—',
                                  color: '#2d6a0a', bold: true,
                                  help: 'AVM × 0.90, rounded to the nearest $1,000. A conservative starting point that leaves room for negotiation and accounts for AVMs running slightly high on unique or recently-renovated homes.',
                                },
                                {
                                  label: 'Confidence',
                                  value: user.hc_avm_confidence || '—',
                                  color: '#374151',
                                  help: "Forecast Standard Deviation (FSD) — expected margin of error on the AVM as a percentage. Lower is better: under 5% is high confidence, 5–10% moderate, over 10% means treat the AVM as a loose guide and pull recent comps manually.",
                                },
                                {
                                  label: 'Market Grade',
                                  value: user.hc_market_grade || '—',
                                  color: '#374151',
                                  help: "HouseCanary's letter grade (A+ through F) for the ZIP's housing market. Reflects price momentum, sales velocity, and inventory levels. A or B suggests strong demand — useful context when deciding how aggressively to offer.",
                                },
                                {
                                  label: 'Flood Zone',
                                  value: user.hc_flood_zone || 'None',
                                  color: user.hc_flood_zone && user.hc_flood_zone !== 'X' ? '#ff082f' : '#2d6a0a',
                                  help: 'FEMA flood zone designation. X = minimal risk, no mandatory flood insurance. A / AE / AH / AO = high-risk inland — flood insurance is required for federally-backed mortgages. V / VE = high-risk coastal.',
                                },
                                {
                                  label: 'AVM Range',
                                  value: user.hc_avm_low && user.hc_avm_high ? `$${Math.round(user.hc_avm_low / 1000)}k–$${Math.round(user.hc_avm_high / 1000)}k` : '—',
                                  color: '#374151',
                                  help: "Low and high bounds of the AVM at roughly 68% confidence (one standard deviation). The actual market value most likely falls within this range. A tighter range means HouseCanary is more confident in its estimate.",
                                },
                              ] : [];

                              return (
                                <div style={baseCardStyle}>
                                  {/* Header copy — matches the legacy inline card header
                                      with "Property Valuation (HouseCanary AVM)", the
                                      AVM / Recommended Offer summary line, and the
                                      Last run date underneath. */}
                                  <div style={{ marginBottom: ran ? 14 : 12 }}>
                                    <strong>Property Valuation</strong> (HouseCanary AVM)
                                  </div>
                                  {ran && (
                                    <div style={{ marginBottom: 14 }}>
                                      AVM: <strong>${Math.round(user.hc_avm_value).toLocaleString()}</strong>
                                      {user.hc_recommended_offer && (
                                        <> · Recommended Offer: <strong>${Math.round(user.hc_recommended_offer).toLocaleString()}</strong></>
                                      )}
                                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                        Last run {user.hc_analyzed_at ? new Date(user.hc_analyzed_at).toLocaleDateString() : '—'}
                                      </div>
                                    </div>
                                  )}

                                  {/* KPI grid — same six tiles the legacy inline card
                                      surfaces (AVM / Recommended Offer / Confidence /
                                      Market Grade / Flood Zone / AVM Range). HelpTip
                                      tooltips ported intact. */}
                                  {ran && (
                                    <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                                      {kpis.map(item => (
                                        <div key={item.label} style={{ background: '#ffffff', borderRadius: 10, padding: '10px 12px', border: '1px solid #e5e7eb', borderLeft: item.bold ? `3px solid ${item.color}` : '1px solid #e5e7eb' }}>
                                          <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                                            <span>{item.label}</span>
                                            {item.help && <HelpTip text={item.help} />}
                                          </div>
                                          <div style={{ fontSize: item.bold ? 16 : 14, fontWeight: item.bold ? 900 : 600, color: item.color }}>{item.value}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* ── Listing Data (3d-property commit 3) ──
                                      Source priority per row: manual user.*
                                      value (admin-saved) → linkedListing.*
                                      (live MLS or cached snapshot). MLS pill
                                      renders when the displayed value came
                                      from linkedListing. The 'activates with
                                      AnyProp integration' subtitle drops out
                                      whenever linkedListing carries data.
                                      'View Listing' button REMOVED per spec
                                      (HAR doesn't ship a borrower-visible
                                      listing URL; the value was always blank
                                      and the disabled state was clutter). */}
                                  <div style={{
                                    background: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 10,
                                    padding: '14px 16px',
                                    marginBottom: 14,
                                  }}>
                                    <div style={{
                                      fontSize: 11, fontWeight: 700,
                                      color: '#6b7280', marginBottom: 10,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em',
                                    }}>
                                      Listing Data
                                      {(!user.list_price && !linkedListing?.price) && (
                                        <span style={{
                                          fontSize: 10, color: '#9ca3af',
                                          fontWeight: 400, marginLeft: 8,
                                          fontStyle: 'italic',
                                          textTransform: 'none', letterSpacing: 0,
                                        }}>
                                          (no MLS data — off-MLS deal or live feed unavailable)
                                        </span>
                                      )}
                                    </div>
                                    {(() => {
                                      // pickWithSource adapted for a 2-tier
                                      // chain (manual → mls). Returns {value,
                                      // source} where source ∈ 'manual'|'mls'|null.
                                      const pickField = (manual, mlsVal) => {
                                        if (manual)  return { value: manual,  source: 'manual' };
                                        if (mlsVal != null && mlsVal !== '') return { value: mlsVal, source: 'mls' };
                                        return { value: null, source: null };
                                      };
                                      // Builder row only renders on new-construction
                                      // listings. On resale listings HAR doesn't
                                      // populate BuilderName, so the row was
                                      // always '—' — visual noise. Gate on
                                      // is_new_construction from the offer
                                      // request (admin can also flip the NC
                                      // checkbox on Step 1).
                                      const isNcListing = !!offerRequest?.is_new_construction
                                                       || !!user.listing_builder
                                                       || !!linkedListing?.builderName;
                                      const rows = [
                                        {
                                          label: 'List Price',
                                          info: pickField(user.list_price, linkedListing?.price),
                                          format: v => v ? `$${Number(v).toLocaleString()}` : '—',
                                        },
                                        {
                                          label: 'Days on Market',
                                          info: pickField(user.time_on_market, linkedListing?.daysOnMarket),
                                          format: v => (v != null && v !== '') ? `${v} days` : '—',
                                        },
                                        ...(isNcListing ? [{
                                          label: 'Builder',
                                          info: pickField(user.listing_builder, linkedListing?.builderName),
                                          format: v => v || '—',
                                        }] : []),
                                        {
                                          label: 'MLS #',
                                          info: pickField(user.mls_number, linkedListing?.listingId),
                                          format: v => v || '—',
                                        },
                                      ];
                                      return rows.map((row, i) => (
                                        <div key={row.label} style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          fontSize: 12,
                                          padding: '5px 0',
                                          borderBottom: i < rows.length - 1 ? '1px solid #f3f4f6' : 'none',
                                        }}>
                                          <span style={{ color: '#6b7280' }}>{row.label}</span>
                                          <span style={{ fontWeight: 600, color: '#111827' }}>
                                            {row.format(row.info.value)}
                                            {row.info.source === 'mls' && mlsPill}
                                          </span>
                                        </div>
                                      ));
                                    })()}
                                  </div>

                                  {/* AVM vs List Price delta — only when both
                                      values are present. Red when list > AVM
                                      (overpriced), green when list < AVM. */}
                                  {ran && user.list_price && (() => {
                                    const avm  = Number(user.hc_avm_value);
                                    const list = Number(user.list_price);
                                    const delta = list - avm;
                                    const deltaPct = avm > 0 ? ((delta / avm) * 100).toFixed(1) : '0.0';
                                    const above = delta > 0;
                                    return (
                                      <div style={{
                                        background: above ? '#fef2f2' : '#f0fdf4',
                                        border: `1px solid ${above ? '#fca5a5' : '#bbf7d0'}`,
                                        borderRadius: 10,
                                        padding: '10px 14px',
                                        marginBottom: 14,
                                        fontSize: 12,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: 8,
                                      }}>
                                        <span style={{ color: '#374151' }}>
                                          AVM <strong>${avm.toLocaleString()}</strong>
                                          {' vs '}List <strong>${list.toLocaleString()}</strong>
                                        </span>
                                        <span style={{ fontWeight: 700, color: above ? '#dc2626' : '#16a34a' }}>
                                          {above ? '+' : '-'}${Math.abs(delta).toLocaleString()}{' '}
                                          ({above ? '+' : ''}{deltaPct}%) {above ? 'above AVM' : 'below AVM'}
                                        </span>
                                      </div>
                                    );
                                  })()}

                                  {/* ── Manual Offer Override ──
                                      Admin checks the box, enters a price + required
                                      notes, then clicks Save. Persists to
                                      users.offer_override_* via POST /offer-override
                                      and pre-populates Step 4's purchase_price. */}
                                  <div style={{
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 10,
                                    padding: '14px 16px',
                                    marginBottom: 14,
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      marginBottom: offerOverride.active ? 12 : 0,
                                      gap: 8, flexWrap: 'wrap',
                                    }}>
                                      <label style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                        color: '#111827',
                                      }}>
                                        <input type="checkbox" checked={offerOverride.active}
                                          onChange={e => setOfferOverride(prev => ({ ...prev, active: e.target.checked }))} />
                                        Override Recommended Offer
                                      </label>
                                      {user.offer_override_price != null && !offerOverride.active && (
                                        <span style={{
                                          fontSize: 11, color: '#92400e',
                                          background: '#fffbeb',
                                          border: '1px solid #fde68a',
                                          borderRadius: 6,
                                          padding: '2px 8px',
                                          fontWeight: 600,
                                        }}>
                                          ✏️ Override active: ${Number(user.offer_override_price).toLocaleString()}
                                        </span>
                                      )}
                                    </div>
                                    {offerOverride.active && (
                                      <div>
                                        <div className="admin-two-col-grid" style={{
                                          display: 'grid',
                                          gridTemplateColumns: '1fr 1fr',
                                          gap: 10, marginBottom: 10,
                                        }}>
                                          <div>
                                            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                                              Override Offer Price ($)
                                            </label>
                                            <input type="number" value={offerOverride.price}
                                              onChange={e => setOfferOverride(prev => ({ ...prev, price: e.target.value }))}
                                              placeholder={user.hc_recommended_offer
                                                ? `HC Recommended: $${Number(user.hc_recommended_offer).toLocaleString()}`
                                                : 'Enter offer price'}
                                              style={{
                                                width: '100%', padding: '7px 10px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: 6, fontSize: 13,
                                                boxSizing: 'border-box',
                                              }} />
                                          </div>
                                          <div>
                                            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                                              HC Recommended
                                            </label>
                                            <div style={{
                                              padding: '7px 10px',
                                              background: '#f9fafb',
                                              border: '1px solid #e5e7eb',
                                              borderRadius: 6, fontSize: 13,
                                              color: '#374151',
                                            }}>
                                              {user.hc_recommended_offer
                                                ? `$${Number(user.hc_recommended_offer).toLocaleString()}`
                                                : '—'}
                                            </div>
                                          </div>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                                            Notes <span style={{ color: '#dc2626' }}>*</span>
                                            <span style={{ fontWeight: 400, marginLeft: 4 }}>
                                              (required — explain why you're overriding)
                                            </span>
                                          </label>
                                          <textarea value={offerOverride.notes}
                                            onChange={e => setOfferOverride(prev => ({ ...prev, notes: e.target.value }))}
                                            placeholder="e.g. Comparable sales support a higher offer. Seller is motivated."
                                            rows={3}
                                            style={{
                                              width: '100%', padding: '7px 10px',
                                              border: `1px solid ${!offerOverride.notes?.trim() ? '#fca5a5' : '#d1d5db'}`,
                                              borderRadius: 6, fontSize: 12,
                                              fontFamily: 'inherit', resize: 'vertical',
                                              boxSizing: 'border-box',
                                            }} />
                                        </div>
                                        <button onClick={handleSaveOfferOverride}
                                          disabled={savingOverride || !offerOverride.price || !offerOverride.notes?.trim()}
                                          style={{
                                            background: (savingOverride || !offerOverride.price || !offerOverride.notes?.trim())
                                              ? '#9ca3af' : '#3a6ad9',
                                            color: '#fff', border: 'none',
                                            borderRadius: 7, padding: '8px 16px',
                                            fontSize: 12, fontWeight: 700,
                                            cursor: (savingOverride || !offerOverride.price || !offerOverride.notes?.trim())
                                              ? 'not-allowed' : 'pointer',
                                          }}>
                                          {savingOverride ? 'Saving…' : 'Save Override'}
                                        </button>
                                        {user.offer_override_at && (
                                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                                            Last set {new Date(user.offer_override_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            {user.offer_override_by ? ` by ${user.offer_override_by}` : ''}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* AVM report PDF link — only when HC returned a
                                      generated report. Auth-aware fetch via blob URL
                                      since window.open can't carry the Bearer header. */}
                                  {ran && user.hc_avm_report_available === true && (
                                    <div style={{ marginBottom: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <span style={{ fontSize: 13, color: '#6b7280' }}>HouseCanary AVM Report</span>
                                      <button
                                        onClick={async () => {
                                          try {
                                            const r = await axios.get(
                                              `${API}/api/admin/borrowers/${user.id}/property-analysis/avm-report`,
                                              { headers, responseType: 'blob' }
                                            );
                                            const blobUrl = URL.createObjectURL(r.data);
                                            window.open(blobUrl, '_blank');
                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                                          } catch (e) {
                                            alert('Failed to load AVM report: ' + (e.response?.data?.error || e.message));
                                          }
                                        }}
                                        style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                        View Report PDF
                                      </button>
                                    </div>
                                  )}

                                  {/* Empty / error states.
                                      The error state surfaces the
                                      backend-captured hc_analysis_error
                                      message (e.g. "Address is missing a
                                      ZIP code") when it exists, falling
                                      back to a generic check-the-address
                                      hint when the column is null (older
                                      failures before hc_analysis_error
                                      landed). */}
                                  {!ran && hcError && (
                                    <div style={{ padding: 12, background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 10, fontSize: 13, color: '#ff082f', marginBottom: 12 }}>
                                      ⚠ {user?.hc_analysis_error
                                        || 'Analysis failed — HouseCanary did not return valuation data for this property. Verify the address is correct and try again.'}
                                    </div>
                                  )}
                                  {!ran && hcEmpty && (
                                    <div style={{ padding: 12, background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: 10, fontSize: 13, color: '#7a5200', marginBottom: 12 }}>
                                      Analysis completed but no valuation data was returned. HouseCanary likely has no AVM coverage for this address.
                                    </div>
                                  )}
                                  {!ran && !hcError && !hcEmpty && !isRunning && (
                                    <div style={{ marginBottom: 12, color: '#9ca3af', fontStyle: 'italic' }}>Not yet analyzed.</div>
                                  )}

                                  {/* Run / Re-Run button — single label per the user's
                                      spec ("Re-Run") when an AVM exists; falls back to
                                      "Run Analysis" before the first run. */}
                                  <button
                                    onClick={handleRunHC}
                                    disabled={isRunning}
                                    style={{
                                      background: isRunning ? '#e5e7eb' : '#3a6ad9',
                                      color:      isRunning ? '#9ca3af' : '#fff',
                                      border: 'none', borderRadius: 8,
                                      padding: '8px 16px', fontSize: 12, fontWeight: 700,
                                      cursor: isRunning ? 'not-allowed' : 'pointer',
                                    }}>
                                    {isRunning ? 'Running…' : ran ? 'Re-Run' : 'Run Analysis'}
                                  </button>
                                </div>
                              );
                            }
                            // Step 3 (NEW) — Underwriting Review. Discrete gate
                            // before the offer can be routed to the listing
                            // agent. Mirrors what was previously a button-level
                            // disabled-tooltip on Offer Details (the legacy
                            // reviewBlockerCopy banner). Promoting it to its own
                            // step makes the dependency visible in the wizard
                            // chrome and keeps the same source of truth
                            // (reviewComplete = every review_sections row
                            // accepted by the underwriter on the Review tab).
                            if (stepNum === 3) {
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                  <div style={baseCardStyle}>
                                    {reviewComplete ? (
                                      <div style={{
                                        padding: '14px 16px',
                                        background: '#f0fdf4',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: 8,
                                        color: '#166534',
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                      }}>
                                        ✓ <strong>Loan Team has completed Underwriting Review.</strong>{' '}
                                        Every review section has been accepted —
                                        the offer can be generated and routed on
                                        the next step.
                                      </div>
                                    ) : (
                                      <div style={{
                                        padding: '14px 16px',
                                        background: '#fef2f2',
                                        border: '1px solid #fca5a5',
                                        borderRadius: 8,
                                        color: '#991b1b',
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                      }}>
                                        ⛔ <strong>Waiting on Loan Team to complete Underwriting Review.</strong>{' '}
                                        {/* 🔑 What the review still needs (backend utils/fileReview, Jesse 2026-10-26) — an internal gate for
                                            the contract desk; the borrower never sees it. */}
                                        The offer can't be routed to the listing agent until the Underwriting Module review is complete.
                                        <div data-testid="pm-file-review-line" style={{ marginTop: 6, fontWeight: 700 }}>{(data.fileReview && data.fileReview.line) || 'The review could not be read just now.'}</div>
                                        <button type="button" data-testid="pm-open-module"
                                          onClick={() => { const first = data.fileReview && data.fileReview.items && data.fileReview.items[0];
                                            setActiveTab('review'); setModuleStepId(first ? (first.module === 'misc' ? 'documents' : first.module) : null); setShowReviewModule(true); }}
                                          style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, color: '#991b1b', textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}>
                                          Open the Underwriting Module
                                        </button>{' '}If they're slow to respond, send a nudge below.
                                        <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                          <button
                                            type="button"
                                            disabled={nudgeLoading || nudgedAt}
                                            onClick={handleNudgeLoanTeam}
                                            style={{
                                              padding: '8px 16px',
                                              background: nudgedAt ? '#e5e7eb' : '#3a6ad9',
                                              color: nudgedAt ? '#6b7280' : '#fff',
                                              border: 'none', borderRadius: 8,
                                              fontSize: 12, fontWeight: 700,
                                              cursor: (nudgeLoading || nudgedAt) ? 'not-allowed' : 'pointer',
                                            }}
                                          >
                                            {nudgeLoading
                                              ? 'Sending…'
                                              : nudgedAt
                                                ? '✓ Loan Team nudged'
                                                : '🛎 Nudge Loan Team'}
                                          </button>
                                          {nudgedAt && (
                                            <span style={{ fontSize: 11, color: '#7a5200', fontStyle: 'italic' }}>
                                              Sent {new Date(nudgedAt).toLocaleTimeString()} — next nudge available in 1 hour.
                                            </span>
                                          )}
                                          {nudgeError && (
                                            <span style={{ fontSize: 11, color: '#991b1b' }}>{nudgeError}</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            if (stepNum === 4) {
                              // Step 4 IS the offer contract surface — the
                              // legacy CollapsibleCard below (gated behind
                              // {false &&}) used to host this whole block.
                              // Moving it inline here means admins fill out
                              // every offer field, hit Generate, see the
                              // result, and route to the listing agent all
                              // without leaving the Property Module. The
                              // legacy card has been removed; all the state
                              // (offerTerms, offerResult, handleGenerateOffer,
                              // offerComms, etc.) lives at the AdminBorrower-
                              // Profile component level so this nested
                              // renderStep callback has direct access.
                              // 3d-property commit 5: banner also renders at
                              // top of Step 4 (the Listing Data card is here).
                              return (
                                <>
                                {banner}
                                <div style={baseCardStyle}>
                                  <div style={{ marginTop: '12px' }}>
                                    <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 14px 0' }}>
                                      Generate a {(user.state || formData.state || 'TX').toUpperCase() === 'FL' ? 'FREC AS IS' : 'TREC 20-18'} offer contract pre-filled with borrower and property details.
                                    </p>

                                    {/* Manually Accept Offer lives on Step 5 now —
                                        Step 4 is just generation + sending. */}
                                    <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                                      {[
                                        { key: 'purchase_price', label: 'Purchase Price ($)', placeholder: '285000' },
                                        { key: 'emd_percent', label: 'EMD %', placeholder: '1' },
                                        { key: 'option_fee', label: 'Option Fee ($)', placeholder: '300' },
                                        { key: 'option_period_days', label: 'Option Period (days)', placeholder: '7' },
                                        { key: 'close_days', label: 'Close In (days)', placeholder: '20' },
                                        { key: 'commission_pct', label: 'Commission (%)', placeholder: '3' },
                                      ].map(({ key, label, placeholder }) => (
                                        <div key={key}>
                                          <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '3px' }}>{label}</label>
                                          <input
                                            type="text"
                                            value={offerTerms[key]}
                                            onChange={e => setOfferTerms(p => ({ ...p, [key]: e.target.value }))}
                                            placeholder={placeholder}
                                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                    {/* Over-max purchase-price warning. */}
                                    {(() => {
                                      const maxApproval = Number(user?.atlas_max_purchase_price || user?.max_approval_amount || 0);
                                      const enteredPrice = parseFloat(offerTerms.purchase_price || 0);
                                      const overMax = maxApproval > 0 && enteredPrice > maxApproval;
                                      return overMax ? (
                                        <div style={{
                                          marginBottom: 14, padding: '8px 12px',
                                          background: '#fef2f2', border: '1px solid #fca5a5',
                                          borderRadius: 8, fontSize: 12, color: '#991b1b', lineHeight: 1.5,
                                        }}>
                                          ⚠️ Purchase price ${enteredPrice.toLocaleString()} exceeds the
                                          borrower's max approval of ${maxApproval.toLocaleString()}.
                                          The offer cannot be generated.
                                        </div>
                                      ) : null;
                                    })()}
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '14px', color: '#374151', cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={offerTerms.is_new_construction || false}
                                        onChange={e => setOfferTerms(prev => ({ ...prev, is_new_construction: e.target.checked }))}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                      />
                                      New construction?
                                    </label>
                                    {/* AI Revision prompt */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '3px' }}>AI Revision (optional)</label>
                                      <textarea
                                        value={offerTerms.ai_revision_prompt}
                                        onChange={e => setOfferTerms(p => ({ ...p, ai_revision_prompt: e.target.value }))}
                                        placeholder='e.g. "Increase earnest money to 2% and add 30-day leaseback"'
                                        rows={2}
                                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
                                      />
                                    </div>
                                    {/* Generate button — disabled state mirrors the
                                        over-max guard above. */}
                                    {(() => {
                                      const maxApproval = Number(user?.atlas_max_purchase_price || user?.max_approval_amount || 0);
                                      const offerPriceOverMax = maxApproval > 0 &&
                                        parseFloat(offerTerms.purchase_price || 0) > maxApproval;
                                      const reviewBlocked = !reviewComplete;
                                      const disabled = offerLoading || !offerTerms.purchase_price || offerPriceOverMax || reviewBlocked;
                                      const disabledReason = reviewBlocked
                                        ? 'Complete underwriting review before generating offer'
                                        : offerPriceOverMax
                                        ? 'Purchase price exceeds borrower max approval'
                                        : !offerTerms.purchase_price
                                        ? 'Enter a purchase price first'
                                        : '';
                                      return (
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                          <button
                                            onClick={handleGenerateOffer}
                                            disabled={disabled}
                                            title={disabledReason}
                                            style={{
                                              padding: '10px 20px',
                                              background: offerLoading ? '#93c5fd' : '#3a6ad9',
                                              color: '#fff',
                                              border: 'none',
                                              borderRadius: '8px',
                                              cursor: disabled ? 'not-allowed' : 'pointer',
                                              fontSize: '14px',
                                              fontWeight: '600',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '6px',
                                              opacity: disabled ? 0.6 : 1,
                                            }}
                                          >
                                            {offerLoading ? '⟳ Generating…' : 'Generate Offer PDF'}
                                          </button>
                                          {offerLoading && <span style={{ fontSize: '12px', color: '#6b7280' }}>Filling contract fields...</span>}
                                          {reviewBlocked && !offerLoading && (
                                            <span style={{ fontSize: '11px', color: '#991b1b', fontWeight: 600 }}>
                                              ⛔ Underwriting review not complete —{' '}
                                              <button type="button" onClick={() => setActiveTab('review')}
                                                style={{ background: 'none', border: 'none', padding: 0, color: '#991b1b', textDecoration: 'underline', cursor: 'pointer', fontWeight: 700, fontSize: 11 }}>
                                                Review tab →
                                              </button>
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    {/* Result */}
                                    {offerResult && (
                                      <div style={{ marginTop: '14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '14px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#166534' }}>
                                            {offerResult.contract_type} Offer Generated
                                          </span>
                                          <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                            {(offerResult.size_bytes / 1024).toFixed(0)} KB
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#374151', marginBottom: '10px' }}>
                                          <strong>${Number(offerResult.terms?.purchase_price || 0).toLocaleString()}</strong> offer
                                          {offerResult.terms?.seller_name && offerResult.terms.seller_name !== '[SELLER NAME]' && ` to ${offerResult.terms.seller_name}`}
                                          {' | '}EM: ${Number(offerResult.terms?.earnest_money || 0).toLocaleString()}
                                          {' | '}Close in {offerResult.terms?.close_days || 20} days
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                          <button
                                            onClick={async () => {
                                              try {
                                                const resp = await axios.get(`${API}${offerResult.pdf_url}`, { headers, responseType: 'blob' });
                                                const url = URL.createObjectURL(resp.data);
                                                window.open(url, '_blank');
                                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                                              } catch (e) {
                                                alert('Unable to open PDF: ' + (e.response?.data?.error || e.message));
                                              }
                                            }}
                                            style={{
                                              padding: '8px 16px',
                                              background: '#acf44e',
                                              color: '#1a1a1a',
                                              border: 'none',
                                              borderRadius: '6px',
                                              fontSize: '12px',
                                              fontWeight: '700',
                                              cursor: 'pointer',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                            }}
                                          >
                                            View PDF
                                          </button>
                                          <button
                                            onClick={openSendOfferModal}
                                            style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                          >
                                            Send to Listing Agent →
                                          </button>
                                        </div>
                                        {lastSentInfo && (
                                          <div style={{ marginTop: 10, padding: '8px 12px', background: '#f4fee8', border: '1px solid #acf44e', borderRadius: 8, fontSize: 12, color: '#2d6a0a' }}>
                                            ✓ Offer sent to <strong>{lastSentInfo.email}</strong>
                                            {lastSentInfo.cc && <> (cc {lastSentInfo.cc})</>} at {new Date(lastSentInfo.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                            {lastSentInfo.resendId && <div style={{ fontSize: 10, color: '#5a7a30', marginTop: 2 }}>Resend ID: <code>{lastSentInfo.resendId}</code></div>}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Offer History — only the offer-with-PDF send rows.
                                        Collapsed by default: shows the most recent
                                        send only, with an "Show N more" toggle to
                                        reveal the prior rounds when needed. */}
                                    {(() => {
                                      const offerPdfComms = offerComms.filter(c => (c.kind || 'offer_pdf') === 'offer_pdf');
                                      const visibleComms = offerHistoryExpanded ? offerPdfComms : offerPdfComms.slice(0, 1);
                                      const hiddenCount = offerPdfComms.length - visibleComms.length;
                                      return offerPdfComms.length > 0 && (
                                        <div style={{ marginTop: 16 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Offer History{offerPdfComms.length > 1 ? ` (${offerPdfComms.length})` : ''}</div>
                                            {offerPdfComms.length > 1 && (
                                              <button type="button"
                                                onClick={() => setOfferHistoryExpanded(v => !v)}
                                                style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, fontWeight: 700, color: '#3a6ad9', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                                {offerHistoryExpanded
                                                  ? 'Hide older ↑'
                                                  : `Show ${hiddenCount} older →`}
                                              </button>
                                            )}
                                          </div>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {visibleComms.map(c => {
                                              const kind = c.kind || 'offer_pdf';
                                              const badge = (() => {
                                                if (kind === 'intro') {
                                                  if (c.status === 'skipped') return { label: 'No LA Email Found', bg: '#fde68a', color: '#7a5200' };
                                                  return { label: 'Offer Coming In', bg: '#c5d3f5', color: '#1e3a8a' };
                                                }
                                                if (kind === 'disclosure_request') {
                                                  if (c.status === 'submitted') return { label: 'Disclosure Received', bg: '#acf44e', color: '#1a1a1a', bold: true };
                                                  if (c.status === 'failed')    return { label: 'Disclosure Failed',   bg: '#fecdd3', color: '#9f1239', bold: true };
                                                  if (c.status === 'pending')   return { label: 'Disclosure Pending…', bg: '#e5e7eb', color: '#374151' };
                                                  return { label: 'Disclosure Requested', bg: '#c5d3f5', color: '#1e3a8a' };
                                                }
                                                if (c.status === 'psa_routing_initiated') return { label: 'PSA Sent to Sellers', bg: '#acf44e', color: '#1a1a1a', bold: true };
                                                if (c.status === 'broker_info_submitted') return { label: 'Broker Info Submitted', bg: '#3a6ad9', color: '#fff' };
                                                if (c.status === 'accepted')              return { label: 'Accepted', bg: '#acf44e', color: '#1a1a1a' };
                                                if (c.status === 'comments_received')     return { label: 'Comments Received', bg: '#ffdd86', color: '#7a5200' };
                                                if (c.status === 'viewed')                return { label: 'Viewed', bg: '#ffdd86', color: '#7a5200' };
                                                if (c.status === 'failed')                return { label: 'Failed', bg: '#fecdd3', color: '#9f1239', bold: true };
                                                if (c.status === 'pending')               return { label: 'Pending…', bg: '#e5e7eb', color: '#374151' };
                                                return { label: 'Sent', bg: '#c5d3f5', color: '#1e3a8a' };
                                              })();
                                              const expanded = expandedComm === c.id;
                                              return (
                                                <div key={c.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                      <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                                                        {c.listing_agent_name || 'Listing Agent'}
                                                        <span style={{ color: '#6b7280', fontWeight: 400 }}> · {c.listing_agent_email || '—'}</span>
                                                      </div>
                                                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                                        {c.status === 'failed' ? 'Attempted' : c.status === 'skipped' ? 'Attempted (no LA email found)' : 'Sent'}{' '}
                                                        {c.email_sent_at ? new Date(c.email_sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                                                        {c.viewed_at && <> · Viewed {new Date(c.viewed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</>}
                                                        {c.submitted_at && <> · Submitted {new Date(c.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</>}
                                                        {c.cc_email && <> · CC: {c.cc_email}</>}
                                                        {c.resend_email_id && <> · Resend: <code style={{ fontSize: 10 }}>{c.resend_email_id.slice(0, 12)}…</code></>}
                                                      </div>
                                                      {(c.purchase_price != null || c.earnest_money != null || c.close_days != null) && (
                                                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                          {c.purchase_price != null && (<span><strong>Offer:</strong> ${Number(c.purchase_price).toLocaleString()}</span>)}
                                                          {c.earnest_money  != null && (<span><strong>EM:</strong> ${Number(c.earnest_money).toLocaleString()}</span>)}
                                                          {c.close_days     != null && (<span><strong>Close:</strong> {c.close_days} days</span>)}
                                                        </div>
                                                      )}
                                                      {c.status === 'failed' && c.last_error && (
                                                        <div style={{ marginTop: 6, padding: '6px 10px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 6, fontSize: 11, color: '#9f1239' }}>
                                                          <strong>Delivery error:</strong> {c.last_error}
                                                        </div>
                                                      )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                                      <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: 10, fontWeight: badge.bold ? 800 : 700, background: badge.bg, color: badge.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>{badge.label}</span>
                                                      <EmailDeliveryPill
                                                        status={c.delivery_status}
                                                        bounceReason={c.bounce_reason}
                                                        lastEventAt={c.delivery_last_event_at}
                                                      />
                                                      {c.offer_filename && (
                                                        <>
                                                          <button
                                                            onClick={async () => {
                                                              try {
                                                                const resp = await axios.get(`${API}/api/admin/offers/${encodeURIComponent(c.offer_filename)}`, { headers, responseType: 'blob' });
                                                                const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                                                window.open(url, '_blank');
                                                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                                                              } catch (err) {
                                                                alert('Unable to open offer PDF: ' + (err.response?.status === 404 ? 'file not found on server' : (err.response?.data?.error || err.message)));
                                                              }
                                                            }}
                                                            style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                            View
                                                          </button>
                                                          <button
                                                            onClick={() => handleResendOfferComm(c)}
                                                            disabled={resendingCommId === c.id}
                                                            title={`Re-fires this offer to ${c.listing_agent_email}${c.cc_email ? ' (cc ' + c.cc_email + ')' : ' (cc contracts@godoorly.com)'}`}
                                                            style={{ padding: '4px 10px', background: resendingCommId === c.id ? '#e5e7eb' : '#fff', color: resendingCommId === c.id ? '#9ca3af' : '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: resendingCommId === c.id ? 'wait' : 'pointer' }}>
                                                            {resendingCommId === c.id ? 'Resending…' : 'Resend'}
                                                          </button>
                                                        </>
                                                      )}
                                                      {c.status === 'comments_received' && (
                                                        <button onClick={() => setExpandedComm(expanded ? null : c.id)}
                                                          style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                          {expanded ? 'Hide' : 'View Comments'}
                                                        </button>
                                                      )}
                                                      {kind === 'disclosure_request' && c.submitted_at && (
                                                        <button
                                                          onClick={async () => {
                                                            try {
                                                              const resp = await axios.get(`${API}/api/admin/borrowers/${id}/disclosure/pdf`, { headers, responseType: 'blob' });
                                                              const url = URL.createObjectURL(resp.data);
                                                              window.open(url, '_blank');
                                                              setTimeout(() => URL.revokeObjectURL(url), 60000);
                                                            } catch (err) {
                                                              alert('Unable to open disclosure PDF: ' + (err.response?.data?.error || err.message));
                                                            }
                                                          }}
                                                          style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                          View PDF
                                                        </button>
                                                      )}
                                                    </div>
                                                  </div>
                                                  {expanded && c.comments && (
                                                    <div style={{ marginTop: 10, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                                      {c.comments}
                                                      {c.comments_submitted_at && (
                                                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8 }}>
                                                          Received {new Date(c.comments_submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })()}

                                    {/* Accepted Offer — broker-info-prefilled PDF created by
                                        routeABPSAWithBrokerInfo. */}
                                    {acceptedOfferAvailable && (
                                      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                                        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Accepted Offer</div>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                          <span style={{ background: '#acf44e', color: '#111827', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>✓ Offer Accepted</span>
                                          <button
                                            onClick={async () => {
                                              try {
                                                const resp = await axios.get(`${API}/api/admin/borrowers/${user.id}/documents/offer_accepted`, { headers, responseType: 'blob' });
                                                const blobUrl = URL.createObjectURL(resp.data);
                                                const a = document.createElement('a');
                                                a.href = blobUrl;
                                                a.download = `Accepted_Offer_${user.id}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                                              } catch (e) {
                                                alert('Failed to download accepted offer: ' + (e.response?.data?.error || e.message));
                                              }
                                            }}
                                            style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                                          >
                                            Download Accepted Offer
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Reject / Withdraw Offer lives on Step 5 now —
                                        Step 4 is just generation + sending. */}
                                  </div>
                                </div>
                                </>
                              );
                            }
                            if (stepNum === 5) {
                              // Step 5 = Accept. Auto-completes when the
                              // Listing Agent accepts via the OfferReview
                              // portal (application_status flips to
                              // offer_accepted) — or admin clicks Manually
                              // Accept Offer here (sets
                              // offer_requests.manual_accept_offer_at and
                              // flips application_status the same way).
                              // Step 6 (Contract Execution) stays locked
                              // until one of those fires.
                              const manualAccepted = !!offerRequest?.manual_accept_offer_at;
                              const POST_ACCEPT_STAGES = [
                                'offer_accepted', 'under_contract', 'option_period',
                                'clear_to_close', 'closing', 'closed', 'funded',
                              ];
                              const laAccepted = POST_ACCEPT_STAGES.includes(user?.application_status);
                              const accepted = manualAccepted || laAccepted;

                              // Find the latest comms row with
                              // accepted_at set — that's the canonical
                              // acceptance signal. The status column
                              // progresses through subsequent values
                              // (broker_info_submitted, psa_routing_initiated)
                              // after the LA accepts, but accepted_at
                              // stays stamped, so searching by it is
                              // robust to status drift.
                              const acceptedComm = (offerComms || []).find(c => c.accepted_at);
                              const acceptedAtRaw = manualAccepted
                                ? offerRequest?.manual_accept_offer_at
                                : (acceptedComm?.accepted_at || null);
                              const acceptedAtStr = acceptedAtRaw
                                ? new Date(acceptedAtRaw).toLocaleString('en-US', {
                                    month: 'long', day: 'numeric', year: 'numeric',
                                    hour: 'numeric', minute: '2-digit',
                                  })
                                : null;

                              // View handler — manual-accepted rows open
                              // the inline details modal (sellers + terms +
                              // notes); LA-accepted rows stream the
                              // broker-info-prefilled PDF and open it in a
                              // new tab. Same blob-URL pattern Step 4's
                              // Offer History View buttons already use.
                              const handleViewAccepted = async () => {
                                if (manualAccepted) {
                                  setAcceptedDetailsRow(acceptedComm || null);
                                  return;
                                }
                                try {
                                  const resp = await axios.get(`${API}/api/admin/borrowers/${user.id}/documents/offer_accepted`, { headers, responseType: 'blob' });
                                  const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (err) {
                                  alert('Unable to open accepted offer PDF: ' + (err.response?.data?.error || err.message));
                                }
                              };
                              // Show View whenever an acceptance signal
                              // exists. For manual: any comms row we found
                              // (we wrote it ourselves at accept time). For
                              // LA: keep acceptedOfferAvailable as the
                              // primary signal but fall back to "did we
                              // find an accepted comms row" so the button
                              // still shows during the brief window
                              // between LA-accept and broker-info-prefill.
                              const canView = manualAccepted
                                ? !!acceptedComm
                                : (acceptedOfferAvailable || !!acceptedComm);

                              return (
                                <div style={baseCardStyle}>
                                  <div style={{ marginBottom: 12 }}><strong>Accept Offer</strong></div>
                                  {accepted ? (
                                    <div style={{ padding: '12px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 13, lineHeight: 1.5 }}>
                                      <div style={{ fontWeight: 700, marginBottom: 6 }}>
                                        ✓ {manualAccepted
                                          ? 'Offer manually accepted'
                                          : 'Offer accepted by Listing Agent via the OfferReview portal'}
                                      </div>
                                      {acceptedAtStr && (
                                        <div style={{ fontSize: 12, color: '#15803d', marginBottom: 10 }}>
                                          Accepted {acceptedAtStr}
                                        </div>
                                      )}
                                      {canView && (
                                        <button
                                          onClick={handleViewAccepted}
                                          style={{ padding: '6px 14px', background: '#fff', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                                          {manualAccepted ? 'View Details →' : 'View PDF →'}
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      <div style={{ marginBottom: 12, color: '#6b7280', lineHeight: 1.5 }}>
                                        Awaiting Listing Agent acceptance via the OfferReview portal. This step auto-completes when they accept.
                                      </div>
                                      <div style={{ padding: '10px 12px', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                        <div style={{ fontSize: 11, color: '#6b7280', flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                                          Listing agent used their own template / external PSA?
                                          Enter the accepted terms manually.
                                        </div>
                                        <button
                                          onClick={() => { setManualAcceptError(''); setManualAcceptOpen(true); }}
                                          style={{ padding: '7px 14px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                          Manually Accept Offer
                                        </button>
                                      </div>
                                    </>
                                  )}
                                  {/* Reject / Withdraw Offer — pre-CTC only.
                                      Same status guard as the old Step 4
                                      location; this is the natural home
                                      now that Step 5 is the Accept/decline
                                      decision surface. */}
                                  {(['offer_submitted', 'offer_accepted', 'option_period', 'under_contract', 'loan_docs', 'closing_disclosures', 'closing'].includes(status)) && (
                                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end' }}>
                                      <button
                                        type="button"
                                        onClick={() => { setRejectStep(1); setRejectReason(''); setRejectDetail(''); setRejectModalOpen(true); }}
                                        style={{ padding: '8px 16px', background: '#fff', color: '#ff082f', border: '1px solid #fecdd3', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                      >
                                        Reject / Withdraw Offer
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            if (stepNum === 6) {
                              // ── Borrower Review signals — pulled
                              // directly from the columns the borrower's
                              // OfferAcceptedReview flow stamps. Step
                              // labels mirror the borrower-side screens:
                              //   le_intent_to_proceed_at        → Step 1 (LE acknowledged)
                              //   disclosures_confirmed_at       → Step 2 (Disclosures reviewed)
                              //   services_disclosure_accepted_at→ Step 3 (Services disclosure)
                              //   purchase_intent_to_proceed_at  → Step 5 (Intent to Proceed)
                              //   offer_review_completed_at      → entire flow finished
                              const leAt   = user?.le_intent_to_proceed_at || null;
                              const discAt = user?.disclosures_confirmed_at || null;
                              const svcAt  = user?.services_disclosure_accepted_at || null;
                              const itpAt  = user?.purchase_intent_to_proceed_at || null;
                              const reviewAt = user?.offer_review_completed_at || null;
                              const leDone   = !!leAt;
                              const discDone = !!discAt;
                              const svcDone  = !!svcAt;
                              const itpDone  = !!itpAt;
                              const reviewDone = !!reviewAt;
                              // Short relative-only formatter — keeps the
                              // per-row strap from blowing out the card.
                              const fmtTs = (raw) => raw
                                ? new Date(raw).toLocaleString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric',
                                    hour: 'numeric', minute: '2-digit',
                                  })
                                : null;

                              // ── A-B PSA package state. Source-of-
                              // truth: psa_esign_packages.status +
                              // ab_sellers_completed_at + all_signed_at,
                              // pulled by fetchAbPsaPackage above. Falls
                              // back to documents.psa_ab_signed when the
                              // package row isn't populated yet
                              // (manual-upload path doesn't always
                              // create one before finalizing).
                              // Scope to the CURRENT offer/property so a prior
                              // offer's executed A-B PSA doesn't mark this step done.
                              const currentOfferId = offerRequest?.id ?? null;
                              const abSignedDoc  = (documents || []).find(d => d.document_type === 'psa_ab_signed' && (currentOfferId == null || d.offer_request_id === currentOfferId));
                              const abSigned     = !!abSignedDoc;
                              const pkg          = abPsaPackage?.package || null;
                              const pkgStatus    = pkg?.status || null;
                              // Fully executed requires DOORLY's countersign. For the in-app
                              // package flow that means status === 'completed' (set by the
                              // Jesse countersign) — NOT merely a psa_ab_signed doc, which
                              // also exists after sellers-only signing (status
                              // 'ab_psa_sellers_signed'). The abSigned-doc shortcut only
                              // applies to the manual-upload path, where no package row
                              // exists and the uploaded PDF is the executed contract.
                              const doorlyCountersigned = pkgStatus === 'completed' || !!pkg?.all_signed_at;
                              const pkgFullyExecuted = pkg ? doorlyCountersigned : abSigned;
                              // Timestamp pulled per lifecycle stage so the
                              // pill always has a "since" / "at" strap.
                              //   Pending Borrower Review → no ts (no event yet)
                              //   Routing → review completed at
                              //   Pending Sellers → pkg.created_at
                              //   Pending Doorly → ab_sellers_completed_at
                              //   Fully Executed → all_signed_at || doc upload_date
                              let abStatusLabel, abStatusBg, abStatusText, abStatusBorder, abEventLabel, abEventAt;
                              if (pkgFullyExecuted) {
                                abStatusLabel = '✓ Fully Executed';
                                abStatusBg = '#f0fdf4'; abStatusText = '#166534'; abStatusBorder = '#bbf7d0';
                                abEventLabel = 'Executed';
                                abEventAt    = pkg?.all_signed_at || abSignedDoc?.upload_date || null;
                              } else if (pkgStatus === 'ab_psa_sellers_signed' || pkg?.ab_sellers_completed_at) {
                                abStatusLabel = 'Sellers Signed · Pending Executive Signature';
                                abStatusBg = '#fef9ee'; abStatusText = '#b45309'; abStatusBorder = '#fbbf24';
                                abEventLabel = 'Sellers signed';
                                abEventAt    = pkg?.ab_sellers_completed_at || null;
                              } else if (pkg) {
                                abStatusLabel = 'Pending Seller Signatures';
                                abStatusBg = '#eff6ff'; abStatusText = '#1e40af'; abStatusBorder = '#bfdbfe';
                                abEventLabel = 'Routed to sellers';
                                abEventAt    = pkg?.created_at || null;
                              } else if (!reviewDone) {
                                abStatusLabel = 'Pending Borrower Review';
                                abStatusBg = '#f9fafb'; abStatusText = '#6b7280'; abStatusBorder = '#e5e7eb';
                                abEventLabel = null;
                                abEventAt    = null;
                              } else {
                                abStatusLabel = 'Ready to Route';
                                abStatusBg = '#fffbeb'; abStatusText = '#92400e'; abStatusBorder = '#fde68a';
                                abEventLabel = 'Review completed';
                                abEventAt    = reviewAt;
                              }
                              const abEventStr = abEventAt && abEventLabel
                                ? `${abEventLabel} ${fmtTs(abEventAt)}`
                                : null;

                              const handleViewAbPsa = async () => {
                                try {
                                  const signed = (documents || []).find(d => d.document_type === 'psa_ab_signed');
                                  if (!signed?.id) {
                                    alert('No executed A-B PSA on file yet.');
                                    return;
                                  }
                                  const resp = await axios.get(`${API}/api/admin/borrowers/${id}/documents/${signed.id}/download`, { headers, responseType: 'blob' });
                                  const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (err) {
                                  alert('Unable to open A-B PSA: ' + (err?.response?.data?.error || err.message));
                                }
                              };

                              const Row = ({ label, done, at }) => (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, fontSize: 13, lineHeight: 1.4 }}>
                                  <span style={{ color: done ? '#16a34a' : '#9ca3af', marginTop: 1 }}>{done ? '✓' : '○'}</span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ color: done ? '#111827' : '#6b7280' }}>{label}</div>
                                    {done && at && (
                                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{fmtTs(at)}</div>
                                    )}
                                  </div>
                                </div>
                              );
                              return (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                  <div style={baseCardStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                      <strong>Borrower Review</strong>
                                      {reviewDone && (
                                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                          ✓ Complete
                                        </span>
                                      )}
                                    </div>
                                    <Row label="LE Acknowledged"          done={leDone}   at={leAt} />
                                    <Row label="Initial Disclosures"      done={discDone} at={discAt} />
                                    <Row label="Transaction Services"     done={svcDone}  at={svcAt} />
                                    <Row label="Intent to Proceed Signed" done={itpDone}  at={itpAt} />
                                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6', fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                                      {reviewDone
                                        ? <>Review completed {fmtTs(reviewAt)} — route the A-B PSA from the card on the right.</>
                                        : 'Once the borrower finishes this review, you can route the A-B PSA from the card on the right.'}
                                    </div>
                                  </div>
                                  <div style={baseCardStyle}>
                                    <div style={{ marginBottom: 10 }}><strong>A-B PSA</strong></div>
                                    <span style={{
                                      display: 'inline-block',
                                      fontSize: 11, fontWeight: 700,
                                      padding: '4px 10px', borderRadius: 9999,
                                      background: abStatusBg, color: abStatusText,
                                      border: `1px solid ${abStatusBorder}`,
                                      marginBottom: abEventStr ? 4 : 12,
                                    }}>
                                      {abStatusLabel}
                                    </span>
                                    {abEventStr && (
                                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
                                        {abEventStr}
                                      </div>
                                    )}
                                    {/* Per-seller signing-email status + Resend.
                                        Latest token per seller; delivery pill is
                                        driven by the Resend webhook. */}
                                    {(() => {
                                      // Tokens table is shared between sellers and the
                                      // executive countersign — drop Jesse's row
                                      // (signer_role='doorly') so he only appears in the
                                      // dedicated Executive Signature section below.
                                      // Email-match is a belt-and-suspenders fallback for
                                      // any legacy row created before signer_role was set.
                                      const allTokens = (abPsaPackage?.tokens || []).filter(t => {
                                        if (t.signer_role === 'doorly') return false;
                                        if ((t.signer_email || '').toLowerCase() === 'jesse@godoorly.com') return false;
                                        return true;
                                      });
                                      const latestByEmail = {};
                                      allTokens.forEach(t => { latestByEmail[(t.signer_email || '').toLowerCase()] = t; });
                                      const sellerTokens = Object.values(latestByEmail);
                                      if (sellerTokens.length === 0) return null;
                                      return (
                                        <div style={{ marginBottom: 12 }}>
                                          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                                            Seller Signing Emails
                                          </div>
                                          {sellerTokens.map(t => (
                                            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 0', borderTop: '1px solid #f3f4f6' }}>
                                              <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontSize: 12, color: '#111827', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                  {t.signer_name || t.signer_email}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                                  {t.used_at ? `Signed ${fmtTs(t.used_at)}` : t.email_sent_at ? `Sent ${fmtTs(t.email_sent_at)}` : 'Not sent yet'}
                                                </div>
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                                <EmailDeliveryPill status={t.delivery_status} bounceReason={t.bounce_reason} lastEventAt={t.delivery_last_event_at} />
                                                {!t.used_at && !pkgFullyExecuted && (
                                                  <button onClick={() => handleResendAbPsa(t.signer_email)} disabled={!!abPsaResending[t.signer_email]}
                                                    style={{ padding: '5px 12px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 7, cursor: abPsaResending[t.signer_email] ? 'default' : 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', opacity: abPsaResending[t.signer_email] ? 0.6 : 1 }}>
                                                    {abPsaResending[t.signer_email] ? 'Resending…' : 'Resend'}
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                    {/* Executive (Doorly) counter-signature —
                                        email routed only to jesse@godoorly.com,
                                        with delivery status + resend. */}
                                    {pkg && (() => {
                                      const csStatus = pkg.jesse_countersign_delivery_status;
                                      const csSentAt = pkg.jesse_countersign_email_sent_at;
                                      const awaitingExec = pkgStatus === 'ab_psa_sellers_signed' && !pkgFullyExecuted;
                                      return (
                                        <div style={{ marginBottom: 12 }}>
                                          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                                            Executive Signature
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 0', borderTop: '1px solid #f3f4f6' }}>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                              <div style={{ fontSize: 12, color: '#111827', fontWeight: 600 }}>jesse@godoorly.com</div>
                                              <div style={{ fontSize: 11, color: pkgFullyExecuted ? '#166534' : awaitingExec ? '#b45309' : '#9ca3af' }}>
                                                {pkgFullyExecuted
                                                  ? `✓ Countersigned ${fmtTs(pkg.all_signed_at)}`
                                                  : awaitingExec
                                                    ? (csSentAt ? `Pending Executive Signature · emailed ${fmtTs(csSentAt)}` : 'Pending Executive Signature')
                                                    : 'Awaiting seller signatures'}
                                              </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                              {!pkgFullyExecuted && (
                                                <EmailDeliveryPill status={csStatus} bounceReason={pkg.jesse_countersign_bounce_reason} lastEventAt={pkg.jesse_countersign_delivery_last_event_at} />
                                              )}
                                              {awaitingExec && (
                                                <button onClick={handleResendCountersign} disabled={countersignResending}
                                                  style={{ padding: '5px 12px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 7, cursor: countersignResending ? 'default' : 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', opacity: countersignResending ? 0.6 : 1 }}>
                                                  {countersignResending ? 'Resending…' : 'Resend'}
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    {pkgFullyExecuted && (
                                      <div>
                                        <button onClick={handleViewAbPsa}
                                          style={{ padding: '7px 14px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                                          View PDF →
                                        </button>
                                      </div>
                                    )}
                                    {!pkgFullyExecuted && (
                                      <>
                                        {/* Route PSA — manual admin action; unlocks once the
                                            borrower completes their OfferAcceptedReview. Hidden
                                            once a package exists (already routed). */}
                                        {!pkg && (
                                          <div style={{ marginBottom: 12 }}>
                                            <button
                                              onClick={handleRouteAbPsa}
                                              disabled={!reviewDone || abPsaRouting}
                                              title={!reviewDone ? 'Unlocks when the borrower completes their review' : 'Send the A-B PSA to the sellers for signature'}
                                              style={{
                                                padding: '8px 16px',
                                                background: (!reviewDone || abPsaRouting) ? '#9ca3af' : '#3a6ad9',
                                                color: '#fff', border: 'none', borderRadius: 7,
                                                cursor: (!reviewDone || abPsaRouting) ? 'not-allowed' : 'pointer',
                                                fontSize: 13, fontWeight: 700,
                                              }}>
                                              {abPsaRouting ? 'Routing…' : (reviewDone ? 'Route PSA →' : '🔒 Route PSA')}
                                            </button>
                                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
                                              {reviewDone
                                                ? 'Sends the seller signing emails for the A-B PSA.'
                                                : 'Unlocks once the borrower completes their offer review.'}
                                            </div>
                                            {abPsaRouteError && (
                                              <div style={{ marginTop: 6, fontSize: 11, color: '#991b1b' }}>{abPsaRouteError}</div>
                                            )}
                                          </div>
                                        )}
                                        {/* Manual upload — externally signed PSAs */}
                                        <div style={{ marginTop: 4, padding: '10px 12px', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8 }}>
                                          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, lineHeight: 1.5 }}>
                                            Signed externally? Upload the executed PDF — it'll populate the Closings + Documents tabs and unlock Step 7.
                                          </div>
                                          <input
                                            ref={abPsaFileInputRef}
                                            type="file" accept="application/pdf"
                                            onChange={e => handleAbPsaManualUpload(e.target.files?.[0])}
                                            style={{ display: 'none' }}
                                          />
                                          <button
                                            onClick={() => abPsaFileInputRef.current?.click()}
                                            disabled={abPsaUploading}
                                            style={{ padding: '7px 14px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 7, cursor: abPsaUploading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, opacity: abPsaUploading ? 0.6 : 1 }}>
                                            {abPsaUploading ? 'Uploading…' : 'Upload Executed A-B PSA'}
                                          </button>
                                          {abPsaUploadError && (
                                            <div style={{ marginTop: 8, fontSize: 11, color: '#991b1b' }}>{abPsaUploadError}</div>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            if (stepNum === 7) {
                              // ── B-C PSA lifecycle. All signing timestamps
                              // live on option_periods (psa_signed_primary_at /
                              // psa_signed_coborrower_at / psa_signed_doorly_at,
                              // plus psa_package_status). Co-borrower presence
                              // comes from loanConnection (parent fetches it
                              // as `coBorrower`).
                              const op           = optionPeriod || {};
                              const primaryAt    = op.psa_signed_primary_at    || null;
                              const coAt         = op.psa_signed_coborrower_at || null;
                              const doorlyAt     = op.psa_signed_doorly_at     || null;
                              const hasCoBorrower = !!coBorrower?.id;
                              // Scope to the CURRENT offer/property so a prior offer's
                              // B-C PSA doesn't mark this done or flip the Step 7 button
                              // to "Regenerate". Legacy/untagged rows (offer_request_id
                              // NULL) are excluded once we know the current offer id.
                              const currentOfferId = offerRequest?.id ?? null;
                              const inCurrentOffer = (d) => currentOfferId == null || d.offer_request_id === currentOfferId;
                              // "Fully Executed" requires Doorly's countersign. `borrower_psa_signed`
                              // is the borrower-only signed doc (written before Jesse countersigns) so
                              // it must NOT mark Step 7 done. Only `borrower_psa_fully_executed` (post-
                              // countersign artifact) or the psa_signed_doorly_at timestamp qualifies.
                              const bcExecutedDoc = (documents || []).find(d => d.document_type === 'borrower_psa_fully_executed' && inCurrentOffer(d));
                              const bcUnsignedExists = (documents || []).some(d => d.document_type === 'borrower_psa' && inCurrentOffer(d));
                              const bcSigned     = !!bcExecutedDoc || !!doorlyAt;

                              // Local fmt (Step 6 has its own scope; redefine
                              // here so this block stays self-contained).
                              const fmtTs7 = (raw) => raw
                                ? new Date(raw).toLocaleString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric',
                                    hour: 'numeric', minute: '2-digit',
                                  })
                                : null;

                              // Status pill mapping.
                              //   No PSA on file yet                                → Awaiting B-C PSA Generation
                              //   PSA exists, primary not signed                    → Pending Borrower Signature
                              //   Primary signed, co-borrower exists + not signed   → Pending Co-Borrower Signature
                              //   All borrowers signed, Doorly not signed           → Pending Doorly Signature
                              //   Doorly countersigned (or fully-executed doc)      → Fully Executed
                              let bcStatusLabel, bcStatusBg, bcStatusText, bcStatusBorder, bcEventLabel, bcEventAt;
                              if (bcSigned) {
                                bcStatusLabel = '✓ Fully Executed';
                                bcStatusBg = '#f0fdf4'; bcStatusText = '#166534'; bcStatusBorder = '#bbf7d0';
                                bcEventLabel = 'Executed';
                                bcEventAt    = doorlyAt || bcExecutedDoc?.upload_date || null;
                              } else if (primaryAt && (!hasCoBorrower || coAt) && !doorlyAt) {
                                bcStatusLabel = 'Pending Doorly Signature';
                                bcStatusBg = '#fef9ee'; bcStatusText = '#b45309'; bcStatusBorder = '#fbbf24';
                                bcEventLabel = hasCoBorrower ? 'Co-borrower signed' : 'Borrower signed';
                                bcEventAt    = coAt || primaryAt;
                              } else if (primaryAt && hasCoBorrower && !coAt) {
                                bcStatusLabel = 'Pending Co-Borrower Signature';
                                bcStatusBg = '#eff6ff'; bcStatusText = '#1e40af'; bcStatusBorder = '#bfdbfe';
                                bcEventLabel = 'Borrower signed';
                                bcEventAt    = primaryAt;
                              } else if (bcUnsignedExists) {
                                bcStatusLabel = 'Pending Borrower Signature';
                                bcStatusBg = '#eff6ff'; bcStatusText = '#1e40af'; bcStatusBorder = '#bfdbfe';
                                bcEventLabel = null;
                                bcEventAt    = null;
                              } else {
                                bcStatusLabel = 'Awaiting B-C PSA Generation';
                                bcStatusBg = '#f9fafb'; bcStatusText = '#6b7280'; bcStatusBorder = '#e5e7eb';
                                bcEventLabel = null;
                                bcEventAt    = null;
                              }
                              const bcEventStr = bcEventAt && bcEventLabel
                                ? `${bcEventLabel} ${fmtTs7(bcEventAt)}`
                                : null;

                              const handleViewBcPsa = async () => {
                                try {
                                  if (!bcExecutedDoc?.id) {
                                    alert('No fully-executed B-C PSA on file yet.');
                                    return;
                                  }
                                  const resp = await axios.get(`${API}/api/admin/borrowers/${id}/documents/${bcExecutedDoc.id}/download`, { headers, responseType: 'blob' });
                                  const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (err) {
                                  alert('Unable to open B-C PSA: ' + (err?.response?.data?.error || err.message));
                                }
                              };

                              // Per-signer rows under the pill so admins can
                              // see exactly which signature is outstanding.
                              const Row7 = ({ label, done, at }) => (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, fontSize: 13, lineHeight: 1.4 }}>
                                  <span style={{ color: done ? '#16a34a' : '#9ca3af', marginTop: 1 }}>{done ? '✓' : '○'}</span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ color: done ? '#111827' : '#6b7280' }}>{label}</div>
                                    {done && at && (
                                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{fmtTs7(at)}</div>
                                    )}
                                  </div>
                                </div>
                              );

                              return (
                                <div style={baseCardStyle}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <strong>B-C PSA</strong>
                                    {bcSigned && (
                                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9999, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                        Module Complete
                                      </span>
                                    )}
                                  </div>
                                  <span style={{
                                    display: 'inline-block',
                                    fontSize: 11, fontWeight: 700,
                                    padding: '4px 10px', borderRadius: 9999,
                                    background: bcStatusBg, color: bcStatusText,
                                    border: `1px solid ${bcStatusBorder}`,
                                    marginBottom: bcEventStr ? 4 : 12,
                                  }}>
                                    {bcStatusLabel}
                                  </span>
                                  {bcEventStr && (
                                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>{bcEventStr}</div>
                                  )}

                                  {/* Per-signer breakdown — always rendered
                                      once a PSA exists. Lets the admin see
                                      who's outstanding at a glance without
                                      hopping to Closings. */}
                                  {(bcUnsignedExists || bcSigned) && (
                                    <div style={{ marginTop: 4, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
                                      <Row7 label="Borrower Signed"     done={!!primaryAt} at={primaryAt} />
                                      {hasCoBorrower && (
                                        <Row7 label="Co-Borrower Signed" done={!!coAt}   at={coAt} />
                                      )}
                                      <Row7 label="Doorly Countersigned" done={!!doorlyAt} at={doorlyAt} />
                                    </div>
                                  )}

                                  {/* Generate / route the B-C PSA. Generating
                                      it creates the borrower's signable PSA,
                                      emails them, and flips the loan to Option
                                      Period — the bridge the manual A-B upload
                                      path otherwise leaves open. Hidden once
                                      fully executed. */}
                                  {!bcSigned && (
                                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                                      <button onClick={openBorrowerPsaModal}
                                        style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                        {bcUnsignedExists ? 'Regenerate B-C PSA' : 'Generate & Route B-C PSA →'}
                                      </button>
                                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8, lineHeight: 1.5 }}>
                                        {bcUnsignedExists
                                          ? 'Regenerating replaces the borrower’s current unsigned B-C PSA.'
                                          : 'Generates the B-C PSA, makes it available for the borrower to sign in-app, emails them, and moves the loan to Option Period.'}
                                      </div>
                                    </div>
                                  )}

                                  {bcSigned && (
                                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                                      <button onClick={handleViewBcPsa}
                                        style={{ padding: '8px 16px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                        View PDF →
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            if (stepNum === 8) {
                              // Inspection & Appraisal Access — standalone
                              // component (own hooks) so this if-chain stays
                              // hook-free. Saves to inspectify-access endpoint.
                              return (
                                <InspectifyAccessStep
                                  optionPeriod={optionPeriod}
                                  userId={user.id}
                                  onSaved={fetchData}
                                />
                              );
                            }
                            return null;
                          }}
                        />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Top-of-tab KPI strip: Property · Stage · Listing Agent · MLS Listing.
                      Listing Agent info currently sources from seller_disclosures (populated
                      when admin requests a disclosure); MLS Listing reads user.listing_url /
                      user.mls_number. Both fall back to "Pending MLS integration" so the
                      slots are ready the moment the MLS feed writes into those columns. */}
                  {(() => {
                    // Resolver priority: option_period (post-acceptance canonical) →
                    // latest offer_request (active shopping target) → form_data
                    // (legacy shopping preference, doesn't auto-refresh when the
                    // borrower submits a new offer_request — would otherwise mask
                    // a new address until admin manually edits via the modal).
                    const propertyAddr = optionPeriod?.property_address || offerRequest?.address || formData.property_address;
                    // Match the listingAgentInfo fallback chain (defined at the
                    // component top, line ~3663): offer_requests → seller_disclosures
                    // → linkedListing.listingAgent. Previously this KPI card read
                    // disclosure-only, so SimplyRETS auto-populated values on
                    // offer_requests.listing_agent_* were invisible until an admin
                    // ran the disclosure flow. Office/broker now displayed too.
                    const agentName   = listingAgentInfo.name   || null;
                    const agentEmail  = listingAgentInfo.email  || null;
                    const agentPhone  = listingAgentInfo.phone  || null;
                    const agentOffice = listingAgentInfo.office || null;
                    const listingUrl = user.listing_url || null;
                    const mlsNumber  = user.mls_number  || null;
                    const listPrice  = Number(user.list_price) > 0 ? Number(user.list_price) : null;
                    const daysOnMkt  = Number(user.time_on_market) > 0 ? Number(user.time_on_market) : null;

                    const kpiShell = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 96 };
                    const kpiLabel = { fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 };
                    const kpiValue = { fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.35, wordBreak: 'break-word' };
                    const kpiSub   = { fontSize: 12, color: '#6b7280', lineHeight: 1.35 };
                    const kpiMuted = { fontSize: 13, color: '#9ca3af', fontStyle: 'italic' };

                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 4 }}>
                        {/* Property */}
                        <div style={kpiShell}>
                          <div style={{ ...kpiLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                            <span>Property</span>
                            <button type="button" onClick={openEditPropertyModal}
                              style={{ padding: '3px 8px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}
                              title="Edit property address (propagates across option periods, offers, disclosures, double-close package)">
                              Edit
                            </button>
                          </div>
                          {propertyAddr ? (
                            <button
                              onClick={() => setShowMapModal(true)}
                              title="View on map"
                              style={{ ...kpiValue, background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', color: '#3a6ad9', textDecoration: 'underline', textDecorationColor: '#3a6ad922', textUnderlineOffset: 3 }}>
                              {propertyAddr}
                            </button>
                          ) : (
                            <div style={kpiMuted}>No address yet</div>
                          )}
                        </div>

                        {/* Stage */}
                        <div style={kpiShell}>
                          <div style={kpiLabel}>Stage</div>
                          <div>
                            <span style={{ display: 'inline-block', fontSize: 13, fontWeight: 700, padding: '4px 12px', borderRadius: 9999, backgroundColor: statusMeta.bg, color: statusMeta.color, border: `1px solid ${statusMeta.color}22`, whiteSpace: 'nowrap' }}>
                              {statusMeta.label}
                            </span>
                          </div>
                        </div>

                        {/* Listing Agent */}
                        <div style={kpiShell}>
                          <div style={{ ...kpiLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                            <span>Listing Agent</span>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button type="button" onClick={openEditLaModal}
                                style={{ padding: '3px 8px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}
                                title="Edit listing agent contact info (manual until MLS integration)">
                                Edit
                              </button>
                              {agentEmail && (
                                <button type="button" onClick={() => setLaModalOpen(true)}
                                  style={{ padding: '3px 10px', background: '#111827', color: '#acf44e', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}
                                  title="Send A-B update to listing agent">
                                  Send Update
                                </button>
                              )}
                            </div>
                          </div>
                          {agentName || agentEmail || agentPhone || agentOffice ? (
                            <>
                              {agentName && <div style={kpiValue}>{agentName}</div>}
                              <div style={{ ...kpiSub, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {agentEmail && (
                                  <a href={`mailto:${agentEmail}`} style={{ color: '#3a6ad9', textDecoration: 'none' }}>{agentEmail}</a>
                                )}
                                {agentPhone && (
                                  <a href={`tel:${String(agentPhone).replace(/[^\d+]/g, '')}`} style={{ color: '#3a6ad9', textDecoration: 'none' }}>{agentPhone}</a>
                                )}
                                {agentOffice && (
                                  <div style={{ color: '#6b7280' }}>{agentOffice}</div>
                                )}
                              </div>
                            </>
                          ) : (
                            <div style={kpiMuted}>Pending MLS integration</div>
                          )}
                        </div>

                        {/* MLS Listing — hidden behind {false &&} per
                            Property Module rollout. Wiring is preserved
                            so the slot is ready the moment MLS/IDX
                            integration writes into the columns. */}
                        {false && (
                        <div style={kpiShell}>
                          <div style={kpiLabel}>MLS Listing</div>
                          {listingUrl ? (
                            <a href={listingUrl} target="_blank" rel="noreferrer"
                              style={{ ...kpiValue, color: '#3a6ad9', textDecoration: 'none' }}>
                              View Listing ↗
                            </a>
                          ) : (
                            <div style={kpiMuted}>Pending MLS integration</div>
                          )}
                          <div style={kpiSub}>
                            {listPrice ? <div><strong style={{ color: '#111827' }}>{fmt$(listPrice)}</strong> listed</div> : <div style={{ color: '#9ca3af' }}>List price —</div>}
                            {daysOnMkt !== null ? <div>{daysOnMkt} {daysOnMkt === 1 ? 'day' : 'days'} on market</div> : <div style={{ color: '#9ca3af' }}>Days on market —</div>}
                            {mlsNumber && <div style={{ color: '#9ca3af' }}>MLS #{mlsNumber}</div>}
                          </div>
                        </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ─── reZEN transaction status card ─────────────────────
                      Visible from offer_accepted onward. Green when the
                      transaction id is stamped, red with a Retry button
                      when rezen_transaction_error is set, amber while
                      we're waiting on the auto-create after A-B PSA. */}
                  {['offer_accepted','under_contract','option_period','clear_to_close','closing','closed','funded'].includes(user?.application_status) && (
                    <div style={{
                      background: user.rezen_transaction_id ? '#f0fdf4'
                                : user.rezen_transaction_error ? '#fef2f2'
                                : '#fffbeb',
                      border: `1px solid ${
                        user.rezen_transaction_id ? '#bbf7d0'
                        : user.rezen_transaction_error ? '#fca5a5'
                        : '#fde68a'
                      }`,
                      borderRadius: 12,
                      padding: '14px 18px',
                      marginBottom: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          fontSize: 13, fontWeight: 700,
                          color: user.rezen_transaction_id ? '#166534'
                            : user.rezen_transaction_error ? '#991b1b'
                            : '#92400e',
                          marginBottom: 2,
                        }}>
                          {user.rezen_transaction_id
                            ? '✓ reZEN Transaction Created'
                            : user.rezen_transaction_error
                            ? '⚠ reZEN Transaction Failed'
                            : '⏳ reZEN Transaction Pending'}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', wordBreak: 'break-word' }}>
                          {user.rezen_transaction_id
                            ? `ID: ${user.rezen_transaction_id}${user.rezen_transaction_created_at ? ` — Created ${new Date(user.rezen_transaction_created_at).toLocaleDateString()}` : ''}`
                            : user.rezen_transaction_error
                            ? user.rezen_transaction_error
                            : 'Will be created automatically when the A-B PSA Completed webhook fires.'}
                        </div>
                      </div>
                      {/* Manual create — shown whenever no reZEN transaction
                          exists yet (Pending OR Failed). The auto-create only
                          fires on the BoldSign/in-app A-B paths, so a manually
                          uploaded A-B leaves this "Pending" with no auto-trigger;
                          this button lets an admin create it on demand. */}
                      {!user.rezen_transaction_id && (
                        <button
                          onClick={handleManualReZENCreate}
                          disabled={creatingReZEN}
                          style={{
                            background: creatingReZEN ? '#9ca3af' : (user.rezen_transaction_error ? '#991b1b' : '#3a6ad9'),
                            color: '#fff', border: 'none', borderRadius: 8,
                            padding: '8px 14px', fontSize: 12, fontWeight: 700,
                            cursor: creatingReZEN ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                          }}>
                          {creatingReZEN
                            ? (user.rezen_transaction_error ? 'Retrying…' : 'Creating…')
                            : (user.rezen_transaction_error ? 'Retry →' : 'Create reZEN Transaction →')}
                        </button>
                      )}
                    </div>
                  )}

                  {/* ─── Property Documents card ───────────────────────────
                      Quick-view links for the four canonical property docs.
                      Each row pulls from its actual canonical source — they
                      do NOT all live in the documents table:
                        - Valuation Report: users.hc_avm_report_available +
                          GET /property-analysis/avm-report (streams PDF)
                        - Seller's Disclosure: seller_disclosures table +
                          GET /disclosure/pdf (streams PDF). Renders as
                          "Not Required" when offerRequest.is_new_construction
                          is set, since new construction skips disclosure.
                        - A-B / B-C PSA: actual document_type rows in the
                          documents table, opened via the generic doc viewer.
                      Previous version looked all four up against the
                      documents array, which silently failed for Valuation
                      and Disclosure (those types never get inserted there). */}
                  {(() => {
                    // Authenticated blob-open helper — works for any
                    // endpoint that streams a PDF with the Bearer header.
                    const openBlobPdf = async (url, errLabel) => {
                      try {
                        const r = await axios.get(url, { headers, responseType: 'blob' });
                        const blobUrl = URL.createObjectURL(r.data);
                        window.open(blobUrl, '_blank');
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                      } catch (err) {
                        alert(`Unable to open ${errLabel}: ` + (err.response?.data?.error || err.message));
                      }
                    };

                    // Per-row source resolution. Each entry knows whether
                    // it has a usable PDF, whether it's been marked "not
                    // required" (new construction skips disclosure), and
                    // how to open the PDF if available.
                    const isNc = !!offerRequest?.is_new_construction;
                    const discStatus = disclosureData?.disclosure?.status;
                    const discAvailable = discStatus === 'received' || discStatus === 'submitted';
                    // Backend (housecanary.js:280) mirrors the AVM PDF into
                    // documents with document_type='property_valuation'. The
                    // previous lookup used 'hc_valuation_report' which has
                    // never existed as a document_type — valDoc was always
                    // undefined, leaving the row entirely dependent on
                    // user.hc_avm_report_available to flip the View button.
                    const valDoc       = (documents || []).find(d => d.document_type === 'property_valuation');
                    const abPsaDoc     = (documents || []).find(d => d.document_type === 'psa_ab_signed');
                    const bcPsaDoc     = (documents || []).find(d => ['borrower_psa_signed','borrower_psa_fully_executed'].includes(d.document_type));

                    const items = [
                      {
                        label: 'Valuation Report (HouseCanary)',
                        // Flip to View whenever Step 3 (Valuation) has
                        // completed — i.e. an AVM value landed. Previously
                        // gated only on hc_avm_report_available, which is
                        // the cached-PDF flag; if HouseCanary's PDF endpoint
                        // failed (separate billing, occasional 500s) the
                        // row stayed "Not yet available" even though the
                        // analytics call succeeded. Now any successful
                        // Step 3 run (hc_avm_value > 0) flips this — the
                        // backend /property-analysis/avm-report endpoint
                        // serves cached PDF when available and surfaces a
                        // clean 404 alert otherwise.
                        available: !!user?.hc_avm_report_available
                          || !!valDoc
                          || Number(user?.hc_avm_value) > 0,
                        onView: () => valDoc
                          ? openBlobPdf(`${API}/api/admin/borrowers/${id}/documents/${valDoc.id}/view`, 'valuation report')
                          : openBlobPdf(`${API}/api/admin/borrowers/${id}/property-analysis/avm-report`, 'AVM report'),
                      },
                      {
                        label: "Seller's Disclosure",
                        notRequired: isNc,
                        available: discAvailable,
                        onView: () => openBlobPdf(`${API}/api/admin/borrowers/${id}/disclosure/pdf`, 'disclosure'),
                      },
                      {
                        label: 'A-B Purchase & Sale Agreement',
                        available: !!abPsaDoc,
                        onView: () => abPsaDoc && openBlobPdf(`${API}/api/admin/borrowers/${id}/documents/${abPsaDoc.id}/view`, 'A-B PSA'),
                      },
                      {
                        label: 'B-C Purchase & Sale Agreement',
                        available: !!bcPsaDoc,
                        onView: () => bcPsaDoc && openBlobPdf(`${API}/api/admin/borrowers/${id}/documents/${bcPsaDoc.id}/view`, 'B-C PSA'),
                      },
                    ];

                    return (
                      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px', marginBottom: 4 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 12 }}>
                          Property Documents
                        </div>
                        {items.map((item, i) => (
                          <div key={item.label} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 0',
                            borderBottom: i < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                          }}>
                            <div style={{ fontSize: 13, color: '#374151' }}>
                              {item.label}
                            </div>
                            {item.notRequired ? (
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#2d6a0a', background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: 6, padding: '3px 9px' }}>
                                Not Required
                              </span>
                            ) : item.available ? (
                              <button onClick={item.onView}
                                style={{ background: '#eff6ff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                View
                              </button>
                            ) : (
                              <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Not yet available</span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Listing Agent Update modal */}
                  <ListingAgentUpdateModal
                    open={laModalOpen}
                    onClose={() => setLaModalOpen(false)}
                    onSent={() => { setLaModalOpen(false); fetchLaComms(); }}
                    userId={Number(id)}
                    headers={headers}
                    defaults={{ recipient: listingAgentInfo.email || null }}
                  />

                  {/* Edit Property Address modal — propagates to option_periods,
                      offer_requests, seller_disclosures, double_close_packages,
                      and user_applications.form_data if the key already exists.
                      Historical tables (showings, VOR, net sheet, property
                      analyses, LA comms) are not touched. */}
                  {editPropertyOpen && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                         onClick={() => !editPropertySaving && setEditPropertyOpen(false)}>
                      <div onClick={(e) => e.stopPropagation()}
                           style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>Edit Property Address</h3>
                        <p style={{ margin: '4px 0 18px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                          Updates the borrower's current property across the option period, active offer, disclosure, and closing package. Historical records (past showings, VORs, net sheets) are not changed.
                        </p>
                        <div style={{ marginBottom: 16 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Property Address *</label>
                          <AddressAutocomplete
                            name="edit_property_address"
                            value={editPropertyValue}
                            placeholder="Start typing an address..."
                            onChange={(e) => setEditPropertyValue(e.target.value)}
                            onSelect={({ street, city, state, zip }) => {
                              // Assemble the full address string for the single-field
                              // model the rest of the app uses (option_periods.property_address,
                              // offer_requests.address, etc. are all single strings).
                              const parts = [street, city, [state, zip].filter(Boolean).join(' ').trim()].filter(Boolean);
                              setEditPropertyValue(parts.join(', '));
                            }}
                            className="edit-property-addr-input"
                            autoComplete="street-address"
                          />
                          <style>{`
                            .edit-property-addr-input {
                              width: 100%;
                              /* left padding makes room for AddressAutocomplete's
                                 absolutely-positioned location pin icon — the
                                 component appends pl-9 to className but my
                                 padding shorthand overrides Tailwind, so set
                                 the left value explicitly here. */
                              padding: 9px 12px 9px 36px;
                              border: 1px solid #d1d5db;
                              border-radius: 8px;
                              font-size: 13px;
                              font-family: inherit;
                              box-sizing: border-box;
                              outline: none;
                            }
                            .edit-property-addr-input:focus {
                              border-color: #3a6ad9;
                              box-shadow: 0 0 0 2px #3a6ad922;
                            }
                          `}</style>
                        </div>
                        {editPropertyError && (
                          <div style={{ marginBottom: 12, padding: '8px 10px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>
                            {editPropertyError}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button onClick={() => setEditPropertyOpen(false)} disabled={editPropertySaving}
                            style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: editPropertySaving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                            Cancel
                          </button>
                          <button onClick={handleSavePropertyAddress} disabled={editPropertySaving || !editPropertyValue.trim()}
                            style={{ padding: '8px 18px', background: editPropertySaving || !editPropertyValue.trim() ? '#e5e7eb' : '#3a6ad9', color: editPropertySaving || !editPropertyValue.trim() ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, cursor: editPropertySaving || !editPropertyValue.trim() ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                            {editPropertySaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Edit Listing Agent modal — manual contact entry. Does
                      NOT fire any email. Updates the most recent
                      seller_disclosures row (or creates a stub) so the LA
                      KPI card and the Send Offer modal pre-fill correctly. */}
                  {editLaModalOpen && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                         onClick={() => !editLaSaving && setEditLaModalOpen(false)}>
                      <div onClick={(e) => e.stopPropagation()}
                           style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#111827' }}>Edit Listing Agent</h3>
                        <p style={{ margin: '4px 0 18px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                          Manual contact entry until MLS/IDX integration lands. Saving does NOT send any email.
                        </p>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Agent Name</label>
                          <input type="text" value={editLaForm.listing_agent_name}
                            onChange={(e) => setEditLaForm((p) => ({ ...p, listing_agent_name: e.target.value }))}
                            placeholder="Jane Smith"
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Agent Email *</label>
                          <input type="email" value={editLaForm.listing_agent_email}
                            onChange={(e) => setEditLaForm((p) => ({ ...p, listing_agent_email: e.target.value }))}
                            placeholder="agent@brokerage.com"
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Agent Phone</label>
                          <input type="tel" value={editLaForm.listing_agent_phone}
                            onChange={(e) => setEditLaForm((p) => ({ ...p, listing_agent_phone: e.target.value }))}
                            placeholder="(555) 123-4567"
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        {editLaError && (
                          <div style={{ marginBottom: 12, padding: '8px 10px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>
                            {editLaError}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button onClick={() => setEditLaModalOpen(false)} disabled={editLaSaving}
                            style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: editLaSaving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                            Cancel
                          </button>
                          <button onClick={handleSaveListingAgent} disabled={editLaSaving || !editLaForm.listing_agent_email.trim()}
                            style={{ padding: '8px 18px', background: editLaSaving || !editLaForm.listing_agent_email.trim() ? '#e5e7eb' : '#3a6ad9', color: editLaSaving || !editLaForm.listing_agent_email.trim() ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, cursor: editLaSaving || !editLaForm.listing_agent_email.trim() ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                            {editLaSaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Listing Agent Communication Log — collapsed by default */}
                  <div style={{ ...card, padding: '12px 16px' }}>
                    <button type="button" onClick={() => setLaLogExpanded(e => !e)}
                      style={{ background: 'none', border: 'none', padding: 0, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', textAlign: 'left' }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>📬 Listing Agent Communication Log</span>
                        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>{laComms.length} {laComms.length === 1 ? 'message' : 'messages'}</span>
                      </div>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{laLogExpanded ? '▲' : '▼'}</span>
                    </button>
                    {laLogExpanded && (
                      laComms.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 10, fontStyle: 'italic' }}>
                          No messages sent yet. Updates fire automatically on inspection/appraisal schedule, report receipt, report review, proceed-to-close decision, and closing-date changes. Termination emails are sent manually from the Send Update button.
                        </div>
                      ) : (
                        <div style={{ marginTop: 10, borderTop: '1px solid #f3f4f6' }}>
                          {laComms.map(c => {
                            // Resend delivery status pill. Sourced from
                            // email_log via the GET aggregator (joined by
                            // resend_id parsed out of the
                            // application_events description). Null when
                            // the webhook hasn't fired yet OR the comm
                            // type doesn't write a resend_id (some legacy
                            // rows).
                            const ds = String(c.delivery_status || '').toLowerCase();
                            const dsStyle = (() => {
                              if (ds === 'delivered')   return { bg: '#f0fde4', border: '#acf44e', color: '#3d7a00', label: 'Delivered' };
                              if (ds === 'opened')      return { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: 'Opened' };
                              if (ds === 'clicked')     return { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: 'Clicked' };
                              if (ds === 'sent')        return { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: 'Sent' };
                              if (ds === 'processing')  return { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: 'Processing' };
                              if (ds === 'bounced' || ds === 'bounce')    return { bg: '#fee2e2', border: '#fecaca', color: '#991b1b', label: 'Bounced' };
                              if (ds === 'complained')  return { bg: '#fee2e2', border: '#fecaca', color: '#991b1b', label: 'Complained' };
                              if (ds === 'failed')      return { bg: '#fee2e2', border: '#fecaca', color: '#991b1b', label: 'Failed' };
                              return null;
                            })();
                            return (
                              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 110px 70px', gap: 10, padding: '8px 0', borderBottom: '1px solid #f9fafb', fontSize: 12, alignItems: 'center' }}>
                                <div style={{ color: '#6b7280' }}>
                                  {new Date(c.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, color: '#111827' }}>{c.subject}</div>
                                  <div style={{ color: '#9ca3af', fontSize: 11 }}>{c.event_type.replace(/_/g, ' ')}</div>
                                </div>
                                <div style={{ color: '#6b7280', wordBreak: 'break-all' }}>{c.recipient_email}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  {dsStyle ? (
                                    <span
                                      title={c.bounce_reason || c.delivery_last_event_at || ''}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        fontSize: 10, fontWeight: 700,
                                        padding: '2px 7px', borderRadius: 9999,
                                        background: dsStyle.bg,
                                        border: `1px solid ${dsStyle.border}`,
                                        color: dsStyle.color,
                                        textTransform: 'uppercase', letterSpacing: 0.3,
                                      }}
                                    >
                                      {dsStyle.label}
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>
                                      Pending
                                    </span>
                                  )}
                                  <span style={{ fontSize: 9, fontWeight: 700, color: c.sent_by === 'auto' ? '#2d6a0a' : '#3a6ad9', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                    {c.sent_by === 'auto' ? 'Auto' : 'Manual'}
                                  </span>
                                </div>
                                <div>
                                  {c.resendable === 'intro_email' ? (
                                    <button
                                      type="button"
                                      onClick={() => handleResendIntroEmail()}
                                      disabled={resendIntroLoading}
                                      style={{
                                        padding: '4px 10px',
                                        background: '#fff',
                                        color: '#374151',
                                        border: '1px solid #d1d5db',
                                        borderRadius: 6,
                                        fontSize: 11, fontWeight: 700,
                                        cursor: resendIntroLoading ? 'not-allowed' : 'pointer',
                                      }}
                                      title="Re-fire the Offer Coming In email to the listing agent"
                                    >
                                      {resendIntroLoading ? '…' : 'Resend'}
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )
                    )}
                  </div>

                  {/* Map Modal */}
                  {showMapModal && (() => {
                    const propertyAddr = optionPeriod?.property_address || offerRequest?.address || formData.property_address;
                    const mapQuery = encodeURIComponent(propertyAddr || '');
                    return (
                      <Modal title="Property Location" onClose={() => setShowMapModal(false)}>
                        <div style={{ width: '100%', height: '450px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                          <iframe
                            title="Property Map"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                          />
                        </div>
                        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{propertyAddr}</span>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '12px', color: '#3a6ad9', fontWeight: '600', textDecoration: 'none' }}
                          >
                            Open in Google Maps ↗
                          </a>
                        </div>
                      </Modal>
                    );
                  })()}

                  {/* HouseCanary Valuation — hidden behind {false &&} per
                      Property Module rollout. handleRunHC + hc_* hydration
                      preserved for use inside Property Module Step 3
                      (Valuation). */}
                  {false && (
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>
                          Property Valuation
                          <span style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', marginLeft: '8px' }}>HouseCanary</span>
                        </h3>
                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                          {user.hc_analyzed_at ? `Last run ${new Date(user.hc_analyzed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : user.hc_analysis_status === 'running' ? 'Analysis running...' : 'Not yet analyzed'}
                        </p>
                      </div>
                      <button onClick={handleRunHC} disabled={hcLoading || user.hc_analysis_status === 'running'}
                        style={{ padding: '8px 16px', background: hcLoading || user.hc_analysis_status === 'running' ? '#e5e7eb' : '#3a6ad9', color: hcLoading || user.hc_analysis_status === 'running' ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                        {hcLoading || user.hc_analysis_status === 'running' ? 'Running...' : user.hc_analyzed_at ? 'Re-Run' : 'Run Analysis'}
                      </button>
                    </div>
                    {user.hc_avm_value ? (
                      <>
                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                        {[
                          {
                            label: 'AVM Value',
                            value: '$' + Math.round(user.hc_avm_value).toLocaleString(),
                            color: '#3a6ad9', bold: true,
                            help: "HouseCanary's Automated Valuation Model estimate of the property's current market value. Calculated from recent comparable sales, property characteristics (size, age, lot), and local market trends. Statistical estimate — not a formal appraisal.",
                          },
                          {
                            label: 'Recommended Offer',
                            value: user.hc_recommended_offer ? '$' + Math.round(user.hc_recommended_offer).toLocaleString() : '—',
                            color: '#2d6a0a', bold: true,
                            help: 'AVM × 0.90, rounded to the nearest $1,000. A conservative starting point that leaves room for negotiation and accounts for AVMs running slightly high on unique or recently-renovated homes.',
                          },
                          {
                            label: 'Confidence',
                            value: user.hc_avm_confidence || '—',
                            color: '#374151',
                            help: "Forecast Standard Deviation (FSD) — expected margin of error on the AVM as a percentage. Lower is better: under 5% is high confidence, 5–10% moderate, over 10% means treat the AVM as a loose guide and pull recent comps manually.",
                          },
                          {
                            label: 'Market Grade',
                            value: user.hc_market_grade || '—',
                            color: '#374151',
                            help: "HouseCanary's letter grade (A+ through F) for the ZIP's housing market. Reflects price momentum, sales velocity, and inventory levels. A or B suggests strong demand — useful context when deciding how aggressively to offer.",
                          },
                          {
                            label: 'Flood Zone',
                            value: user.hc_flood_zone || 'None',
                            color: user.hc_flood_zone && user.hc_flood_zone !== 'X' ? '#ff082f' : '#2d6a0a',
                            help: 'FEMA flood zone designation. X = minimal risk, no mandatory flood insurance. A / AE / AH / AO = high-risk inland — flood insurance is required for federally-backed mortgages. V / VE = high-risk coastal.',
                          },
                          {
                            label: 'AVM Range',
                            value: user.hc_avm_low && user.hc_avm_high ? `$${Math.round(user.hc_avm_low / 1000)}k–$${Math.round(user.hc_avm_high / 1000)}k` : '—',
                            color: '#374151',
                            help: "Low and high bounds of the AVM at roughly 68% confidence (one standard deviation). The actual market value most likely falls within this range. A tighter range means HouseCanary is more confident in its estimate.",
                          },
                        ].map(item => (
                          <div key={item.label} style={{ background: '#f9fafb', borderRadius: '10px', padding: '10px 12px', borderLeft: item.bold ? `3px solid ${item.color}` : 'none' }}>
                            <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                              <span>{item.label}</span>
                              {item.help && <HelpTip text={item.help} />}
                            </div>
                            <div style={{ fontSize: item.bold ? '16px' : '14px', fontWeight: item.bold ? '900' : '600', color: item.color }}>{item.value}</div>
                          </div>
                        ))}
                      </div>
                      {user.hc_avm_report_available === true && (
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 13, color: '#6b7280' }}>HouseCanary AVM Report</span>
                          <button
                            onClick={async () => {
                              try {
                                // window.open() doesn't carry the Authorization
                                // header, so fetch the PDF with auth and open
                                // the resulting blob URL instead. Avoids
                                // exposing the JWT in a query string.
                                const r = await axios.get(
                                  `${API}/api/admin/borrowers/${user.id}/property-analysis/avm-report`,
                                  { headers, responseType: 'blob' }
                                );
                                const blobUrl = URL.createObjectURL(r.data);
                                window.open(blobUrl, '_blank');
                                // Revoke after a delay so the new tab has time
                                // to load the URL before the browser frees it.
                                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                              } catch (e) {
                                alert('Failed to load AVM report: ' + (e.response?.data?.error || e.message));
                              }
                            }}
                            style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            View Report PDF
                          </button>
                        </div>
                      )}
                      </>
                    ) : user.hc_analysis_status === 'error' ? (
                      <div style={{ padding: '12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '10px', fontSize: '13px', color: '#ff082f' }}>
                        ⚠ {user.hc_analysis_error
                          || 'Analysis failed — HouseCanary did not return valuation data for this property. Check Render logs for the specific HC endpoint error (most often: address not in HC coverage, or one of the zip/* endpoints failed). Verify the address is correct and try again.'}
                      </div>
                    ) : (user.hc_analysis_status === 'partial' || user.hc_analysis_status === 'complete') && user.hc_analyzed_at && !user.hc_avm_value ? (
                      <div style={{ padding: '12px', background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: '10px', fontSize: '13px', color: '#7a5200' }}>Analysis completed but no valuation data was returned. HouseCanary likely has no AVM coverage for this address. Verify the address is correct (especially for new construction or rural properties).</div>
                    ) : null}
                  </div>
                  )}

                  {/* Seller's Disclosure — hidden behind {false &&} per
                      Property Module rollout. handleRequestDisclosure +
                      handleAnalyzeDisclosure + disclosureData preserved
                      for use inside Property Module Step 2 (Disclosure). */}
                  {false && (
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>Seller's Disclosure</h3>
                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>Request from listing agent or upload manually</p>
                      </div>
                      <button onClick={() => showDisclosureForm ? setShowDisclosureForm(false) : openDisclosureFormPrefilled()}
                        style={{ padding: '7px 14px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                        Request from Agent
                      </button>
                    </div>
                    {showDisclosureForm && (
                      <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '16px', marginBottom: '14px' }}>
                        {disclosureError && (
                          <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>
                            {disclosureError}
                          </div>
                        )}
                        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                          {[
                            { key: 'listing_agent_name', label: 'Agent Name', placeholder: 'Jane Smith' },
                            { key: 'listing_agent_email', label: 'Agent Email *', placeholder: 'agent@brokerage.com' },
                            { key: 'listing_agent_phone', label: 'Agent Phone', placeholder: '(512) 000-0000' },
                          ].map(f => (
                            <div key={f.key}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                              <input value={disclosureForm[f.key]} onChange={e => setDisclosureForm(p => ({ ...p, [f.key]: e.target.value }))}
                                placeholder={f.placeholder} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
                            </div>
                          ))}
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px' }}>CC (optional)</label>
                          <input value={disclosureForm.cc_email} onChange={e => setDisclosureForm(p => ({ ...p, cc_email: e.target.value }))}
                            placeholder="contracts@godoorly.com, broker@brokerage.com"
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }} />
                          <p style={{ margin: '4px 0 0', fontSize: 10, color: '#9ca3af' }}>Confirms the request left Doorly even if the agent's gateway silently quarantines it.</p>
                        </div>
                        <button onClick={handleRequestDisclosure} disabled={!disclosureForm.listing_agent_email || disclosureLoading}
                          style={{ padding: '9px 20px', background: disclosureForm.listing_agent_email ? '#3a6ad9' : '#e5e7eb', color: disclosureForm.listing_agent_email ? '#fff' : '#9ca3af', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}>
                          {disclosureLoading ? 'Sending...' : 'Send Request'}
                        </button>
                      </div>
                    )}
                    {disclosureSentInfo && !showDisclosureForm && (
                      <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f4fee8', border: '1px solid #acf44e', borderRadius: 8, fontSize: 12, color: '#2d6a0a' }}>
                        ✓ Disclosure request sent to <strong>{disclosureSentInfo.email}</strong>
                        {disclosureSentInfo.cc && <> (cc {disclosureSentInfo.cc})</>} at {new Date(disclosureSentInfo.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {disclosureSentInfo.resendId && <div style={{ fontSize: 10, color: '#5a7a30', marginTop: 2 }}>Resend ID: <code>{disclosureSentInfo.resendId}</code></div>}
                      </div>
                    )}
                    {/* Hide the stub seller_disclosures row created by the
                        Edit Listing Agent PATCH (status='not_requested') —
                        no email has been sent yet, so showing "Disclosure
                        Requested" is misleading. The pill only renders for
                        actual requested or received states. */}
                    {disclosureData?.disclosure && disclosureData.disclosure.status !== 'not_requested' && (
                      <div style={{ padding: '12px 14px', background: disclosureData.disclosure.status === 'received' ? '#f4fee8' : '#fffbef', border: `1px solid ${disclosureData.disclosure.status === 'received' ? '#d4f99a' : '#ffe4a0'}`, borderRadius: '10px', marginBottom: disclosureData.analysis ? '12px' : '0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: disclosureData.disclosure.status === 'received' ? '#2d6a0a' : '#7a5200' }}>
                              {disclosureData.disclosure.status === 'received' ? '✓ Disclosure Received' : '⏳ Disclosure Requested'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{disclosureData.disclosure.listing_agent_email}</div>
                          </div>
                          {disclosureData.disclosure.status === 'received' && (
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                              <button
                                onClick={async () => {
                                  try {
                                    const resp = await axios.get(`${API}/api/admin/borrowers/${id}/disclosure/pdf`, { headers, responseType: 'blob' });
                                    const url = URL.createObjectURL(resp.data);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  } catch (e) {
                                    alert('Unable to open PDF: ' + (e.response?.data?.error || e.message));
                                  }
                                }}
                                style={{ padding: '7px 12px', background: '#fff', color: '#3a6ad9', border: '1.5px solid #3a6ad9', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                                View PDF
                              </button>
                              <button onClick={handleAnalyzeDisclosure} disabled={disclosureLoading}
                                style={{ padding: '7px 14px', background: disclosureLoading ? '#e5e7eb' : '#3a6ad9', color: disclosureLoading ? '#9ca3af' : '#fff', border: 'none', borderRadius: '8px', cursor: disclosureLoading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>
                                {disclosureLoading ? 'Analyzing...' : (disclosureData.analysis ? 'Re-Analyze' : 'Analyze with AI')}
                              </button>
                            </div>
                          )}
                        </div>
                        {disclosureData.disclosure.pdf_filename && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>📄</span>
                            <span style={{ fontWeight: 500 }}>{disclosureData.disclosure.pdf_filename}</span>
                            {disclosureData.disclosure.submitted_at && (
                              <span style={{ color: '#9ca3af' }}>· uploaded {new Date(disclosureData.disclosure.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {disclosureData?.analysis && (() => {
                      const flags = typeof disclosureData.analysis.flags === 'string' ? JSON.parse(disclosureData.analysis.flags || '[]') : (disclosureData.analysis.flags || []);
                      const disqualifiers = flags.filter(f => f.found && f.severity === 'disqualifier');
                      const warnings = flags.filter(f => f.found && f.severity === 'warning');
                      return (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ padding: '12px 14px', background: disqualifiers.length ? '#fff0f3' : warnings.length ? '#fffbef' : '#f4fee8', border: `1px solid ${disqualifiers.length ? '#ffc5ca' : warnings.length ? '#ffe4a0' : '#d4f99a'}`, borderRadius: '10px', marginBottom: '8px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '800', color: disqualifiers.length ? '#ff082f' : warnings.length ? '#7a5200' : '#2d6a0a', marginBottom: '4px' }}>
                              {disqualifiers.length ? 'Property Disqualified' : warnings.length ? 'Review Required' : 'Disclosure Clear'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{disclosureData.analysis.disqualifier_count} disqualifying · {disclosureData.analysis.warning_count} warning(s)</div>
                          </div>
                          {[...disqualifiers, ...warnings].map(flag => (
                            <div key={flag.key} style={{ padding: '10px 12px', background: flag.severity === 'disqualifier' ? '#fff0f3' : '#fffbef', border: `1px solid ${flag.severity === 'disqualifier' ? '#ffc5ca' : '#ffe4a0'}`, borderRadius: '8px', marginBottom: '6px' }}>
                              <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>{flag.label}</div>
                              {flag.excerpt && <div style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>"{flag.excerpt}"</div>}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  )}

                  {/* Offer Generation — legacy CollapsibleCard removed.
                      The full offer-contract surface (Manual Accept,
                      term inputs, over-max guard, AI revision, Generate
                      button, result, Send to LA, offer history,
                      Accepted Offer download, Reject/Withdraw) now
                      lives inline in Property Module Step 4 (the
                      renderStep callback above, stepNum === 4). All
                      shared state — offerTerms, handleGenerateOffer,
                      offerResult, offerComms, expandedComm, etc. — is
                      defined at the AdminBorrowerProfile component
                      level, so the move into renderStep was a pure
                      relocation with no state lifting needed. */}
                  {false && (
                  <CollapsibleCard title="Offer Contract — moved to Property Module Step 4" defaultOpen={false} badge={offerResult ? 'Generated' : null}>
                    <div style={{ marginTop: '12px' }}>
                      <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 14px 0' }}>
                        Generate a {(user.state || formData.state || 'TX').toUpperCase() === 'FL' ? 'FREC AS IS' : 'TREC 20-18'} offer contract pre-filled with borrower and property details.
                      </p>

                      {/* Manual Accept Offer — when the listing agent uses
                          their own template / PSA outside the app, admin
                          enters the accepted terms by hand here and we
                          advance the borrower as if the LA had accepted
                          through the OfferReview flow. */}
                      <div style={{ marginBottom: 14, padding: '10px 12px', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ fontSize: 11, color: '#6b7280', flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                          Listing agent used their own template / external PSA?
                          Enter the accepted terms manually.
                        </div>
                        <button
                          onClick={() => { setManualAcceptError(''); setManualAcceptOpen(true); }}
                          style={{ padding: '7px 14px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          Manually Accept Offer
                        </button>
                      </div>
                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                        {[
                          { key: 'purchase_price', label: 'Purchase Price ($)', placeholder: '285000' },
                          // EMD collected as a % of purchase price (default 1%).
                          // Backend /generate-offer computes the dollar amount
                          // via Math.round(purchase_price × emd_percent / 100).
                          { key: 'emd_percent', label: 'EMD %', placeholder: '1' },
                          { key: 'option_fee', label: 'Option Fee ($)', placeholder: '300' },
                          { key: 'option_period_days', label: 'Option Period (days)', placeholder: '7' },
                          { key: 'close_days', label: 'Close In (days)', placeholder: '20' },
                          { key: 'commission_pct', label: 'Commission (%)', placeholder: '3' },
                        ].map(({ key, label, placeholder }) => (
                          <div key={key}>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '3px' }}>{label}</label>
                            <input
                              type="text"
                              value={offerTerms[key]}
                              onChange={e => setOfferTerms(p => ({ ...p, [key]: e.target.value }))}
                              placeholder={placeholder}
                              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                            />
                          </div>
                        ))}
                      </div>
                      {/* Over-max purchase-price warning — blocks generation
                          when the entered price exceeds the borrower's max
                          approval (atlas_max_purchase_price wins; falls
                          back to max_approval_amount). Mirrors the guard
                          inside handleGenerateOffer. */}
                      {(() => {
                        const maxApproval = Number(user?.atlas_max_purchase_price || user?.max_approval_amount || 0);
                        const enteredPrice = parseFloat(offerTerms.purchase_price || 0);
                        const overMax = maxApproval > 0 && enteredPrice > maxApproval;
                        return overMax ? (
                          <div style={{
                            marginBottom: 14, padding: '8px 12px',
                            background: '#fef2f2', border: '1px solid #fca5a5',
                            borderRadius: 8, fontSize: 12, color: '#991b1b', lineHeight: 1.5,
                          }}>
                            ⚠️ Purchase price ${enteredPrice.toLocaleString()} exceeds the
                            borrower's max approval of ${maxApproval.toLocaleString()}.
                            The offer cannot be generated.
                          </div>
                        ) : null;
                      })()}
                      {/* New construction toggle — feeds is_new_construction
                          on offerTerms, forwarded to /generate-offer. Once
                          the §7.B field names are confirmed via the
                          annotated TREC_field_map_test.pdf, the generator
                          will use this to flip option (3) "Seller is not
                          required to furnish the notice". Plumbed in
                          advance so the data is live the moment that
                          wiring lands. */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '14px', color: '#374151', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={offerTerms.is_new_construction || false}
                          onChange={e => setOfferTerms(prev => ({ ...prev, is_new_construction: e.target.checked }))}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        New construction?
                      </label>
                      {/* AI Revision prompt */}
                      <div style={{ marginBottom: '14px' }}>
                        <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '3px' }}>AI Revision (optional)</label>
                        <textarea
                          value={offerTerms.ai_revision_prompt}
                          onChange={e => setOfferTerms(p => ({ ...p, ai_revision_prompt: e.target.value }))}
                          placeholder='e.g. "Increase earnest money to 2% and add 30-day leaseback"'
                          rows={2}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
                        />
                      </div>
                      {/* Generate button — disabled state mirrors the
                          over-max guard above so the button can't be
                          clicked when the price would 422 server-side
                          (or for unwaivable approval reasons). */}
                      {(() => {
                        const maxApproval = Number(user?.atlas_max_purchase_price || user?.max_approval_amount || 0);
                        const offerPriceOverMax = maxApproval > 0 &&
                          parseFloat(offerTerms.purchase_price || 0) > maxApproval;
                        const reviewBlocked = !reviewComplete;
                        const disabled = offerLoading || !offerTerms.purchase_price || offerPriceOverMax || reviewBlocked;
                        const disabledReason = reviewBlocked
                          ? 'Complete underwriting review before generating offer'
                          : offerPriceOverMax
                          ? 'Purchase price exceeds borrower max approval'
                          : !offerTerms.purchase_price
                          ? 'Enter a purchase price first'
                          : '';
                        return (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          onClick={handleGenerateOffer}
                          disabled={disabled}
                          title={disabledReason}
                          style={{
                            padding: '10px 20px',
                            background: offerLoading ? '#93c5fd' : '#3a6ad9',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: disabled ? 0.6 : 1,
                          }}
                        >
                          {offerLoading ? '⟳ Generating…' : 'Generate Offer PDF'}
                        </button>
                        {offerLoading && <span style={{ fontSize: '12px', color: '#6b7280' }}>Filling contract fields...</span>}
                        {reviewBlocked && !offerLoading && (
                          <span style={{ fontSize: '11px', color: '#991b1b', fontWeight: 600 }}>
                            ⛔ Underwriting review not complete —{' '}
                            <button type="button" onClick={() => setActiveTab('review')}
                              style={{ background: 'none', border: 'none', padding: 0, color: '#991b1b', textDecoration: 'underline', cursor: 'pointer', fontWeight: 700, fontSize: 11 }}>
                              Review tab →
                            </button>
                          </span>
                        )}
                      </div>
                        );
                      })()}
                      {/* Result */}
                      {offerResult && (
                        <div style={{ marginTop: '14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#166534' }}>
                              {offerResult.contract_type} Offer Generated
                            </span>
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>
                              {(offerResult.size_bytes / 1024).toFixed(0)} KB
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#374151', marginBottom: '10px' }}>
                            <strong>${Number(offerResult.terms?.purchase_price || 0).toLocaleString()}</strong> offer
                            {offerResult.terms?.seller_name && offerResult.terms.seller_name !== '[SELLER NAME]' && ` to ${offerResult.terms.seller_name}`}
                            {' | '}EM: ${Number(offerResult.terms?.earnest_money || 0).toLocaleString()}
                            {' | '}Close in {offerResult.terms?.close_days || 20} days
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={async () => {
                                // Fetch the protected endpoint with the admin bearer token,
                                // then open the resulting blob in a new tab. A plain <a href>
                                // can't send the Authorization header so the backend 401s.
                                try {
                                  const resp = await axios.get(`${API}${offerResult.pdf_url}`, { headers, responseType: 'blob' });
                                  const url = URL.createObjectURL(resp.data);
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (e) {
                                  alert('Unable to open PDF: ' + (e.response?.data?.error || e.message));
                                }
                              }}
                              style={{
                                padding: '8px 16px',
                                background: '#acf44e',
                                color: '#1a1a1a',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              View PDF
                            </button>
                            <button
                              onClick={openSendOfferModal}
                              style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                              Send to Listing Agent →
                            </button>
                          </div>

                          {/* One-shot confirmation after sending */}
                          {lastSentInfo && (
                            <div style={{ marginTop: 10, padding: '8px 12px', background: '#f4fee8', border: '1px solid #acf44e', borderRadius: 8, fontSize: 12, color: '#2d6a0a' }}>
                              ✓ Offer sent to <strong>{lastSentInfo.email}</strong>
                              {lastSentInfo.cc && <> (cc {lastSentInfo.cc})</>} at {new Date(lastSentInfo.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                              {lastSentInfo.resendId && <div style={{ fontSize: 10, color: '#5a7a30', marginTop: 2 }}>Resend ID: <code>{lastSentInfo.resendId}</code></div>}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Offer History — only the offer-with-PDF send rows.
                          Intro emails + Seller's Disclosure requests now
                          surface in the top-of-page Listing Agent
                          Communication Log instead. Backend
                          /offer-comms already scopes to offer_pdf, but
                          the .filter is kept as a UI-level guard so a
                          schema/back-compat drift can't leak non-offer
                          rows into this section. */}
                      {(() => {
                        const offerPdfComms = offerComms.filter(c => (c.kind || 'offer_pdf') === 'offer_pdf');
                        const visibleComms = offerHistoryExpanded ? offerPdfComms : offerPdfComms.slice(0, 1);
                        const hiddenCount = offerPdfComms.length - visibleComms.length;
                        return offerPdfComms.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Offer History{offerPdfComms.length > 1 ? ` (${offerPdfComms.length})` : ''}</div>
                            {offerPdfComms.length > 1 && (
                              <button type="button"
                                onClick={() => setOfferHistoryExpanded(v => !v)}
                                style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, fontWeight: 700, color: '#3a6ad9', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                {offerHistoryExpanded
                                  ? 'Hide older ↑'
                                  : `Show ${hiddenCount} older →`}
                              </button>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {visibleComms.map(c => {
                              // Each row carries a `kind` discriminator from
                              // the merged backend response:
                              //   offer_pdf          → the offer-with-PDF send (full lifecycle)
                              //   intro              → "Offer Coming In" auto-fire (send-only)
                              //   disclosure_request → Seller's Disclosure request (own response flow)
                              // Defaults to offer_pdf for back-compat with any
                              // older payload shape that doesn't yet include kind.
                              const kind = c.kind || 'offer_pdf';
                              const badge = (() => {
                                if (kind === 'intro') {
                                  if (c.status === 'skipped') return { label: 'No LA Email Found', bg: '#fde68a', color: '#7a5200' };
                                  return                            { label: 'Offer Coming In',   bg: '#c5d3f5', color: '#1e3a8a' };
                                }
                                if (kind === 'disclosure_request') {
                                  if (c.status === 'submitted') return { label: 'Disclosure Received', bg: '#acf44e', color: '#1a1a1a', bold: true };
                                  if (c.status === 'failed')    return { label: 'Disclosure Failed',   bg: '#fecdd3', color: '#9f1239', bold: true };
                                  if (c.status === 'pending')   return { label: 'Disclosure Pending…', bg: '#e5e7eb', color: '#374151' };
                                  return                              { label: 'Disclosure Requested', bg: '#c5d3f5', color: '#1e3a8a' };
                                }
                                // Status flow for offer_pdf: sent → viewed →
                                // comments_received → accepted → psa_routing_initiated.
                                // Every state gets its own branch so the badge
                                // never collapses a later state back to "Sent"
                                // (prior bug — the catch-all swallowed `accepted`
                                // until the psa_routing_initiated work landed).
                                if (c.status === 'psa_routing_initiated') return { label: 'PSA Sent to Sellers', bg: '#acf44e', color: '#1a1a1a', bold: true };
                                if (c.status === 'broker_info_submitted') return { label: 'Broker Info Submitted', bg: '#3a6ad9', color: '#fff' };
                                if (c.status === 'accepted')              return { label: 'Accepted',           bg: '#acf44e', color: '#1a1a1a' };
                                if (c.status === 'comments_received')     return { label: 'Comments Received',  bg: '#ffdd86', color: '#7a5200' };
                                if (c.status === 'viewed')                return { label: 'Viewed',             bg: '#ffdd86', color: '#7a5200' };
                                if (c.status === 'failed')                return { label: 'Failed',             bg: '#fecdd3', color: '#9f1239', bold: true };
                                if (c.status === 'pending')               return { label: 'Pending…',           bg: '#e5e7eb', color: '#374151' };
                                return                                          { label: 'Sent',               bg: '#c5d3f5', color: '#1e3a8a' };
                              })();
                              const expanded = expandedComm === c.id;
                              return (
                                <div key={c.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                                        {c.listing_agent_name || 'Listing Agent'}
                                        <span style={{ color: '#6b7280', fontWeight: 400 }}> · {c.listing_agent_email || '—'}</span>
                                      </div>
                                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                                        {c.status === 'failed'  ? 'Attempted'
                                          : c.status === 'skipped' ? 'Attempted (no LA email found)'
                                          : 'Sent'} {c.email_sent_at ? new Date(c.email_sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                                        {c.viewed_at && <> · Viewed {new Date(c.viewed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</>}
                                        {c.submitted_at && <> · Submitted {new Date(c.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</>}
                                        {c.cc_email && <> · CC: {c.cc_email}</>}
                                        {c.resend_email_id && <> · Resend: <code style={{ fontSize: 10 }}>{c.resend_email_id.slice(0, 12)}…</code></>}
                                      </div>
                                      {/* Offer terms snapshot — purchase
                                          price / EM / close days as
                                          stored on the comm row at send
                                          time. Older rows pre-this
                                          migration have nulls — the
                                          subtext silently omits each
                                          field with no value. */}
                                      {(c.purchase_price != null || c.earnest_money != null || c.close_days != null) && (
                                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                          {c.purchase_price != null && (
                                            <span><strong>Offer:</strong> ${Number(c.purchase_price).toLocaleString()}</span>
                                          )}
                                          {c.earnest_money != null && (
                                            <span><strong>EM:</strong> ${Number(c.earnest_money).toLocaleString()}</span>
                                          )}
                                          {c.close_days != null && (
                                            <span><strong>Close:</strong> {c.close_days} days</span>
                                          )}
                                        </div>
                                      )}
                                      {c.status === 'failed' && c.last_error && (
                                        <div style={{ marginTop: 6, padding: '6px 10px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 6, fontSize: 11, color: '#9f1239' }}>
                                          <strong>Delivery error:</strong> {c.last_error}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                      <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: 10, fontWeight: badge.bold ? 800 : 700, background: badge.bg, color: badge.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>{badge.label}</span>
                                      <EmailDeliveryPill
                                        status={c.delivery_status}
                                        bounceReason={c.bounce_reason}
                                        lastEventAt={c.delivery_last_event_at}
                                      />
                                      {c.offer_filename && (
                                        <>
                                          <button
                                            onClick={async () => {
                                              try {
                                                const resp = await axios.get(`${API}/api/admin/offers/${encodeURIComponent(c.offer_filename)}`, { headers, responseType: 'blob' });
                                                const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                                window.open(url, '_blank');
                                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                                              } catch (err) {
                                                alert('Unable to open offer PDF: ' + (err.response?.status === 404 ? 'file not found on server' : (err.response?.data?.error || err.message)));
                                              }
                                            }}
                                            style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                            View
                                          </button>
                                          <button
                                            onClick={() => handleResendOfferComm(c)}
                                            disabled={resendingCommId === c.id}
                                            title={`Re-fires this offer to ${c.listing_agent_email}${c.cc_email ? ' (cc ' + c.cc_email + ')' : ' (cc contracts@godoorly.com)'}`}
                                            style={{ padding: '4px 10px', background: resendingCommId === c.id ? '#e5e7eb' : '#fff', color: resendingCommId === c.id ? '#9ca3af' : '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: resendingCommId === c.id ? 'wait' : 'pointer' }}>
                                            {resendingCommId === c.id ? 'Resending…' : 'Resend'}
                                          </button>
                                        </>
                                      )}
                                      {c.status === 'comments_received' && (
                                        <button onClick={() => setExpandedComm(expanded ? null : c.id)}
                                          style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                          {expanded ? 'Hide' : 'View Comments'}
                                        </button>
                                      )}
                                      {/* Disclosure submitted → open the uploaded
                                          PDF via the existing admin disclosure
                                          PDF endpoint. */}
                                      {kind === 'disclosure_request' && c.submitted_at && (
                                        <button
                                          onClick={async () => {
                                            try {
                                              const resp = await axios.get(`${API}/api/admin/borrowers/${id}/disclosure/pdf`, { headers, responseType: 'blob' });
                                              const url = URL.createObjectURL(resp.data);
                                              window.open(url, '_blank');
                                              setTimeout(() => URL.revokeObjectURL(url), 60000);
                                            } catch (err) {
                                              alert('Unable to open disclosure PDF: ' + (err.response?.data?.error || err.message));
                                            }
                                          }}
                                          style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                          View PDF
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {expanded && c.comments && (
                                    <div style={{ marginTop: 10, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                      {c.comments}
                                      {c.comments_submitted_at && (
                                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8 }}>
                                          Received {new Date(c.comments_submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                      })()}

                      {/* Accepted Offer — broker-info-prefilled PDF created by
                          routeABPSAWithBrokerInfo. Available once the LA submits
                          both broker info + sellers; ops downloads it here for
                          manual e-sign routing. */}
                      {acceptedOfferAvailable && (
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Accepted Offer</div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{
                              background: '#acf44e', color: '#111827',
                              fontSize: 11, fontWeight: 700,
                              padding: '2px 8px', borderRadius: 4
                            }}>✓ Offer Accepted</span>
                            <button
                              onClick={async () => {
                                // Auth-aware download: window.open won't carry the
                                // admin bearer, so fetch the blob with axios + headers,
                                // then trigger an anchor download with the server's
                                // filename (Content-Disposition header).
                                try {
                                  const resp = await axios.get(
                                    `${API}/api/admin/borrowers/${user.id}/documents/offer_accepted`,
                                    { headers, responseType: 'blob' }
                                  );
                                  const blobUrl = URL.createObjectURL(resp.data);
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.download = `Accepted_Offer_${user.id}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                                } catch (e) {
                                  alert('Failed to download accepted offer: ' + (e.response?.data?.error || e.message));
                                }
                              }}
                              style={{
                                background: '#3a6ad9', color: '#fff',
                                border: 'none', borderRadius: 6,
                                padding: '6px 14px', fontSize: 13,
                                fontWeight: 600, cursor: 'pointer'
                              }}
                            >
                              Download Accepted Offer
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Offer Rejected / Withdrawn — visible from
                          offer_submitted through closing (anywhere a
                          withdrawal is still legitimately possible).
                          Hidden once the loan reaches clear_to_close /
                          closed / funded — at CTC the closing date is
                          locked and a unilateral withdraw doesn't make
                          sense. Outline-style danger button so it
                          doesn't overshadow Generate Offer; opens the
                          2-step rejection modal. */}
                      {(['offer_submitted', 'offer_accepted', 'option_period', 'under_contract', 'loan_docs', 'closing_disclosures', 'closing'].includes(status)) && (
                        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => { setRejectStep(1); setRejectReason(''); setRejectDetail(''); setRejectModalOpen(true); }}
                            style={{
                              padding: '8px 16px',
                              background: '#fff',
                              color: '#ff082f',
                              border: '1px solid #fecdd3',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            Reject / Withdraw Offer
                          </button>
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>
                  )}

                  {/* ── Purchase & Sale Agreements — hidden behind
                      {false &&} per Property Module rollout. PSA docs now
                      surface in the Property Documents card above; signing
                      controls live on the Closings tab. Existing handlers
                      (handleDoorlyPsaUpload, handleGenerateBorrowerPsa,
                      AbPsaInAppPanel) preserved for module use. */}
                  {false && (
                  <CollapsibleCard title="Purchase & Sale Agreements" defaultOpen={false}>
                    <div className="admin-two-col-grid" style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

                      {/* Doorly PSA (A-B) — seller ↔ Doorly, upload only */}
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Doorly PSA (A–B)</span>
                          <span style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Seller → Doorly</span>
                        </div>
                        <p style={{ margin: '0 0 10px', fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                          Upload the fully-executed A-B agreement. Replacing overwrites the prior file.
                        </p>
                        {(() => {
                          const doc = (documents || []).find(d => d.document_type === 'doorly_psa');
                          return doc ? (
                            <div style={{ padding: '8px 12px', background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: 8, fontSize: 12, color: '#2d6a0a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span>📄</span>
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <div style={{ fontWeight: 600 }}>{doc.original_name || 'Doorly PSA'}</div>
                                <div style={{ fontSize: 10, color: '#6b7280' }}>Uploaded {new Date(doc.upload_date || doc.created_at).toLocaleDateString()}</div>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    const resp = await axios.get(`${API}/api/admin/borrowers/${id}/documents/${doc.id}/view`, { headers, responseType: 'blob' });
                                    const url = URL.createObjectURL(resp.data);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  } catch (e) { alert('Unable to open PDF: ' + (e.response?.data?.error || e.message)); }
                                }}
                                style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                                View
                              </button>
                            </div>
                          ) : (
                            <div style={{ padding: '8px 12px', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
                              No Doorly PSA uploaded yet.
                            </div>
                          );
                        })()}
                        <label style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '8px 14px', background: doorlyPsaUploading ? '#e5e7eb' : '#3a6ad9',
                          color: doorlyPsaUploading ? '#9ca3af' : '#fff',
                          borderRadius: 8, cursor: doorlyPsaUploading ? 'wait' : 'pointer',
                          fontSize: 12, fontWeight: 700,
                        }}>
                          <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={doorlyPsaUploading}
                            onChange={(e) => { if (e.target.files?.[0]) handleUploadDoorlyPsa(e.target.files[0]); e.target.value = ''; }} />
                          {doorlyPsaUploading ? 'Uploading…' : ((documents || []).some(d => d.document_type === 'doorly_psa') ? 'Replace PSA' : 'Upload Doorly PSA')}
                        </label>
                        {doorlyPsaError && <div style={{ marginTop: 8, fontSize: 11, color: '#ff082f' }}>{doorlyPsaError}</div>}

                        {/* In-app A-B PSA signing — Initiate routes the
                            magic-link to sellers, then Jesse counter-signs
                            here. Lives on the Property tab (not Closings)
                            because the Closings tab only renders post-A-B
                            full-execution — admins need this BEFORE the
                            contract is executed to fire the routing flow. */}
                        <AbPsaInAppPanel
                          userId={id}
                          borrowerName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || (user?.email || '')}
                          propertyAddress={user?.property_address || ''}
                          onAfterCountersign={fetchData}
                        />
                      </div>

                      {/* Borrower PSA (B-C) — Doorly ↔ Borrower, generated */}
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Borrower PSA (B–C)</span>
                          <span style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Doorly → Borrower</span>
                        </div>
                        <p style={{ margin: '0 0 10px', fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                          Generated from the Doorly purchase price.
                        </p>
                        {(() => {
                          const op = optionPeriod;
                          const pkgStatus = op?.psa_package_status;
                          const hasBoldSign = !!op?.boldsign_document_id;
                          const signedDoc = (documents || []).find(d => d.document_type === 'borrower_psa_signed');
                          const unsignedDoc = (documents || []).find(d => d.document_type === 'borrower_psa');
                          const pill = (() => {
                            if (signedDoc || pkgStatus === 'completed') return { label: 'Fully Executed ✓', bg: '#acf44e', color: '#1a1a1a' };
                            if (pkgStatus === 'declined' || pkgStatus === 'expired' || pkgStatus === 'revoked') {
                              return { label: pkgStatus.charAt(0).toUpperCase() + pkgStatus.slice(1), bg: '#fef2f2', color: '#ff082f' };
                            }
                            if (hasBoldSign || pkgStatus === 'sent') return { label: 'Sent for Signature', bg: '#eff6ff', color: '#3a6ad9' };
                            if (unsignedDoc) return { label: 'Generated (Unsigned)', bg: '#fff7ed', color: '#7a5200' };
                            return null;
                          })();
                          const renderDocRow = (doc, label, isSigned) => (
                            <div style={{ padding: '8px 12px', background: isSigned ? '#f4fee8' : '#f9fafb', border: `1px solid ${isSigned ? '#d4f99a' : '#e5e7eb'}`, borderRadius: 8, fontSize: 12, color: isSigned ? '#2d6a0a' : '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span>📄</span>
                              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <div style={{ fontWeight: 600 }}>{label}</div>
                                <div style={{ fontSize: 10, color: '#6b7280' }}>{isSigned ? 'Signed' : 'Generated'} {new Date(doc.upload_date || doc.created_at).toLocaleDateString()}</div>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    const resp = await axios.get(`${API}/api/admin/borrowers/${id}/documents/${doc.id}/view`, { headers, responseType: 'blob' });
                                    const url = URL.createObjectURL(resp.data);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  } catch (e) { alert('Unable to open PDF: ' + (e.response?.data?.error || e.message)); }
                                }}
                                style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                                View
                              </button>
                            </div>
                          );
                          return (
                            <>
                              {pill && (
                                <div style={{ marginBottom: 8 }}>
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: pill.bg, color: pill.color, letterSpacing: '0.3px' }}>{pill.label}</span>
                                </div>
                              )}
                              {signedDoc && renderDocRow(signedDoc, 'Borrower PSA (Signed)', true)}
                              {unsignedDoc && renderDocRow(unsignedDoc, signedDoc ? 'Original (Unsigned)' : (unsignedDoc.original_name || 'Borrower PSA'), false)}
                              {!signedDoc && !unsignedDoc && (
                                <div style={{ padding: '8px 12px', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 8, fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
                                  Borrower PSA not generated yet.
                                </div>
                              )}
                            </>
                          );
                        })()}
                        <button onClick={openBorrowerPsaModal}
                          style={{
                            padding: '8px 14px', background: '#3a6ad9', color: '#fff',
                            border: 'none', borderRadius: 8, cursor: 'pointer',
                            fontSize: 12, fontWeight: 700,
                          }}>
                          {(documents || []).some(d => d.document_type === 'borrower_psa') ? 'Regenerate Borrower PSA' : 'Generate Borrower PSA'}
                        </button>
                      </div>
                    </div>
                  </CollapsibleCard>
                  )}

                  {/* ── Real Broker (reZEN) — hidden behind {false &&}
                      per Property Module rollout. The new reZEN
                      Transaction Status card at the top of the Property
                      tab covers the green/red/amber state + Retry CTA.
                      handleManualReZENCreate preserved for the Retry
                      button on that status card. */}
                  {false && (
                  <CollapsibleCard title="Real Broker (reZEN)" defaultOpen={false}
                    badge={user?.rezen_transaction_id ? 'Created' : null}>
                    <div style={{ padding: '12px 0' }}>
                      {user?.rezen_transaction_id ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#16a34a', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                            ✓ Transaction Created
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                            reZEN ID: <span style={{ fontFamily: 'monospace', color: '#111827' }}>{user.rezen_transaction_id}</span>
                          </div>
                          {user.rezen_transaction_created_at && (
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                              Created: {new Date(user.rezen_transaction_created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </div>
                          )}
                          {user.rezen_upload_status && user.rezen_upload_status !== 'uploaded' && (
                            <div style={{ fontSize: 12, color: '#7a5200', marginBottom: 4 }}>
                              PSA upload: {user.rezen_upload_status} — admin may need to attach manually in reZEN.
                            </div>
                          )}
                          <a
                            href={`https://bolt.therealbrokerage.com/transactions/${user.rezen_transaction_id}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 12, color: '#3a6ad9', textDecoration: 'none', display: 'inline-block', marginTop: 6, fontWeight: 600 }}>
                            View in reZEN →
                          </a>
                        </div>
                      ) : ['option_period', 'closing', 'clear_to_close', 'closed', 'funded'].includes(status) ? (
                        <div>
                          <div style={{ fontSize: 13, color: '#ff082f', fontWeight: 700, marginBottom: 8 }}>
                            ⚠ Not submitted to reZEN
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                            A-B PSA must be submitted to Real Broker within 48 hours of execution.
                          </div>
                          <button
                            onClick={handleManualReZENCreate}
                            disabled={creatingReZEN}
                            style={{
                              background: creatingReZEN ? '#e5e7eb' : '#111111',
                              color: creatingReZEN ? '#9ca3af' : '#fff',
                              border: 'none', borderRadius: 6, padding: '8px 14px',
                              fontSize: 13, fontWeight: 600,
                              cursor: creatingReZEN ? 'not-allowed' : 'pointer',
                            }}>
                            {creatingReZEN ? 'Creating...' : 'Create reZEN Transaction'}
                          </button>
                          {rezenError && (
                            <div style={{ marginTop: 8, fontSize: 11, color: '#ff082f', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                              {rezenError}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
                          reZEN transaction will be created automatically when the A-B PSA is fully executed.
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>
                  )}

                  {/* Key Dates moved to Date Tracking tab */}
                  {/* Option Period Status + EMD Status cards moved to Closings tab */}
                  {/* Property Documents — removed; use the Documents tab's Property filter instead */}
                </>
              )}

              {/* Property module notes — same thread as the Property Modules
                  dashboard card. */}
              <div style={card}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Notes</h3>
                <ModuleNotes moduleType="property" borrowerId={id} />
              </div>
            </div>
          )}

          {/* ── TAB: Date Tracking ─────────────────────────────────────── */}
          {activeTab === 'date_tracking' && (() => {
            // CST-safe date helpers
            const parseDate = (v) => { if (!v) return ''; const s = typeof v === 'string' ? v : new Date(v).toISOString(); return s.slice(0, 10); };
            const fmtDate = (v) => {
              if (!v) return null;
              const s = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().split('T')[0];
              const [y, m, d] = s.split('-').map(Number);
              return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            };
            const daysUntil = (v) => {
              if (!v) return null;
              const s = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().split('T')[0];
              const [y, m, d] = s.split('-').map(Number);
              const target = new Date(y, m - 1, d);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              return Math.ceil((target - today) / 86400000);
            };
            // Date math helpers removed — auto-calculation done server-side

            const DateRow = ({ label, field, highlight, auto, manual }) => {
              const val = user[field];
              const isEditing = editingDateField === field;
              const formatted = fmtDate(val);
              const days = daysUntil(val);
              const isToday = days === 0;
              const isPast = days !== null && days < 0;
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '8px', background: isEditing ? '#eff6ff' : highlight && !val ? '#fffbeb' : '#f9fafb', border: `1px solid ${isEditing ? '#bfdbfe' : '#f3f4f6'}`, marginBottom: '6px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>{label}</span>
                      {auto && <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb' }}>AUTO</span>}
                      {manual && <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', background: '#eff6ff', color: '#3a6ad9', border: '1px solid #bfdbfe' }}>MANUAL</span>}
                      {highlight && !val && <span style={{ fontSize: '9px', background: '#fef3c7', color: '#7a5200', padding: '1px 5px', borderRadius: '4px', fontWeight: '700' }}>Required</span>}
                    </div>
                    {auto && <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{auto}</div>}
                  </div>
                  {isEditing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="date" value={dateInputVal} onChange={e => setDateInputVal(e.target.value)} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveDate(field, dateInputVal); if (e.key === 'Escape') setEditingDateField(null); }}
                        style={{ padding: '5px 8px', border: '1.5px solid #3a6ad9', borderRadius: '6px', fontSize: '13px', outline: 'none' }} />
                      <button onClick={() => handleSaveDate(field, dateInputVal)} disabled={savingDate}
                        style={{ padding: '4px 10px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>{savingDate ? '...' : 'Save'}</button>
                      <button onClick={() => setEditingDateField(null)} type="button" style={{ padding: '4px 8px', background: '#f3f4f6', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {formatted ? (
                        <>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{formatted}</span>
                          {isToday && <span style={{ fontSize: '10px', background: '#acf44e', color: '#1a1a1a', padding: '1px 6px', borderRadius: '3px', fontWeight: '700' }}>Today</span>}
                          {!isPast && !isToday && days <= 7 && <span style={{ fontSize: '10px', background: '#fff7ed', color: '#c2410c', padding: '1px 6px', borderRadius: '3px', fontWeight: '600' }}>{days}d</span>}
                          {isPast && <span style={{ fontSize: '10px', color: '#9ca3af' }}>Passed</span>}
                        </>
                      ) : <span style={{ fontSize: '13px', color: '#d1d5db' }}>—</span>}
                      <button onClick={() => { setDateInputVal(parseDate(val)); setEditingDateField(field); }} type="button"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '13px', padding: '1px 3px' }} title={`Edit ${label}`}>✏️</button>
                    </div>
                  )}
                </div>
              );
            };
            const Section = ({ title, children, note, badge }) => (
              <div style={{ ...card, marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: note ? '4px' : '14px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827' }}>{title}</h4>
                  {badge && <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '9999px', background: '#fff0f3', color: '#ff082f' }}>{badge}</span>}
                </div>
                {note && <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#9ca3af' }}>{note}</p>}
                {children}
              </div>
            );

            // Auto-calculate helper for Contract Date button
            const handleAutoCalc = async () => {
              const contractVal = parseDate(user.date_contract);
              if (!contractVal) { alert('Set a Contract Date first.'); return; }
              setSavingDate(true);
              try {
                const closingVal = parseDate(user.date_closing);
                const payload = {
                  date_contract: contractVal,
                  date_closing: closingVal || undefined,
                  auto_calculate: true,
                };
                await axios.patch(`${API}/api/admin/borrowers/${id}/dates`, payload, { headers });
                fetchData();
              } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
              finally { setSavingDate(false); }
            };

            return (
              <div>
                {/* Auto-calculate banner */}
                <div style={{ ...card, marginBottom: '16px', background: '#eff6ff', borderColor: '#bfdbfe' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#2854c0' }}>Auto-Calculate Dates</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Set Contract Date, then click to auto-fill all derived dates. You can override any field individually.</div>
                    </div>
                    <button onClick={handleAutoCalc} disabled={savingDate || !user.date_contract} type="button"
                      style={{ padding: '8px 16px', background: user.date_contract ? '#3a6ad9' : '#e5e7eb', color: user.date_contract ? '#fff' : '#9ca3af', border: 'none', borderRadius: '8px', cursor: user.date_contract ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      {savingDate ? 'Calculating...' : 'Recalculate All'}
                    </button>
                  </div>
                </div>

                {/* ── Contract Dates ──
                    Option Period End is filtered out when the loan is
                    new construction — NC loans skip the option period
                    entirely (builder warranty replaces inspection-period
                    remedies; no third-party seller to renegotiate with),
                    so date_option_period_end is NULL on the backend and
                    the row would just render "—" which is misleading.
                    NC detection prefers offerRequest.is_new_construction
                    (canonical) and falls back to optionPeriod's mirror. */}
                {(() => {
                  const isNC = !!(offerRequest?.is_new_construction || optionPeriod?.is_new_construction);
                  const rows = [
                    { label: 'Contract / PSA Date', field: 'date_contract', highlight: true, auto: 'Auto: Set when A-B PSA is fully executed' },
                    { label: 'EMD Due Date (A-B PSA) / Down Payment Due Date (B-C PSA)', field: 'date_emd_due', highlight: true, auto: 'Auto: Contract + 2 days' },
                    { label: 'Inspection & Appraisal Deadline', field: 'date_appraisal_ordered', auto: 'Auto: Contract + 3 days' },
                    { label: 'Option Period End', field: 'date_option_period_end', highlight: true, auto: 'Auto: Contract + 7 days', skipIfNC: true },
                    { label: 'Funding Request Deadline', field: 'date_funding_request_deadline', auto: 'Auto: Closing − 3 business days (excl. weekends + holidays)' },
                    { label: 'Closing Date', field: 'date_closing', highlight: true, manual: true, auto: 'Auto: Contract + 20 days (editable)' },
                  ].filter(r => !(isNC && r.skipIfNC));
                  return (
                    <Section title="Contract Dates" note={isNC
                      ? 'Primary dates — set Contract Date first, derived dates auto-calculate. New construction: option period skipped.'
                      : 'Primary dates — set Contract Date first, derived dates auto-calculate'}>
                      {rows.map(p => {
                        const { skipIfNC, ...rowProps } = p;
                        return <DateRow key={p.field} {...rowProps} />;
                      })}
                    </Section>
                  );
                })()}

                {/* ── Inspection & Appraisal Schedule ──
                    Display-only. The Inspectify webhook
                    (POST /api/webhooks/inspectify) is the canonical
                    writer of option_periods.{inspection,appraisal}_-
                    scheduled_at; the legacy manual override path
                    (PATCH /api/admin/listing-agent-schedule) was
                    removed from this surface. The schedule listing-
                    agent-email auto-fires happen on the WEBHOOK,
                    not on a save here, so removing the input doesn't
                    remove the LA notification. */}
                {optionPeriod && (
                  <div style={{ ...card, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>Inspection & Appraisal Schedule</h4>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5' }}>Inspectify webhook</span>
                    </div>
                    <ScheduleRow label="Inspection Appointment" currentValue={optionPeriod.inspection_scheduled_at} source={optionPeriod.inspection_scheduled_source} />
                    <ScheduleRow label="Appraisal Appointment"  currentValue={optionPeriod.appraisal_scheduled_at}  source={optionPeriod.appraisal_scheduled_source} />
                  </div>
                )}

                {/* ── Loan Milestones (system-generated, read-only) ── */}
                {/* Loan Milestones — read from CANONICAL event sources,
                    not the legacy users.date_le_x / date_cd_x columns.
                    Those columns were either never populated
                    (date_le_received, date_cd_received) or written as
                    a PROJECTION (date_cd_issued = closing minus 4
                    calendar days via dateUtils.calculateDerivedDates,
                    NOT the actual CD issuance event). The card now
                    binds each row to the real event timestamp:
                      LE Issued     -> users.le_delivered_at
                      LE Accepted   -> users.le_intent_to_proceed_at
                      CD Issued     -> docmagic_documents[cd].generated_at
                      CD Accepted   -> closing_disclosures.cd_signed_at
                                       (with docmagic primary_signed_at fallback)
                    Single source of truth per milestone. */}
                <div style={{ ...card, marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827' }}>Loan Milestones</h4>
                    <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '6px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5' }}>System Generated</span>
                  </div>
                  <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#9ca3af' }}>These dates are set automatically when milestones are triggered. They cannot be edited manually.</p>
                  {[
                    { id: 'le_issued',   label: 'Loan Estimate Issued',      note: 'Set when LE is delivered to borrower',              value: user?.le_delivered_at },
                    { id: 'le_received', label: 'Loan Estimate Accepted',    note: 'Set when borrower signs Intent to Proceed on LE',  value: user?.le_intent_to_proceed_at },
                    { id: 'cd_issued',   label: 'Closing Disclosure Issued', note: 'Set when the CD package is generated',        value: data?.docmagic_cd?.generated_at },
                    { id: 'cd_received', label: 'Closing Disclosure Accepted', note: 'Set when borrower signs the CD',                  value: closingDisclosure?.cd_signed_at || data?.docmagic_cd?.primary_signed_at },
                    // 🔑 THE RATE HOLD'S END (fp-next item 1 — utils/approvalValidity.syncRateLockDate). Staff only: the
                    // Closing Disclosure (H-25) has no rate-lock field. A date column, shown as that calendar date.
                    { id: 'rate_lock_expires', label: 'Rate Lock Expires', note: 'The rate hold: acceptance + 60 days, capped by the credit report age. Not printed on the CD (the form has no rate-lock field)', value: user?.date_rate_lock_expiration, dateOnly: true },
                  ].map(m => {
                    const formatted = !m.value ? null
                      : m.dateOnly ? new Date(`${String(m.value).slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })
                      : new Date(m.value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                    return (
                      <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '8px', background: '#fafafa', border: '1px solid #f3f4f6', marginBottom: '6px', opacity: m.value ? 1 : 0.6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '13px', color: '#374151', fontWeight: '600' }}>{m.label}</span>
                            <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5' }}>SYSTEM</span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{m.note}</div>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: formatted ? '700' : '400', color: formatted ? '#111827' : '#d1d5db', fontStyle: formatted ? 'normal' : 'italic' }}>
                          {formatted || 'Not yet triggered'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Option Period Documents card removed — see the
                    leUrlDraft/psaUrlDraft state comment up top. LE is
                    delivered via the offer-accepted overlay; PSA is
                    signed via the native e-sign canvas flow
                    (PSASigningModal). No admin URL-entry surface
                    is needed. */}

              </div>
            );
          })()}

          {/* ── TAB 5: Activity ─────────────────────────────────────────── */}
          {activeTab === 'activity' && (
            <div style={card}>
              <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '16px' }}>Activity Timeline</h3>
              {/* Merge events + audit logs */}
              {[
                ...events.map((e) => ({ ...e, _type: 'event', ts: e.created_at })),
                ...data.auditLogs.map((a) => ({ ...a, _type: 'audit', ts: a.created_at, description: a.action_description, actor: a.admin_name ? `Admin: ${a.admin_name}` : 'System', event_type: a.action_type })),
                ...(documents || []).map((d) => ({ _type: 'doc', ts: d.upload_date, description: `Document uploaded: ${d.original_name || d.document_type}`, actor: 'Borrower', event_type: 'document_uploaded' })),
                ...(data.conversations || []).flatMap((c) => [{ _type: 'msg', ts: c.created_at, description: `Message thread started: ${c.subject}`, actor: 'Borrower', event_type: 'message' }]),
              ]
                .filter((e) => e.ts)
                .sort((a, b) => new Date(b.ts) - new Date(a.ts))
                .slice(0, 50)
                .map((e, i) => {
                  const iconMap = { status_change: '🔄', document_uploaded: '📄', message: '💬', flag_added: '🚩', dates_updated: '📅', audit: '📋', event: '•' };
                  const colorMap = { status_change: '#3a6ad9', document_uploaded: '#acf44e', message: '#3a6ad9', flag_added: '#ff082f', dates_updated: '#d97706' };
                  const icon = iconMap[e.event_type] || '•';
                  const color = colorMap[e.event_type] || '#9ca3af';
                  return (
                    <div key={i} style={{ display: 'flex', gap: '12px', paddingBottom: '12px', borderBottom: '1px solid #f9fafb', marginBottom: '12px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '9999px', backgroundColor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>{icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', color: '#111827' }}>{e.description}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          {e.actor || 'System'} · <span title={new Date(e.ts).toLocaleString()}>{relTime(e.ts)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              }
              {events.length === 0 && data.auditLogs.length === 0 && <p style={{ fontSize: '13px', color: '#9ca3af' }}>No activity yet.</p>}
            </div>
          )}

          {/* ── TAB 6: Communication ────────────────────────────────────── */}
          {activeTab === 'communication' && (() => {
            // Joint-file thread state — pick the active recipient by
            // activeMsgOwner (defaults to primary). When there's no co-
            // borrower on the file, the selector hides and the tab
            // behaves exactly like the single-borrower flow it used to.
            const activeRecipient = activeMsgOwner === 'coborrower' && coBorrower
              ? coBorrower
              : user;
            // ── 🚨 UNREAD IS SUMMED ACROSS A BORROWER'S THREADS ─────────────
            //
            // Jesse: "the Communication tab shows two unread that won't clear
            // even after I open the tab."
            //
            // 🔑 TWO BUGS MET HERE. This read `.find(...)?.unread` — the FIRST
            // thread's count — so Cathy's other threads, holding four and one
            // unread, were never represented at all. And nothing refetched the
            // profile payload after the tab marked a thread read, so the count
            // it did show was the pre-read one and sat there.
            //
            // ⚠️ SUMMED, AND THREADS OPENED THIS SESSION SUBTRACTED. Refetching
            // from the loading effect would loop — it depends on `data` — so the
            // correction is applied here from convReadLocally, which only records
            // a thread after its GET succeeded and therefore after the server
            // marked it read.
            const unreadFor = (ownerType) => (conversations || [])
              .filter(c => c.owner_type === ownerType)
              .reduce((sum, c) => sum
                + (convReadLocally.includes(c.id) ? 0 : Number(c.unread || 0)), 0);
            const activeUnread  = unreadFor(activeMsgOwner);
            const primaryUnread = unreadFor('primary');
            const coBorrowerUnread = unreadFor('coborrower');
            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Primary / Co-Borrower thread selector — joint files only.
                  Mirrors the chip pattern used in other Review/Documents
                  cards. Unread badges on each pill so admin can see at a
                  glance which thread has new messages. */}
              {coBorrower && (
                <div style={{ display: 'flex', gap: 8, background: '#f3f4f6', padding: 4, borderRadius: 10, width: 'fit-content' }}>
                  {[
                    { id: 'primary', label: `Primary · ${user.first_name}`, unread: primaryUnread },
                    { id: 'coborrower', label: `Co-Borrower · ${coBorrower.first_name}`, unread: coBorrowerUnread },
                  ].map(opt => {
                    const active = activeMsgOwner === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => { setActiveMsgOwner(opt.id); setActiveConvId(null); setShowCompose(false); }}
                        style={{
                          padding: '7px 14px', borderRadius: 8, border: 'none',
                          background: active ? '#3a6ad9' : 'transparent',
                          color: active ? '#fff' : '#374151',
                          fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        {opt.label}
                        {opt.unread > 0 && (
                          <span style={{
                            background: active ? '#fff' : '#dc2626',
                            color:      active ? '#3a6ad9' : '#fff',
                            fontSize: 10, fontWeight: 800,
                            padding: '1px 6px', borderRadius: 999,
                          }}>
                            {opt.unread}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ── Send Message compose ── */}
              <div>
                {!showCompose ? (
                  <button
                    onClick={() => setShowCompose(true)}
                    style={{ padding: '10px 20px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    ✉️ Send Message to {activeRecipient.first_name}
                  </button>
                ) : (
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2854c0' }}>New Message to {activeRecipient.first_name}</h4>
                      <button onClick={() => setShowCompose(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '18px', lineHeight: 1 }}>✕</button>
                    </div>
                    {/* 🔑 THE ONE STAFF COMPOSER (fp-format): toolbar, preview and attachment, as in the inbox. */}
                    <StaffComposer
                      key={activeMsgOwner}
                      token={token}
                      onSend={handleSendMessage}
                      testId="profile-new-message-composer"
                      rows={4}
                      autoFocus
                      placeholder={`Write a message to ${activeRecipient.first_name}… (⌘↵ to send)`}
                      sendLabel={messageSending ? 'Sending…' : 'Send Message'}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>Appears in {activeRecipient.first_name}&rsquo;s portal</span>
                      <button onClick={() => setShowCompose(false)} style={{ padding: '7px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                    </div>
                  </div>
                )}
                {messageSent && (
                  <div style={{ marginTop: '8px', padding: '8px 14px', background: '#acf44e', color: '#1a1a1a', borderRadius: '6px', fontSize: '13px', fontWeight: '600', display: 'inline-block' }}>
                    ✓ Message sent to {activeRecipient.first_name}
                  </div>
                )}
              </div>

              {/* Messages */}
              <div style={card}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                  Messages
                  {coBorrower && (
                    <span style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 600,
                      color: activeMsgOwner === 'coborrower' ? '#9333ea' : '#3a6ad9',
                    }}>
                      — {activeRecipient.first_name} {activeRecipient.last_name}
                      {activeUnread > 0 && ` · ${activeUnread} unread`}
                    </span>
                  )}
                </h3>
                {/* ── 🚨 EVERY THREAD THIS BORROWER HAS, NOT JUST THE NEWEST ──
                    Jesse: "I can't scroll up in the messages thread to view
                    older messages."

                    🔑 THERE WAS NOTHING TO SCROLL TO. Cathy has three
                    conversations — #88 "Support" with 2 messages, #91 "Document
                    Upload Help" with 7, #89 with 1 — and the page rendered only
                    the most recent, so eight of her ten messages had no route to
                    the screen and five unread sat in threads that could not be
                    opened or marked read.

                    ⚠️ ONLY SHOWN WHEN THERE IS MORE THAN ONE. A file with a
                    single thread looks exactly as it did. */}
                {(() => {
                  const ownerConvs = (conversations || [])
                    .filter(c => c.owner_type === activeMsgOwner);
                  if (ownerConvs.length < 2) return null;
                  const selectedId = String(
                    ownerConvs.find(c => String(c.id) === String(activeConvId))?.id
                    ?? ownerConvs[0].id);
                  return (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                        {ownerConvs.length} threads
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {ownerConvs.map((c) => {
                          const sel = String(c.id) === selectedId;
                          const unread = convReadLocally.includes(c.id) ? 0 : Number(c.unread || 0);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => { setActiveConvId(c.id); setShowCompose(false); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                                padding: '7px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                                background: sel ? '#eef2ff' : '#fff',
                                border: `1px solid ${sel ? '#6366f1' : '#e5e7eb'}`,
                                fontWeight: sel ? 700 : 500,
                                color: '#111827',
                              }}
                            >
                              <span style={{ flex: 1 }}>{c.subject || '(no subject)'}</span>
                              {unread > 0 && (
                                <span style={{
                                  fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 9999,
                                  background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5',
                                }}>{unread} unread</span>
                              )}
                              <span style={{ fontSize: 10, color: '#9ca3af' }}>
                                {c.last_message_at
                                  ? new Date(c.last_message_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                  : ''}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {conversations.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#9ca3af' }}>No message thread yet.</p>
                ) : convLoading ? (
                  <p style={{ fontSize: '13px', color: '#9ca3af' }}>Loading messages…</p>
                ) : (
                  <>
                    {/* Message history */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px 12px 4px 12px', marginBottom: '16px' }}>
                      {convMessages.map((msg, i) => {
                        // isEva must be checked FIRST and kept out of isAdmin:
                        // the old two-way form put Eva on the borrower side, so
                        // staff reviewing the file would attribute her answer to
                        // the borrower. She is outbound, but she is not staff.
                        const isEva = msg.sender_role === 'eva';
                        const isAdmin = (msg.sender_role === 'support' || msg.sender_role === 'admin') || isEva;
                        // sender_first_name / sender_role_internal come
                        // from the LEFT JOIN added on the admin GET
                        // endpoint — fall back to "Doorly Team" for
                        // legacy rows missing sender_id attribution.
                        // Eva must NOT fall through to the 'Doorly Team' default:
                        // her rows have NULL sender_* (the JOIN is ON
                        // sender_role='support'), so without this she would be
                        // labelled as the team — a human byline on an AI answer.
                        const senderName = isEva
                          ? 'Eva · AI assistant'
                          : (msg.sender_first_name
                              ? `${msg.sender_first_name} ${msg.sender_last_name || ''}`.trim()
                              : 'Doorly Team');
                        const isREDeskSender = !isEva && msg.sender_role_internal === 'contract_desk';
                        const isLOSender     = !isEva && msg.sender_role_internal === 'loan_officer';
                        const roleSuffix     = isREDeskSender ? ' — Real Estate Desk'
                                             : isLOSender     ? ' — Loan Officer'
                                             : '';
                        // Attachment handling. Borrower-facing Messages.js
                        // sends body=`[Attachment: foo.png]` as a placeholder
                        // when there's no caption. Detection is defensive
                        // across deploy versions:
                        //   - new backend  → has_attachment boolean
                        //   - legacy rows  → attachment_url set (has_attachment true server-side)
                        //   - body parse   → body matches [Attachment: …]
                        // Whatever signal we get, render *something* useful
                        // instead of falling through to the raw placeholder.
                        const bodyTrim = (msg.body || '').trim();
                        const placeholderMatch = bodyTrim.match(/^\[Attachment:\s*(.+?)\]$/);
                        const placeholderName = placeholderMatch ? placeholderMatch[1] : null;
                        const inferredName    = msg.attachment_name || placeholderName;
                        const hasAttachment   = !!(msg.has_attachment || msg.attachment_url || placeholderName) && !!inferredName;
                        // Attachments (has_attachment) are served by <MessageAttachment>
                        // via a short-lived signed URL — the staff JWT never goes in
                        // the URL. 🚨 NO STATIC HREF (Jesse, 2026-10-28): the public
                        // file mount is closed; placeholder-only → "no longer available".
                        // Hide the [Attachment: foo.png] placeholder body
                        // any time we have an attachment signal, so we
                        // don't render both the text AND the preview.
                        const placeholderBody = hasAttachment && /^\[Attachment:\s.*\]$/.test(bodyTrim);
                        return (
                          <div key={i} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                            <div style={{ maxWidth: '70%', padding: '9px 13px', borderRadius: isAdmin ? '14px 14px 4px 14px' : '14px 14px 14px 4px', backgroundColor: isEva ? '#eef3fd' : isAdmin ? '#3a6ad9' : '#f3f4f6', color: (isAdmin && !isEva) ? '#fff' : '#111827', fontSize: '13px' }}>
                              {isAdmin && (
                                <div style={{ fontSize: '10px', marginBottom: '3px', opacity: 0.85, fontWeight: 600 }}>
                                  {senderName}{roleSuffix}
                                </div>
                              )}
                              {hasRichBody(msg) ? (
                                <div style={{ marginBottom: hasAttachment ? 6 : 0 }}><RichMessageBody msg={msg} dark={isAdmin && !isEva} /></div>
                              ) : msg.body && !placeholderBody && (
                                <p style={{ margin: 0, marginBottom: hasAttachment ? 6 : 0 }}>{msg.body}</p>
                              )}
                              {hasAttachment && msg.has_attachment && (
                                <MessageAttachment msgId={msg.id} name={inferredName} token={token} dark={isAdmin} />
                              )}
                              {hasAttachment && !msg.has_attachment && (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontStyle: 'italic',
                                              padding: '4px 8px', borderRadius: 6,
                                              background: isAdmin ? 'rgba(255,255,255,0.15)' : '#fef3c7',
                                              color: isAdmin ? '#fff' : '#92400e' }}>
                                  📎 {inferredName} — no longer available
                                </div>
                              )}
                              <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7, textAlign: isAdmin ? 'right' : 'left' }}>{relTime(msg.created_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={msgEndRef} />
                    </div>
                    {/* Reply compose at bottom */}
                    <div style={{ paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                      <StaffComposer key={`${activeMsgOwner}-${activeConvId || ''}`} token={token} onSend={sendReply} testId="profile-reply-composer"
                        placeholder="Type a message… (⌘↵ to send)" sendLabel="Send" />
                    </div>
                  </>
                )}
              </div>

              {/* Email Log */}
              <div style={card}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                  Email Log {emailLog.length > 0 && <span style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af' }}>({emailLog.length})</span>}
                </h3>
                {emailLog.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#9ca3af' }}>No emails sent to this borrower yet.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '500px' }}>
                      <thead><tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {['Subject', 'To', 'From', 'Date', 'Status'].map((h) => <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#6b7280', fontWeight: '600', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {emailLog.map((row) => (
                          <tr key={row.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                            <td style={{ padding: '8px 10px', color: '#111827', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.subject}</td>
                            <td style={{ padding: '8px 10px', color: '#6b7280' }}>{row.to_email}</td>
                            <td style={{ padding: '8px 10px', color: '#6b7280', fontSize: '11px' }}>{(row.from_email || '').replace(/<.*>/, '').trim() || 'Doorly'}</td>
                            <td style={{ padding: '8px 10px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{row.sent_at ? new Date(row.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' }) + ' CT' : '—'}</td>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', backgroundColor: row.status === 'sent' ? '#f0fde4' : '#fff0f3', color: row.status === 'sent' ? '#2d6a0a' : '#ff082f' }}>
                                {row.status === 'sent' ? 'Sent' : row.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* SMS Log (mock) */}
              <div style={card}>
                <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>SMS Log</h3>
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>SMS integration coming soon.</p>
              </div>

              {/* Quick Actions */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {user.phone && <a href={`tel:${user.phone}`} style={{ ...btnStyle, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>📞 Call {user.first_name}</a>}
              </div>
            </div>
            );
          })()}

          {/* ── TAB: Documents ──────────────────────────────────────────── */}
          {activeTab === 'documents' && (
            <DocumentsTab documents={allDocuments} borrowerId={id} user={data?.user} closingDisclosure={data?.closingDisclosure} isREDesk={isREDesk} onSwitchTab={(t) => setActiveTab(t)} housingStatus={formData.housing_status || data?.user?.housing_status || null} onUploadComplete={() => {
              const coId = data?.loanConnection?.coborrower_id;
              const primaryName = `${data?.user?.first_name || ''} ${data?.user?.last_name || ''}`.trim();
              const coName = data?.coBorrower ? `${data.coBorrower.first_name || ''} ${data.coBorrower.last_name || ''}`.trim() : '';
              Promise.all([
                axios.get(`${API}/api/admin/borrowers/${id}/documents`, { headers }),
                coId ? axios.get(`${API}/api/admin/borrowers/${coId}/documents`, { headers }).catch(() => ({ data: { documents: [] } })) : Promise.resolve({ data: { documents: [] } }),
              ]).then(([pRes, cRes]) => {
                const pDocs = (pRes.data.documents || []).map(d => ({ ...d, owner_type: 'primary', owner_name: primaryName }));
                const cDocs = (cRes.data.documents || []).map(d => ({ ...d, owner_type: 'coborrower', owner_name: coName }));
                setAllDocuments([...pDocs, ...cDocs]);
              }).catch(() => {});
            }}
            onMarkAllReviewed={async () => {
              try {
                await axios.post(`${API}/api/admin/borrowers/${id}/documents/mark-reviewed`, {}, { headers });
                // Clear the tab badge immediately; refresh docs so rows reflect
                // the new reviewed status.
                setTabCounts(t => ({ ...t, documentReview: 0 }));
                await fetchData();
              } catch (_) { /* swallow — admin can retry */ }
            }} />
          )}

        </div>

      </div>

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      {modal === 'move' && (
        <MoveStageModal currentStatus={status} userId={id} token={token} onClose={() => setModal(null)}
          onMoved={(newStatus) => { setModal(null); setData((d) => ({ ...d, user: { ...d.user, application_status: newStatus, status_updated_at: new Date().toISOString() } })); }} />
      )}
      {modal === 'docs' && (
        <RequestDocsModal userId={id} token={token} onClose={() => setModal(null)} onRequested={() => { setModal(null); fetchData(); }} />
      )}

      {/* Manual Accept — read-only details modal. Opens from the
          "View Details →" button on Property Module Step 5 when the
          offer was accepted via the manual-accept path. Renders the
          comms row we stored at acceptance time (sellers + LA contact
          + terms + notes) so admins can audit what was captured
          without paging through the full LA-comms history. */}
      {acceptedDetailsRow && (() => {
        let sellers = [];
        try {
          sellers = Array.isArray(acceptedDetailsRow.sellers_json)
            ? acceptedDetailsRow.sellers_json
            : (typeof acceptedDetailsRow.sellers_json === 'string'
                ? JSON.parse(acceptedDetailsRow.sellers_json)
                : []);
        } catch (_) { sellers = []; }
        const acceptedAt = acceptedDetailsRow.accepted_at
          ? new Date(acceptedDetailsRow.accepted_at).toLocaleString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
            })
          : '—';
        const fieldRow = (label, value) => (
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
            <div style={{ fontSize: 13, color: '#111827' }}>{value || '—'}</div>
          </div>
        );
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={() => setAcceptedDetailsRow(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.6)' }} />
            <div style={{ position: 'relative', zIndex: 10001, background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.22)' }}>
              <div style={{ background: '#16a34a', padding: '18px 22px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>✓ Manually Accepted Offer</h3>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>Accepted {acceptedAt}</p>
                </div>
                <button onClick={() => setAcceptedDetailsRow(null)}
                  style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: '8px 22px 18px', overflowY: 'auto' }}>
                {fieldRow('A-B Purchase Price', acceptedDetailsRow.purchase_price != null
                  ? '$' + Number(acceptedDetailsRow.purchase_price).toLocaleString()
                  : null)}
                {fieldRow('Earnest Money', acceptedDetailsRow.earnest_money != null
                  ? '$' + Number(acceptedDetailsRow.earnest_money).toLocaleString()
                  : null)}
                {fieldRow('Close In', acceptedDetailsRow.close_days != null
                  ? `${acceptedDetailsRow.close_days} days`
                  : null)}
                {fieldRow('Listing Agent', [
                  acceptedDetailsRow.listing_agent_name,
                  acceptedDetailsRow.listing_agent_email,
                ].filter(Boolean).join(' · ') || null)}
                {fieldRow('Sellers', sellers.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {sellers.map((s, i) => (
                      <div key={i}>
                        <span style={{ fontWeight: 600 }}>{s?.name || '—'}</span>
                        {s?.email && <span style={{ color: '#6b7280' }}> · {s.email}</span>}
                      </div>
                    ))}
                  </div>
                ) : null)}
                {acceptedDetailsRow.comments && fieldRow('Notes', (
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{acceptedDetailsRow.comments}</div>
                ))}
              </div>
              <div style={{ padding: '12px 22px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setAcceptedDetailsRow(null)}
                  style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Manual Accept Offer modal — collects accepted terms when the
          listing agent worked outside our OfferReview flow. Submit calls
          handleManualAcceptOffer → POST /manual-accept-offer. */}
      {manualAcceptOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={() => !manualAcceptLoading && setManualAcceptOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.6)' }} />
          <div style={{ position: 'relative', zIndex: 10001, background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.22)' }}>
            <div style={{ background: '#3a6ad9', padding: '18px 22px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Manually Accept Offer</h3>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>Use when the listing agent accepted on their own template / external PSA. These terms become the canonical A-B offer.</p>
              </div>
              <button onClick={() => !manualAcceptLoading && setManualAcceptOpen(false)}
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', width: 28, height: 28, borderRadius: 6, cursor: manualAcceptLoading ? 'not-allowed' : 'pointer', fontSize: 16 }}>×</button>
            </div>
            <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
              {manualAcceptError && (
                <div style={{ marginBottom: 14, padding: '10px 12px', background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13 }}>{manualAcceptError}</div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Property Address</label>
                <input type="text" value={manualAcceptForm.propertyAddress}
                  onChange={(e) => setManualAcceptForm(p => ({ ...p, propertyAddress: e.target.value }))}
                  placeholder="Street, City, TX ZIP — required if the file has no showing address"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>A-B Purchase Price ($) *</label>
                  <input type="number" value={manualAcceptForm.abPurchasePrice}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, abPurchasePrice: e.target.value }))}
                    placeholder="285000"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Option Period (days)</label>
                  <input type="number" value={manualAcceptForm.optionPeriodDays}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, optionPeriodDays: e.target.value }))}
                    placeholder="7"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Effective Date *</label>
                  <input type="date" value={manualAcceptForm.effectiveDate}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, effectiveDate: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Closing Date *</label>
                  <input type="date" value={manualAcceptForm.closingDate}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, closingDate: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Earnest Money ($)</label>
                  <input type="number" value={manualAcceptForm.emdAmount}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, emdAmount: e.target.value }))}
                    placeholder="2850"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Option Fee ($)</label>
                  <input type="number" value={manualAcceptForm.optionFee}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, optionFee: e.target.value }))}
                    placeholder="300"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>HOA Dues per Month ($, optional)</label>
                <input type="number" min="0" value={manualAcceptForm.hoaMonthly}
                  onChange={(e) => setManualAcceptForm(p => ({ ...p, hoaMonthly: e.target.value }))}
                  placeholder="From the listing. 0 if no HOA; blank if not known"
                  data-testid="manual-accept-hoa-monthly"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Listing Agent (optional)</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input type="text" value={manualAcceptForm.listingAgentName}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, listingAgentName: e.target.value }))}
                    placeholder="Listing Agent Name"
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                  <input type="email" value={manualAcceptForm.listingAgentEmail}
                    onChange={(e) => setManualAcceptForm(p => ({ ...p, listingAgentEmail: e.target.value }))}
                    placeholder="agent@brokerage.com"
                    style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sellers * (1-4 — 1st two go in TREC blocks)</div>
                  {manualAcceptForm.sellers.length < 4 && (
                    <button type="button"
                      onClick={() => setManualAcceptForm(p => ({ ...p, sellers: [...p.sellers, { name: '', email: '' }] }))}
                      style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      + Add Seller
                    </button>
                  )}
                </div>
                {manualAcceptForm.sellers.map((s, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                    <input type="text" value={s.name}
                      onChange={(e) => setManualAcceptForm(p => ({ ...p, sellers: p.sellers.map((row, idx) => idx === i ? { ...row, name: e.target.value } : row) }))}
                      placeholder={`Seller ${i + 1} name`}
                      style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                    <input type="email" value={s.email}
                      onChange={(e) => setManualAcceptForm(p => ({ ...p, sellers: p.sellers.map((row, idx) => idx === i ? { ...row, email: e.target.value } : row) }))}
                      placeholder="seller@example.com"
                      style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                    {manualAcceptForm.sellers.length > 1 ? (
                      <button type="button"
                        onClick={() => setManualAcceptForm(p => ({ ...p, sellers: p.sellers.filter((_, idx) => idx !== i) }))}
                        style={{ padding: '0 10px', background: '#fff', color: '#9f1239', border: '1px solid #fecaca', borderRadius: 7, fontSize: 16, cursor: 'pointer' }}>×</button>
                    ) : <div />}
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 6 }}>
                <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Admin Notes (optional)</label>
                <textarea value={manualAcceptForm.notes}
                  onChange={(e) => setManualAcceptForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Listing agent used Texas REALTORS PSA template; sent via DocuSign."
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 10, alignItems: 'center', background: '#f9fafb' }}>
              <button onClick={() => setManualAcceptOpen(false)} disabled={manualAcceptLoading}
                style={{ padding: '9px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: manualAcceptLoading ? 'not-allowed' : 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleManualAcceptOffer} disabled={manualAcceptLoading}
                style={{ marginLeft: 'auto', padding: '10px 22px', background: manualAcceptLoading ? '#bfdbfe' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: manualAcceptLoading ? 'not-allowed' : 'pointer' }}>
                {manualAcceptLoading ? 'Accepting…' : 'Manually Accept Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Offer to Listing Agent modal */}
      {sendOfferModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={() => !sendOfferLoading && setSendOfferModalOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.6)' }} />
          <div style={{ position: 'relative', zIndex: 10001, background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, boxShadow: '0 20px 50px rgba(0,0,0,0.22)', overflow: 'hidden' }}>
            <div style={{ background: '#3a6ad9', padding: '18px 22px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Send Offer to Listing Agent</h3>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>Emails a secure review link for the listing agent to view the offer and submit comments.</p>
              </div>
              <button onClick={() => !sendOfferLoading && setSendOfferModalOpen(false)}
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', width: 28, height: 28, borderRadius: 6, cursor: sendOfferLoading ? 'not-allowed' : 'pointer', fontSize: 16 }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {sendOfferError && (
                <div style={{ marginBottom: 14, padding: '10px 12px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>
                  {sendOfferError}
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Listing Agent Name</label>
                <input type="text" value={sendOfferForm.listing_agent_name}
                  onChange={(e) => setSendOfferForm((p) => ({ ...p, listing_agent_name: e.target.value }))}
                  placeholder="Jane Smith"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Listing Agent Email *</label>
                <input type="email" value={sendOfferForm.listing_agent_email}
                  onChange={(e) => setSendOfferForm((p) => ({ ...p, listing_agent_email: e.target.value }))}
                  placeholder="agent@brokerage.com"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>CC (optional)</label>
                <input type="text" value={sendOfferForm.cc_email}
                  onChange={(e) => setSendOfferForm((p) => ({ ...p, cc_email: e.target.value }))}
                  placeholder="contracts@godoorly.com, broker@brokerage.com"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                <p style={{ margin: '4px 0 0', fontSize: 10, color: '#9ca3af' }}>Confirms the email left Doorly even if the listing agent's gateway silently quarantines it. Comma- or semicolon-separated for multiple.</p>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Offer File</label>
                <input type="text" value={offerResult?.file_name || '—'} readOnly
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, color: '#6b7280', background: '#f9fafb', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Comments to Listing Agent (optional)</label>
                <textarea value={sendOfferForm.comments_to_la}
                  onChange={(e) => setSendOfferForm((p) => ({ ...p, comments_to_la: e.target.value }))}
                  placeholder="e.g. Looking forward to working together. Borrower is flexible on closing date — happy to accommodate."
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <p style={{ margin: 0, fontSize: 10, color: '#9ca3af', flex: 1, minWidth: 0 }}>
                    Surfaced in the LA's email and on the offer-review page as "Comments from Doorly".
                  </p>
                  <button type="button" onClick={handleImproveLaComments}
                    disabled={improvingLaComments || !sendOfferForm.comments_to_la?.trim()}
                    title={!sendOfferForm.comments_to_la?.trim() ? 'Write a draft first' : 'Rewrite for clarity + professionalism'}
                    style={{
                      padding: '5px 12px', fontSize: 11, fontWeight: 700,
                      borderRadius: 6,
                      background: (improvingLaComments || !sendOfferForm.comments_to_la?.trim()) ? '#f3f4f6' : '#fff',
                      color: (improvingLaComments || !sendOfferForm.comments_to_la?.trim()) ? '#9ca3af' : '#3a6ad9',
                      border: `1px solid ${(improvingLaComments || !sendOfferForm.comments_to_la?.trim()) ? '#e5e7eb' : '#3a6ad9'}`,
                      cursor: (improvingLaComments || !sendOfferForm.comments_to_la?.trim()) ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}>
                    {improvingLaComments ? '⟳ Improving…' : '✨ Improve with AI'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setSendOfferModalOpen(false)} disabled={sendOfferLoading}
                  style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: sendOfferLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Cancel
                </button>
                <button onClick={handleSendOffer} disabled={sendOfferLoading || !sendOfferForm.listing_agent_email}
                  style={{ padding: '8px 18px', background: sendOfferLoading || !sendOfferForm.listing_agent_email ? '#e5e7eb' : '#3a6ad9', color: sendOfferLoading || !sendOfferForm.listing_agent_email ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, cursor: sendOfferLoading || !sendOfferForm.listing_agent_email ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                  {sendOfferLoading ? 'Sending…' : 'Send Offer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Borrower PSA generation modal */}
      {psaModalOpen && (() => {
        const dpp = Number(psaForm.doorly_purchase_price) || 0;
        const borrowerPP = dpp > 0 ? Math.round(dpp) : 0;   // D3: no resale adjustment
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={() => !borrowerPsaLoading && setPsaModalOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.6)' }} />
            <div style={{ position: 'relative', zIndex: 10001, background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, boxShadow: '0 20px 50px rgba(0,0,0,0.22)', overflow: 'hidden' }}>
              <div style={{ background: '#3a6ad9', padding: '18px 22px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Generate Borrower PSA (B–C)</h3>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>Doorly → {user?.first_name} {user?.last_name}</p>
                </div>
                <button onClick={() => !borrowerPsaLoading && setPsaModalOpen(false)}
                  style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', width: 28, height: 28, borderRadius: 6, cursor: borrowerPsaLoading ? 'not-allowed' : 'pointer', fontSize: 16 }}>×</button>
              </div>
              <div style={{ padding: 20 }}>
                {borrowerPsaResult ? (
                  <>
                    <div style={{ padding: '14px 16px', background: '#f4fee8', border: '1px solid #d4f99a', borderRadius: 10, marginBottom: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#2d6a0a', marginBottom: 4 }}>✓ Generated</div>
                      <div style={{ fontSize: 12, color: '#374151' }}>
                        Borrower purchase price: <strong>${borrowerPsaResult.borrower_purchase_price.toLocaleString()}</strong>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={async () => {
                          try {
                            const resp = await axios.get(`${API}${borrowerPsaResult.pdf_url}`, { headers, responseType: 'blob' });
                            const url = URL.createObjectURL(resp.data);
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          } catch (e) { alert('Unable to open PDF: ' + (e.response?.data?.error || e.message)); }
                        }}
                        style={{ padding: '8px 14px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        View PDF
                      </button>
                      <button onClick={() => setPsaModalOpen(false)}
                        style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        Done
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {borrowerPsaError && (
                      <div style={{ marginBottom: 14, padding: '10px 12px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: 8, fontSize: 12, color: '#ff082f' }}>
                        {borrowerPsaError}
                      </div>
                    )}
                    <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>Doorly Purchase Price</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: '#6b7280', fontSize: 13 }}>$</span>
                          <input type="text" value={psaForm.doorly_purchase_price}
                            onChange={(e) => setPsaForm((p) => ({ ...p, doorly_purchase_price: e.target.value.replace(/[^0-9]/g, '') }))}
                            style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: '12px 14px', background: '#f0f4ff', border: '1px solid #c5d3f5', borderRadius: 10, marginBottom: 14 }}>
                      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Borrower Purchase Price</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: '#3a6ad9' }}>
                        {borrowerPP > 0 ? '$' + borrowerPP.toLocaleString() : '—'}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                        The Doorly purchase price (no resale adjustment)
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => setPsaModalOpen(false)} disabled={borrowerPsaLoading}
                        style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: borrowerPsaLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600 }}>
                        Cancel
                      </button>
                      <button onClick={handleGenerateBorrowerPsa} disabled={borrowerPsaLoading || borrowerPP <= 0}
                        style={{ padding: '8px 16px', background: borrowerPsaLoading || borrowerPP <= 0 ? '#e5e7eb' : '#3a6ad9', color: borrowerPsaLoading || borrowerPP <= 0 ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, cursor: borrowerPsaLoading || borrowerPP <= 0 ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                        {borrowerPsaLoading ? 'Generating…' : 'Generate'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Document viewer modal */}
      {showDocModal && viewingDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflowX: 'hidden' }}
          onClick={() => setShowDocModal(false)}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', maxWidth: '860px', width: '100%', maxHeight: '88vh', overflow: 'auto', boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>{viewingDoc.original_name || viewingDoc.file_name || viewingDoc.document_type || 'Document'}</h3>
                {viewingDoc.upload_date && <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>Uploaded {new Date(viewingDoc.upload_date).toLocaleDateString()}</p>}
              </div>
              <button onClick={() => setShowDocModal(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>
            {/\.pdf$/i.test(viewingDoc.file_url) ? (
              <iframe src={viewingDoc.file_url} width="100%" height="520px" title="Document" style={{ border: 'none', borderRadius: '8px' }} />
            ) : /\.(jpg|jpeg|png|gif|webp)$/i.test(viewingDoc.file_url) ? (
              <img src={viewingDoc.file_url} alt={viewingDoc.original_name || 'Document'} style={{ maxWidth: '100%', borderRadius: '8px' }} />
            ) : (
              <div style={{ padding: '32px', textAlign: 'center', background: '#f9fafb', borderRadius: '8px', color: '#6b7280', fontSize: '13px' }}>
                Preview not available for this file type.<br />
                <a href={viewingDoc.file_url} target="_blank" rel="noreferrer" style={{ color: '#3a6ad9', fontWeight: '600' }}>Open in new tab ↗</a>
              </div>
            )}
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <a href={viewingDoc.file_url} target="_blank" rel="noreferrer"
                style={{ padding: '8px 16px', background: '#3a6ad9', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>
                Open / Download ↗
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Notes Slide-Out Panel ─────────────────────────────────────────── */}
      {showNotesPanel && (
        <>
          <div onClick={() => setShowNotesPanel(false)} style={{ position: 'fixed', inset: 0, zIndex: 200 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '380px', maxWidth: 'calc(100vw - 24px)', background: '#ffffff', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)', zIndex: 201, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {data && <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Notes — {data.user.first_name} {data.user.last_name}</h3>}
              <button onClick={() => setShowNotesPanel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ padding: '16px', borderBottom: '1px solid #f3f4f6' }}>
              <textarea
                value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder="Add a note..." rows={3}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAddNote(); }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input type="checkbox" checked={pinNote} onChange={e => setPinNote(e.target.checked)} />
                  Pin
                </label>
                <button onClick={handleAddNote} disabled={!newNote.trim() || notesSaving}
                  style={{ padding: '6px 14px', background: newNote.trim() ? '#3a6ad9' : '#d1d5db', color: '#fff', border: 'none', borderRadius: '6px', cursor: newNote.trim() ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600' }}>
                  {notesSaving ? 'Saving…' : 'Add Note'}
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              {notes.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No notes yet.</p>
              ) : notes.map(note => (
                <div key={note.id} style={{ background: note.is_pinned ? '#fefce8' : '#f9fafb', border: note.is_pinned ? '1px solid #fde68a' : '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
                  {note.is_pinned && <span style={{ fontSize: '11px', color: '#d97706', fontWeight: '600', display: 'block', marginBottom: '4px' }}>📌 Pinned</span>}
                  <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#111827', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{note.note}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>{note.created_by} · {new Date(note.created_at).toLocaleDateString()}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => handlePinToggle(note)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#6b7280' }}>{note.is_pinned ? 'Unpin' : 'Pin'}</button>
                      <button onClick={() => handleDeleteNote(note.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#ff082f' }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {/* Offer Rejected / Withdrawn — 2-step modal */}
      {rejectModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
             onClick={() => !rejectSubmitting && setRejectModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 500, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
            {rejectStep === 1 && (
              <>
                <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Reject or Withdraw Offer</h3>
                <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                  Select the reason for rejecting or withdrawing this offer.
                </p>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reason</label>
                <select value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}>
                  <option value="">Select a reason…</option>
                  <option value="Listing agent accepted another offer">Listing agent accepted another offer</option>
                  <option value="Seller rejected our offer">Seller rejected our offer</option>
                  <option value="Offer withdrawn by Doorly — pricing">Offer withdrawn by Doorly — pricing</option>
                  <option value="Offer withdrawn by Doorly — borrower request">Offer withdrawn by Doorly — borrower request</option>
                  <option value="Other">Other</option>
                </select>
                {rejectReason === 'Other' && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Please describe</label>
                    <textarea value={rejectDetail} onChange={(e) => setRejectDetail(e.target.value)} rows={3}
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                  <button onClick={() => setRejectModalOpen(false)}
                          style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={() => setRejectStep(2)} disabled={!rejectReason || (rejectReason === 'Other' && !rejectDetail.trim())}
                          style={{ padding: '8px 16px', background: (!rejectReason || (rejectReason === 'Other' && !rejectDetail.trim())) ? '#cbd5e1' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (!rejectReason || (rejectReason === 'Other' && !rejectDetail.trim())) ? 'not-allowed' : 'pointer' }}>Next →</button>
                </div>
              </>
            )}
            {rejectStep === 2 && (
              <>
                <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#ff082f' }}>Confirm — this action cannot be undone</h3>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>This will:</p>
                <ul style={{ margin: '0 0 14px', paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                  <li>Reset the loan file to <strong>Home Shopping</strong> status</li>
                  <li>Send the borrower a notification about the offer outcome</li>
                  <li>Log this action on the borrower's file</li>
                  <li>Waive the next $500 offer fee</li>
                </ul>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#374151', marginBottom: 18 }}>
                  <strong>Reason:</strong> {rejectReason}{rejectDetail ? ` — ${rejectDetail}` : ''}
                </div>
                <button onClick={async () => {
                  setRejectSubmitting(true);
                  try {
                    await axios.post(`${API}/api/admin/borrowers/${id}/offer-rejected`,
                      { reason: rejectReason, reasonDetail: rejectReason === 'Other' ? rejectDetail : null },
                      { headers });
                    setRejectModalOpen(false);
                    await fetchData();
                  } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
                  setRejectSubmitting(false);
                }} disabled={rejectSubmitting}
                  style={{ width: '100%', padding: '10px 16px', background: rejectSubmitting ? '#fca5a5' : '#ff082f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 800, cursor: rejectSubmitting ? 'wait' : 'pointer', marginBottom: 8 }}>
                  {rejectSubmitting ? 'Submitting…' : 'Confirm — Reject/Withdraw Offer'}
                </button>
                <button onClick={() => setRejectStep(1)} disabled={rejectSubmitting}
                        style={{ width: '100%', padding: '8px 16px', background: 'none', color: '#6b7280', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Archive Confirm */}
      {showArchiveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '420px', width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '17px', fontWeight: '700' }}>📦 Archive Borrower?</h3>
            <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 6px', lineHeight: '1.6' }}>
              Are you sure you want to archive <strong>{user.first_name} {user.last_name}</strong>?
            </p>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 24px' }}>
              This borrower will be removed from the active pipeline. Their data is preserved and can be viewed in the archived files section.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowArchiveConfirm(false)} style={{ flex: 1, padding: '10px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Cancel</button>
              <button onClick={handleArchiveBorrower} style={{ flex: 1, padding: '10px', background: '#374151', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>Yes, Archive</button>
            </div>
          </div>
        </div>
      )}

      {/* Adverse Action Modal */}
      {showAdverseModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '560px', width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>Application Off-Ramp</h3>
              <button onClick={() => setShowAdverseModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Applicant</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{user.first_name} {user.last_name}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.email} · {user.loan_number}</div>
            </div>
            {/* Mode toggle — cure and deny are separate Reg B instruments */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              <button onClick={() => setAdverseMode('cure')} style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${adverseMode === 'cure' ? '#3a6ad9' : '#e5e7eb'}`, background: adverseMode === 'cure' ? '#eff6ff' : '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#111827' }}>Request missing items (cure)</button>
              <button onClick={() => setAdverseMode('deny')} style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${adverseMode === 'deny' ? '#ff082f' : '#e5e7eb'}`, background: adverseMode === 'deny' ? '#fef2f2' : '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#111827' }}>Deny (adverse action)</button>
            </div>
            {adverseMode === 'cure' && (
              <div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 12, color: '#1e3a8a' }}>
                  {/* ── 🚨 THIS SAID "Borrower email is currently OFF" AS A FACT ──────
                      🚨 IT WAS A STATIC STRING ASSERTING A SERVER CONFIG STATE. The
                      send is gated by INCOMPLETENESS_SEND_ENABLED in Render, and the
                      moment that is switched on this sentence tells staff the
                      opposite of what the button will do — the worst direction for a
                      control that decides whether a borrower receives a regulated
                      notice.

                      🔑 SO IT NAMES THE MECHANISM AND DEFERS TO THE ANSWER. The route
                      returns email_sent for the send it just made, and the
                      confirmation below reports that. A sentence that cannot be
                      wrong beats a sentence that is currently right. */}
                  Notice of Incompleteness (Reg B §1002.9(c)) — <strong>not a denial</strong>. Lists the missing items, gives the borrower a cure window, and keeps them in the funnel. The PDF is always stored and ops is always alerted; whether the borrower is <em>emailed</em> is controlled server-side (INCOMPLETENESS_SEND_ENABLED) and the confirmation after you submit says which happened.
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Missing items *</div>
                {cureItems.map((it, i) => (
                  <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <input value={it.title} placeholder="Item (e.g. Two most recent pay stubs)" onChange={e => setCureItems(arr => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 6, boxSizing: 'border-box' }} />
                    <textarea value={it.description} placeholder="Detail shown to the borrower (optional)" onChange={e => setCureItems(arr => arr.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, minHeight: 44, boxSizing: 'border-box' }} />
                    {/* 🚨 REQUIRED, WITH NO DEFAULT. The section decides which
                        screen the borrower satisfies the item on; an unchosen one
                        is refused by the server rather than guessed at. */}
                    <select
                      value={it.section}
                      onChange={e => setCureItems(arr => arr.map((x, j) => j === i ? { ...x, section: e.target.value } : x))}
                      style={{ width: '100%', padding: '8px', border: `1px solid ${it.section ? '#d1d5db' : '#fca5a5'}`, borderRadius: 6, fontSize: 12.5, marginTop: 6, boxSizing: 'border-box', background: it.section ? '#fff' : '#fff1f2', color: '#111827' }}
                    >
                      <option value="">Which section? (required)</option>
                      {cureSections.map((sec) => (
                        <option key={sec.key} value={sec.key}>{sec.label} — {sec.hint}</option>
                      ))}
                    </select>
                    {cureItems.length > 1 && (
                      <button onClick={() => setCureItems(arr => arr.filter((_, j) => j !== i))} style={{ marginTop: 6, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setCureItems(arr => [...arr, { title: '', description: '', section: '' }])} style={{ background: 'none', border: '1px dashed #9ca3af', borderRadius: 8, padding: '8px', width: '100%', cursor: 'pointer', fontSize: 13, color: '#374151', marginBottom: 16 }}>+ Add item</button>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setShowAdverseModal(false)} style={{ flex: 1, padding: '11px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>Cancel</button>
                  <button onClick={handleSubmitCure} disabled={cureSubmitting} style={{ flex: 2, padding: '11px', background: cureSubmitting ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, cursor: cureSubmitting ? 'wait' : 'pointer', fontWeight: 700, fontSize: 14 }}>{cureSubmitting ? 'Preparing…' : 'Prepare Notice of Incompleteness'}</button>
                </div>
              </div>
            )}
            {adverseMode === 'deny' && (<>
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', fontSize: '12px', color: '#7a5200' }}>
              This form records an adverse action per ECOA (Reg B) and FCRA requirements. For an incomplete application, use "Request missing items (cure)" instead — a denial is a different instrument.
            </div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Step 1 — Select Adverse Action Reasons (ECOA/FCRA codes) *</div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '10px' }}>Check every reason that applies. At least one is required.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {ADVERSE_REASON_CATALOG.map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#374151', padding: '8px 10px', background: adverseForm[key] ? '#eff6ff' : '#f9fafb', borderRadius: '8px', border: `1px solid ${adverseForm[key] ? '#bfdbfe' : '#e5e7eb'}` }}>
                    <input type="checkbox" checked={adverseForm[key]} onChange={e => setAdverseForm(p => ({ ...p, [key]: e.target.checked }))} style={{ marginTop: '1px', flexShrink: 0 }} />
                    {label}
                  </label>
                ))}
              </div>
              {adverseForm.reason_other && (
                <textarea value={adverseForm.reason_other_text} onChange={e => setAdverseForm(p => ({ ...p, reason_other_text: e.target.value }))}
                  placeholder="Written explanation for 'Other' (required)..." rows={3}
                  style={{ width: '100%', marginTop: '8px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              )}
            </div>

            <div style={{ marginBottom: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>Step 2 — Confirm Adverse Action Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px', fontSize: '13px', color: '#374151', marginBottom: '12px' }}>
                <div style={{ color: '#6b7280' }}>Applicant</div>
                <div>{user?.first_name} {user?.last_name}</div>
                <div style={{ color: '#6b7280' }}>Application Date</div>
                <div>{user?.date_application_submitted ? new Date(user.date_application_submitted).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : (user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—')}</div>
                <div style={{ color: '#6b7280' }}>Action Date</div>
                <div>{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (today)</div>
                <div style={{ color: '#6b7280' }}>Loan Number</div>
                <div>{user?.loan_number || '—'}</div>
              </div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Notification Method</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['Mail', 'Email', 'Both'].map((val) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', padding: '7px 12px', borderRadius: '8px', border: `2px solid ${adverseForm.notification_method === val ? '#3a6ad9' : '#e5e7eb'}`, background: adverseForm.notification_method === val ? '#eff6ff' : '#fff' }}>
                    <input type="radio" name="adv_method" value={val} checked={adverseForm.notification_method === val} onChange={() => setAdverseForm(p => ({ ...p, notification_method: val }))} />
                    {val}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>Step 3 — Review Notice Preview</div>
                <button onClick={() => setAdverseForm(p => ({ ...p, showPreview: !p.showPreview }))}
                  style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', color: '#3a6ad9' }}>
                  {adverseForm.showPreview ? 'Hide' : 'Show'} preview
                </button>
              </div>
              {adverseForm.showPreview && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px 16px', fontSize: '12px', color: '#374151', lineHeight: 1.6, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap', maxHeight: '320px', overflow: 'auto' }}>
{`NOTICE OF ADVERSE ACTION

Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
Applicant: ${user?.first_name || ''} ${user?.last_name || ''}
Application Number: ${user?.loan_number || '—'}
Lender: Go Doorly, LLC | NMLS #2764749 | 8708 S Congress Ave, Ste E500-202, Austin, TX 78745

We have carefully reviewed your application for a mortgage loan and regret to inform you that we are unable to approve your application at this time.

REASON(S) FOR THIS DECISION:
${adverseSelectedReasons.map(r => `  • ${r}`).join('\n') || '  (no reasons selected yet)'}${adverseHasOther && adverseForm.reason_other_text.trim() ? `\n  • Other — ${adverseForm.reason_other_text.trim()}` : ''}

YOUR RIGHT TO KNOW THE REASONS:
You have the right to receive a written statement of the specific reason(s) for this decision. To obtain the written statement, please contact us within 60 days of receiving this notice at:

  Go Doorly, LLC
  8708 S Congress Ave, Ste E500-202
  Austin, TX 78745
  support@godoorly.com

NOTICE OF RIGHTS UNDER THE EQUAL CREDIT OPPORTUNITY ACT (ECOA):
The federal Equal Credit Opportunity Act prohibits creditors from discriminating against credit applicants on the basis of race, color, religion, national origin, sex, marital status, age (provided the applicant has the capacity to enter into a binding contract); because all or part of the applicant's income derives from any public assistance program; or because the applicant has in good faith exercised any right under the Consumer Credit Protection Act.

The federal agency that administers compliance with this law concerning this creditor is:
  Federal Trade Commission
  Consumer Response Center
  600 Pennsylvania Ave NW
  Washington, DC 20580${adverseForm.reason_credit ? `

NOTICE OF RIGHTS UNDER THE FAIR CREDIT REPORTING ACT (FCRA):
Our decision was based in whole or in part on information obtained from a consumer reporting agency. The consumer reporting agency did not make this decision and cannot explain why the decision was made. You have the right to obtain a free copy of your credit report from the consumer reporting agency within 60 days of receiving this notice. You also have the right to dispute the accuracy or completeness of any information in your credit report.

Credit Reporting Agency Used:
  ${user?.fico_score_source || 'Xactus'}` : ''}`}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowAdverseModal(false)} style={{ flex: 1, padding: '11px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Cancel</button>
              <button onClick={handleSubmitAdverse}
                disabled={!adverseHasAnyReason}
                style={{ flex: 2, padding: '11px', background: adverseHasAnyReason ? '#ff082f' : '#fca5a5', color: '#fff', border: 'none', borderRadius: '8px', cursor: adverseHasAnyReason ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '14px' }}>
                Confirm &amp; Send
              </button>
            </div>
            </>)}
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditLogModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflowX: 'hidden' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '680px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '700' }}>📋 Audit Log</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{user.first_name} {user.last_name} · {user.loan_number}</p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleExportAuditCSV} style={{ padding: '7px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Export CSV</button>
                <button onClick={() => setShowAuditLogModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {auditLogEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No audit log entries yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0 }}>
                    {['Timestamp', 'Event', 'Description', 'Actor'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {auditLogEntries.map((e, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(e.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td style={{ padding: '10px 14px' }}><span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 7px', borderRadius: '4px', background: '#f3f4f6', color: '#374151' }}>{(e.event_type || '').replace(/_/g, ' ')}</span></td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', color: '#374151' }}>{e.description}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{e.actor || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
